import React, { useState, useEffect, useRef, useCallback } from 'react';
import { appDialog } from '../utils/appDialog';
import { Calendar, DollarSign, Users, Award, Camera, Settings, CheckCircle, XCircle, Clock, Eye, Plus, Filter, Calculator, TrendingUp, Image, Video, FileText, MapPin, GraduationCap, Gift, X, Building2, Edit, Crown, Trophy, Star, Maximize2, Minimize2, RotateCcw, RefreshCw, ChevronLeft, ChevronRight, Radio, QrCode, Tag, Ticket, ArrowRight, Trash2, Link2 } from 'lucide-react';
import { Menu } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CreateContestForm, { CreateContestFormHandle } from '../components/create_contest_form';
import CreateSponsorshipForm from '../components/CreateSponsorshipForm';
import ParticipantTypeModal from '../components/ParticipantTypeModal';
import SimpleModal from '../components/SimpleModal';
import S3UploadModal from '../components/S3UploadModal';
import DeleteTagsModal from '../components/DeleteTagsModal';
import ProposalShowcase from '../components/ProposalShowcase';
import { uploadImageToS3 } from '../services/s3UploadService';
import contestService from '../services/contestService';
import sponsorshipService from '../services/sponsorshipService';
import participantService from '../services/participantService';
import couponService from '../services/couponService';
import petflixIntegrationService from '../services/petflixIntegrationService';
import ambassadorService from '../services/ambassadorService';
import voteBoosterService from '../services/voteBoosterService';
import importService from '../services/importService';
import { useI18n, t } from '../i18n';
import { environment, routes } from '../config/environment';
import { getImageUrlFromMedia, getCompleteImagePath } from '../utils/imageUtils';
import { getApiHeaders, getApiHeadersForGet, fetchCouponPassQRLinksBulk } from '../utils/apiUtils';
import { contestMatchesSearch } from '../utils/contestSearchUtils';
import { buildContestTokenQrLink, buildContestCouponQrLink } from '../utils/contestCouponQrLink';
import { loadCouponDashboardData } from '../utils/couponDashboardData';
import { attachParticipantCountsBulk } from '../utils/contestParticipantCounts';
import { attachProposalSummariesBulk } from '../utils/contestProposalSummaries';
import { getContestAvailability } from '../utils/contestAvailability';
import {
  extractCostDescriptionFromPaylinkResponse,
  extractInvoiceNoFromPaylinkResponse,
  extractPaylinkFromResponse,
  normalizeSponsorshipTypes,
  resolveSponsorBillToEmail,
} from '../utils/sponsorshipTypesUtils';
import {
  calculateTotalPrizePool,
  formatPercent,
  getDynamicPrizesTotal,
  getGrandPrizeSharePercent,
  sumGrandPrizePlaces,
  sumSecondaryPrizePool,
} from '../utils/prizeUtils';
import OptimizedImage from '../components/OptimizedImage';

import { getPetflixAccessToken, setPetflixAccessToken } from '../config/petflixAuth';
import firebaseAuthService from '../services/firebaseAuthService';
import petflixAuthService from '../services/petflixAuthService';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle, ImageRun, SectionType } from 'docx';
import { saveAs } from 'file-saver';
// PDF generation moved to server to avoid React version conflicts
// Chart.js removido debido a problemas de compatibilidad con React 18 en modales
// Usamos gráficos simples con HTML/CSS nativo

// Componente para mostrar gráficos simples usando HTML/CSS
const SimpleBarChart: React.FC<{ data: { label: string; value: number; maxValue: number }[] }> = ({ data }) => {
  const maxValue = Math.max(...data.map(d => d.value), 1);

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {data.map((item, index) => (
        <Box key={index} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{item.label}</Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>${item.value.toFixed(2)}</Typography>
          </Box>
          <Box sx={{
            width: '100%',
            height: 30,
            backgroundColor: '#e5e7eb',
            borderRadius: 1,
            overflow: 'hidden',
            position: 'relative'
          }}>
            <Box sx={{
              width: `${(item.value / maxValue) * 100}%`,
              height: '100%',
              backgroundColor: '#2563eb',
              transition: 'width 0.5s ease',
            }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const SimplePieChart: React.FC<{ data: { label: string; value: number; color: string }[] }> = ({ data }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {data.map((item, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Box sx={{
            width: 16,
            height: 16,
            backgroundColor: item.color,
            borderRadius: '50%',
            mr: 1.5
          }} />
          <Typography variant="body2" sx={{ flex: 1 }}>{item.label}</Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', mr: 1 }}>
            {item.value} ({(item.value / total * 100).toFixed(0)}%)
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Grid,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Divider,
  Stack,
  LinearProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  RadioGroup,
  FormControlLabel,
  Radio as MuiRadio,
  FormHelperText,
  Tooltip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab
} from '@mui/material';
import {
  Close as CloseIcon,
  Info as InfoIcon,
  Public as PublicIcon,
  Group as GroupIcon,
  Subscriptions as SubscriptionsIcon
} from '@mui/icons-material';

/**
 * Section header used to visually distinguish the two code systems inside the
 * "Passes & Codes" modal:
 *  - "coupon": CouponPass    — amber accent.
 *  - "pass":   Contest Pass  — emerald accent.
 * Keeps the warm NAPC palette and reinforces the color-coding used in the tabs.
 */
const PASS_SYSTEM_VARIANTS = {
  coupon: {
    accent: '#9a3412',
    softBorder: 'rgba(154,52,18,0.25)',
    softBg: 'rgba(154,52,18,0.08)',
    title: 'CouponPass',
    system: 'CouponPass',
    description:
      'Discount/reward codes that can be redeemed. Grouped into a CouponPass per contest and may include a QR.',
  },
  pass: {
    accent: '#065f46',
    softBorder: 'rgba(6,95,70,0.25)',
    softBg: 'rgba(6,95,70,0.08)',
    title: 'Contest Pass',
    system: 'Contest Pass',
    description:
      'Entry/access passes for a contest. Each Contest Pass code lets a pet take part.',
  },
} as const;

const PassSystemSectionHeader: React.FC<{
  variant: keyof typeof PASS_SYSTEM_VARIANTS;
  count: number;
  contestCount: number;
}> = ({ variant, count, contestCount }) => {
  const cfg = PASS_SYSTEM_VARIANTS[variant];
  const Icon = variant === 'coupon' ? Tag : Ticket;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        mb: 2,
        borderRadius: 2,
        backgroundColor: cfg.softBg,
        border: `1px solid ${cfg.softBorder}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 44,
          height: 44,
          borderRadius: '50%',
          backgroundColor: '#fff',
          border: `1px solid ${cfg.softBorder}`,
          color: cfg.accent,
          flexShrink: 0,
        }}
      >
        <Icon className="w-5 h-5" />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: cfg.accent }}>
            {cfg.title}
          </Typography>
          <Chip
            label={cfg.system}
            size="small"
            sx={{
              backgroundColor: '#fff',
              color: cfg.accent,
              border: `1px solid ${cfg.softBorder}`,
              fontWeight: 'bold',
            }}
          />
        </Box>
        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mt: 0.25 }}>
          {cfg.description}
        </Typography>
      </Box>

      <Box sx={{ textAlign: 'center', flexShrink: 0, pl: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: cfg.accent, lineHeight: 1 }}>
          {count}
        </Typography>
        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
          {count === 1 ? 'code' : 'codes'}
        </Typography>
        {contestCount > 0 && (
          <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block' }}>
            {contestCount} {contestCount === 1 ? 'contest' : 'contests'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Mock data that matches the actual database schema
const mockContests = [
  {
    id: '880e8400-e29b-41d4-a716-446655440000',
    name: 'Spring Pet Photo Challenge 2024',
    description: 'Show us your pet enjoying spring activities!',
    organizer_id: 'user_123',
    status: 'approved',
    phase: 'submissions_open',
    entrance_fee: 5.00,
    organizer_buy_rate: 0.00,
    weighted_average_price: 2.83,
    min_participants: 200,
    current_participants: 300,
    members_only: false,
    subscribers_only: false,
    prize_pool_total: 250.00,
    prize_first_place: 100.00,
    prize_second_place: 75.00,
    prize_third_place: 50.00,
    prize_fourth_place: 25.00,
    prize_type: 'cash',
    submission_start: '2024-02-01T00:00:00Z',
    submission_deadline: '2024-02-15T23:59:59Z',
    voting_start: null,
    voting_end: null,
    winner_announcement: '2024-02-22T12:00:00Z',
    allowed_media_types: 'both',
    max_images: 5,
    max_videos: 2,
    max_image_size_mb: 10,
    max_video_size_mb: 50,
    max_video_duration_seconds: 60,
    allowed_countries: ['all'],
    allowed_pet_types: ['all'],
    age_group: 'all_ages',
    education_type: 'none',
    photo_rewards_mode: 'required',
    photo_rewards_user_percentage: 80,
    photo_rewards_organizer_percentage: 10,
    photo_rewards_noahs_ark_percentage: 10,
    ops_cost: 50.00,
    s3_bucket_name: 'petcontest-prod-media',
    s3_folder_prefix: 'contests/contest_880e8400/',
    sponsorship: {
      sponsor_id: '550e8400-e29b-41d4-a716-446655440000',
      sponsor_name: 'PetCorp',
      sponsorship_type: 'rewards_only',
      reward_contribution: 250.00,
      total_sponsor_cost: 250.00,
      impressions: 15000,
      engagements: 780,
      clicks: 320
    },
    economics: {
      sponsor_cost: 250.00,
      participant_revenue: 875.00,
      organizer_revenue: 0.00,
      photo_revenue_projection: 180.00,
      photo_revenue_actual: 0.00,
      total_revenue: 1125.00,
      total_costs: 300.00,
      prize_costs: 250.00,
      ops_costs: 50.00,
      noahs_ark_profit: 825.00,
      profit_margin: 73.33,
      total_roi: 275.00,
      revenue_per_participant: 3.75,
      cost_per_participant: 1.00,
      voucher_redemption_value: 250.00,
      total_discount_given: 625.00,
      full_price_revenue: 500.00,
      discounted_revenue: 375.00,
      weighted_average_price: 2.92,
      breakeven_participants: 60,
      revenue_efficiency_percentage: 58.33,
      potential_revenue: 1500.00
    },
    membership_breakdown: [
      {
        membership_key: 'voucherFreePass',
        discount_type: 'Couponpasses',
        membership_level: '',
        base_price: 5.00,
        actual_paid: 0.00,
        discount_amount: 5.00,
        discount_percentage: 100.00,
        participant_count: 50,
        total_revenue: 0.00,
        percentage_of_total: 16.67
      },
      {
        membership_key: 'subscribersDiscounted',
        discount_type: 'Noah\'s Ark Subscribers (50% off)',
        membership_level: 'Paid Subscriber',
        base_price: 5.00,
        actual_paid: 2.50,
        discount_amount: 2.50,
        discount_percentage: 50.00,
        participant_count: 0,
        total_revenue: 0.00,
        percentage_of_total: 0.00
      },
      {
        membership_key: 'membersFullPrice',
        discount_type: 'Members - Full Price',
        membership_level: 'Free Member',
        base_price: 5.00,
        actual_paid: 5.00,
        discount_amount: 0.00,
        discount_percentage: 0.00,
        participant_count: 70,
        total_revenue: 350.00,
        percentage_of_total: 23.33
      },
      {
        membership_key: 'nonMembersFull',
        discount_type: 'Non-Members - Full Price',
        membership_level: 'Public',
        base_price: 5.00,
        actual_paid: 5.00,
        discount_amount: 0.00,
        discount_percentage: 0.00,
        participant_count: 30,
        total_revenue: 150.00,
        percentage_of_total: 10.00
      }
    ]
  },
  {
    id: 'bb0e8400-e29b-41d4-a716-446655440000',
    name: 'Premium Members Exclusive Contest',
    description: 'VIP contest for our premium members',
    organizer_id: 'user_456',
    status: 'approved',
    phase: 'upcoming',
    entrance_fee: 10.00,
    organizer_buy_rate: 0.00,
    weighted_average_price: 5.41,
    min_participants: 75,
    current_participants: 185,
    members_only: true,
    subscribers_only: false,
    prize_pool_total: 400.00,
    prize_first_place: 150.00,
    prize_second_place: 100.00,
    prize_third_place: 75.00,
    prize_fourth_place: 50.00,
    prize_fifth_place: 25.00,
    prize_type: 'cash',
    submission_start: '2024-03-01T00:00:00Z',
    submission_deadline: '2024-03-15T23:59:59Z',
    winner_announcement: '2024-03-22T12:00:00Z',
    allowed_media_types: 'both',
    max_images: 5,
    max_videos: 2,
    allowed_countries: ['us', 'ca'],
    allowed_pet_types: ['all'],
    age_group: 'all_ages',
    education_type: 'none',
    photo_rewards_mode: 'required',
    ops_cost: 50.00,
    s3_bucket_name: 'petcontest-prod-media',
    s3_folder_prefix: 'contests/contest_bb0e8400/',
    sponsorship: null,
    economics: {
      sponsor_cost: 0.00,
      participant_revenue: 900.00,
      organizer_revenue: 0.00,
      total_revenue: 900.00,
      total_costs: 450.00,
      prize_costs: 400.00,
      ops_costs: 50.00,
      noahs_ark_profit: 450.00,
      profit_margin: 50.00,
      total_roi: 100.00,
      revenue_per_participant: 4.86,
      voucher_redemption_value: 350.00,
      total_discount_given: 950.00,
      full_price_revenue: 300.00,
      discounted_revenue: 600.00,
      weighted_average_price: 4.86
    },
    membership_breakdown: [
      {
        membership_key: 'voucherFreePass',
        discount_type: 'Couponpasses',
        membership_level: '',
        base_price: 10.00,
        actual_paid: 0.00,
        discount_amount: 10.00,
        discount_percentage: 100.00,
        participant_count: 35,
        total_revenue: 0.00,
        percentage_of_total: 18.92
      },
      {
        membership_key: 'subscribersDiscounted',
        discount_type: 'Subscribers (50% perk)',
        membership_level: 'Paid Subscriber',
        base_price: 10.00,
        actual_paid: 5.00,
        discount_amount: 5.00,
        discount_percentage: 50.00,
        participant_count: 0,
        total_revenue: 0.00,
        percentage_of_total: 0.00
      },
      {
        membership_key: 'membersFullPrice',
        discount_type: 'Free Members - Full Price',
        membership_level: 'Free Member',
        base_price: 10.00,
        actual_paid: 10.00,
        discount_amount: 0.00,
        discount_percentage: 0.00,
        participant_count: 30,
        total_revenue: 300.00,
        percentage_of_total: 16.22
      }
    ]
  },
  {
    id: 'cc0e8400-e29b-41d4-a716-446655440000',
    name: 'Subscribers Only Elite Challenge',
    description: 'Exclusive challenge for paid subscribers',
    organizer_id: 'user_789',
    status: 'approved',
    phase: 'upcoming',
    entrance_fee: 15.00,
    organizer_buy_rate: 0.00,
    weighted_average_price: 7.50,
    min_participants: 50,
    current_participants: 95,
    members_only: true,
    subscribers_only: true,
    prize_pool_total: 500.00,
    prize_first_place: 200.00,
    prize_second_place: 150.00,
    prize_third_place: 100.00,
    prize_fourth_place: 50.00,
    prize_type: 'cash',
    submission_start: '2024-03-15T00:00:00Z',
    submission_deadline: '2024-03-30T23:59:59Z',
    winner_announcement: '2024-04-06T12:00:00Z',
    allowed_media_types: 'both',
    max_images: 5,
    max_videos: 2,
    allowed_countries: ['all'],
    allowed_pet_types: ['all'],
    age_group: 'all_ages',
    education_type: 'none',
    photo_rewards_mode: 'required',
    ops_cost: 50.00,
    s3_bucket_name: 'petcontest-prod-media',
    s3_folder_prefix: 'contests/contest_cc0e8400/',
    sponsorship: {
      sponsor_id: '550e8400-e29b-41d4-a716-446655440001',
      sponsor_name: 'Premium Pet Brand',
      sponsorship_type: 'partial_entrance',
      entrance_coverage_percentage: 40.00,
      reward_contribution: 200.00,
      total_sponsor_cost: 300.00,
      impressions: 8000,
      engagements: 450
    },
    economics: {
      sponsor_cost: 300.00,
      participant_revenue: 712.50,
      total_revenue: 1012.50,
      total_costs: 550.00,
      prize_costs: 500.00,
      ops_costs: 50.00,
      noahs_ark_profit: 462.50,
      profit_margin: 45.70,
      total_roi: 84.10,
      revenue_per_participant: 10.66,
      voucher_redemption_value: 375.00,
      total_discount_given: 712.50,
      full_price_revenue: 375.00,
      discounted_revenue: 337.50,
      weighted_average_price: 7.50
    },
    membership_breakdown: [
      {
        membership_key: 'voucherFreePass',
        discount_type: 'Couponpasses',
        membership_level: '',
        base_price: 15.00,
        actual_paid: 0.00,
        discount_amount: 15.00,
        discount_percentage: 100.00,
        participant_count: 25,
        total_revenue: 0.00,
        percentage_of_total: 26.32
      },
      {
        membership_key: 'subscribersDiscounted',
        discount_type: 'Subscribers (50% perk)',
        membership_level: 'Paid Subscriber',
        base_price: 15.00,
        actual_paid: 7.50,
        discount_amount: 7.50,
        discount_percentage: 50.00,
        participant_count: 0,
        total_revenue: 0.00,
        percentage_of_total: 0.00
      },
      {
        membership_key: 'subscribersFullPrice',
        discount_type: 'Subscribers - Premium Tier',
        membership_level: 'Premium Subscriber',
        base_price: 15.00,
        actual_paid: 15.00,
        discount_amount: 0.00,
        discount_percentage: 0.00,
        participant_count: 25,
        total_revenue: 375.00,
        percentage_of_total: 26.32
      }
    ]
  }
];

const MembershipBreakdownChart = ({ membershipBreakdown, totalParticipants, entranceFee, membersOnly, subscribersOnly, contest }: {
  membershipBreakdown: any[];
  totalParticipants: number;
  entranceFee: number;
  membersOnly: boolean;
  subscribersOnly: boolean;
  contest: any;
}) => {
  const { t } = useI18n();
  // Debug logging
  console.log('MembershipBreakdownChart props:', {
    membershipBreakdown,
    totalParticipants,
    entranceFee,
    membersOnly,
    subscribersOnly,
    contest
  });

  console.log('Contest couponpasses data:', {
    couponpasses_used: contest.couponpasses_used,
    couponpasses_assigned: contest.couponpasses_assigned,
    couponpasses_enabled: contest.couponpasses_enabled
  });

  // Obtener datos de cupones del contest_info (viene del endpoint de economía)
  const couponpassesUsed = contest.couponpasses_used || 0;
  // Usar couponpasses_assigned del backend (ya multiplicado por uses_per_coupon)
  // Solo usar max_coupass como fallback si couponpasses_assigned no está disponible
  const couponpassesAssigned = contest.couponpasses_assigned ?? (contest.max_coupass || 0);

  // Transformar los datos para Couponpasses
  const transformedMembershipBreakdown = membershipBreakdown?.map((memberType: any) => {
    if (memberType.membership_key === 'voucherFreePass') {
      return {
        ...memberType,
        discount_type: 'Couponpasses',
        membership_level: '',
        // Usar datos reales de cupones
        participant_count: couponpassesUsed,
        // Calcular porcentaje basado en cupones asignados
        percentage_of_total: couponpassesAssigned > 0 ?
          (couponpassesUsed / couponpassesAssigned) * 100 :
          memberType.percentage_of_total
      };
    }
    return memberType;
  }) || [];

  if (!membershipBreakdown || membershipBreakdown.length === 0) {
    console.log('No membership breakdown data available');
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#111827', mb: 2, display: 'flex', alignItems: 'center' }}>
          <Users className="w-4 h-4 mr-2" style={{ color: '#6b7280' }} />
          Membership & Payment Breakdown 2
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280' }}>
          No membership data available
        </Typography>
      </Card>
    );
  }

  const getColorForMembershipType = (key: string) => {
    const colors: { [key: string]: string } = {
      voucherFreePass: 'bg-blue-500',
      subscribersDiscounted: 'bg-green-500',
      subscribersFullPrice: 'bg-emerald-600',
      membersFullPrice: 'bg-purple-500',
      nonMembersFull: 'bg-orange-500',
      freeEntries: 'bg-gray-500'
    };
    return colors[key] || 'bg-gray-400';
  };

  const getColorStyle = (key: string) => {
    const colorMap: { [key: string]: string } = {
      voucherFreePass: '#3b82f6',
      subscribersDiscounted: '#10b981',
      subscribersFullPrice: '#059669',
      membersFullPrice: '#8b5cf6',
      nonMembersFull: '#f59e0b',
      freeEntries: '#6b7280',
      noahsArkSubscribers100: '#10b981' // Verde para Noah's Ark Subscribers (100% off)
    };
    return colorMap[key] || '#9ca3af';
  };

  const totalRevenue = membershipBreakdown.reduce((sum: number, item: any) => sum + item.total_revenue, 0);
  const totalDiscounts = membershipBreakdown.reduce((sum: number, item: any) => sum + (item.discount_amount * item.participant_count), 0);
  const potentialRevenue = totalParticipants * entranceFee;

  const getAccessDescription = () => {
    if (subscribersOnly) return "Subscribers Only";
    if (membersOnly) return "Members Only";
    return t('details_contest.OpenToAll');
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3, width: '100%' }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#111827', mb: 2, display: 'flex', alignItems: 'center' }}>
        <Users className="w-4 h-4 mr-2" style={{ color: '#6b7280' }} />
        {t('details_contest.MembershipAndPaymentBreakdown')}
      </Typography>
      <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
        {t('details_contest.ContentAccess')}: {getAccessDescription()} | {t('details_contest.BasePrice')}: ${entranceFee.toFixed(2)}
      </Typography>

      <Stack spacing={3} sx={{ flex: 1 }}>
        {transformedMembershipBreakdown.map((memberType: any, index: number) => {
          const percentage = Number(memberType.percentage_of_total) || 0;
          const avgRevenue = memberType.participant_count > 0 ? (Number(memberType.total_revenue) || 0) / memberType.participant_count : 0;

          // Para Couponpasses y Members - Full Price, usar datos ya transformados
          const isCouponpasses = memberType.discount_type === 'Couponpasses';
          const isMembersFullPrice = memberType.membership_key === 'membersFullPrice';

          // Asegurar que todos los valores numéricos estén definidos
          const actualPaid = Number(memberType.actual_paid) || 0;
          const discountAmount = Number(memberType.discount_amount) || 0;
          const discountPercentage = Number(memberType.discount_percentage) || 0;
          const totalRevenue = Number(memberType.total_revenue) || 0;

          return (
            <Box key={index} sx={{ borderBottom: '1px solid #f3f4f6', pb: 2, '&:last-child': { borderBottom: 'none' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: getColorStyle(memberType.membership_key)
                    }}
                  />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#111827' }}>
                      {memberType.discount_type}
                    </Typography>
                    {!isCouponpasses && (
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        {memberType.membership_level}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#374151' }}>
                    {memberType.participant_count}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                    {isCouponpasses ?
                      `de ${couponpassesAssigned} asignados` :
                      isMembersFullPrice ?
                        `de ${memberType.total_assigned || memberType.participant_count} asignados` :
                        `(${percentage.toFixed(1)}%)`
                    }
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
                <Paper elevation={0} sx={{ backgroundColor: '#f9fafb', p: 2, borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>{t('details_contest.Pays')}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#111827' }}>
                    ${actualPaid.toFixed(2)}
                  </Typography>
                </Paper>
                <Paper elevation={0} sx={{ backgroundColor: '#f9fafb', p: 2, borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>{t('details_contest.Discounts')}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#dc2626' }}>
                    ${discountAmount.toFixed(2)}
                    {discountPercentage > 0 && (
                      <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>
                        ({discountPercentage.toFixed(0)}%)
                      </Typography>
                    )}
                  </Typography>
                </Paper>
                <Paper elevation={0} sx={{ backgroundColor: '#f9fafb', p: 2, borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>{t('details_contest.Revenue')}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#111827' }}>
                    ${totalRevenue.toFixed(2)}
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1, backgroundColor: '#e5e7eb', height: 8, borderRadius: 1, position: 'relative' }}>
                  <Box
                    sx={{
                      width: `${percentage}%`,
                      height: 8,
                      borderRadius: 1,
                      backgroundColor: getColorStyle(memberType.membership_key),
                      minWidth: percentage > 0 ? 2 : 0,
                      position: 'absolute',
                      top: 0,
                      left: 0
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: '#374151', fontWeight: 'medium' }}>
                  {isCouponpasses ?
                    `${percentage.toFixed(1)}% usado` :
                    memberType.show_percentage ?
                      `${percentage.toFixed(1)}%` :
                      `$${avgRevenue.toFixed(2)}/ea`
                  }
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>

      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e5e7eb' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#4b5563' }}> {t('details_contest.Totla_Participants')}:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#111827' }}>{totalParticipants}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#4b5563' }}> {t('details_contest.Actual_Revenue')}:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#059669' }}>${totalRevenue.toFixed(2)}</Typography>
            </Box>
            {entranceFee > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: '#4b5563' }}> {t('details_contest.PotentialRevenue')}:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#111827' }}>${potentialRevenue.toFixed(2)}</Typography>
              </Box>
            )}
          </Stack>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#4b5563' }}> {t('details_contest.Total_Discounts')}:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#dc2626' }}>${totalDiscounts.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#4b5563' }}> {t('details_contest.Avg_Per_Participant')}:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#111827' }}>${totalParticipants > 0 ? (totalRevenue / totalParticipants).toFixed(2) : '0.00'}</Typography>
            </Box>
            {entranceFee > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: '#4b5563' }}> {t('details_contest.RevenueEfficiency')}:</Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 'bold',
                    color: (totalRevenue / potentialRevenue * 100) > 60 ? '#059669' : (totalRevenue / potentialRevenue * 100) > 40 ? '#d97706' : '#dc2626'
                  }}
                >
                  {potentialRevenue > 0 ? (totalRevenue / potentialRevenue * 100).toFixed(1) : 0}%
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </Box>
    </Card>
  );
};

// Traduce el resultado de getContestAvailability (utils/contestAvailability.ts)
// a la clave de i18n correspondiente (contestcard.reason<ReasonKey>) más sus
// parámetros de interpolación, para mantener una sola fuente de verdad sobre
// las reglas de disponibilidad entre el panel admin y la página pública.
const getContestAvailabilityI18n = (contest: any): { available: boolean; reasonKey: string; reasonParams?: Record<string, string> } => {
  const info = getContestAvailability(contest);
  const formattedDate = info.date ? new Date(info.date).toLocaleDateString() : '';
  return {
    available: info.available,
    reasonKey: `reason${info.reasonKey}`,
    reasonParams: formattedDate ? { date: formattedDate } : undefined,
  };
};

const ContestCard = ({ contest, onViewDetails, onApprove, onReject, onReactivate, onSponsorClick, onEditContest, onDeleteContest, onSimulateParticipant, onViewContestParticipants, onViewWinners, onViewLive, hasSponsor, onSendSponsor, onSendWinners, onViewInvoice, onViewVoteBoosterReport, onViewTagStatistics, isMobile }: {
  contest: any;
  onViewDetails: (contest: any) => void;
  onApprove: (contestId: string) => void;
  onReject: (contestId: string) => void;
  onReactivate?: (contestId: string) => void;
  onSponsorClick?: (contest: any) => void;
  onEditContest?: (contest: any) => void;
  onDeleteContest?: (contest: any) => void;
  onSimulateParticipant?: (contestId: string, entranceFee: number, contestName: string) => void;
  onViewContestParticipants?: (contestId: string, contestName: string) => void;
  onViewWinners?: (contestId: string, contestName: string) => void;
  onViewLive?: (contestId: string, contestName: string) => void;
  hasSponsor?: boolean;
  onSendSponsor?: (contest: any) => void;
  onSendWinners?: (contest: any) => void;
  onViewInvoice?: (contestId: string, contestName: string) => void;
  onViewVoteBoosterReport?: (contestId: string, contestName: string) => void;
  onViewTagStatistics?: (contestId: string, contestName: string) => void;
  isMobile?: boolean;
}) => {
  const { t } = useI18n();
  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      approved: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800',
      active: 'bg-blue-100 text-blue-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPhaseColor = (phase: string) => {
    const colors: { [key: string]: string } = {
      submissions_open: 'bg-green-50 text-green-700',
      submissions_closed: 'bg-orange-50 text-orange-700',
      voting_open: 'bg-blue-50 text-blue-700',
      voting_closed: 'bg-purple-50 text-purple-700',
      upcoming: 'bg-yellow-50 text-yellow-700',
      completed: 'bg-gray-50 text-gray-700'
    };
    return colors[phase] || 'bg-gray-50 text-gray-700';
  };

  const getProfitColor = (profitMargin: number) => {
    if (profitMargin > 30) return 'text-green-600';
    if (profitMargin > 10) return 'text-blue-600';
    if (profitMargin > 0) return 'text-yellow-600';
    return 'text-red-600';
  };

  // La activación solo requiere el mínimo de participantes, no el máximo
  // Después de activar, se pueden seguir agregando participantes hasta que se acaben los Pass_Codes normales
  const canLaunch = contest.current_participants >= contest.min_participants;
  const economics = contest.economics || {};
  const sponsorship = contest.sponsorship;
  const availability = getContestAvailabilityI18n(contest);
  const availabilityReason = t(`contestcard.${availability.reasonKey}`, availability.reasonParams) || availability.reasonKey;

  return (
    <div className="napc-contest-card bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow" style={{ border: '1px solid #E7E5EA', borderRadius: '16px', boxShadow: '0 1px 0 rgba(14,15,18,.04), 0 18px 44px -28px rgba(14,15,18,.28)', position: 'relative', overflow: 'hidden' }}>
      <div className="p-6" style={{ padding: isMobile ? '18px' : '22px 24px 18px' }}>
        {/* Header Section */}
        <div className="flex justify-between items-start mb-4" style={{ paddingLeft: 0, paddingBottom: '12px', paddingRight: isMobile ? 0 : '56px' }}>
          <div className="flex-1">
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
              {contest.cover_image ? (
                <img
                  src={getCompleteImagePath(contest.cover_image) || contest.cover_image}
                  alt={contest.name}
                  style={{
                    width: '96px',
                    height: '96px',
                    objectFit: 'cover',
                    borderRadius: '999px',
                    border: '2px solid #ffffff',
                    boxShadow: '0 0 0 1px #E7E5EA, 0 8px 20px -10px rgba(14,15,18,.25)',
                    flexShrink: 0,
                    backgroundColor: '#FBF9F6'
                  }}
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <div
                  aria-hidden="true"
                  style={{
                    width: '96px',
                    height: '96px',
                    borderRadius: '999px',
                    border: '2px solid #ffffff',
                    boxShadow: '0 0 0 1px #E7E5EA, 0 8px 20px -10px rgba(14,15,18,.25)',
                    background: 'linear-gradient(135deg, rgba(237,23,144,.18), rgba(48,111,200,.18))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: '#ED1790'
                  }}
                >
                  <Award className="w-10 h-10" />
                </div>
              )}
              <h3 className="text-lg font-bold text-gray-900" style={{ fontSize: '22px', fontWeight: 700, color: '#0E0F12', margin: 0, letterSpacing: '-0.02em' }}>{contest.name}</h3>
              {/* Botón: Simular Participante */}
              {onSimulateParticipant && (
                <button
                  onClick={() => onSimulateParticipant(contest.id, contest.entrance_fee || 0, contest.name)}
                  className="p-1.5 text-purple-400 hover:text-purple-600 hover:bg-purple-100 rounded-lg transition-colors"
                  title="Simular Participante"
                  style={{ margin: 0, padding: '6px' }}
                >
                  <Users className="w-4 h-4" />
                </button>
              )}
              {/* Botón: Ver Participantes (siempre visible) */}
              {onViewContestParticipants && (
                <button
                  onClick={() => onViewContestParticipants(contest.id, contest.name)}
                  className="p-1.5 text-blue-400 hover:text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                  title={t('contestcard.viewparticipants')}
                  style={{ margin: 0, padding: '6px' }}
                >
                  <Eye className="w-4 h-4" />
                </button>
              )}
              {/* Botón: Live (siempre visible) */}
              {onViewLive && (
                <button
                  onClick={() => onViewLive(contest.id, contest.name)}
                  className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-lg transition-colors"
                  title="Live - Winners"
                  style={{ margin: 0, padding: '6px' }}
                >
                  <Radio className="w-4 h-4" />
                </button>
              )}
              {/* Botón: Ver Ganadores (activo o completado) */}
              {onViewWinners && (contest.status === 'active' || contest.status === 'completed') && (
                <button
                  onClick={() => onViewWinners(contest.id, contest.name)}
                  className="p-1.5 text-yellow-500 hover:text-yellow-700 hover:bg-yellow-100 rounded-lg transition-colors"
                  title={t('contestcard.viewWinners')}
                  style={{ margin: 0, padding: '6px' }}
                >
                  <Crown className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex items-center" style={{ gap: '14px', flexWrap: 'wrap', marginTop: '4px' }}>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(contest.status)}`} style={{ padding: '5px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: '700', fontFamily: 'var(--napc-mono)', letterSpacing: '.04em', backgroundColor: 'rgba(237, 23, 144, .08)', color: '#B01F8E', border: '1px solid rgba(237, 23, 144, .18)' }}>
                {t(`contestcard.status.${contest.status}`) || contest.status.toUpperCase()}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPhaseColor(contest.phase)}`} style={{ padding: '5px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: '700', fontFamily: 'var(--napc-mono)', letterSpacing: '.04em', backgroundColor: 'rgba(48, 111, 200, .08)', color: '#306FC8', border: '1px solid rgba(48, 111, 200, .18)' }}>
                {t(`contestcard.phase.${contest.phase}`) || contest.phase.replace(/_/g, ' ').toUpperCase()}
              </span>
              <Tooltip title={availabilityReason} arrow>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '5px 10px',
                    borderRadius: '999px',
                    fontSize: '11px',
                    fontWeight: '700',
                    fontFamily: 'var(--napc-mono)',
                    letterSpacing: '.04em',
                    cursor: 'help',
                    backgroundColor: availability.available ? 'rgba(16, 185, 129, .1)' : 'rgba(220, 38, 38, .1)',
                    color: availability.available ? '#047857' : '#B91C1C',
                    border: `1px solid ${availability.available ? 'rgba(16, 185, 129, .3)' : 'rgba(220, 38, 38, .3)'}`,
                  }}
                >
                  {availability.available ? (
                    <CheckCircle className="w-3 h-3" />
                  ) : (
                    <XCircle className="w-3 h-3" />
                  )}
                  {availability.available ? t('contestcard.availableLabel') : t('contestcard.notAvailableLabel')}
                </span>
              </Tooltip>
              {contest.linkedProposal && (
                <Tooltip
                  title={`${contest.linkedProposal.proposal_number || t('contestcard.linkedProposal')}${contest.linkedProposal.quarter ? ` · ${contest.linkedProposal.quarter}` : ''}${contest.linkedProposal.sponsor_name ? ` · ${contest.linkedProposal.sponsor_name}` : ''}`}
                  arrow
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '5px 10px',
                      borderRadius: '999px',
                      fontSize: '11px',
                      fontWeight: '700',
                      fontFamily: 'var(--napc-mono)',
                      letterSpacing: '.04em',
                      cursor: 'help',
                      backgroundColor: 'rgba(124, 58, 237, .1)',
                      color: '#6D28D9',
                      border: '1px solid rgba(124, 58, 237, .3)',
                    }}
                  >
                    <Link2 className="w-3 h-3" />
                    {t('contestcard.linkedProposal')}{contest.linkedProposal.proposal_number ? ` · ${contest.linkedProposal.proposal_number}` : ''}
                  </span>
                </Tooltip>
              )}
            </div>
            <p style={{
              margin: '6px 0 0',
              fontSize: '12px',
              color: availability.available ? '#047857' : '#B91C1C',
              fontWeight: 500,
            }}>
              {availabilityReason}
            </p>
          </div>
          <div style={{
            position: 'absolute',
            top: isMobile ? '18px' : '20px',
            right: isMobile ? '18px' : '18px',
            display: 'flex',
            flexDirection: isMobile ? 'row' : 'column',
            gap: '7px'
          }}>
            {/* Primer botón: Editar Concurso */}
            {onEditContest && (
              <button
                onClick={() => onEditContest(contest)}
                className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-100 rounded-lg"
                title={t('contestcard.editcontest')}
              >
                <Edit className="w-4 h-4" />
              </button>
            )}
            {/* Botón: Eliminar Concurso */}
            {onDeleteContest && (
              <button
                onClick={() => onDeleteContest(contest)}
                className="p-2 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-lg"
                title={t('contestcard.deletecontest')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            {/* Segundo botón: Ver Patrocinador */}
            {onSponsorClick && (
              <button
                onClick={() => onSponsorClick(contest)}
                className="p-2 text-green-400 hover:text-green-600 hover:bg-green-100 rounded-lg"
                title={t('contestcard.viewsponsor')}
              >
                <Building2 className="w-4 h-4" />
              </button>
            )}
            {/* Tercer botón: Ver Reporte Vote Boosters */}
            {onViewVoteBoosterReport && (
              <button
                onClick={() => onViewVoteBoosterReport(contest.id, contest.name)}
                className="p-2 text-yellow-500 hover:text-yellow-600 hover:bg-yellow-100 rounded-lg"
                title={t('contestcard.viewvoteboosterreport')}
              >
                <Star className="w-4 h-4" />
              </button>
            )}
            {/* Botón: Ver Estadísticas de Tags */}
            {onViewTagStatistics && (
              <button
                onClick={() => onViewTagStatistics(contest.id, contest.name)}
                className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-100 rounded-lg"
                title={t('contestcard.viewtagstatistics')}
              >
                <Tag className="w-4 h-4" />
              </button>
            )}
            {/* Botón: Ver Invoice */}
            {onViewInvoice && (
              <button
                onClick={() => onViewInvoice(contest.id, contest.name)}
                className="p-2 text-purple-400 hover:text-purple-600 hover:bg-purple-100 rounded-lg"
                title={t('contestcard.viewinvoice')}
              >
                <DollarSign className="w-4 h-4" />
              </button>
            )}
            {/* Botón: Ver Ganadores */}
            {onViewWinners && (contest.status === 'active' || contest.status === 'completed') && (
              <button
                onClick={() => onViewWinners(contest.id, contest.name)}
                className="p-2 text-amber-500 hover:text-amber-700 hover:bg-amber-100 rounded-lg"
                title={t('contestcard.viewWinners')}
              >
                <Crown className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => onViewDetails(contest)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              title={t('contestcard.viewdetails')}
            >
              <Eye className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="napc-card-content-grid">
          {/* Financial Metrics Section */}
          <div className="napc-profit-panel bg-gray-50 rounded-lg p-4 mb-4" style={{ backgroundColor: 'rgba(237, 23, 144, .045)', border: '1px solid rgba(237, 23, 144, .14)', borderRadius: '12px', padding: isMobile ? '10px' : '12px 14px', marginBottom: '10px' }}>
            <div className="flex justify-between items-center" style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(3, minmax(0, 1fr))' : '1fr',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: isMobile ? '10px' : '14px'
            }}>
              <div className="text-center" style={{
                flex: isMobile ? '0 1 auto' : '1',
                textAlign: 'center',
                paddingLeft: isMobile ? '0' : '0',
                paddingRight: isMobile ? '0' : '0',
                minWidth: isMobile ? 'auto' : '0'
              }}>
                <p className="text-xs text-gray-500 mb-1" style={{ fontSize: isMobile ? '10px' : '12px', color: '#6b7280', marginBottom: isMobile ? '2px' : '4px' }}>{t('contestcard.profit')}</p>
                <p className={`text-lg font-bold ${getProfitColor(economics.profit_margin || 0)}`} style={{ fontSize: isMobile ? '14px' : '18px', fontWeight: 'bold' }}>
                  ${(economics.noahs_ark_profit || 0).toFixed(2)}
                </p>
              </div>
              <div className="text-center" style={{
                flex: isMobile ? '0 1 auto' : '1',
                textAlign: 'center',
                paddingLeft: isMobile ? '0' : '0',
                paddingRight: isMobile ? '0' : '0',
                minWidth: isMobile ? 'auto' : '0'
              }}>
                <p className="text-xs text-gray-500 mb-1" style={{ fontSize: isMobile ? '10px' : '12px', color: '#6b7280', marginBottom: isMobile ? '2px' : '4px' }}>{t('contestcard.margin')}</p>
                <p className={`text-lg font-bold ${getProfitColor(economics.profit_margin || 0)}`} style={{ fontSize: isMobile ? '14px' : '18px', fontWeight: 'bold' }}>
                  {(economics.profit_margin || 0).toFixed(1)}%
                </p>
              </div>
              <div className="text-center" style={{
                flex: isMobile ? '0 1 auto' : '1',
                textAlign: 'center',
                paddingLeft: isMobile ? '0' : '0',
                paddingRight: isMobile ? '0' : '0',
                minWidth: isMobile ? 'auto' : '0'
              }}>
                <p className="text-xs text-gray-500 mb-1" style={{ fontSize: isMobile ? '10px' : '12px', color: '#6b7280', marginBottom: isMobile ? '2px' : '4px' }}>{t('contestcard.roi')}</p>
                <p className={`text-lg font-bold ${getProfitColor(economics.total_roi || 0)}`} style={{ fontSize: isMobile ? '14px' : '18px', fontWeight: 'bold' }}>
                  {(economics.total_roi || 0).toFixed(1)}%
                </p>
              </div>
            </div>
          </div>

          {/* Contest Details Section */}
          <div className="grid grid-cols-2 gap-6 text-sm napc-contest-specs" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: isMobile ? '8px' : '6px', paddingBottom: '4px' }}>
            {/* First Column - Participants and Total Revenue */}
            <div className="space-y-4" style={{ marginRight: 0, paddingLeft: 0, display: 'grid', gap: '6px' }}>
              <div>
                <p className="text-xs text-gray-500 mb-1" style={{ fontSize: '12px', color: '#6b7280' }}>{t('contestcard.participants')}</p>
                <div className="flex items-center">
                  <Users className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-gray-900 font-medium">
                    {(() => {
                      const currentParticipants = contest.actualParticipantCount !== undefined ? contest.actualParticipantCount : contest.current_participants;
                      const minParticipants = contest.min_participants;
                      const maxParticipants = contest.max_participants || contest.min_participants;

                      if (currentParticipants < minParticipants) {
                        // No se ha alcanzado el mínimo: mostrar "actual/min min - max max"
                        return (
                          <>
                            <span className={canLaunch ? 'text-green-600' : 'text-red-600'}>
                              {currentParticipants}
                            </span>
                            <span className="text-gray-500">/{t('contestcard.min')} {minParticipants} - {t('contestcard.max')} {maxParticipants}</span>
                          </>
                        );
                      } else {
                        // Se alcanzó el mínimo: mostrar "actual/max max"
                        return (
                          <>
                            <span className={canLaunch ? 'text-green-600' : 'text-red-600'}>
                              {currentParticipants}
                            </span>
                            <span className="text-gray-500">/{t('contestcard.max')} {maxParticipants}</span>
                          </>
                        );
                      }
                    })()}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1" style={{ fontSize: '12px', color: '#6b7280' }}>{t('contestcard.totalrevenue')}</p>
                <div className="flex items-center">
                  <span className="text-gray-900 font-medium">
                    ${(() => {
                      // Total Revenue incluye: participantes + sponsor + vote boosters + tags (sponsor tag tiers)
                      // El servidor ya calcula esto correctamente en economics.total_revenue
                      const totalRevenue = Number(economics.total_revenue) || 0;
                      return totalRevenue.toFixed(2);
                    })()}
                  </span>
                </div>
              </div>
              <div style={{ marginTop: 0 }}>
                <p className="text-xs text-gray-500 mb-1" style={{ fontSize: '12px', color: '#6b7280' }}>{t('contestcard.duedate')}</p>
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-gray-900">
                    {new Date(contest.submission_deadline).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1" style={{ fontSize: '12px', color: '#6b7280' }}>{t('contestcard.medialimits')}</p>
                <div className="flex items-center space-x-2">
                  {((contest.allowed_media_types || 'both') === 'image' || (contest.allowed_media_types || 'both') === 'both') && (
                    <>
                      <Image className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">&nbsp; 1 {t('contestcard.images')} &nbsp;&nbsp;&nbsp; </span>
                    </>
                  )}
                  {((contest.allowed_media_types || 'both') === 'video' || (contest.allowed_media_types || 'both') === 'both') && (
                    <>
                      <Video className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">&nbsp; {(contest.allowed_media_types || 'both') === 'both' ? '1 ' + t('contestcard.videos') + ' (o imagen)' : '1 ' + t('contestcard.videos')} </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Second Column - Entry Fee and Sponsor Info */}
            <div className="space-y-4" style={{ marginRight: 0, paddingLeft: 0, display: 'grid', gap: '6px' }}>
              <div>
                <p className="text-xs text-gray-500 mb-1" style={{ fontSize: '12px', color: '#6b7280' }}>{t('contestcard.entryfee')}</p>
                <div className="flex items-center">
                  <span className="text-gray-900 font-medium">
                    ${(contest.entrance_fee || 0).toFixed(2)}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1" style={{ fontSize: '12px', color: '#6b7280' }}>{t('contestcard.sponsor')}</p>
                <div className="text-gray-900 font-medium text-sm" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {(contest.sponsor_logo_url || contest.linkedProposal?.sponsor_logo_url) && (
                    <img
                      src={contest.sponsor_logo_url || contest.linkedProposal.sponsor_logo_url}
                      alt={contest.sponsor_name || contest.linkedProposal?.sponsor_name || 'Sponsor'}
                      style={{ width: '20px', height: '20px', objectFit: 'contain', borderRadius: '4px', flexShrink: 0 }}
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                  <span>
                    {contest.sponsor_id
                      ? (contest.sponsor_name || sponsorship?.sponsor_name || contest.linkedProposal?.sponsor_name || t('contestcard.unnamedSponsor'))
                      : (contest.linkedProposal?.sponsor_name || t('contestcard.noSponsor'))}
                  </span>
                </div>
                <div className="text-gray-500 text-xs">
                  {t('contestcard.investment')}: ${(Number(economics.sponsor_cost) || Number(contest.linkedProposal?.total_investment_usd) || Number(sponsorship?.total_sponsor_cost) || 0).toFixed(2)}
                  {(Number(contest.prize_pool_total) || Number(contest.linkedProposal?.prize_pool_usd) || 0) > 0 && (
                    <span> · {t('details_contest.Grand_Prize')}: ${(Number(contest.prize_pool_total) || Number(contest.linkedProposal?.prize_pool_usd) || 0).toFixed(2)}</span>
                  )}
                </div>
                {contest.linkedProposal && (
                  <div className="text-gray-500 text-xs" style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px', color: '#6D28D9' }}>
                    <Link2 className="w-3 h-3" />
                    <span>
                      {contest.linkedProposal.proposal_number || t('contestcard.linkedProposal')}
                      {contest.linkedProposal.quarter ? ` · ${contest.linkedProposal.quarter}` : ''}
                      {contest.linkedProposal.total_investment_usd ? ` · ${t('contestcard.investment')} $${Number(contest.linkedProposal.total_investment_usd).toFixed(2)}` : ''}
                    </span>
                  </div>
                )}
              </div>
              <div style={{ marginTop: 0 }}>
                <p className="text-xs text-gray-500 mb-1" style={{ fontSize: '12px', color: '#6b7280' }}>{t('contestcard.accesslevel')}</p>
                <div className="flex items-center">
                  <span className={contest.subscribers_only ? 'text-purple-600 font-medium' : contest.members_only ? 'text-blue-600 font-medium' : 'text-green-600 font-medium'}>
                    {contest.subscribers_only ? 'Subscribers' : contest.members_only ? 'Members' : 'Public'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {contest.status === 'pending' && (
          <div
            className="mt-6 pt-5 border-t border-gray-200"
            style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-start', gap: '14px', marginTop: '24px', paddingTop: '20px', marginBottom: '4px' }}
          >
            <button
              onClick={() => onApprove(contest.id)}
              style={{
                width: 'auto',
                minWidth: '140px',
                backgroundImage: 'linear-gradient(90deg, #059669, #10b981)',
                color: '#ffffff',
                padding: '10px 16px',
                borderRadius: '10px',
                fontSize: '0.875rem',
                fontWeight: 600,
                boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                border: '1px solid #0ea5a3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              <CheckCircle className="w-4 h-4" style={{ marginRight: '8px' }} />
              {t('contestcard.approve')}
            </button>
            <button
              onClick={() => onReject(contest.id)}
              style={{
                width: 'auto',
                minWidth: '140px',
                backgroundImage: 'linear-gradient(90deg, #dc2626, #ef4444)',
                color: '#ffffff',
                padding: '10px 16px',
                borderRadius: '10px',
                fontSize: '0.875rem',
                fontWeight: 600,
                boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                border: '1px solid #ef4444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              <XCircle className="w-4 h-4" style={{ marginRight: '8px' }} />
              {t('contestcard.contestCancel')}
            </button>
          </div>
        )}

        {(() => {
          // Verificar si el concurso está cancelado
          // Consideramos cancelado si contest_state es 'cancelled' o 'cancel', o si status es 'cancelled'
          const contestState = contest.contest_state || '';
          const contestStatus = contest.status || '';
          const isCancelled = contestState.toLowerCase() === 'cancelled' ||
            contestState.toLowerCase() === 'cancel' ||
            contestStatus.toLowerCase() === 'cancelled';

          // Debug: log para verificar valores
          if (isCancelled) {
            console.log('Concurso cancelado detectado:', {
              id: contest.id,
              name: contest.name,
              contest_state: contestState,
              status: contestStatus,
              isCancelled: isCancelled,
              hasOnReactivate: !!onReactivate
            });
          }

          return isCancelled && onReactivate && (
            <div
              className="mt-6 pt-5 border-t border-gray-200"
              style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-start', gap: '14px', marginTop: '24px', paddingTop: '20px', marginBottom: '4px' }}
            >
              <button
                onClick={() => onReactivate(contest.id)}
                style={{
                  width: 'auto',
                  minWidth: '160px',
                  backgroundImage: 'linear-gradient(90deg, #2563eb, #3b82f6)',
                  color: '#ffffff',
                  padding: '10px 16px',
                  borderRadius: '10px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                  border: '1px solid #3b82f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <RotateCcw className="w-4 h-4" style={{ marginRight: '8px' }} />
                {t('contestcard.reactivateContest')}
              </button>
            </div>
          );
        })()}

        {!canLaunch && contest.status === 'approved' && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <div className="flex items-center">
                <Clock className="w-4 h-4 text-yellow-600 mr-2" />
                <span className="text-sm text-yellow-800">
                  Need {contest.min_participants - contest.current_participants} more participants to launch
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Botones Ver/Enviar Ganadores y Patrocinador en la parte inferior derecha */}
        <div className="mt-4 pt-4 border-t border-gray-200" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px', paddingTop: '10px', marginBottom: 0, marginRight: 0, flexWrap: 'wrap' }}>
          {onViewWinners && (contest.status === 'active' || contest.status === 'completed') && (
            <button
              onClick={() => onViewWinners(contest.id, contest.name)}
              style={{
                width: 'auto',
                minWidth: '150px',
                backgroundColor: '#ffffff',
                color: '#B45309',
                padding: '10px 16px',
                borderRadius: '999px',
                fontSize: '0.875rem',
                fontWeight: 600,
                border: '1px solid rgba(245, 158, 11, .45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              <Crown className="w-4 h-4" style={{ marginRight: '8px' }} />
              {t('contestcard.viewWinners')}
            </button>
          )}
          <button
            onClick={() => onSendWinners && onSendWinners(contest)}
            style={{
              width: 'auto',
              minWidth: '150px',
              backgroundImage: 'linear-gradient(90deg, #ED1790, #B01F8E)',
              color: '#ffffff',
              padding: '10px 16px',
              borderRadius: '999px',
              fontSize: '0.875rem',
              fontWeight: 600,
              boxShadow: '0 14px 28px -20px rgba(237,23,144,.85)',
              border: '1px solid rgba(237, 23, 144, .28)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <Trophy className="w-4 h-4" style={{ marginRight: '8px' }} />
            {t('contestcard.sendWinners')}
          </button>
          <button
            onClick={() => onSendSponsor && onSendSponsor(contest)}
            disabled={!hasSponsor}
            style={{
              width: 'auto',
              minWidth: '150px',
              backgroundImage: hasSponsor ? 'linear-gradient(90deg, #306FC8, #5E8FD7)' : 'linear-gradient(90deg, #A5A9B5, #d1d5db)',
              color: '#ffffff',
              padding: '10px 16px',
              borderRadius: '999px',
              fontSize: '0.875rem',
              fontWeight: 600,
              boxShadow: hasSponsor ? '0 14px 28px -20px rgba(48,111,200,.75)' : 'none',
              border: hasSponsor ? '1px solid rgba(48,111,200,.28)' : '1px solid #d1d5db',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: hasSponsor ? 'pointer' : 'not-allowed',
              opacity: hasSponsor ? 1 : 0.6
            }}
          >
            <Building2 className="w-4 h-4" style={{ marginRight: '8px', marginBottom: '2px' }} />
            {t('contestcard.sendsponsorship')}
          </button>
        </div>
      </div>
    </div>
  );
};

const ContestDetailsModal = ({ contest, onClose, onUpdateSponsorship }: {
  contest: any;
  onClose: () => void;
  onUpdateSponsorship?: (contest: any) => void;
}) => {
  const [fullSponsorship, setFullSponsorship] = useState<any>(null);
  const [loadingSponsorship, setLoadingSponsorship] = useState(false);
  const [realEconomics, setRealEconomics] = useState<any>(null);
  const [loadingEconomics, setLoadingEconomics] = useState(false);
  const [localContest, setLocalContest] = useState<any>(contest);
  const [realCouponPasses, setRealCouponPasses] = useState<any[]>([]);
  const [contestParticipants, setContestParticipants] = useState<any[]>([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      // Cargar datos de patrocinio
      if (contest?.sponsor_id) {
        setLoadingSponsorship(true);
        try {
          const sponsorship = await sponsorshipService.getSponsorshipById(contest.sponsor_id);
          setFullSponsorship(sponsorship);
        } catch (error) {
          console.error('Error loading full sponsorship:', error);
          setFullSponsorship(null);
        } finally {
          setLoadingSponsorship(false);
        }
      } else if (contest?.linkedProposal?.sponsor_name) {
        setFullSponsorship({
          sponsor_name: contest.linkedProposal.sponsor_name,
          logo_url: contest.linkedProposal.sponsor_logo_url || '',
          total_sponsor_cost: Number(contest.linkedProposal.total_investment_usd || 0),
          reward_contribution: Number(contest.linkedProposal.prize_pool_usd || 0),
          sponsorship_type: 'sponsorship_builder',
        });
      } else {
        setFullSponsorship(contest?.sponsorship || null);
      }

      // Cargar datos económicos reales
      setLoadingEconomics(true);
      try {
        const economicsData = await contestService.getContestEconomics(contest.id);
        setRealEconomics(economicsData.economics);

        // Obtener todos los CouponPasses y CouponCodes (misma lógica que el modal)
        const [couponPassesData, couponCodesData] = await Promise.all([
          couponService.getAllCouponPasses(),
          couponService.getAllCouponCodes()
        ]);

        // Filtrar CouponPasses del concurso específico que tienen códigos
        console.log('Debug - Datos antes del filtrado:', {
          contest_id: contest.id,
          all_couponpasses: couponPassesData.length,
          all_couponcodes: couponCodesData.length,
          couponpasses_for_contest: couponPassesData.filter((cp: any) => cp.contest_id === contest.id).length
        });

        const contestCouponPasses = couponPassesData.filter((couponPass: any) => {
          const contestCodes = couponCodesData.filter((code: any) => code.coupon_pass_id === couponPass.id);
          const isForThisContest = couponPass.contest_id === contest.id;
          const hasCodes = contestCodes.length > 0;

          console.log('Debug - CouponPass:', {
            id: couponPass.id,
            contest_id: couponPass.contest_id,
            name: couponPass.name,
            isForThisContest,
            codes_count: contestCodes.length,
            hasCodes,
            will_include: isForThisContest && hasCodes
          });

          return isForThisContest && hasCodes;
        });

        setRealCouponPasses(contestCouponPasses);

        // Actualizar el objeto contest con los datos de contest_info
        // Los valores de couponpasses_used y couponpasses_assigned ya vienen calculados correctamente del backend
        if (economicsData.contest_info) {
          setLocalContest((prevContest: any) => ({
            ...prevContest,
            ...economicsData.contest_info,
            // Usar los valores del backend que ya están calculados correctamente
            couponpasses_used: economicsData.contest_info.couponpasses_used || 0,
            couponpasses_assigned: economicsData.contest_info.couponpasses_assigned || 0
          }));
        }

        console.log('Datos económicos reales cargados:', economicsData);
        console.log('CouponPasses reales del concurso:', {
          contest_id: contest.id,
          total_couponpasses: couponPassesData.length,
          total_couponcodes: couponCodesData.length,
          contest_couponpasses: contestCouponPasses.length,
          contestCouponPasses
        });
        console.log('Contest actualizado con contest_info (desde backend):', {
          couponpasses_used: economicsData.contest_info?.couponpasses_used,
          couponpasses_assigned: economicsData.contest_info?.couponpasses_assigned,
          max_coupass: economicsData.contest_info?.max_coupass
        });
      } catch (error) {
        console.error('Error loading real economics:', error);
        setRealEconomics(null);
      } finally {
        setLoadingEconomics(false);
      }
    };

    loadData();
  }, [contest]);

  if (!localContest) return null;

  const economics = realEconomics || localContest.economics || {};
  const sponsorship = fullSponsorship || localContest.sponsorship;
  const totalRevenue = Number(economics.total_revenue) || 0;
  const totalCosts = Number(economics.total_costs) || 0;

  // Generar membership_breakdown dinámico basado en datos reales
  const generateDynamicMembershipBreakdown = () => {
    const entranceFee = localContest.entrance_fee || 0;
    const totalParticipants = localContest.current_participants || 0;
    const passCodesUsed = economics.total_pass_codes_used || 0;

    // Obtener datos reales de CouponPasses del concurso
    const couponpassesUsed = localContest.couponpasses_used || 0;
    // Usar couponpasses_assigned del backend (ya multiplicado por uses_per_coupon)
    // Solo usar max_coupass como fallback si couponpasses_assigned no está disponible
    const couponpassesAssigned = localContest.couponpasses_assigned ?? (localContest.max_coupass || 0);

    // Calcular el progreso real: usados vs asignados
    const couponpassesProgress = couponpassesAssigned > 0 ? (couponpassesUsed / couponpassesAssigned) * 100 : 0;

    // Usar el membership_breakdown original pero actualizar solo los datos de couponpasses
    const originalBreakdown = localContest.membership_breakdown || [];

    const updatedBreakdown = originalBreakdown.map((memberType: any) => {
      if (memberType.membership_key === 'voucherFreePass') {
        return {
          ...memberType,
          discount_type: 'Couponpasses',
          membership_level: '',
          participant_count: couponpassesUsed,
          actual_paid: 0, // Los couponpasses no pagan
          total_revenue: 0, // Los couponpasses no generan ingresos
          percentage_of_total: couponpassesProgress // Usar el progreso real calculado
        };
      }

      // Actualizar Members - Full Price con datos de Pass_Codes normales
      if (memberType.membership_key === 'membersFullPrice') {
        // Usar max_pass desde base de datos como total asignado para Members - Full Price
        const maxAssigned = (localContest.max_pass ?? localContest.max_participants ?? localContest.min_participants) || 100;
        const percentage = maxAssigned > 0 ? (passCodesUsed / maxAssigned) * 100 : 0;
        return {
          ...memberType,
          participant_count: passCodesUsed,
          actual_paid: entranceFee, // Valor de la entrada individual
          total_revenue: passCodesUsed * entranceFee, // Ingresos totales
          percentage_of_total: percentage,
          total_assigned: maxAssigned, // Total asignado para members full price desde DB
          show_percentage: true // Flag para mostrar porcentaje en lugar de precio
        };
      }

      // Actualizar Noah's Ark Subscribers (50% off) para que tenga 0 usuarios
      if (memberType.membership_key === 'subscribersDiscounted') {
        return {
          ...memberType,
          participant_count: 0, // 0 usuarios
          actual_paid: 0, // Sin pagos
          total_revenue: 0, // Sin ingresos
          percentage_of_total: 0 // 0% de progreso
        };
      }

      // Actualizar Non-Members - Full Price para que tenga 0 usuarios por ahora
      if (memberType.membership_key === 'nonMembersFull') {
        return {
          ...memberType,
          participant_count: 0, // 0 usuarios
          actual_paid: 0, // Sin pagos
          total_revenue: 0, // Sin ingresos
          percentage_of_total: 0 // 0% de progreso
        };
      }

      return memberType;
    });

    // Crear nueva sección "Noah's Ark Subscribers (100% off)"
    const noahsArkSubscribers100 = {
      membership_key: 'noahsArkSubscribers100',
      discount_type: "Noah's Ark Subscribers (100% off)",
      membership_level: 'Paid Subscriber',
      discount_amount: entranceFee, // 100% de descuento = precio completo
      discount_percentage: 100,
      participant_count: 0, // 0 usuarios
      actual_paid: 0, // Paga $0.0 ya que tiene 100% de descuento
      total_revenue: 0, // Sin ingresos
      percentage_of_total: 0 // 0% de progreso
    };

    // Reordenar las secciones: Couponpasses primero, luego Members - Full Price, luego el resto
    const result = [];
    let couponpassesSection = null;
    let membersFullPriceSection = null;
    let nonMembersFullSection = null;
    let noahsArk50Section = null;
    let otherSections = [];

    // Separar las secciones
    for (let i = 0; i < updatedBreakdown.length; i++) {
      const section = updatedBreakdown[i];

      if (section.membership_key === 'voucherFreePass') {
        couponpassesSection = section;
      } else if (section.membership_key === 'membersFullPrice') {
        membersFullPriceSection = section;
      } else if (section.membership_key === 'nonMembersFull') {
        nonMembersFullSection = section;
      } else if (section.discount_type && section.discount_type.includes("Noah's Ark Subscribers (50% off)")) {
        noahsArk50Section = section;
      } else {
        otherSections.push(section);
      }
    }

    // Construir el orden: Couponpasses, Members - Full Price, Noah's Ark 50%, Noah's Ark 100%, resto
    if (couponpassesSection) result.push(couponpassesSection);
    if (membersFullPriceSection) result.push(membersFullPriceSection);
    if (nonMembersFullSection) result.push(nonMembersFullSection);
    if (noahsArk50Section) result.push(noahsArk50Section);
    result.push(noahsArkSubscribers100);
    result.push(...otherSections);

    return result;
  };

  const dynamicMembershipBreakdown = generateDynamicMembershipBreakdown();

  // Debug logs
  const couponpassesProgress = localContest.couponpasses_assigned > 0 ?
    (localContest.couponpasses_used / localContest.couponpasses_assigned) * 100 : 0;

  console.log('Debug couponpasses data:', {
    contest_couponpasses_used: localContest.couponpasses_used,
    contest_couponpasses_assigned: localContest.couponpasses_assigned,
    couponpasses_progress: couponpassesProgress,
    economics_total_pass_codes_used: economics.total_pass_codes_used,
    contest_current_participants: localContest.current_participants,
    dynamicMembershipBreakdown
  });

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          borderRadius: 0,
          margin: 0
        }
      }}
      sx={{
        '& .MuiDialog-container': {
          padding: 0,
          margin: 0
        },
        '& .MuiDialog-paper': {
          margin: 0
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        pb: 0,
        borderBottom: '1px solid #e5e7eb'
      }}>
        <Box>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', color: '#111827', mb: 1 }}>
            {contest.name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<FileText className="w-4 h-4" />}
            onClick={async () => {
              try {
                // Crear el documento Word
                const doc = new Document({
                  sections: [{
                    properties: {},
                    children: [
                      // Título
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: `Economic Dashboard for the Contest "${contest.name}"`,
                            size: 44,  // 22pt
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_1,
                        alignment: AlignmentType.CENTER,
                        spacing: { after: 400 }
                      }),

                      // Información General del Concurso
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: t('details_contest.ContestConfiguration'),
                            size: 36,  // 18pt
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_2,
                        spacing: { before: 400, after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Status')}: `, bold: true, size: 28 }),
                          new TextRun({ text: contest.status || 'N/A', size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Phase')}: `, bold: true, size: 28 }),
                          new TextRun({ text: contest.phase?.replace(/_/g, ' ') || 'N/A', size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.AccessLevel')}: `, bold: true, size: 28 }),
                          new TextRun({ text: contest.subscribers_only ? 'Subscribers Only' : contest.members_only ? 'Members Only' : 'Public', size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.PhotoRewardsMode')}: `, bold: true, size: 28 }),
                          new TextRun({ text: contest.photo_rewards_mode || 'N/A', size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.PrizeType')}: `, bold: true, size: 28 }),
                          new TextRun({ text: contest.prize_type || 'N/A', size: 28 })
                        ]
                      }),

                      // Línea divisoria antes de Rendimiento Económico
                      new Paragraph({
                        border: {
                          bottom: {
                            color: '000000',
                            size: 6,
                            style: BorderStyle.SINGLE
                          }
                        },
                        spacing: { after: 300, before: 200 }
                      }),

                      // Rendimiento Económico
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: t('details_contest.economic_performance'),
                            size: 36,  // 18pt
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_2,
                        spacing: { before: 400, after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.total_revenue')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${totalRevenue.toFixed(2)}`, size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.participant')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(Number(economics.participant_revenue) || 0).toFixed(2)}`, size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.Sponsor')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(Number(contest.total_sponsor_cost) || 0).toFixed(2)}`, size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.total_costs')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${totalCosts.toFixed(2)}`, size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.Main_Prize')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(Number(economics.prize_costs) || 0).toFixed(2)}`, size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.Secondary_Prize')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(Number(economics.ops_costs) || 0).toFixed(2)}`, size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.net_profit')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(Number(economics.noahs_ark_profit) || 0).toFixed(2)}`, size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.Margin')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `${(Number(economics.profit_margin) || 0).toFixed(1)}%`, size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `ROI: `, bold: true, size: 28 }),
                          new TextRun({ text: `${(Number(economics.total_roi) || 0).toFixed(1)}%`, size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.Per_Participant')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(Number(economics.revenue_per_participant) || 0).toFixed(2)}`, size: 28 })
                        ]
                      }),

                      // Línea divisoria antes de Análisis de Ingresos
                      new Paragraph({
                        border: {
                          bottom: {
                            color: '000000',
                            size: 6,
                            style: BorderStyle.SINGLE
                          }
                        },
                        spacing: { after: 300, before: 200 }
                      }),

                      // Análisis de Ingresos
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: t('details_contest.RevenueAnalysis'),
                            size: 36,  // 18pt
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_2,
                        spacing: { before: 400, after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.FullPriceRevenue')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(economics.full_price_revenue || 0).toFixed(2)}`, size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.DiscountedRevenue')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(economics.discounted_revenue || 0).toFixed(2)}`, size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.VoucherValueUsed')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(economics.voucher_redemption_value || 0).toFixed(2)}`, size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.TotalDiscountsGiven')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(economics.total_discount_given || 0).toFixed(2)}`, size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.RevenueEfficiency')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `${(economics.revenue_efficiency_percentage || 0).toFixed(1)}%`, size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.PotentialRevenue')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(economics.potential_revenue || 0).toFixed(2)}`, size: 28 })
                        ]
                      }),

                      // Línea divisoria antes de Premios y Patrocinio
                      new Paragraph({
                        border: {
                          bottom: {
                            color: '000000',
                            size: 6,
                            style: BorderStyle.SINGLE
                          }
                        },
                        spacing: { after: 300, before: 200 }
                      }),

                      // Premios y Patrocinio
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: t('details_contest.PrizeandSponsorship'),
                            size: 36,  // 18pt
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_2,
                        spacing: { before: 400, after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: t('details_contest.PrizePoolBreakdown'),
                            size: 32,  // 16pt
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_3,
                        spacing: { before: 200, after: 100 }
                      }),
                      ...(contest.prize_first_place > 0 ? [new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Frist_Place')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${contest.prize_first_place.toFixed(2)}`, size: 28 })
                        ]
                      })] : []),
                      ...(contest.prize_second_place > 0 ? [new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Second_Place')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${contest.prize_second_place.toFixed(2)}`, size: 28 })
                        ]
                      })] : []),
                      ...(contest.prize_third_place > 0 ? [new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Third_Place')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${contest.prize_third_place.toFixed(2)}`, size: 28 })
                        ]
                      })] : []),
                      ...(contest.prize_fourth_place > 0 ? [new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Fourth_Place')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${contest.prize_fourth_place.toFixed(2)}`, size: 28 })
                        ]
                      })] : []),
                      new Paragraph({
                        children: [
                          new TextRun({ text: 'Total: ', bold: true, size: 28 }),
                          new TextRun({
                            text: `$${((Number(contest.prize_first_place) || 0) +
                              (Number(contest.prize_second_place) || 0) +
                              (Number(contest.prize_third_place) || 0) +
                              (Number(contest.prize_fourth_place) || 0) +
                              (Number(contest.prize_fifth_place) || 0) +
                              (Number(contest.prize_sixth_place) || 0) +
                              (Number(contest.prize_seventh_place) || 0) +
                              (Number(contest.prize_eighth_place) || 0)).toFixed(2)}`, size: 28
                          })
                        ]
                      }),

                      // Detalles de Patrocinio
                      ...((sponsorship || contest.sponsor_name) ? [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: t('details_contest.Sponsorship_Details'),
                              size: 32,  // 16pt
                              bold: true
                            })
                          ],
                          heading: HeadingLevel.HEADING_3,
                          spacing: { before: 200, after: 100 }
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({ text: `${t('details_contest.Sponsor')}: `, bold: true, size: 28 }),
                            new TextRun({ text: sponsorship?.sponsor_name || contest.sponsor_name || 'Noah\'s Ark', size: 28 })
                          ]
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({ text: `${t('details_contest.type')}: `, bold: true, size: 28 }),
                            new TextRun({ text: sponsorship?.sponsorship_type?.replace(/_/g, ' ') || contest.sponsorship_type?.replace(/_/g, ' ') || 'N/A', size: 28 })
                          ]
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({ text: `${t('details_contest.contribution')}: `, bold: true, size: 28 }),
                            new TextRun({ text: `$${(sponsorship?.total_sponsor_cost || contest.total_sponsor_cost || 0).toFixed(2)}`, size: 28 })
                          ]
                        }),
                        ...(sponsorship?.impressions > 0 ? [new Paragraph({
                          children: [
                            new TextRun({ text: `${t('details_contest.impressions')}: `, bold: true, size: 28 }),
                            new TextRun({ text: sponsorship.impressions.toLocaleString(), size: 28 })
                          ]
                        })] : []),
                        ...(sponsorship?.engagements > 0 ? [new Paragraph({
                          children: [
                            new TextRun({ text: `${t('details_contest.engagements')}: `, bold: true, size: 28 }),
                            new TextRun({ text: sponsorship.engagements.toLocaleString(), size: 28 })
                          ]
                        })] : []),
                        ...(sponsorship?.contact_name ? [new Paragraph({
                          children: [
                            new TextRun({ text: 'Contact: ', bold: true, size: 28 }),
                            new TextRun({ text: sponsorship.contact_name, size: 28 })
                          ]
                        })] : []),
                        ...(sponsorship?.contact_email ? [new Paragraph({
                          children: [
                            new TextRun({ text: 'Email: ', bold: true, size: 28 }),
                            new TextRun({ text: sponsorship.contact_email, size: 28 })
                          ]
                        })] : [])
                      ] : []),

                      // Photo Rewards Split
                      ...(contest.photo_rewards_mode !== 'disabled' ? [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: t('details_contest.PhotoRewardsSplit'),
                              size: 32,  // 16pt
                              bold: true
                            })
                          ],
                          heading: HeadingLevel.HEADING_3,
                          spacing: { before: 200, after: 100 }
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({ text: `${t('details_contest.Photo_Rewards_User_Percentage')}: `, bold: true, size: 28 }),
                            new TextRun({ text: `${contest.photo_rewards_user_percentage || 0}%`, size: 28 })
                          ]
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({ text: `${t('details_contest.Photo_Rewards_Organizer_Percentage')}: `, bold: true, size: 28 }),
                            new TextRun({ text: `${contest.photo_rewards_organizer_percentage || 0}%`, size: 28 })
                          ]
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({ text: `${t('details_contest.Photo_Rewards_Noahs_Ark_Percentage')}: `, bold: true, size: 28 }),
                            new TextRun({ text: `${contest.photo_rewards_noahs_ark_percentage || 0}%`, size: 28 })
                          ]
                        })
                      ] : []),

                      // Línea divisoria antes de Cronograma
                      new Paragraph({
                        border: {
                          bottom: {
                            color: '000000',
                            size: 6,
                            style: BorderStyle.SINGLE
                          }
                        },
                        spacing: { after: 300, before: 200 }
                      }),

                      // Cronograma
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: t('details_contest.ScheduleandTimeline'),
                            size: 36,  // 18pt
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_2,
                        spacing: { before: 400, after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.SubmissionPeriod')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `${new Date(contest.submission_start).toLocaleDateString()} - ${new Date(contest.submission_deadline).toLocaleDateString()}`, size: 28 })
                        ]
                      }),
                      ...(contest.voting_start ? [new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.VotingPeriod')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `${new Date(contest.voting_start).toLocaleDateString()} - ${new Date(contest.voting_end).toLocaleDateString()}`, size: 28 })
                        ]
                      })] : []),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.WinnerAnnouncement')}: `, bold: true, size: 28 }),
                          new TextRun({ text: new Date(contest.winner_announcement).toLocaleDateString(), size: 28 })
                        ]
                      }),

                      // Línea divisoria antes de Requisitos de Medios
                      new Paragraph({
                        border: {
                          bottom: {
                            color: '000000',
                            size: 6,
                            style: BorderStyle.SINGLE
                          }
                        },
                        spacing: { after: 300, before: 200 }
                      }),

                      // Requisitos de Medios
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: t('details_contest.MediaRequirements'),
                            size: 36,  // 18pt
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_2,
                        spacing: { before: 400, after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Allowed_Types')}: `, bold: true, size: 28 }),
                          new TextRun({ text: contest.allowed_media_types || 'both', size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Max_Images')}: `, bold: true, size: 28 }),
                          new TextRun({ text: String(contest.max_images || 0), size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Max_Videos')}: `, bold: true, size: 28 }),
                          new TextRun({ text: String(contest.max_videos || 0), size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Imagesize_limit')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `${contest.max_file_size || 0}MB`, size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Videosize_limit')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `${contest.max_file_size || 0}MB`, size: 28 })
                        ]
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Video_duration')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `${contest.max_video_duration_seconds || 0}s`, size: 28 })
                        ]
                      }),
                      ...(contest.media_requirements ? [new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Media_Requirements')}: `, bold: true, size: 28 }),
                          new TextRun({ text: contest.media_requirements, size: 28 })
                        ]
                      })] : []),

                      // Línea divisoria antes de Progreso de Participación
                      new Paragraph({
                        border: {
                          bottom: {
                            color: '000000',
                            size: 6,
                            style: BorderStyle.SINGLE
                          }
                        },
                        spacing: { after: 300, before: 200 }
                      }),

                      // Progreso de Participación
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: t('details_contest.ParticipationProgress'),
                            size: 36,  // 18pt
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_2,
                        spacing: { before: 400, after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Current_Progress')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `${economics.total_pass_codes_used || 0} / ${contest.max_participants || contest.min_participants} ${t('details_contest.participants')}`, size: 28 })
                        ]
                      }),

                      // Línea divisoria antes de Membership Breakdown
                      new Paragraph({
                        border: {
                          bottom: {
                            color: '000000',
                            size: 6,
                            style: BorderStyle.SINGLE
                          }
                        },
                        spacing: { after: 300, before: 200 }
                      }),

                      // Membership Breakdown
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: t('details_contest.MembershipAndPaymentBreakdown'),
                            size: 36,  // 18pt
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_2,
                        spacing: { before: 400, after: 200 }
                      }),
                      ...(dynamicMembershipBreakdown.map((member: any) => [
                        new Paragraph({
                          children: [
                            new TextRun({ text: `${member.membership_level || member.discount_type}: `, bold: true, size: 28 }),
                            new TextRun({ text: `${member.participant_count || 0} participantes`, size: 28 })
                          ]
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({ text: `  Ingresos: `, bold: true, size: 28 }),
                            new TextRun({ text: `$${(member.total_revenue || 0).toFixed(2)}`, size: 28 })
                          ]
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({ text: `  Porcentaje: `, bold: true, size: 28 }),
                            new TextRun({ text: `${(member.percentage_of_total || 0).toFixed(1)}%`, size: 28 })
                          ]
                        })
                      ]).flat())
                    ]
                  }]
                });

                // Generar el blob y descargar
                const blob = await Packer.toBlob(doc);
                const fileName = `${contest.name.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.docx`;
                saveAs(blob, fileName);
              } catch (error) {
                console.error('Error generating Word document:', error);
                void appDialog.alert('Error al generar el documento. Por favor, inténtalo de nuevo.');
              }
            }}
            sx={{
              backgroundColor: '#2563eb',
              '&:hover': { backgroundColor: '#1d4ed8' }
            }}
          >
            {t('details_contest.downloadData') || 'Descargar Datos'}
          </Button>
          {/* Botón Download Platforms ocultado */}
          {false && <Button
            variant="contained"
            startIcon={<FileText className="w-4 h-4" />}
            onClick={async () => {
              try {
                // Obtener el patrocinio completo
                const fullContest = localContest;
                const sponsorship = fullSponsorship || localContest.sponsorship;

                if (!sponsorship) {
                  void appDialog.alert('No hay patrocinio asociado a este concurso');
                  return;
                }

                // Función helper para cargar imágenes correctamente para el documento Word
                const loadImageForWord = async (imageUrl: string | null | undefined): Promise<{ data: Uint8Array; width: number; height: number } | null> => {
                  if (!imageUrl) {
                    console.warn('loadImageForWord: No image URL provided');
                    return null;
                  }

                  // Normalizar la URL usando getCompleteImagePath si es necesario
                  let normalizedUrl = imageUrl;
                  if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
                    normalizedUrl = getCompleteImagePath(imageUrl) || imageUrl;
                  }

                  console.log('loadImageForWord: Loading image from URL:', normalizedUrl);

                  // Función helper para cargar imagen usando proxy
                  const loadImageViaProxy = async (url: string): Promise<{ data: Uint8Array; width: number; height: number } | null> => {
                    try {
                      const proxyUrl = `${environment.apiBaseUrl}/proxy/image?url=${encodeURIComponent(url)}`;
                      console.log('loadImageForWord: Loading via proxy URL:', proxyUrl);

                      const response = await fetch(proxyUrl);
                      if (!response.ok) {
                        const errorText = await response.text();
                        console.error('loadImageForWord: Proxy request failed:', response.status, errorText);
                        throw new Error(`Proxy request failed: ${response.status} - ${errorText}`);
                      }

                      const contentType = response.headers.get('content-type');
                      if (!contentType || !contentType.startsWith('image/')) {
                        const errorText = await response.text();
                        console.error('loadImageForWord: Proxy returned non-image content:', contentType, errorText);
                        throw new Error(`Proxy returned non-image content: ${contentType}`);
                      }

                      const blob = await response.blob();
                      if (blob.size === 0) {
                        console.error('loadImageForWord: Received empty blob from proxy');
                        throw new Error('Received empty blob from proxy');
                      }

                      const arrayBuffer = await blob.arrayBuffer();
                      const uint8Array = new Uint8Array(arrayBuffer);

                      // Crear imagen para obtener dimensiones
                      const img = document.createElement('img');
                      const imageUrl = URL.createObjectURL(blob);

                      await new Promise((resolve, reject) => {
                        const timeout = setTimeout(() => {
                          URL.revokeObjectURL(imageUrl);
                          console.error('loadImageForWord: Image load timeout after 30 seconds');
                          reject(new Error('Image load timeout'));
                        }, 30000);

                        img.onload = () => {
                          clearTimeout(timeout);
                          URL.revokeObjectURL(imageUrl);
                          console.log('loadImageForWord: Image loaded successfully from blob, dimensions:', img.width, 'x', img.height);
                          resolve(null);
                        };
                        img.onerror = (error: any) => {
                          clearTimeout(timeout);
                          URL.revokeObjectURL(imageUrl);
                          console.error('loadImageForWord: Error loading image from blob:', error);
                          reject(new Error('Failed to load image from blob'));
                        };
                        img.src = imageUrl;
                      });

                      console.log('loadImageForWord: Image loaded via proxy successfully, dimensions:', img.width, 'x', img.height, 'data size:', uint8Array.length);
                      return {
                        data: uint8Array,
                        width: img.width,
                        height: img.height
                      };
                    } catch (error) {
                      console.error('loadImageForWord: Error loading via proxy:', error);
                      return null;
                    }
                  };

                  return await loadImageViaProxy(normalizedUrl);
                };

                // Función para crear imagen compuesta usando SOLO la configuración de platform_word
                // Esta función usa la misma lógica que el botón Word del Preview
                const createCompositeImage = async (platform: any): Promise<{ data: Uint8Array; width: number; height: number } | null> => {
                  const logoUrl = platform.logo_url || sponsorship.media_assets?.logo_url || sponsorship.logo_url || '';
                  const contestName = fullContest.name || 'NOMBRE DEL CONCURSO';
                  const customText = platform.custom_text || 'your ad goes here';

                  // Obtener configuración de platform_word (usar valores por defecto si no existe)
                  const config = platform.platform_word_config || {
                    box_width: 244.8,
                    box_height: 316.8,
                    box_x: 541.2,
                    box_y: 709.2,
                    contest_name_y: 40,
                    text_y: 296.8,
                    font_size_contest_name: 18,
                    font_size_custom_text: 16,
                    logo_y: 15,
                    logo_top_distance: 15,
                    logo_text_distance: 40,
                    border_width: 3
                  };

                  try {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    if (!ctx) return null;

                    // Cargar imagen de fondo
                    const imageUrl = platform.image || platform.image_url;
                    if (!imageUrl) {
                      throw new Error('No image URL provided');
                    }

                    // Normalizar URL si es necesario
                    let normalizedImageUrl = imageUrl;
                    if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
                      normalizedImageUrl = getCompleteImagePath(imageUrl) || imageUrl;
                    }

                    // Usar loadImageForWord para manejar CORS automáticamente
                    const imageData = await loadImageForWord(normalizedImageUrl);
                    if (!imageData) {
                      throw new Error(`Failed to load background image: ${normalizedImageUrl}`);
                    }

                    // Crear un elemento Image desde los datos cargados
                    const blob = new Blob([imageData.data as any], { type: 'image/png' });
                    const imageUrlFromBlob = URL.createObjectURL(blob);
                    const bgImage = document.createElement('img') as HTMLImageElement;

                    await new Promise((resolve, reject) => {
                      const timeout = setTimeout(() => {
                        URL.revokeObjectURL(imageUrlFromBlob);
                        reject(new Error('Background image load timeout'));
                      }, 30000);

                      bgImage.onload = () => {
                        clearTimeout(timeout);
                        URL.revokeObjectURL(imageUrlFromBlob);
                        resolve(null);
                      };
                      bgImage.onerror = (error: any) => {
                        clearTimeout(timeout);
                        URL.revokeObjectURL(imageUrlFromBlob);
                        reject(new Error(`Failed to load background image from blob`));
                      };
                      bgImage.src = imageUrlFromBlob;
                    });

                    // Usar las dimensiones reales de los datos cargados
                    bgImage.width = imageData.width;
                    bgImage.height = imageData.height;

                    // Usar el tamaño real de la imagen de fondo, pero limitar el tamaño máximo
                    // para reducir el tamaño del archivo
                    const maxCanvasDimension = 1200;
                    let canvasWidth = bgImage.width;
                    let canvasHeight = bgImage.height;

                    // Reducir tamaño si es muy grande
                    if (canvasWidth > maxCanvasDimension || canvasHeight > maxCanvasDimension) {
                      const scale = Math.min(maxCanvasDimension / canvasWidth, maxCanvasDimension / canvasHeight);
                      canvasWidth = Math.round(canvasWidth * scale);
                      canvasHeight = Math.round(canvasHeight * scale);
                    }

                    canvas.width = canvasWidth;
                    canvas.height = canvasHeight;

                    // Rellenar el canvas con fondo blanco
                    ctx.fillStyle = '#FFFFFF';
                    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

                    // Dibujar la imagen de fondo escalada
                    ctx.drawImage(bgImage, 0, 0, canvasWidth, canvasHeight);

                    // Usar configuración de platform_word para el volante
                    // Escalar las posiciones y tamaños según el tamaño real del canvas
                    // Las configuraciones de platform_word están en píxeles para una página de 816x1056
                    const referenceWidth = 816;
                    const referenceHeight = 1056;
                    const scaleX = canvasWidth / referenceWidth;
                    const scaleY = canvasHeight / referenceHeight;

                    const flyerX = (config.box_x || 541.2) * scaleX;
                    const flyerY = (config.box_y || 709.2) * scaleY;
                    const flyerWidth = (config.box_width || 244.8) * scaleX;
                    const flyerHeight = (config.box_height || 316.8) * scaleY;
                    const borderWidth = (config.border_width || 3) * Math.min(scaleX, scaleY);

                    // Dibujar fondo del volante
                    ctx.fillStyle = '#f5f5dc';
                    ctx.strokeStyle = '#ff6b35';
                    ctx.lineWidth = borderWidth;
                    const radius = 8;
                    ctx.beginPath();
                    ctx.moveTo(flyerX + radius, flyerY);
                    ctx.lineTo(flyerX + flyerWidth - radius, flyerY);
                    ctx.quadraticCurveTo(flyerX + flyerWidth, flyerY, flyerX + flyerWidth, flyerY + radius);
                    ctx.lineTo(flyerX + flyerWidth, flyerY + flyerHeight - radius);
                    ctx.quadraticCurveTo(flyerX + flyerWidth, flyerY + flyerHeight, flyerX + flyerWidth - radius, flyerY + flyerHeight);
                    ctx.lineTo(flyerX + radius, flyerY + flyerHeight);
                    ctx.quadraticCurveTo(flyerX, flyerY + flyerHeight, flyerX, flyerY + flyerHeight - radius);
                    ctx.lineTo(flyerX, flyerY + radius);
                    ctx.quadraticCurveTo(flyerX, flyerY, flyerX + radius, flyerY);
                    ctx.closePath();
                    ctx.fill();
                    ctx.stroke();

                    // Cargar y dibujar logo usando configuración de platform_word
                    let logoBottom = flyerY + (config.logo_y || 15);
                    if (logoUrl) {
                      try {
                        // Normalizar URL del logo si es necesario
                        let normalizedLogoUrl = logoUrl;
                        if (!logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
                          normalizedLogoUrl = getCompleteImagePath(logoUrl) || logoUrl;
                        }

                        // Usar loadImageForWord para cargar el logo
                        const logoData = await loadImageForWord(normalizedLogoUrl);
                        if (!logoData) {
                          throw new Error(`Failed to load logo image: ${normalizedLogoUrl}`);
                        }

                        // Crear un elemento Image desde los datos cargados
                        const logoBlob = new Blob([logoData.data as any], { type: 'image/png' });
                        const logoUrlFromBlob = URL.createObjectURL(logoBlob);
                        const logoImage = document.createElement('img') as HTMLImageElement;

                        await new Promise((resolve, reject) => {
                          const timeout = setTimeout(() => {
                            URL.revokeObjectURL(logoUrlFromBlob);
                            reject(new Error('Logo load timeout'));
                          }, 10000);

                          logoImage.onload = () => {
                            clearTimeout(timeout);
                            URL.revokeObjectURL(logoUrlFromBlob);
                            resolve(logoImage);
                          };
                          logoImage.onerror = (error: any) => {
                            clearTimeout(timeout);
                            URL.revokeObjectURL(logoUrlFromBlob);
                            reject(error);
                          };
                          logoImage.src = logoUrlFromBlob;
                        });

                        // Usar las dimensiones reales de los datos cargados
                        logoImage.width = logoData.width;
                        logoImage.height = logoData.height;

                        // Tamaño máximo del logo (150x150)
                        const maxLogoSize = 150;
                        const imageAspectRatio = logoImage.width / logoImage.height;

                        let logoWidth, logoHeight;
                        if (imageAspectRatio > 1) {
                          logoWidth = maxLogoSize;
                          logoHeight = maxLogoSize / imageAspectRatio;
                        } else {
                          logoHeight = maxLogoSize;
                          logoWidth = maxLogoSize * imageAspectRatio;
                        }

                        const logoX = flyerX + (flyerWidth - logoWidth) / 2;
                        const logoY = flyerY + (config.logo_y || 15) * scaleY;

                        // Dibujar el logo
                        ctx.drawImage(logoImage, logoX, logoY, logoWidth, logoHeight);

                        logoBottom = logoY + logoHeight;

                        // Dibujar texto del concurso usando configuración de platform_word
                        const contestNameText = contestName.toUpperCase();
                        const fontSizeContest = (config.font_size_contest_name || 18) * Math.min(scaleX, scaleY);
                        ctx.fillStyle = '#7c3aed';
                        ctx.font = `bold ${fontSizeContest}px "DejaVu Sans", "Liberation Sans", Arial, Helvetica, sans-serif`;
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'top';
                        const contestY = logoBottom + (config.logo_text_distance || 40) * scaleY;

                        const drawMultilineText = (text: string, x: number, y: number, maxWidth: number, lineHeight: number) => {
                          const words = text.split(' ');
                          const lines: string[] = [];
                          let currentLine = words[0];

                          for (let i = 1; i < words.length; i++) {
                            const word = words[i];
                            const testLine = currentLine + ' ' + word;
                            const metrics = ctx.measureText(testLine);
                            if (metrics.width > maxWidth && currentLine.length > 0) {
                              lines.push(currentLine);
                              currentLine = word;
                            } else {
                              currentLine = testLine;
                            }
                          }
                          lines.push(currentLine);

                          lines.forEach((line, index) => {
                            ctx.fillText(line, x, y + (index * lineHeight));
                          });

                          return lines.length;
                        };

                        const textMaxWidth = flyerWidth * 0.9;
                        const lineHeight = fontSizeContest * 1.2;
                        const lineCount = drawMultilineText(contestNameText, flyerX + flyerWidth / 2, contestY, textMaxWidth, lineHeight);
                        const finalContestY = contestY + (lineCount - 1) * lineHeight;

                        // Dibujar texto personalizado usando configuración de platform_word
                        const fontSizeCustom = (config.font_size_custom_text || 16) * Math.min(scaleX, scaleY);
                        ctx.font = `bold ${fontSizeCustom}px "DejaVu Sans", "Liberation Sans", Arial, Helvetica, sans-serif`;
                        const customTextY = finalContestY + ((config.text_y || 296.8) - (config.contest_name_y || 40)) * scaleY;
                        ctx.fillText(customText.toUpperCase(), flyerX + flyerWidth / 2, customTextY);
                      } catch (logoError) {
                        console.error('Error loading logo:', logoError);
                        // Continuar sin logo
                      }
                    } else {
                      // Si no hay logo, posicionar el texto desde arriba usando configuración
                      const contestNameText = contestName.toUpperCase();
                      const fontSizeContest = (config.font_size_contest_name || 18) * Math.min(scaleX, scaleY);
                      ctx.fillStyle = '#7c3aed';
                      ctx.font = `bold ${fontSizeContest}px "DejaVu Sans", "Liberation Sans", Arial, Helvetica, sans-serif`;
                      ctx.textAlign = 'center';
                      ctx.textBaseline = 'top';
                      const contestY = flyerY + (config.contest_name_y || 40) * scaleY;
                      ctx.fillText(contestNameText, flyerX + flyerWidth / 2, contestY);

                      const fontSizeCustom = (config.font_size_custom_text || 16) * Math.min(scaleX, scaleY);
                      ctx.font = `bold ${fontSizeCustom}px "DejaVu Sans", "Liberation Sans", Arial, Helvetica, sans-serif`;
                      const customTextY = contestY + ((config.text_y || 296.8) - (config.contest_name_y || 40)) * scaleY;
                      ctx.fillText(customText.toUpperCase(), flyerX + flyerWidth / 2, customTextY);
                    }

                    return new Promise((resolve) => {
                      canvas.toBlob((blob) => {
                        if (!blob) {
                          resolve(null);
                          return;
                        }
                        blob.arrayBuffer().then((buffer) => {
                          resolve({
                            data: new Uint8Array(buffer),
                            width: canvasWidth,
                            height: canvasHeight
                          });
                        });
                      }, 'image/png');
                    });
                  } catch (error) {
                    console.error('Error creating composite image:', error);
                    return null;
                  }
                };

                // Obtener todas las plataformas ordenadas según platform_order del patrocinio
                // Obtener imágenes desde la tabla platforms SOLO para las URLs, NO para posiciones
                let orderedPlatforms: any[] = [];

                try {
                  // Obtener todas las plataformas base SOLO para obtener las URLs de las imágenes
                  let allPlatformsMap: Record<string, any> = {};
                  try {
                    const platformsResponse = await fetch(`${environment.apiBaseUrl}/platforms`, {
                      headers: getApiHeadersForGet()
                    });
                    if (platformsResponse.ok) {
                      const allPlatforms = await platformsResponse.json();
                      // Crear un mapa de ID -> imagen para acceso rápido
                      allPlatforms.forEach((p: any) => {
                        allPlatformsMap[p.id] = {
                          image: p.image_url || p.image,
                          logo_url: p.logo_url
                        };
                      });
                      // También agregar voletaje si existe
                      const voletajePlatform = allPlatforms.find((p: any) => p.platform_type === 'voletaje');
                      if (voletajePlatform) {
                        allPlatformsMap['voletaje'] = {
                          image: voletajePlatform.image_url || voletajePlatform.image,
                          logo_url: voletajePlatform.logo_url
                        };
                      }
                    }
                  } catch (platformsError) {
                    console.warn('No se pudieron obtener plataformas base para imágenes:', platformsError);
                  }

                  // Verificar si existe platform_order en el sponsorship
                  if (sponsorship.platform_order && Array.isArray(sponsorship.platform_order) && sponsorship.platform_order.length > 0) {
                    // Ordenar el array por position
                    const sortedPlatformOrder = [...sponsorship.platform_order].sort((a: any, b: any) => a.position - b.position);

                    // Construir el array de plataformas según el orden de platform_order
                    // Para cada plataforma, obtener su configuración de platform_word
                    for (const orderItem of sortedPlatformOrder) {
                      const platformName = orderItem.name || orderItem.platform_id || 'default';

                      // Obtener configuración de platform_word para esta plataforma
                      let platformWordConfig = null;
                      try {
                        const configResponse = await fetch(`${environment.apiBaseUrl}/platform-word/${encodeURIComponent(platformName)}`, {
                          headers: getApiHeadersForGet()
                        });
                        if (configResponse.ok) {
                          platformWordConfig = await configResponse.json();
                        }
                      } catch (configError) {
                        console.warn(`No se pudo obtener configuración de platform_word para ${platformName}:`, configError);
                      }

                      // Obtener imagen de la plataforma
                      let platformImage = null;
                      let platformLogoUrl = sponsorship.media_assets?.logo_url || sponsorship.logo_url || '';

                      if (orderItem.type === 'voletaje') {
                        // Para voletaje, obtener imagen desde allPlatformsMap
                        if (allPlatformsMap['voletaje']) {
                          platformImage = allPlatformsMap['voletaje'].image;
                          if (allPlatformsMap['voletaje'].logo_url) {
                            platformLogoUrl = allPlatformsMap['voletaje'].logo_url;
                          }
                        }
                      } else if (orderItem.type === 'base') {
                        // Para plataformas base, obtener imagen desde allPlatformsMap usando platform_id
                        let platformId = orderItem.platform_id;
                        if (platformId.startsWith('base-')) {
                          platformId = platformId.replace('base-', '');
                        }
                        if (allPlatformsMap[platformId]) {
                          platformImage = allPlatformsMap[platformId].image;
                          if (allPlatformsMap[platformId].logo_url) {
                            platformLogoUrl = allPlatformsMap[platformId].logo_url;
                          }
                        }
                      } else if (orderItem.type === 'custom') {
                        // Para plataformas personalizadas, obtener de custom_platforms
                        if (sponsorship.custom_platforms && Array.isArray(sponsorship.custom_platforms)) {
                          const customPlatform = sponsorship.custom_platforms.find((cp: any) => {
                            const customId = `custom-${cp.name}-${cp.image}`;
                            return orderItem.platform_id.includes(customId) || orderItem.platform_id === customId;
                          });
                          if (customPlatform) {
                            platformImage = customPlatform.image;
                          }
                        }
                      }

                      // Agregar plataforma con su configuración de platform_word
                      orderedPlatforms.push({
                        name: platformName,
                        image: platformImage,
                        image_url: platformImage,
                        logo_url: platformLogoUrl,
                        custom_text: sponsorship.media_assets?.slogan || 'your ad goes here',
                        platform_word_config: platformWordConfig,
                        type: orderItem.type,
                        platform_id: orderItem.platform_id
                      });
                    }
                  } else {
                    // Si no hay platform_order, usar enabled_base_platforms
                    if (sponsorship.enabled_base_platforms && Array.isArray(sponsorship.enabled_base_platforms)) {
                      for (const platformId of sponsorship.enabled_base_platforms) {
                        const platformName = platformId;

                        // Obtener configuración de platform_word
                        let platformWordConfig = null;
                        try {
                          const configResponse = await fetch(`${environment.apiBaseUrl}/platform-word/${encodeURIComponent(platformName)}`, {
                            headers: getApiHeadersForGet()
                          });
                          if (configResponse.ok) {
                            platformWordConfig = await configResponse.json();
                          }
                        } catch (configError) {
                          console.warn(`No se pudo obtener configuración de platform_word para ${platformName}:`, configError);
                        }

                        // Obtener imagen desde allPlatformsMap
                        let platformImage = null;
                        let platformLogoUrl = sponsorship.media_assets?.logo_url || sponsorship.logo_url || '';
                        if (allPlatformsMap[platformId]) {
                          platformImage = allPlatformsMap[platformId].image;
                          if (allPlatformsMap[platformId].logo_url) {
                            platformLogoUrl = allPlatformsMap[platformId].logo_url;
                          }
                        }

                        orderedPlatforms.push({
                          name: platformName,
                          image: platformImage,
                          image_url: platformImage,
                          logo_url: platformLogoUrl,
                          custom_text: sponsorship.media_assets?.slogan || 'your ad goes here',
                          platform_word_config: platformWordConfig,
                          type: 'base',
                          platform_id: platformId
                        });
                      }
                    }

                    // Agregar plataformas personalizadas
                    if (sponsorship.custom_platforms && Array.isArray(sponsorship.custom_platforms)) {
                      for (const cp of sponsorship.custom_platforms) {
                        const platformName = cp.name || 'custom';

                        // Obtener configuración de platform_word
                        let platformWordConfig = null;
                        try {
                          const configResponse = await fetch(`${environment.apiBaseUrl}/platform-word/${encodeURIComponent(platformName)}`, {
                            headers: getApiHeadersForGet()
                          });
                          if (configResponse.ok) {
                            platformWordConfig = await configResponse.json();
                          }
                        } catch (configError) {
                          console.warn(`No se pudo obtener configuración de platform_word para ${platformName}:`, configError);
                        }

                        orderedPlatforms.push({
                          name: platformName,
                          image: cp.image,
                          image_url: cp.image,
                          logo_url: sponsorship.media_assets?.logo_url || sponsorship.logo_url || '',
                          custom_text: sponsorship.media_assets?.slogan || 'your ad goes here',
                          platform_word_config: platformWordConfig,
                          type: 'custom',
                          platform_id: `custom-${cp.name}-${cp.image}`
                        });
                      }
                    }
                  }
                } catch (error) {
                  console.error('Error obteniendo plataformas:', error);
                }

                if (orderedPlatforms.length === 0) {
                  void appDialog.alert('No hay plataformas para descargar');
                  return;
                }

                // Crear secciones para cada plataforma (cada una en su propia página)
                const sections = await Promise.all(orderedPlatforms.map(async (platform: any, index: number) => {
                  const children: any[] = [];

                  // Título de la plataforma
                  children.push(
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `${index + 1}. ${platform.name || platform.platform_type || 'Platform'}${platform.isBase ? ' (Base)' : ''}`,
                          size: 36,
                          bold: true
                        })
                      ],
                      spacing: { before: 400, after: 0, line: 240 }
                    })
                  );

                  if (platform.image || platform.image_url) {
                    try {
                      const compositeImage = await createCompositeImage(platform);

                      if (compositeImage) {
                        // Usar el tamaño real de la imagen compuesta
                        // Convertir píxeles a TWIPS: 1 pulgada = 1440 TWIPS, 1 píxel = 15 TWIPS (a 96 DPI)
                        // Pero limitar el tamaño máximo para que no sea demasiado grande
                        const maxWidthPx = 600; // Ancho máximo razonable
                        const maxHeightPx = 800; // Alto máximo razonable

                        let finalWidth = compositeImage.width;
                        let finalHeight = compositeImage.height;

                        // Escalar si es necesario para que quepa en el máximo
                        if (finalWidth > maxWidthPx || finalHeight > maxHeightPx) {
                          const scaleX = maxWidthPx / finalWidth;
                          const scaleY = maxHeightPx / finalHeight;
                          const scale = Math.min(scaleX, scaleY);
                          finalWidth = Math.round(finalWidth * scale);
                          finalHeight = Math.round(finalHeight * scale);
                        }

                        // Convertir a TWIPS
                        const imageWidthTwips = Math.round(finalWidth * 15);
                        const imageHeightTwips = Math.round(finalHeight * 15);

                        children.push(
                          new Paragraph({
                            children: [
                              new ImageRun({
                                data: compositeImage.data,
                                transformation: {
                                  width: imageWidthTwips,
                                  height: imageHeightTwips,
                                },
                                type: 'png',
                              } as any),
                            ],
                            alignment: AlignmentType.CENTER,
                            spacing: { before: 0, after: 300 }
                          })
                        );
                      } else {
                        // Si falla la creación compuesta, intentar solo la imagen de fondo
                        const imageData = await loadImageForWord(platform.image || platform.image_url);

                        if (imageData) {
                          // Limitar tamaño máximo
                          const maxWidthPx = 600;
                          const maxHeightPx = 800;

                          let finalWidth = imageData.width || 600;
                          let finalHeight = imageData.height || 400;

                          if (finalWidth > maxWidthPx || finalHeight > maxHeightPx) {
                            const scaleX = maxWidthPx / finalWidth;
                            const scaleY = maxHeightPx / finalHeight;
                            const scale = Math.min(scaleX, scaleY);
                            finalWidth = Math.round(finalWidth * scale);
                            finalHeight = Math.round(finalHeight * scale);
                          }

                          const imageWidthTwips = Math.round(finalWidth * 15);
                          const imageHeightTwips = Math.round(finalHeight * 15);

                          children.push(
                            new Paragraph({
                              children: [
                                new ImageRun({
                                  data: imageData.data,
                                  transformation: {
                                    width: imageWidthTwips,
                                    height: imageHeightTwips,
                                  },
                                  type: 'png',
                                } as any),
                              ],
                              alignment: AlignmentType.CENTER,
                              spacing: { after: 300 }
                            })
                          );
                        }
                      }
                    } catch (imgError) {
                      console.error('Error loading image:', imgError);
                    }
                  } else {
                    children.push(
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: 'No hay imagen disponible',
                            size: 24,
                            color: '666666'
                          })
                        ],
                        spacing: { after: 200 }
                      })
                    );
                  }

                  return {
                    properties: {
                      type: SectionType.NEXT_PAGE
                    },
                    children: children
                  };
                }));

                // Crear el documento Word
                const doc = new Document({
                  sections: [
                    // Primera sección con título
                    {
                      properties: {},
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: `Plataformas - ${fullContest.name || 'Contest'}`,
                              size: 44,
                              bold: true
                            })
                          ],
                          heading: HeadingLevel.HEADING_1,
                          alignment: AlignmentType.CENTER,
                          spacing: { after: 600 }
                        })
                      ]
                    },
                    // Resto de secciones (una por plataforma)
                    ...sections
                  ]
                });

                // Generar el blob y descargar
                const blob = await Packer.toBlob(doc);
                const fileName = `${fullContest.name.replace(/[^a-z0-9]/gi, '_')}_platforms_${new Date().toISOString().split('T')[0]}.docx`;
                saveAs(blob, fileName);
              } catch (error: any) {
                console.error('Error generating Word document with platforms:', error);
                void appDialog.alert('Error al generar el documento. Por favor, inténtalo de nuevo.');
              }
            }}
            sx={{
              backgroundColor: '#10b981',
              '&:hover': { backgroundColor: '#059669' }
            }}
          >
            {t('details_contest.downloadPlatforms') || 'Descargar Plataformas'}
          </Button>}
          <IconButton
            onClick={onClose}
            sx={{
              color: '#6b7280',
              '&:hover': {
                color: '#374151',
                backgroundColor: '#f3f4f6'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, margin: 0, width: '100%', maxWidth: '100%' }}>

        {/* Economic Performance Section */}
        <Box sx={{ p: 4, background: 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)', borderRadius: 2, mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#111827', mb: 3 }}>
            {t('details_contest.economic_performance')}
            {loadingEconomics && (
              <Typography variant="body2" sx={{ ml: 2, color: '#6b7280' }}>
                (Cargando datos reales...)
              </Typography>
            )}
          </Typography>

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Card sx={{ flex: '1 1 250px', minWidth: '250px', minHeight: 140, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('details_contest.total_revenue')}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2563eb', mb: 1 }}>
                  ${totalRevenue.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('details_contest.participant')}: ${(Number(economics.participant_revenue) || 0).toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('details_contest.Sponsor')}: ${(Number(economics.sponsor_cost) || Number(sponsorship?.reward_contribution) || Number(contest.total_sponsor_cost) || 0).toFixed(2)}
                </Typography>
                {economics.vote_booster_revenue > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Vote Boosters: ${(Number(economics.vote_booster_revenue) || 0).toFixed(2)}
                  </Typography>
                )}
                {economics.tag_revenue > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Tags Vendidos: ${(Number(economics.tag_revenue) || 0).toFixed(2)}
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card sx={{ flex: '1 1 250px', minWidth: '250px', minHeight: 140, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('details_contest.total_costs')}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#374151', mb: 1 }}>
                  ${totalCosts.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('details_contest.Grand_Prize')}: ${(Number(localContest.main_prize) || sumGrandPrizePlaces(localContest)).toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('details_contest.Secondary_Prize')}: ${(Number(localContest.secondary_prize) || sumSecondaryPrizePool(localContest) || Number(economics.ops_costs) || 0).toFixed(2)}
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: '1 1 250px', minWidth: '250px', minHeight: 140, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('details_contest.net_profit')}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 'bold',
                    color: (Number(economics.noahs_ark_profit) || 0) >= 0 ? '#059669' : '#dc2626',
                    mb: 1
                  }}
                >
                  ${(Number(economics.noahs_ark_profit) || 0).toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Noah's Ark
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 'medium',
                    color: (Number(economics.profit_margin) || 0) > 30 ? '#059669' : (Number(economics.profit_margin) || 0) > 10 ? '#2563eb' : '#d97706'
                  }}
                >
                  {(Number(economics.profit_margin) || 0).toFixed(1)}% {t('details_contest.Margin')}
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: '1 1 250px', minWidth: '250px', minHeight: 140, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  ROI
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 'bold',
                    color: (Number(economics.total_roi) || 0) >= 0 ? '#059669' : '#dc2626',
                    mb: 1
                  }}
                >
                  {(Number(economics.total_roi) || 0).toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('details_contest.Per_Participant')}:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#374151' }}>
                  ${(Number(economics.revenue_per_participant) || 0).toFixed(2)}
                </Typography>
              </CardContent>
            </Card>

          </Box>
        </Box>

        {/* Detailed Analysis Sections - Three Columns */}
        <Box sx={{ display: 'flex', gap: 3, mb: 8, flexWrap: 'wrap', alignItems: 'stretch' }}>
          {/* Membership & Payment Breakdown (Left Column) */}
          <Box sx={{ flex: '1 1 300px', minWidth: '300px', display: 'flex' }}>
            <MembershipBreakdownChart
              membershipBreakdown={dynamicMembershipBreakdown}
              totalParticipants={localContest.current_participants}
              entranceFee={localContest.entrance_fee}
              membersOnly={localContest.members_only}
              subscribersOnly={localContest.subscribers_only}
              contest={localContest}
            />
          </Box>

          {/* Revenue Analysis (Middle Column) */}
          <Box sx={{ flex: '1 1 300px', minWidth: '300px', display: 'flex' }}>
            <Card sx={{ height: '100%', minHeight: '600px', display: 'flex', flexDirection: 'column', width: '100%' }}>
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#111827', mb: 3, display: 'flex', alignItems: 'center' }}>
                  <TrendingUp className="w-4 h-4 mr-2" style={{ color: '#6b7280' }} />
                  {t('details_contest.RevenueAnalysis')}
                </Typography>

                <Stack spacing={2} sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{t('details_contest.FullPriceRevenue')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#059669' }}>
                      ${(economics.full_price_revenue || 0).toFixed(2)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{t('details_contest.DiscountedRevenue')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2563eb' }}>
                      ${(economics.discounted_revenue || 0).toFixed(2)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{t('details_contest.VoucherValueUsed')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#d97706' }}>
                      ${(economics.voucher_redemption_value || 0).toFixed(2)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{t('details_contest.TotalDiscountsGiven')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#d97706' }}>
                      ${(economics.total_discount_given || 0).toFixed(2)}
                    </Typography>
                  </Box>

                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: '#dbeafe',
                      p: 2,
                      mt: 2,
                      borderRadius: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#1e3a8a' }}>{t('details_contest.RevenueEfficiency')}:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1d4ed8' }}>
                        {(economics.revenue_efficiency_percentage || 0).toFixed(1)}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#1e3a8a' }}>{t('details_contest.PotentialRevenue')}:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1d4ed8' }}>
                        ${(economics.potential_revenue || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Paper>

                  {/* Prize & Sponsorship Section */}
                  <Divider sx={{ my: 3 }} />

                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#111827', mb: 3, display: 'flex', alignItems: 'center' }}>
                    <Gift className="w-4 h-4 mr-2" style={{ color: '#6b7280' }} />
                    {t('details_contest.PrizeandSponsorship')}
                  </Typography>

                  {/* Disposición vertical: uno debajo del otro */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Prize Pool Breakdown */}
                    <Paper
                      elevation={0}
                      sx={{
                        backgroundColor: '#f8f0fc',
                        p: 2,
                        borderRadius: 1
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#6b21a8', mb: 2 }}>
                        {t('details_contest.PrizePoolBreakdown')}
                      </Typography>
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 1, borderBottom: '1px dashed #d8b4fe' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#6b21a8' }}>
                            {t('details_contest.Grand_Prize')}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#6b21a8' }}>
                            ${(Number(localContest.main_prize) || sumGrandPrizePlaces(localContest)).toFixed(2)}
                          </Typography>
                        </Box>
                        {localContest.prize_first_place > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('details_contest.Frist_Place')} ({formatPercent(getGrandPrizeSharePercent(Number(localContest.prize_first_place) || 0, Number(localContest.main_prize) || sumGrandPrizePlaces(localContest)))}):
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>${Number(localContest.prize_first_place).toFixed(2)}</Typography>
                          </Box>
                        )}
                        {localContest.prize_second_place > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('details_contest.Second_Place')} ({formatPercent(getGrandPrizeSharePercent(Number(localContest.prize_second_place) || 0, Number(localContest.main_prize) || sumGrandPrizePlaces(localContest)))}):
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>${Number(localContest.prize_second_place).toFixed(2)}</Typography>
                          </Box>
                        )}
                        {localContest.prize_third_place > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('details_contest.Third_Place')} ({formatPercent(getGrandPrizeSharePercent(Number(localContest.prize_third_place) || 0, Number(localContest.main_prize) || sumGrandPrizePlaces(localContest)))}):
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>${Number(localContest.prize_third_place).toFixed(2)}</Typography>
                          </Box>
                        )}
                        {localContest.prize_fourth_place > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('details_contest.Fourth_Place')} ({formatPercent(getGrandPrizeSharePercent(Number(localContest.prize_fourth_place) || 0, Number(localContest.main_prize) || sumGrandPrizePlaces(localContest)))}):
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>${Number(localContest.prize_fourth_place).toFixed(2)}</Typography>
                          </Box>
                        )}
                        {sumSecondaryPrizePool(localContest) > 0 && (
                          <>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">{t('details_contest.Secondary_Prize')}:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                ${sumSecondaryPrizePool(localContest).toFixed(2)}
                              </Typography>
                            </Box>
                          </>
                        )}
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#6b21a8' }}>{t('prizesinfo.total_prize_pool')}:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#6b21a8' }}>
                            ${(Number(localContest.prize_pool_total) || calculateTotalPrizePool(localContest)).toFixed(2)}
                          </Typography>
                        </Box>
                        {/* SB-002: monto pendiente por financiar = Gran Premio − aporte del sponsor. */}
                        {Number(sponsorship?.reward_contribution) > 0 && (() => {
                          const target = Number(localContest.main_prize) || sumGrandPrizePlaces(localContest);
                          const funded = Number(sponsorship?.reward_contribution) || 0;
                          const pending = Math.round((target - funded) * 100) / 100;
                          return (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" sx={{ fontWeight: 'medium', color: pending > 0 ? '#b45309' : '#166534' }}>
                                {t('prizesinfo.pending_to_fund') !== 'prizesinfo.pending_to_fund'
                                  ? t('prizesinfo.pending_to_fund')
                                  : 'Pendiente por financiar'}:
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: pending > 0 ? '#b45309' : '#166534' }}>
                                {pending > 0 ? `$${pending.toFixed(2)}` : '✓ Financiado'}
                              </Typography>
                            </Box>
                          );
                        })()}
                      </Stack>
                    </Paper>

                    {/* Sponsorship Details debajo de Distribución de Premios */}
                    {(sponsorship || contest.sponsor_name) && (
                      <Paper
                        elevation={0}
                        sx={{
                          backgroundColor: '#e6faed',
                          p: 2,
                          borderRadius: 1
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#166534', mb: 2 }}>
                          {t('details_contest.Sponsorship_Details')}
                        </Typography>
                        {loadingSponsorship ? (
                          <Typography variant="body2" color="text.secondary">Loading sponsorship details...</Typography>
                        ) : (
                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">{t('details_contest.Sponsor')}:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {sponsorship?.sponsor_name || contest.sponsor_name || 'Noah\'s Ark'}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">{t('details_contest.type')}:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {sponsorship?.sponsorship_type?.replace(/_/g, ' ') || contest.sponsorship_type?.replace(/_/g, ' ') || 'N/A'}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">{t('details_contest.Reward_Contribution')}:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#166534' }}>
                                ${(Number(sponsorship?.reward_contribution) || Number(localContest.main_prize) || 0).toFixed(2)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">{t('details_contest.contribution')}:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                ${(sponsorship?.total_sponsor_cost || contest.total_sponsor_cost || 0).toFixed(2)}
                              </Typography>
                            </Box>
                            {sponsorship?.impressions > 0 && (
                              <>
                                <Divider sx={{ my: 1 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2" color="text.secondary">{t('details_contest.impressions')}:</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{sponsorship.impressions.toLocaleString()}</Typography>
                                </Box>
                              </>
                            )}
                            {sponsorship?.engagements > 0 && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">{t('details_contest.engagements')}:</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{sponsorship.engagements.toLocaleString()}</Typography>
                              </Box>
                            )}
                            {sponsorship?.contact_name && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">Contact:</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{sponsorship.contact_name}</Typography>
                              </Box>
                            )}
                            {sponsorship?.contact_email && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">Email:</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{sponsorship.contact_email}</Typography>
                              </Box>
                            )}
                            {sponsorship?.id && onUpdateSponsorship && (
                              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => {
                                    onClose();
                                    onUpdateSponsorship(contest);
                                  }}
                                  sx={{
                                    backgroundColor: '#2563eb',
                                    color: 'white',
                                    '&:hover': {
                                      backgroundColor: '#1d4ed8'
                                    }
                                  }}
                                >
                                  Actualizar datos
                                </Button>
                              </Box>
                            )}
                          </Stack>
                        )}
                      </Paper>
                    )}
                    {/* Photo Rewards Split debajo del patrocinio */}
                    {contest.photo_rewards_mode !== 'disabled' && (
                      <Paper
                        elevation={0}
                        sx={{
                          backgroundColor: '#fffbe6',
                          p: 2,
                          borderRadius: 1
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#a16207', mb: 2 }}>
                          {t('details_contest.PhotoRewardsSplit')}
                        </Typography>
                        <Stack spacing={1}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">{t('details_contest.Photo_Rewards_User_Percentage')}:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{contest.photo_rewards_user_percentage || 0}%</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">{t('details_contest.Photo_Rewards_Organizer_Percentage')}:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{contest.photo_rewards_organizer_percentage || 0}%</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">{t('details_contest.Photo_Rewards_Noahs_Ark_Percentage')}:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{contest.photo_rewards_noahs_ark_percentage || 0}%</Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>

        </Box>

        {/* Propuesta del Sponsorship Builder (NAPC) enlazada a este concurso */}
        <Box sx={{ mb: 4 }}>
          <ProposalShowcase contestId={contest.id} title="Propuesta del Sponsorship Builder" />
        </Box>

        {/* Economy Flow Section - Complete Flow Visualization */}
        <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', borderRadius: 2, mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#111827', mb: 3, display: 'flex', alignItems: 'center' }}>
            <ArrowRight className="w-5 h-5 mr-2" style={{ color: '#2563eb' }} />
            {t('economyflow.completeFlow')}
          </Typography>

          {/* Economy Flow Diagram */}
          <Card sx={{ mb: 3, backgroundColor: '#ffffff' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#111827', mb: 3 }}>
                {t('economyflow.flowTitle')}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'center', justifyContent: 'space-between' }}>
                {/* Sponsors */}
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: '#dbeafe', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e40af', mb: 1 }}>
                    {t('economyflow.sponsorsLabel')}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1e3a8a', mb: 1 }}>
                    ${(Number(economics.sponsor_cost) || Number(sponsorship?.total_sponsor_cost) || Number(contest.linkedProposal?.total_investment_usd) || Number(contest.total_sponsor_cost) || 0).toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('economyflow.sponsorsDescription')}
                  </Typography>
                </Box>

                <ArrowRight className="w-6 h-6" style={{ color: '#6b7280' }} />

                {/* Ambassadors */}
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: '#f3e8ff', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#6b21a8', mb: 1 }}>
                    {t('economyflow.ambassadorsLabel')}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#7c3aed', mb: 1 }}>
                    ${(Number(economics.tag_revenue) || 0).toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('economyflow.ambassadorsDescription')}
                  </Typography>
                </Box>

                <ArrowRight className="w-6 h-6" style={{ color: '#6b7280' }} />

                {/* Participants */}
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: '#ecfdf5', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#065f46', mb: 1 }}>
                    {t('economyflow.participantsLabel')}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#047857', mb: 1 }}>
                    ${(Number(economics.participant_revenue) || 0).toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('economyflow.participantsDescription')}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: '#059669', fontWeight: 'medium' }}>
                    {t('economyflow.voteBoostersLabel')} ${(Number(economics.vote_booster_revenue) || 0).toFixed(2)}
                  </Typography>
                </Box>

                <ArrowRight className="w-6 h-6" style={{ color: '#6b7280' }} />

                {/* Noah's Ark */}
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: '#fef3c7', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#92400e', mb: 1 }}>
                    {t('economyflow.noahsArkLabel')}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#b45309', mb: 1 }}>
                    ${(Number(economics.noahs_ark_profit) || 0).toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('economyflow.netProfitLabel')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Value Loops Section */}
          <Card sx={{ backgroundColor: '#ffffff' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#111827', mb: 3 }}>
                {t('economyflow.threeValueLoops')}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Loop 1: Sponsor Value Loop */}
                <Paper elevation={0} sx={{ p: 2, backgroundColor: '#eff6ff', borderRadius: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1e40af', mb: 1 }}>
                    {t('economyflow.sponsorValueLoopTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('economyflow.sponsorValueLoopDescription')}
                  </Typography>
                </Paper>

                {/* Loop 2: Ambassador Profit Loop */}
                <Paper elevation={0} sx={{ p: 2, backgroundColor: '#faf5ff', borderRadius: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#6b21a8', mb: 1 }}>
                    {t('economyflow.ambassadorProfitLoopTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('economyflow.ambassadorProfitLoopDescription')}
                  </Typography>
                </Paper>

                {/* Loop 3: Platform Revenue Loop */}
                <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f0fdf4', borderRadius: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#065f46', mb: 1 }}>
                    {t('economyflow.platformRevenueLoopTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('economyflow.platformRevenueLoopDescription')}
                  </Typography>
                </Paper>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Contest Details Section - Three Columns */}
        <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
          {/* Contest Configuration (Left Column) */}
          <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#111827', mb: 3, display: 'flex', alignItems: 'center' }}>
                  <Settings className="w-4 h-4 mr-2" style={{ color: '#6b7280' }} />
                  {t('details_contest.ContestConfiguration')}
                </Typography>

                <Stack spacing={2} sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{t('details_contest.Status')}:</Typography>
                    <Chip
                      label={contest.status}
                      color={contest.status === 'approved' ? 'success' : contest.status === 'pending' ? 'warning' : 'error'}
                      size="small"
                    />
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{t('details_contest.Phase')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{contest.phase?.replace(/_/g, ' ') || 'N/A'}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{t('details_contest.AccessLevel')}:</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {contest.subscribers_only ? (
                        <Chip icon={<SubscriptionsIcon />} label="Subscribers Only" color="primary" size="small" />
                      ) : contest.members_only ? (
                        <Chip icon={<GroupIcon />} label="Members Only" color="info" size="small" />
                      ) : (
                        <Chip icon={<PublicIcon />} label="Public" color="success" size="small" />
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{t('details_contest.PhotoRewardsMode')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{contest.photo_rewards_mode}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{t('details_contest.PrizeType')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{contest.prize_type}</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>

          {/* Schedule & Timeline (Middle Column) */}
          <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#111827', mb: 3, display: 'flex', alignItems: 'center' }}>
                  <Calendar className="w-4 h-4 mr-2" style={{ color: '#6b7280' }} />
                  {t('details_contest.ScheduleandTimeline')}
                </Typography>

                <Stack spacing={2} sx={{ flex: 1 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t('details_contest.SubmissionPeriod')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {new Date(contest.submission_start).toLocaleDateString()} - {new Date(contest.submission_deadline).toLocaleDateString()}
                    </Typography>
                  </Box>

                  {contest.voting_start && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t('details_contest.VotingPeriod')}:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {new Date(contest.voting_start).toLocaleDateString()} - {new Date(contest.voting_end).toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t('details_contest.WinnerAnnouncement')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {new Date(contest.winner_announcement).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>

          {/* Media Requirements (Right Column) */}
          <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#111827', mb: 3, display: 'flex', alignItems: 'center' }}>
                  <Camera className="w-4 h-4 mr-2" style={{ color: '#6b7280' }} />
                  {t('details_contest.MediaRequirements')}
                </Typography>

                <Stack spacing={2} sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{t('details_contest.Allowed_Types')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{contest.allowed_media_types || 'both'}</Typography>
                  </Box>

                  {((contest.allowed_media_types || 'both') === 'image' || (contest.allowed_media_types || 'both') === 'both') && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">{t('details_contest.Max_Images')}:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{contest.max_images || 0}</Typography>
                    </Box>
                  )}

                  {((contest.allowed_media_types || 'both') === 'video' || (contest.allowed_media_types || 'both') === 'both') && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">{t('details_contest.Max_Videos')}:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{contest.max_videos || 0}</Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{t('details_contest.Imagesize_limit')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{contest.max_file_size || 0}MB</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{t('details_contest.Videosize_limit')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{contest.max_file_size || 0}MB</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{t('details_contest.Video_duration')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{contest.max_video_duration_seconds}s</Typography>
                  </Box>

                  {contest.media_requirements && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">{t('details_contest.Media_Requirements')}:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'pre-wrap' }}>
                        {contest.media_requirements}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Participation Progress */}
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#111827', mb: 3, display: 'flex', alignItems: 'center' }}>
              <Users className="w-4 h-4 mr-2" style={{ color: '#6b7280' }} />
              {t('details_contest.ParticipationProgress')}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">{t('details_contest.Current_Progress')}:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'semibold' }}>
                    {economics.total_pass_codes_used || 0} / {contest.max_participants || contest.min_participants} {t('details_contest.participants')}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, ((economics.total_pass_codes_used || 0) / (contest.max_participants || contest.min_participants)) * 100)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#e5e7eb',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: (economics.total_pass_codes_used || 0) >= (contest.max_participants || contest.min_participants) ? '#10b981' : '#3b82f6',
                      borderRadius: 4
                    }
                  }}
                />
              </Box>

              <Alert
                severity={(economics.total_pass_codes_used || 0) >= (contest.max_participants || contest.min_participants) ? 'success' : 'warning'}
                sx={{
                  minWidth: 200,
                  '& .MuiAlert-message': {
                    fontWeight: 'semibold'
                  }
                }}
              >
                {(economics.total_pass_codes_used || 0) >= (contest.max_participants || contest.min_participants)
                  ? '✓ Ready to Launch'
                  : `${t('details_contest.Need')} ${(contest.max_participants || contest.min_participants) - (economics.total_pass_codes_used || 0)} ${t('details_contest.more')}`
                }
              </Alert>
            </Box>
          </CardContent>
        </Card>

      </DialogContent>
    </Dialog>
  );
};

interface PetContestDashboardProps {
  onLogout?: () => void;
}

// PDF generation moved to server - component removed from client to avoid React version conflicts

const PetContestDashboard = ({ onLogout }: PetContestDashboardProps) => {
  const { locale, t } = useI18n();
  const navigate = useNavigate();
  const [contests, setContests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshingContests, setRefreshingContests] = useState(false);
  const [filter, setFilter] = useState('active');
  const [contestsSearchTerm, setContestsSearchTerm] = useState('');
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920);

  // Estado para el modo de configuración (Test API / Normal)
  const [apiMode, setApiMode] = useState<'test' | 'normal'>(() => {
    try {
      const savedMode = localStorage.getItem('petflix_api_mode');
      return (savedMode === 'test' || savedMode === 'normal') ? savedMode : 'normal';
    } catch (e) {
      return 'normal';
    }
  });

  // Estado para el menú de configuración
  const [configMenuAnchor, setConfigMenuAnchor] = useState<null | HTMLElement>(null);
  const configMenuOpen = Boolean(configMenuAnchor);
  const [s3UploadModalOpen, setS3UploadModalOpen] = useState(false);
  const [showWordDocModal, setShowWordDocModal] = useState(false);
  const [wordDocImageUrl, setWordDocImageUrl] = useState('');
  const [wordDocLoading, setWordDocLoading] = useState(false);

  // Funciones para manejar el menú de configuración
  const handleConfigMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setConfigMenuAnchor(event.currentTarget);
  };

  const handleConfigMenuClose = () => {
    setConfigMenuAnchor(null);
  };

  const handleModeChange = (mode: 'test' | 'normal') => {
    setApiMode(mode);
    handleConfigMenuClose();
  };

  // Guardar el modo en localStorage cuando cambie
  useEffect(() => {
    try {
      localStorage.setItem('petflix_api_mode', apiMode);
    } catch (e) {
      console.warn('Error guardando modo API:', e);
    }
  }, [apiMode]);

  // Sincronizar token de localStorage con configuración al cargar la página
  useEffect(() => {
    try {
      const tokenFromStorage = petflixAuthService.getAccessToken();
      if (tokenFromStorage && tokenFromStorage.trim() !== '') {
        // Sincronizar el token de configuración con el del localStorage
        setPetflixAccessToken(tokenFromStorage);
        console.log('✅ Token del usuario logueado sincronizado');
      }
    } catch (e) {
      console.warn('Error sincronizando token:', e);
    }
  }, []);

  // Detectar cambios en el ancho de la ventana
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determinar si es móvil (ancho < 1000px)
  const isMobile = windowWidth < 1000;
  const [selectedContest, setSelectedContest] = useState<any>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreatingContest, setIsCreatingContest] = useState(false);
  const [showContestForm, setShowContestForm] = useState(false);
  const [editingContest, setEditingContest] = useState<any>(null);
  const createFormRef = useRef<CreateContestFormHandle>(null);
  const editFormRef = useRef<CreateContestFormHandle>(null);

  const handleCreateFormClose = async () => {
    const canClose = (await createFormRef.current?.requestClose()) ?? true;
    if (canClose) setShowCreateForm(false);
  };

  const handleEditFormClose = async () => {
    const canClose = (await editFormRef.current?.requestClose()) ?? true;
    if (canClose) {
      setShowContestForm(false);
      setEditingContest(null);
    }
  };
  const [showSponsorshipForm, setShowSponsorshipForm] = useState(false);
  const [showSponsorshipOptions, setShowSponsorshipOptions] = useState(false);
  const [expandedFloatingButton1, setExpandedFloatingButton1] = useState(false);
  const [expandedFloatingButton2, setExpandedFloatingButton2] = useState(false);
  const [expandedFloatingButton3, setExpandedFloatingButton3] = useState(false);

  // Cerrar botones flotantes al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Si el click no es dentro de los botones flotantes, cerrarlos
      if (!target.closest('[data-floating-button]')) {
        setExpandedFloatingButton1(false);
        setExpandedFloatingButton2(false);
        setExpandedFloatingButton3(false);
      }
    };

    if (expandedFloatingButton1 || expandedFloatingButton2 || expandedFloatingButton3) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [expandedFloatingButton1, expandedFloatingButton2, expandedFloatingButton3]);
  const [showContestConfirmation, setShowContestConfirmation] = useState(false);
  const [showReplaceSponsorConfirmation, setShowReplaceSponsorConfirmation] = useState(false);
  const [existingSponsor, setExistingSponsor] = useState<any>(null);
  const [sponsorshipData, setSponsorshipData] = useState<any>(null);
  const [selectedContestForSponsorship, setSelectedContestForSponsorship] = useState<any>(null);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [selectedSponsor, setSelectedSponsor] = useState<any>(null);
  const [contestsWithSponsors, setContestsWithSponsors] = useState<Set<string>>(new Set());
  const [simulatingParticipant, setSimulatingParticipant] = useState(false);
  const [couponPasses, setCouponPasses] = useState<any[]>([]);
  const [couponCodes, setCouponCodes] = useState<any[]>([]);
  const [passCodes, setPassCodes] = useState<any[]>([]);
  const [inventorySummary, setInventorySummary] = useState({
    couponPassCount: 0,
    passCodeCount: 0,
    couponCodeCount: 0,
  });
  const couponInventoryLoadRef = useRef<Promise<void> | null>(null);
  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [selectedContestForParticipant, setSelectedContestForParticipant] = useState<{
    id: string;
    entranceFee: number;
    name: string;
  } | null>(null);
  const [showContestParticipantsModal, setShowContestParticipantsModal] = useState(false);
  const [selectedContestForParticipants, setSelectedContestForParticipants] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [contestParticipants, setContestParticipants] = useState<any[]>([]);
  const [participantVoteStats, setParticipantVoteStats] = useState<any>(null);
  const [loadingParticipantVoteStats, setLoadingParticipantVoteStats] = useState(false);
  const [showLiveModal, setShowLiveModal] = useState(false);
  const [selectedContestForLive, setSelectedContestForLive] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [liveTopParticipants, setLiveTopParticipants] = useState<any[]>([]);
  const [selectedContestStatus, setSelectedContestStatus] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [selectedMediaType, setSelectedMediaType] = useState<'image' | 'video' | null>(null);
  const [isVideoExpanded, setIsVideoExpanded] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [galleryMediaItems, setGalleryMediaItems] = useState<Array<{ url: string; type: 'image' | 'video' }>>([]);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const [showWinnersModal, setShowWinnersModal] = useState(false);
  const [contestWinners, setContestWinners] = useState<any[]>([]);
  const [selectedContestForWinners, setSelectedContestForWinners] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [showCouponPassesModal, setShowCouponPassesModal] = useState(false);
  const [couponPassesSearchTerm, setCouponPassesSearchTerm] = useState('');
  // Vista unificada "Passes & Codes": filtros compartidos por Sistema/Tipo/Estado.
  // Tab actual del modal "Passes & Codes": CouponPass vs Contest Pass.
  const [unifiedSystemFilter, setUnifiedSystemFilter] = useState<'coupon' | 'pass'>('coupon');
  const [unifiedKindFilter, setUnifiedKindFilter] = useState<'all' | 'printed' | 'digital'>('all');
  const [unifiedStatusFilter, setUnifiedStatusFilter] = useState<'all' | 'available' | 'used'>('all');
  const [couponPassQRLinks, setCouponPassQRLinks] = useState<Record<string, string | null>>({});
  const [couponPassQRClaimed, setCouponPassQRClaimed] = useState<Record<string, boolean>>({});
  const [couponPassQRTagTypes, setCouponPassQRTagTypes] = useState<Record<string, string>>({});
  const [couponPassQRTagCodes, setCouponPassQRTagCodes] = useState<Record<string, string | null>>({});
  const [contestTokens, setContestTokens] = useState<Record<string, any>>({});
  const [rotatingContestTokens, setRotatingContestTokens] = useState<Record<string, boolean>>({});
  const [selectedQRCode, setSelectedQRCode] = useState<string | null>(null);
  // Estado para almacenar los pet-codes por concurso (mapeado por código)
  const [petCodesByContest, setPetCodesByContest] = useState<Record<string, Record<string, { activated: boolean }>>>({});
  const [loadingPetCodes, setLoadingPetCodes] = useState<Record<string, boolean>>({});
  const [showImportsModal, setShowImportsModal] = useState(false);
  const [imports, setImports] = useState<any[]>([]);
  const [loadingImports, setLoadingImports] = useState(false);
  const [selectedImport, setSelectedImport] = useState<any | null>(null);
  const [showMappingsModal, setShowMappingsModal] = useState(false);
  const [fieldMappings, setFieldMappings] = useState<any[]>([]);
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [showPassCodesModal, setShowPassCodesModal] = useState(false);
  const [showPlatformsModal, setShowPlatformsModal] = useState(false);
  const [platformsSearchTerm, setPlatformsSearchTerm] = useState('');
  const [platforms, setPlatforms] = useState<any[]>([]);
  const [loadingPlatforms, setLoadingPlatforms] = useState(false);
  const [showAddPlatformModal, setShowAddPlatformModal] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState<any | null>(null);
  const [showDeleteTagsModal, setShowDeleteTagsModal] = useState(false);
  const [platformImage, setPlatformImage] = useState<string | null>(null);
  const [platformImageFileName, setPlatformImageFileName] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState<boolean>(false);
  const [platformName, setPlatformName] = useState<string>('');
  const [platformPosition, setPlatformPosition] = useState({ x: 50, y: 50, width: 200, height: 150 });
  const [tempWidth, setTempWidth] = useState<string>('200');
  const [tempHeight, setTempHeight] = useState<string>('150');
  const [logoSize, setLogoSize] = useState({ width: 60, height: 60 }); // Porcentaje del volante
  const [tempLogoWidth, setTempLogoWidth] = useState<string>('60');
  const [tempLogoHeight, setTempLogoHeight] = useState<string>('60');
  const [platformShape, setPlatformShape] = useState<'circular' | 'square'>('circular');
  const [platformLogoUrl, setPlatformLogoUrl] = useState<string>('');
  const [platformCustomText, setPlatformCustomText] = useState<string>('your ad goes here');
  const [platformFontSize, setPlatformFontSize] = useState<number>(0.9);
  const [platformRotation, setPlatformRotation] = useState<number>(0);
  const [tempRotation, setTempRotation] = useState<string>('0');
  const [platformBackgroundColor, setPlatformBackgroundColor] = useState<string>('#f5f5dc');
  const [platformDisplayType, setPlatformDisplayType] = useState<'volante' | 'imagen' | 'nada'>('volante');
  const [showPlatformPreview, setShowPlatformPreview] = useState(false);
  const [selectedPlatformForPreview, setSelectedPlatformForPreview] = useState<any | null>(null);
  const [previewScale, setPreviewScale] = useState<number>(100);
  const [tempPreviewScale, setTempPreviewScale] = useState<string>('100');
  const [platformImageError, setPlatformImageError] = useState<boolean>(false);
  const [previewImageError, setPreviewImageError] = useState<boolean>(false);

  // Estados para Ambassadors
  const [showAmbassadorsModal, setShowAmbassadorsModal] = useState(false);
  const [ambassadors, setAmbassadors] = useState<any[]>([]);
  const [loadingAmbassadors, setLoadingAmbassadors] = useState(false);
  const [ambassadorSearchTerm, setAmbassadorSearchTerm] = useState('');
  const [showAmbassadorForm, setShowAmbassadorForm] = useState(false);
  const [editingAmbassador, setEditingAmbassador] = useState<any>(null);
  const [ambassadorFormStatus, setAmbassadorFormStatus] = useState<string>('active');
  const [loadingPetflixUsers, setLoadingPetflixUsers] = useState(false);
  const [showInviteAmbassadorModal, setShowInviteAmbassadorModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);

  // Estados para Vote Boosters
  const [showVoteBoosterModal, setShowVoteBoosterModal] = useState(false);
  const [selectedParticipantForBooster, setSelectedParticipantForBooster] = useState<{
    id: string;
    contest_id: string;
    participant_name: string;
  } | null>(null);
  const [voteBoosters, setVoteBoosters] = useState<any[]>([]);
  const [loadingVoteBoosters, setLoadingVoteBoosters] = useState(false);

  // Estados para Dashboard de Reportes de Vote Boosters
  const [showVoteBoosterReportModal, setShowVoteBoosterReportModal] = useState(false);
  const [selectedContestForReport, setSelectedContestForReport] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [voteBoosterStats, setVoteBoosterStats] = useState<any>(null);
  const [loadingVoteBoosterStats, setLoadingVoteBoosterStats] = useState(false);
  const [exportingVoteBoosterReport, setExportingVoteBoosterReport] = useState(false);

  // Estados para Estadísticas de Tags por Concurso
  const [showTagStatisticsModal, setShowTagStatisticsModal] = useState(false);
  const [selectedContestForTagStats, setSelectedContestForTagStats] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [tagStatistics, setTagStatistics] = useState<any>(null);
  const [loadingTagStatistics, setLoadingTagStatistics] = useState(false);

  // Estados para Tags del Sponsor
  const [showSponsorTagsModal, setShowSponsorTagsModal] = useState(false);
  const [sponsorTags, setSponsorTags] = useState<any[]>([]);
  const [loadingSponsorTags, setLoadingSponsorTags] = useState(false);

  // Estados para Tags del Embajador
  const [showAmbassadorTagsModal, setShowAmbassadorTagsModal] = useState(false);
  const [ambassadorTagsView, setAmbassadorTagsView] = useState<any[]>([]);
  const [loadingAmbassadorTagsView, setLoadingAmbassadorTagsView] = useState(false);
  const [selectedAmbassadorForTags, setSelectedAmbassadorForTags] = useState<any>(null);
  const [ambassadorTagsSearchTerm, setAmbassadorTagsSearchTerm] = useState('');

  // Estados para Formulario de Venta de Tag
  const [showTagSaleForm, setShowTagSaleForm] = useState(false);
  const [selectedAmbassadorForSale, setSelectedAmbassadorForSale] = useState<any>(null);
  const [ambassadorTags, setAmbassadorTags] = useState<any[]>([]);
  const [loadingAmbassadorTags, setLoadingAmbassadorTags] = useState(false);
  const [selectedTagForSale, setSelectedTagForSale] = useState<any>(null);
  const [salePrice, setSalePrice] = useState<string>('');
  const [selectedContestSponsor, setSelectedContestSponsor] = useState<{ contestId: string, sponsorId: string } | null>(null);
  const [tagGroups, setTagGroups] = useState<any[]>([]);
  const [contestsForTags, setContestsForTags] = useState<any[]>([]);
  const [sponsorshipsForTags, setSponsorshipsForTags] = useState<any[]>([]);

  // Estados para Dashboard de Compensación
  const [showCompensationDashboard, setShowCompensationDashboard] = useState(false);
  const [selectedAmbassadorForCompensation, setSelectedAmbassadorForCompensation] = useState<any>(null);
  const [compensations, setCompensations] = useState<any[]>([]);

  // Estados para Dashboard Global de Compensación
  const [showGlobalCompensationDashboard, setShowGlobalCompensationDashboard] = useState(false);
  const [globalCompensationStats, setGlobalCompensationStats] = useState<any>(null);
  const [loadingGlobalCompensationStats, setLoadingGlobalCompensationStats] = useState(false);
  const [loadingCompensations, setLoadingCompensations] = useState(false);

  // Estados para Digital Passes de Embajadores
  const [showAmbassadorDigitalPassesModal, setShowAmbassadorDigitalPassesModal] = useState(false);
  const [selectedAmbassadorForDigitalPasses, setSelectedAmbassadorForDigitalPasses] = useState<any>(null);
  const [ambassadorDigitalPasses, setAmbassadorDigitalPasses] = useState<any[]>([]);
  const [loadingAmbassadorDigitalPasses, setLoadingAmbassadorDigitalPasses] = useState(false);
  const [showAmbassadorAssignmentsModal, setShowAmbassadorAssignmentsModal] = useState(false);
  const [selectedAmbassadorForAssignments, setSelectedAmbassadorForAssignments] = useState<any>(null);
  const [ambassadorAssignments, setAmbassadorAssignments] = useState<any[]>([]);
  const [loadingAmbassadorAssignments, setLoadingAmbassadorAssignments] = useState(false);

  // Estados para Digital Passes de Sponsors
  const [showSponsorDigitalPassesModal, setShowSponsorDigitalPassesModal] = useState(false);
  const [selectedSponsorForDigitalPasses, setSelectedSponsorForDigitalPasses] = useState<any>(null);
  const [sponsorDigitalPasses, setSponsorDigitalPasses] = useState<any[]>([]);
  const [loadingSponsorDigitalPasses, setLoadingSponsorDigitalPasses] = useState(false);

  // Estados para Gestión de Referral Bonuses
  const [showBonusManagementModal, setShowBonusManagementModal] = useState(false);
  const [selectedAmbassadorForBonus, setSelectedAmbassadorForBonus] = useState<any>(null);
  const [bonusHistory, setBonusHistory] = useState<any[]>([]);
  const [loadingBonusHistory, setLoadingBonusHistory] = useState(false);
  const [assigningBadge, setAssigningBadge] = useState(false);
  const [grantingTags, setGrantingTags] = useState(false);
  const [bonusTagsQuantity, setBonusTagsQuantity] = useState<string>('0');

  // Estados para Reporte de Sponsors por Tier
  const [showSponsorTierReportModal, setShowSponsorTierReportModal] = useState(false);
  const [sponsorTierStats, setSponsorTierStats] = useState<any>(null);
  const [loadingSponsorTierStats, setLoadingSponsorTierStats] = useState(false);

  // Estados para Diagrama de Flujo Económico
  const [showEconomyFlowDiagramModal, setShowEconomyFlowDiagramModal] = useState(false);
  const [economyFlowData, setEconomyFlowData] = useState<any>(null);
  const [loadingEconomyFlowData, setLoadingEconomyFlowData] = useState(false);

  // Estado para controlar el renderizado de gráficos (evitar errores de contexto)
  const [chartsMountedSponsor, setChartsMountedSponsor] = useState(false);
  const [chartsMountedVoteBooster, setChartsMountedVoteBooster] = useState(false);

  // Efecto para montar gráficos cuando el modal de Sponsors esté abierto
  useEffect(() => {
    if (showSponsorTierReportModal && sponsorTierStats) {
      // Esperar a que el Portal del Dialog esté completamente montado
      const timer = setTimeout(() => {
        setChartsMountedSponsor(true);
      }, 800);
      return () => {
        clearTimeout(timer);
        setChartsMountedSponsor(false);
      };
    } else {
      setChartsMountedSponsor(false);
    }
  }, [showSponsorTierReportModal, sponsorTierStats]);

  // Efecto para montar gráficos cuando el modal de Vote Boosters esté abierto
  useEffect(() => {
    if (showVoteBoosterReportModal && voteBoosterStats) {
      // Esperar a que el Portal del Dialog esté completamente montado
      const timer = setTimeout(() => {
        setChartsMountedVoteBooster(true);
      }, 800);
      return () => {
        clearTimeout(timer);
        setChartsMountedVoteBooster(false);
      };
    } else {
      setChartsMountedVoteBooster(false);
    }
  }, [showVoteBoosterReportModal, voteBoosterStats]);

  // Efecto para ocultar scrollbar inferior del contenedor de imagen
  useEffect(() => {
    if (platformImage && showAddPlatformModal) {
      const scrollbarId = `scrollbar-bottom-${editingPlatform?.id || 'new'}`;
      const styleId = `hide-scrollbar-${scrollbarId}`;

      // Verificar si el estilo ya existe
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          #${scrollbarId}::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
            background: transparent !important;
          }
          #${scrollbarId}::-webkit-scrollbar-track {
            display: none !important;
            background: transparent !important;
          }
          #${scrollbarId}::-webkit-scrollbar-thumb {
            display: none !important;
            background: transparent !important;
          }
          #${scrollbarId} {
            -ms-overflow-style: none !important;
            scrollbar-width: none !important;
          }
        `;
        document.head.appendChild(style);
      }

      // Limpiar cuando se desmonte
      return () => {
        const existingStyle = document.getElementById(styleId);
        if (existingStyle) {
          existingStyle.remove();
        }
      };
    }
  }, [platformImage, showAddPlatformModal, editingPlatform?.id]);

  // Estados para interfaz de prueba de imágenes
  const [showTestImagesModal, setShowTestImagesModal] = useState(false);
  const [testImages, setTestImages] = useState<any[]>([]);
  const [loadingTestImages, setLoadingTestImages] = useState(false);
  const [showTokenInput, setShowTokenInput] = useState(false);
  const [tokenInputValue, setTokenInputValue] = useState<string>('');

  // Estados para GEO-IP
  const [showGeoIpModal, setShowGeoIpModal] = useState(false);
  const [geoIpData, setGeoIpData] = useState<any>(null);
  const [loadingGeoIp, setLoadingGeoIp] = useState(false);
  const [geoIpError, setGeoIpError] = useState<string | null>(null);

  // Estados para datos de usuario
  const [showUserDataModal, setShowUserDataModal] = useState(false);
  const [userDataText, setUserDataText] = useState<string>('');

  // Función para obtener datos de GEO-IP
  const fetchGeoIpData = async () => {
    try {
      setLoadingGeoIp(true);
      setGeoIpError(null);
      setGeoIpData(null);

      const response = await fetch(`${environment.apiBaseUrl}/geoip`, {
        headers: getApiHeadersForGet()
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        // Si hay un error pero incluye información útil, mostrarla
        let errorMessage = data.error || `Error HTTP: ${response.status}`;

        if (data.isPrivate) {
          errorMessage = `IP privada detectada (${data.ip || 'localhost'}). `;
          if (data.suggestion) {
            errorMessage += data.suggestion;
          } else {
            errorMessage += 'No se pudo obtener la ubicación porque estás accediendo desde localhost. Esto es normal en desarrollo.';
          }
        }

        throw new Error(errorMessage);
      }

      setGeoIpData(data);
    } catch (error: any) {
      console.error('Error obteniendo datos de GEO-IP:', error);
      setGeoIpError(error.message || 'Error desconocido al obtener ubicación');
    } finally {
      setLoadingGeoIp(false);
    }
  };

  // Estados para gestión de tipos de patrocinio
  const [showSponsorshipTypesModal, setShowSponsorshipTypesModal] = useState(false);
  const [sponsorshipTypes, setSponsorshipTypes] = useState<any[]>([]);
  const [loadingSponsorshipTypes, setLoadingSponsorshipTypes] = useState(false);
  const [availablePlatformsForTypes, setAvailablePlatformsForTypes] = useState<string[]>(['facebook', 'instagram', 'voletaje']);
  const [platformsMap, setPlatformsMap] = useState<Record<string, string>>({});
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [loadingPassCodes, setLoadingPassCodes] = useState(false);
  const [expandedCouponPass, setExpandedCouponPass] = useState<string | null>(null);
  const [expandedContest, setExpandedContest] = useState<string | null>(null);
  const [expandedPassCode, setExpandedPassCode] = useState<string | null>(null);
  const [couponPassesUpdateKey, setCouponPassesUpdateKey] = useState(0);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState<any[]>([]);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [selectedContestForInvoice, setSelectedContestForInvoice] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Estados para obtener token de admin
  const [showAdminLoginModal, setShowAdminLoginModal] = useState(false);
  const [adminEmail, setAdminEmail] = useState('pavan-admin@yopmail.com');
  const [adminPassword, setAdminPassword] = useState('');
  const [loadingAdminToken, setLoadingAdminToken] = useState(false);

  // Función para obtener token de admin automáticamente
  const getAdminToken = async () => {
    if (!adminEmail || !adminPassword) {
      void appDialog.alert('Por favor, ingresa email y contraseña');
      return;
    }

    setLoadingAdminToken(true);
    try {
      // Paso 1: Login con Firebase
      console.log('🔐 Paso 1: Autenticando con Firebase...');
      const firebaseResult = await firebaseAuthService.signInWithPassword(adminEmail, adminPassword);

      if (!firebaseResult.success) {
        throw new Error(`Error en Firebase: ${firebaseResult.error}`);
      }

      if (!firebaseResult.data) {
        throw new Error('No se recibieron datos de Firebase');
      }

      const idToken = firebaseResult.data.idToken;
      console.log('✅ Firebase autenticación exitosa');

      // Paso 2: Autenticar con Petflix usando el token de Firebase
      console.log('🔐 Paso 2: Autenticando con Petflix...');
      const petflixResult = await petflixAuthService.authenticateWithFirebase(idToken);

      if (!petflixResult.success) {
        throw new Error(`Error en Petflix: ${petflixResult.error}`);
      }

      if (!petflixResult.data) {
        throw new Error('No se recibieron datos de Petflix');
      }

      const accessToken = petflixResult.data.accessToken;
      console.log('✅ Petflix autenticación exitosa');
      console.log('🎫 Access Token obtenido:', accessToken.substring(0, 50) + '...');

      // Paso 3: Guardar el token
      setPetflixAccessToken(accessToken);
      petflixAuthService.setAccessToken(accessToken);

      void appDialog.alert(`✅ Token de admin obtenido y guardado exitosamente!\n\nEl token ha sido configurado y está listo para usar.`);
      setShowAdminLoginModal(false);
      setAdminPassword(''); // Limpiar contraseña por seguridad

    } catch (error: any) {
      console.error('Error obteniendo token de admin:', error);
      void appDialog.alert(`❌ Error: ${error.message || 'Error al obtener token de admin'}`);
    } finally {
      setLoadingAdminToken(false);
    }
  };

  // Función para mostrar el token del usuario logueado
  const showCurrentToken = () => {
    const token = getPetflixAccessToken();
    const tokenFromStorage = petflixAuthService.getAccessToken();

    // Usar el token de la configuración o del localStorage
    const currentToken = token || tokenFromStorage;

    if (!currentToken) {
      void appDialog.alert('❌ No hay token disponible.\n\nPor favor, inicia sesión primero o configura el token manualmente.');
      return;
    }

    // Obtener información del usuario
    const userInfo = petflixAuthService.getUserInfo();

    // Mostrar el token completo en la consola
    console.log('\n🎫 ============================================');
    console.log('🎫 TOKEN DEL USUARIO LOGUEADO:');
    console.log('🎫 ============================================');
    console.log(currentToken);
    console.log('🎫 ============================================');

    // Mostrar información del usuario si está disponible
    if (userInfo) {
      console.log('\n👤 ============================================');
      console.log('👤 INFORMACIÓN DEL USUARIO LOGUEADO:');
      console.log('👤 ============================================');
      console.log('Email:', userInfo.email || 'N/A');
      console.log('Nombre:', userInfo.firstName || 'N/A', userInfo.lastName || '');
      console.log('Username:', userInfo.username || 'N/A');
      console.log('Roles:', userInfo.roles ? (Array.isArray(userInfo.roles) ? userInfo.roles.join(', ') : userInfo.roles) : 'N/A');
      console.log('Teléfono:', userInfo.phone || 'N/A');
      console.log('ID:', userInfo._id || 'N/A');
      console.log('Firebase ID:', userInfo.firebaseId || 'N/A');
      console.log('👤 ============================================');
    } else {
      console.log('\n⚠️  No hay información del usuario disponible');
    }

    console.log('\n💡 Para usar en Postman:');
    console.log('   Authorization: Bearer ' + currentToken);
    console.log('\n📋 Primeros 50 caracteres:', currentToken.substring(0, 50) + '...');
    console.log('📏 Longitud del token:', currentToken.length, 'caracteres');

    // Construir mensaje para el alert
    let alertMessage = '🎫 TOKEN DEL USUARIO LOGUEADO:\n\n' + currentToken;

    if (userInfo) {
      alertMessage += '\n\n👤 USUARIO LOGUEADO:\n';
      alertMessage += `Email: ${userInfo.email || 'N/A'}\n`;
      if (userInfo.firstName || userInfo.lastName) {
        alertMessage += `Nombre: ${userInfo.firstName || ''} ${userInfo.lastName || ''}\n`;
      }
      alertMessage += `Username: ${userInfo.username || 'N/A'}\n`;
      if (userInfo.roles) {
        const rolesStr = Array.isArray(userInfo.roles) ? userInfo.roles.join(', ') : userInfo.roles;
        alertMessage += `Roles: ${rolesStr}\n`;
      }
    }

    alertMessage += '\n💡 Revisa la consola para ver el token completo y las instrucciones para usarlo en Postman.';
    void appDialog.alert(alertMessage);
  };

  // Función para mostrar datos del usuario en modal
  const showUserData = () => {
    try {
      // Obtener información del usuario desde localStorage
      const userInfo = petflixAuthService.getUserInfo();
      const token = getPetflixAccessToken() || petflixAuthService.getAccessToken();

      // Crear objeto con todos los datos disponibles
      const userData: any = {
        userInfo: userInfo || null,
        token: token ? {
          value: token,
          length: token.length,
          first50Chars: token.substring(0, 50) + '...',
          last50Chars: '...' + token.substring(token.length - 50)
        } : null,
        localStorage: {
          petflix_access_token: localStorage.getItem('petflix_access_token') || null,
          petflix_user_info: localStorage.getItem('petflix_user_info') ? JSON.parse(localStorage.getItem('petflix_user_info') || '{}') : null
        },
        timestamp: new Date().toISOString()
      };

      // Convertir a JSON formateado
      const formattedData = JSON.stringify(userData, null, 2);
      setUserDataText(formattedData);
      setShowUserDataModal(true);
    } catch (error: any) {
      console.error('Error obteniendo datos del usuario:', error);
      setUserDataText(JSON.stringify({
        error: 'Error al obtener datos del usuario',
        message: error.message,
        timestamp: new Date().toISOString()
      }, null, 2));
      setShowUserDataModal(true);
    }
  };

  // Función para obtener usuarios de Petflix
  const fetchPetflixUsers = async () => {
    try {
      // Intentar obtener el token de múltiples fuentes
      let accessToken = getPetflixAccessToken(); // De la configuración

      // Si no está en la configuración, intentar desde localStorage
      if (!accessToken) {
        accessToken = petflixAuthService.getAccessToken();
      }

      if (!accessToken) {
        void appDialog.alert('Por favor, inicia sesión primero o configura el token de acceso de Petflix.\n\nEl token se obtiene automáticamente al hacer login con OTP.');
        setShowTokenInput(true);
        return;
      }

      console.log('🔑 Usando token para obtener usuarios de Petflix');
      console.log('📋 Token (primeros 50 caracteres):', accessToken.substring(0, 50) + '...');

      const response = await fetch(`${environment.apiBaseUrl}/petflix/users`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({
          accessToken: accessToken
          // No enviar limit ni skip para obtener todos los usuarios sin límites
        })
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          // Si no es JSON, leer como texto
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status}. ${errorText.substring(0, 200)}`);
        }
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        void appDialog.alert('Datos obtenidos exitosamente. Revisa la terminal del servidor para ver los detalles.');
      } else {
        const errorMsg = result.message || result.error || 'Error desconocido';
        void appDialog.alert(`Error: ${errorMsg}\n\n${result.details ? `Detalles: ${result.details}` : ''}`);
      }
    } catch (error: any) {
      console.error('Error obteniendo usuarios de Petflix:', error);
      let errorMessage = error.message || 'Error al obtener usuarios de Petflix';

      // Mensaje más específico si es un error de permisos
      if (errorMessage.includes('HTML') || errorMessage.includes('permisos') || errorMessage.includes('admin')) {
        errorMessage += '\n\n⚠️ IMPORTANTE: Necesitas autenticarte con una cuenta de administrador (pavan-admin@yopmail.com) para obtener un token con permisos adecuados.';
      }

      void appDialog.alert(`Error: ${errorMessage}`);
    }
  };

  // Función para actualizar embajadores desde usuarios de Petflix (obtiene y crea automáticamente)
  const fetchPetflixAmbassadorUsers = async () => {
    // Prevenir múltiples solicitudes simultáneas
    if (loadingPetflixUsers) {
      return;
    }

    try {
      setLoadingPetflixUsers(true);
      // Intentar obtener el token de múltiples fuentes
      let accessToken = getPetflixAccessToken(); // De la configuración

      // Si no está en la configuración, intentar desde localStorage
      if (!accessToken) {
        accessToken = petflixAuthService.getAccessToken();
      }

      if (!accessToken) {
        void appDialog.alert('Por favor, inicia sesión primero o configura el token de acceso de Petflix.\n\nEl token se obtiene automáticamente al hacer login con OTP.');
        setShowTokenInput(true);
        return;
      }

      console.log('🔑 Obteniendo y creando embajadores desde usuarios de Petflix con rol ambassador');
      console.log('📋 Token (primeros 50 caracteres):', accessToken.substring(0, 50) + '...');

      // Agregar un pequeño delay inicial para evitar solicitudes concurrentes
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Función helper para hacer la solicitud con retry y backoff exponencial
      // Aumentamos los reintentos a 5 y los delays iniciales para dar más tiempo a la API
      const fetchWithRetry = async (maxRetries = 5, initialDelay = 5000): Promise<Response> => {
        for (let attempt = 0; attempt < maxRetries; attempt++) {
          try {
            console.log(`🔄 Intento ${attempt + 1}/${maxRetries} de obtener usuarios de Petflix...`);

            const response = await fetch(`${environment.apiBaseUrl}/petflix/get-ambassador-users`, {
              method: 'GET',
              headers: {
                ...getApiHeadersForGet(),
                'Authorization': `Bearer ${accessToken}`
              }
            });

            if (!response.ok) {
              let errorData;
              try {
                errorData = await response.json();
              } catch (e) {
                const errorText = await response.text();
                throw new Error(`HTTP error! status: ${response.status}. ${errorText.substring(0, 200)}`);
              }

              // Detectar error de rate limiting
              const errorMessage = errorData.message || errorData.error || '';
              const isRateLimitError = response.status === 500 &&
                (errorMessage.toLowerCase().includes('too many concurrent') ||
                  errorMessage.toLowerCase().includes('rate limit') ||
                  errorMessage.toLowerCase().includes('try again'));

              if (isRateLimitError && attempt < maxRetries - 1) {
                // Calcular delay con backoff exponencial: 5s, 10s, 20s, 40s, 80s
                const delay = initialDelay * Math.pow(2, attempt);
                const delaySeconds = delay / 1000;
                console.log(`⏳ Rate limit detectado. Esperando ${delaySeconds} segundos antes del siguiente intento... (Intento ${attempt + 1}/${maxRetries})`);

                // Mostrar mensaje al usuario solo en el primer intento
                if (attempt === 0) {
                  void appDialog.alert(`⚠️ Demasiadas solicitudes simultáneas detectadas.\n\n` +
                    `El sistema esperará ${delaySeconds} segundos y reintentará automáticamente.\n\n` +
                    `Por favor, no cierres esta ventana.`);
                }

                // Mostrar progreso en la consola cada 5 segundos
                let remainingSeconds = delaySeconds;
                const progressInterval = setInterval(() => {
                  remainingSeconds -= 5;
                  if (remainingSeconds > 0) {
                    console.log(`⏳ Esperando... ${remainingSeconds} segundos restantes...`);
                  } else {
                    clearInterval(progressInterval);
                  }
                }, 5000);

                await new Promise(resolve => setTimeout(resolve, delay));
                clearInterval(progressInterval);
                console.log(`✅ Espera completada. Reintentando...`);
                continue; // Reintentar
              }

              // Si no es rate limit o es el último intento, lanzar el error
              let errorMessageFull = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;
              if (errorData.message && errorData.message !== errorData.error) {
                errorMessageFull += `\n\nDetalles: ${errorData.message}`;
              }
              if (errorData.statusCode) {
                errorMessageFull += `\nCódigo de estado: ${errorData.statusCode}`;
              }

              throw new Error(errorMessageFull);
            }

            console.log(`✅ Solicitud exitosa en el intento ${attempt + 1}`);
            return response; // Éxito
          } catch (error: any) {
            // Verificar si es un error de rate limit
            const isRateLimitInMessage = error.message?.toLowerCase().includes('too many concurrent') ||
              error.message?.toLowerCase().includes('rate limit') ||
              error.message?.toLowerCase().includes('try again');

            // Si es el último intento o no es un error de rate limit, lanzar el error
            if (attempt === maxRetries - 1 || !isRateLimitInMessage) {
              throw error;
            }

            // Si es rate limit y hay más intentos, continuar con el retry
            const delay = initialDelay * Math.pow(2, attempt);
            const delaySeconds = delay / 1000;
            console.log(`⏳ Error de rate limit detectado. Esperando ${delaySeconds} segundos... (Intento ${attempt + 1}/${maxRetries})`);

            // Mostrar progreso
            let remainingSeconds = delaySeconds;
            const progressInterval = setInterval(() => {
              remainingSeconds -= 5;
              if (remainingSeconds > 0) {
                console.log(`⏳ Esperando... ${remainingSeconds} segundos restantes...`);
              } else {
                clearInterval(progressInterval);
              }
            }, 5000);

            await new Promise(resolve => setTimeout(resolve, delay));
            clearInterval(progressInterval);
            console.log(`✅ Espera completada. Reintentando...`);
          }
        }
        throw new Error('No se pudo completar la solicitud después de múltiples intentos. La API de Petflix está recibiendo demasiadas solicitudes. Por favor, espera unos minutos y vuelve a intentar.');
      };

      const response = await fetchWithRetry();

      const result = await response.json();

      if (result.success) {
        const users = result.users || [];
        console.log(`\n=== Usuarios de Petflix con rol ambassador: ${users.length} ===`);
        users.forEach((user: any, index: number) => {
          console.log(`${index + 1}. ${user.name} (${user.email})`);
        });

        // Crear embajadores automáticamente
        const results: {
          created: Array<{ id: string; email: string; name: string }>;
          skipped: Array<{ email: string; name: string; reason: string }>;
          errors: Array<{ user: string; email?: string; error: string }>;
        } = {
          created: [] as Array<{ id: string; email: string; name: string }>,
          skipped: [] as Array<{ email: string; name: string; reason: string }>,
          errors: [] as Array<{ user: string; email?: string; error: string }>
        };

        // Procesar cada usuario y crear embajadores
        for (const user of users) {
          try {
            const email = user.email?.toLowerCase().trim();

            if (!email) {
              results.errors.push({
                user: user.name || 'unknown',
                error: 'No email found'
              });
              continue;
            }

            // Verificar si ya existe un embajador con este email
            const existingAmbassador = ambassadors.find((amb: any) =>
              amb.email?.toLowerCase().trim() === email
            );

            if (existingAmbassador) {
              results.skipped.push({
                email: email,
                name: user.name,
                reason: 'Ya existe un embajador con este email'
              });
              continue;
            }

            // Crear nuevo embajador usando el email como ID único
            const ambassadorId = email;
            const now = new Date().toISOString();

            const ambassadorData = {
              id: ambassadorId,
              name: user.name.trim(),
              email: email,
              phone: user.phone || null,
              status: 'active',
              total_sponsors_signed: 0,
              total_tags_sold: 0,
              total_revenue: 0,
              recruiter_badge: false,
              senior_badge: false,
              elite_badge: false,
              referral_count: 0,
              bonus_tags_received: 0,
              created_at: now,
              updated_at: now
            };

            await ambassadorService.createAmbassador(ambassadorData);

            results.created.push({
              id: ambassadorId,
              email: email,
              name: user.name
            });

          } catch (error: any) {
            results.errors.push({
              user: user.name || 'unknown',
              email: user.email || 'unknown',
              error: error.message || 'Error desconocido'
            });
          }
        }

        // Recargar lista de embajadores
        const updatedAmbassadors = await ambassadorService.getAllAmbassadors();
        setAmbassadors(updatedAmbassadors);

        const message = `✅ Proceso completado!\n\n` +
          `📊 Resumen:\n` +
          `• Creados: ${results.created.length}\n` +
          `• Omitidos (ya existían): ${results.skipped.length}\n` +
          `• Errores: ${results.errors.length}`;

        void appDialog.alert(message);

        console.log('\n=== Embajadores Creados ===');
        results.created.forEach((amb: any, index: number) => {
          console.log(`${index + 1}. ${amb.name} (${amb.email})`);
        });

        if (results.skipped.length > 0) {
          console.log('\n=== Embajadores Omitidos (ya existían) ===');
          results.skipped.forEach((amb: any, index: number) => {
            console.log(`${index + 1}. ${amb.name} (${amb.email})`);
          });
        }

        if (results.errors.length > 0) {
          console.log('\n=== Errores ===');
          results.errors.forEach((error: any, index: number) => {
            console.log(`${index + 1}. Usuario: ${error.user || error.email || 'unknown'} - ${error.error}`);
          });
        }
      } else {
        const errorMsg = result.message || result.error || 'Error desconocido';
        void appDialog.alert(`Error: ${errorMsg}\n\n${result.details ? `Detalles: ${result.details}` : ''}`);
      }
    } catch (error: any) {
      console.error('Error actualizando embajadores desde Petflix:', error);
      let errorMessage = error.message || 'Error al actualizar embajadores desde Petflix';

      // Detectar si es un error de rate limiting
      const isRateLimitError = errorMessage.toLowerCase().includes('too many concurrent') ||
        errorMessage.toLowerCase().includes('rate limit') ||
        errorMessage.toLowerCase().includes('try again') ||
        errorMessage.toLowerCase().includes('múltiples intentos');

      if (isRateLimitError) {
        void appDialog.alert(`⚠️ Error: Demasiadas solicitudes simultáneas\n\n` +
          `La API de Petflix está recibiendo demasiadas solicitudes al mismo tiempo.\n\n` +
          `El sistema ha intentado ${5} veces con esperas progresivas, pero la API aún está ocupada.\n\n` +
          `Recomendaciones:\n` +
          `• Espera 2-3 minutos antes de volver a intentar\n` +
          `• Cierra otras pestañas o aplicaciones que puedan estar usando la API de Petflix\n` +
          `• Intenta en un momento de menor tráfico\n\n` +
          `Si el problema persiste, contacta al administrador del sistema.`);
      } else {
        // Si el error contiene información adicional, mostrarla
        if (errorMessage.includes('\n\n')) {
          // El mensaje ya tiene detalles, mostrarlo tal cual
          void appDialog.alert(`Error: ${errorMessage}`);
        } else {
          // Mensaje simple, intentar obtener más detalles del error
          const errorDetails = error.response?.data?.message || error.details || '';
          if (errorDetails) {
            void appDialog.alert(`Error: ${errorMessage}\n\nDetalles: ${errorDetails}`);
          } else {
            void appDialog.alert(`Error: ${errorMessage}`);
          }
        }
      }
    } finally {
      setLoadingPetflixUsers(false);
    }
  };

  // Función para enviar invitación por email a un embajador
  const handleGenerateWordDoc = async () => {
    if (!wordDocImageUrl) {
      void appDialog.alert(t('buttonsforms.generateWordDoc') + ': ' + 'Ingresa la URL de la imagen');
      return;
    }

    setWordDocLoading(true);
    try {
      const response = await fetch(`${environment.apiBaseUrl}/generar-docx`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({ imageUrl: wordDocImageUrl }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || 'Error generando el documento');
      }

      // Recibir el .docx como blob
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Crear link temporal para forzar la descarga
      const a = document.createElement('a');
      a.href = url;
      a.download = 'imagen_en_word.docx';
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);

      // Cerrar el modal y limpiar el campo
      setShowWordDocModal(false);
      setWordDocImageUrl('');
    } catch (err: any) {
      console.error(err);
      void appDialog.alert(err.message || 'Error generando el documento');
    } finally {
      setWordDocLoading(false);
    }
  };

  const handleInviteAmbassador = async () => {
    if (!inviteEmail || !inviteEmail.trim()) {
      void appDialog.alert('Por favor, ingresa un correo electrónico válido');
      return;
    }

    // Validar formato de email básico
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail.trim())) {
      void appDialog.alert('Por favor, ingresa un correo electrónico válido');
      return;
    }

    try {
      setSendingInvite(true);

      const response = await fetch(`${environment.apiBaseUrl}/ambassadors/invite`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({
          email: inviteEmail.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Error al enviar la invitación');
      }

      const result = await response.json();

      if (result.success) {
        void appDialog.alert(`✅ ${result.message || 'Invitación enviada exitosamente'}`);
        setInviteEmail('');
        setShowInviteAmbassadorModal(false);
      } else {
        throw new Error(result.error || 'Error al enviar la invitación');
      }
    } catch (error: any) {
      console.error('Error enviando invitación:', error);
      void appDialog.alert(`Error: ${error.message || 'Error al enviar la invitación'}`);
    } finally {
      setSendingInvite(false);
    }
  };

  // Función para cargar imágenes de prueba
  const loadTestImages = async (accessToken: string) => {
    setLoadingTestImages(true);
    try {
      // Intentar con diferentes límites si falla (empezar con uno más bajo)
      // El error 500 puede ser porque el límite de 1000 es muy alto
      const limits = [100, 500, 1000];
      let lastError: any = null;
      let posts: any[] = [];

      for (const limit of limits) {
        try {
          const response = await fetch(`https://dev.petflix.io/posts/social-contests?$limit=${limit}`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            if (response.status === 401) {
              throw new Error('Token inválido o expirado. Por favor, actualiza el token.');
            }
            // Si es error 500 y no es el último intento, continuar con el siguiente límite
            if (response.status === 500 && limit !== limits[limits.length - 1]) {
              console.warn(`⚠️ Error 500 con límite ${limit}, intentando con límite más bajo...`);
              lastError = new Error(`HTTP error! status: ${response.status}`);
              continue;
            }
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          console.log(`✅ Posts cargados exitosamente con límite ${limit}:`, data.data?.length || 0);

          posts = data.data || [];

          // Si obtuvimos resultados, salir del loop
          if (posts.length > 0) {
            break;
          }
        } catch (error: any) {
          lastError = error;
          // Si es el último intento, lanzar el error
          if (limit === limits[limits.length - 1]) {
            throw error;
          }
          // Continuar con el siguiente límite
          continue;
        }
      }

      // Si llegamos aquí sin posts, usar el último error
      if (posts.length === 0 && lastError) {
        throw lastError;
      }

      console.log('Total de posts cargados:', posts.length);
      setTestImages(posts);

      // Imprimir todos los links de imágenes (solo en desarrollo)
      if (process.env.NODE_ENV === 'development') {
        posts.forEach((post: any, postIndex: number) => {
          console.log(`\n=== Post ${postIndex + 1} (ID: ${post._id}) ===`);
          console.log('Body:', post.body);
          console.log('Media items:', post.media?.length || 0);
          console.log('Classifiers:', post.classifiers?.map((c: any) => c.name).join(', ') || 'Sin classifiers');

          if (post.media && post.media.length > 0) {
            post.media.forEach((mediaItem: any, mediaIndex: number) => {
              const imageUrl = getImageUrlFromMedia(mediaItem);
              console.log(`  Imagen ${mediaIndex + 1}:`, {
                path: mediaItem.path,
                url: imageUrl,
                _id: mediaItem._id
              });
              console.log(`  Link completo: ${imageUrl}`);
            });
          } else {
            console.log('  No hay media items en este post');
          }
        });
      }

      // Devolver los posts para uso inmediato
      return posts;
    } catch (error: any) {
      console.error('Error obteniendo imágenes de Petflix:', error);

      // No mostrar alert si es un error 500 (problema del servidor, no del usuario)
      if (error.message?.includes('500')) {
        console.warn('⚠️ La API de Petflix está teniendo problemas. Continuando sin imágenes de Petflix.');
        // Devolver array vacío para que la funcionalidad continúe
        setTestImages([]);
        return [];
      }

      // Para otros errores, mostrar mensaje pero no bloquear
      if (error.message?.includes('Token inválido') || error.message?.includes('401')) {
        console.warn('⚠️ Token inválido o expirado. Continuando sin imágenes de Petflix.');
        setShowTokenInput(true);
        setTokenInputValue('');
        setTestImages([]);
        return [];
      }

      // Para otros errores, solo loggear y continuar
      console.warn('⚠️ Error al obtener imágenes de Petflix. Continuando sin imágenes.');
      setTestImages([]);
      return [];
    } finally {
      setLoadingTestImages(false);
    }
  };

  // Función para procesar posts de PetFlix e inscribir usuarios en concursos
  const processPetflixPosts = async (posts: any[]) => {
    if (!posts || posts.length === 0) {
      void appDialog.alert('No hay posts para procesar');
      return;
    }

    try {
      const totalPosts = posts.length;

      // Calcular total de imágenes/media items en todos los posts
      const totalMediaItems = posts.reduce((total, post) => {
        return total + (post.media && Array.isArray(post.media) ? post.media.length : 0);
      }, 0);

      // Analizar por qué se filtran posts
      const postsWithoutClassifiers = posts.filter(post =>
        !post.classifiers || !Array.isArray(post.classifiers) || post.classifiers.length === 0
      );
      const postsWithoutUser = posts.filter(post =>
        post.classifiers && Array.isArray(post.classifiers) && post.classifiers.length > 0 && !post.user
      );

      // Filtrar posts que tengan classifiers y user
      const postsToProcess = posts.filter(post =>
        post.classifiers &&
        Array.isArray(post.classifiers) &&
        post.classifiers.length > 0 &&
        post.user
      );

      const filteredCount = totalPosts - postsToProcess.length;

      if (postsToProcess.length === 0) {
        void appDialog.alert(`No hay posts con classifiers y datos de usuario para procesar.\n\nTotal de posts: ${totalPosts}\nPosts sin classifiers: ${postsWithoutClassifiers.length}\nPosts sin usuario: ${postsWithoutUser.length}`);
        return;
      }

      // Contar posts con classifier "Ark Awards" antes de procesar
      const postsWithArkAwards = postsToProcess.filter(post =>
        post.classifiers &&
        Array.isArray(post.classifiers) &&
        post.classifiers.some((c: any) => c.name === 'Ark Awards')
      );
      const totalArkAwardsPosts = postsWithArkAwards.length;

      const result = await petflixIntegrationService.processMultiplePosts(postsToProcess) as {
        total: number;
        successful: number;
        failed: number;
        skipped: number;
        details: any[];
      };

      // Contar cuántos "Ark Awards" se procesaron exitosamente
      let arkAwardsProcessed = 0;
      let arkAwardsSkipped = 0;
      let arkAwardsFailed = 0;

      result.details.forEach((detail: any) => {
        // Verificar si el post completo fue omitido porque ya fue procesado
        if (detail.status === 'success' && detail.data && detail.data.message === 'Post ya procesado') {
          // El post completo fue omitido, contar todos los classifiers como omitidos
          const post = postsToProcess.find((p: any) => p._id === detail.postId);
          if (post && post.classifiers && Array.isArray(post.classifiers)) {
            post.classifiers.forEach((c: any) => {
              if (c.name === 'Ark Awards') {
                arkAwardsSkipped++;
                console.log(`⊘ Post ${detail.postId} ya fue procesado anteriormente (Ark Awards)`);
              }
            });
          }
        } else if (detail.status === 'success' && detail.data) {
          // Buscar en los resultados si hay registros exitosos para "Ark Awards"
          if (detail.data.results && Array.isArray(detail.data.results)) {
            detail.data.results.forEach((r: any) => {
              if (r.contest === 'Ark Awards') {
                if (r.status === 'success') {
                  arkAwardsProcessed++;
                  console.log(`✓ Ark Awards procesado exitosamente para: ${r.participantName || 'N/A'}`);
                } else if (r.status === 'skipped') {
                  arkAwardsSkipped++;
                  const skipReason = r.message || 'Usuario ya registrado';
                  console.log(`⊘ Ark Awards omitido: ${skipReason}`);
                }
              }
            });
          }
          // También contar errores relacionados con "Ark Awards"
          if (detail.data.errors && Array.isArray(detail.data.errors)) {
            detail.data.errors.forEach((e: any) => {
              if (e.classifier === 'Ark Awards') {
                arkAwardsFailed++;
                console.log(`✗ Ark Awards error: ${e.error || 'Error desconocido'}`);
              }
            });
          }
        } else if (detail.status === 'failed') {
          // Si el post completo falló, verificar si tenía "Ark Awards" en sus classifiers
          const post = postsToProcess.find((p: any) => p._id === detail.postId);
          if (post && post.classifiers && Array.isArray(post.classifiers)) {
            const hasArkAwards = post.classifiers.some((c: any) => c.name === 'Ark Awards');
            if (hasArkAwards) {
              arkAwardsFailed++;
              console.log(`✗ Post ${detail.postId} falló y tenía Ark Awards`);
            }
          }
        }
      });

      // Mostrar resultados detallados
      const message = `Procesamiento completado:
- Total de posts cargados: ${totalPosts}
- Total de imágenes/media en posts: ${totalMediaItems}
- Posts filtrados (sin classifiers/usuario): ${filteredCount}
- Total de posts procesados: ${result.total}
- Registros exitosos: ${result.successful}
- Registros omitidos: ${result.skipped}
- Errores: ${result.failed}

Ark Awards:
- Posts con classifier "Ark Awards": ${totalArkAwardsPosts}
- Ark Awards procesados exitosamente: ${arkAwardsProcessed}
- Ark Awards omitidos: ${arkAwardsSkipped}
- Ark Awards con errores: ${arkAwardsFailed}

Nota: Se procesan POSTS (no imágenes individuales). 
Si un post tiene múltiples imágenes, todas se incluyen en el mismo registro.`;

      void appDialog.alert(message);
      console.log('Resultados del procesamiento:', result);
      console.log(`Posts filtrados: ${filteredCount} de ${totalPosts}`);
      console.log(`Posts sin classifiers: ${postsWithoutClassifiers.length}`);
      console.log(`Posts sin usuario: ${postsWithoutUser.length}`);
      console.log(`=== ARK AWARDS ===`);
      console.log(`Total de posts con classifier "Ark Awards": ${totalArkAwardsPosts}`);
      console.log(`Ark Awards procesados exitosamente: ${arkAwardsProcessed}`);
      console.log(`Ark Awards omitidos: ${arkAwardsSkipped}`);
      console.log(`Ark Awards con errores: ${arkAwardsFailed}`);
      console.log(`Posts con Ark Awards:`, postsWithArkAwards.map((p: any) => ({
        postId: p._id,
        userName: p.user ? `${p.user.firstName} ${p.user.lastName}` : 'Sin usuario',
        classifiers: p.classifiers?.map((c: any) => c.name) || []
      })));

      // Recargar concursos para actualizar contadores
      const contestsData = await contestService.getAllContests();
      const contestsWithParticipantCount = await attachProposalSummariesBulk(await attachParticipantCountsBulk(contestsData));
      setContests(contestsWithParticipantCount);

    } catch (error: any) {
      console.error('Error procesando posts de PetFlix:', error);
      void appDialog.alert(`Error al procesar posts: ${error.message || 'Error desconocido'}`);
    }
  };

  // Función para generar y descargar CSV de códigos de pase
  const downloadPassCodesCSV = (contestId: string, contestName: string) => {
    // Filtrar códigos de pase para este concurso específico
    const contestPassCodes = passCodes.filter(passCode => passCode.pass_id === contestId);

    if (contestPassCodes.length === 0) {
      void appDialog.alert('No hay códigos de pase para este concurso');
      return;
    }

    // Crear encabezados del CSV
    const headers = ['Código', 'Tipo', 'Estado', 'Fecha de Creación', 'Fecha de Uso'];

    // Crear filas de datos
    const rows = contestPassCodes.map(passCode => [
      passCode.code,
      passCode.code_kind === 'printed' ? 'Impreso' : passCode.code_kind === 'digital' ? 'Digital' : 'NA',
      passCode.used_at ? t('menufilters.Used') : t('menufilters.Available'),
      new Date(passCode.created_at).toLocaleString(),
      passCode.used_at ? new Date(passCode.used_at).toLocaleString() : 'No usado'
    ]);

    // Combinar encabezados y datos
    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    // Crear y descargar archivo
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `pases_${contestName.replace(/[^a-zA-Z0-9]/g, '_')}_${contestId}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Cargar concursos desde la base de datos
  const attachParticipantCounts = useCallback(
    async (contestsData: any[]) => attachProposalSummariesBulk(await attachParticipantCountsBulk(contestsData)),
    [],
  );

  const loadSponsorships = useCallback(async () => {
    try {
      const sponsorships = await sponsorshipService.getAllSponsorships();

      if (sponsorships && sponsorships.length > 0) {
        const contestIdsWithSponsors = new Set(sponsorships.map((s: any) => s.contest_id)) as Set<string>;
        setContestsWithSponsors(contestIdsWithSponsors);
      } else {
        setContestsWithSponsors(new Set());
      }
    } catch (error: any) {
      console.error('Error loading sponsorships:', error);
      setContestsWithSponsors(new Set());
    }
  }, []);

  const fetchContests = useCallback(async (options?: { refresh?: boolean }) => {
    const isRefresh = Boolean(options?.refresh);

    try {
      if (isRefresh) {
        setRefreshingContests(true);
      } else {
        setLoading(true);
      }

      const contestsData = await contestService.getAllContests(isRefresh);
      const contestsWithParticipantCount = await attachParticipantCounts(contestsData);

      setContests(contestsWithParticipantCount);

      if (isRefresh) {
        await loadSponsorships();
      }
    } catch (error) {
      console.error('Error loading contests:', error);
      if (!isRefresh) {
        setContests(mockContests);
      }
    } finally {
      if (isRefresh) {
        setRefreshingContests(false);
      } else {
        setLoading(false);
      }
    }
  }, [attachParticipantCounts, loadSponsorships]);

  useEffect(() => {
    void fetchContests();
  }, [fetchContests]);

  const loadContestTokensForCouponPasses = useCallback(async (couponPassesData: any[], passCodesData: any[] = []): Promise<Record<string, any>> => {
    // Los Contest Pass se agrupan por `pass_id` (== contest_id); reutilizan el
    // mismo token de concurso que los CouponPass para construir el QR.
    const contestIds = [...new Set([
      ...couponPassesData.map((couponPass: any) => couponPass.contest_id),
      ...passCodesData.map((passCode: any) => passCode.pass_id),
    ].filter(Boolean))] as string[];
    if (contestIds.length === 0) {
      setContestTokens({});
      return {};
    }

    const tokens: Record<string, any> = await couponService.getContestTokens(contestIds);
    setContestTokens(prev => ({ ...prev, ...tokens }));
    return tokens;
  }, []);

  const loadInventorySummary = useCallback(async () => {
    try {
      const summary = await couponService.getInventorySummary();
      setInventorySummary(summary);
    } catch (error) {
      console.error('Error loading inventory summary:', error);
    }
  }, []);

  const loadFullCouponInventory = useCallback(async (options?: { forceRefresh?: boolean }) => {
    const forceRefresh = Boolean(options?.forceRefresh);
    if (couponInventoryLoadRef.current) {
      return couponInventoryLoadRef.current;
    }

    const task = (async () => {
      try {
        const { couponPasses: couponPassesData, couponCodes: couponCodesData, passCodes: passCodesData, passCodesError } =
          await loadCouponDashboardData({ couponService, forceCouponPassRefresh: forceRefresh });

        setCouponPasses(couponPassesData);
        setCouponCodes(couponCodesData);
        setPassCodes(passCodesData);
        setInventorySummary({
          couponPassCount: couponPassesData.length,
          passCodeCount: passCodesData.length,
          couponCodeCount: couponCodesData.length,
        });

        await loadContestTokensForCouponPasses(couponPassesData, passCodesData);

        const ids = couponPassesData.map((cp: any) => cp.id);
        const qrData = await fetchCouponPassQRLinksBulk(ids);
        setCouponPassQRLinks(qrData.linksMap);
        setCouponPassQRClaimed(qrData.claimedMap);
        setCouponPassQRTagTypes(qrData.tagTypesMap);
        setCouponPassQRTagCodes(qrData.tagCodesMap);

        if (passCodesError) {
          console.warn('No se pudieron cargar Pass_Codes:', passCodesError);
        }
      } catch (error) {
        console.error('Error loading full coupon inventory:', error);
        throw error;
      } finally {
        couponInventoryLoadRef.current = null;
      }
    })();

    couponInventoryLoadRef.current = task;
    return task;
  }, [loadContestTokensForCouponPasses]);

  // Sidebar: solo conteos ligeros al montar (no descargar miles de filas).
  useEffect(() => {
    void loadInventorySummary();
  }, [loadInventorySummary]);

  // Carga completa solo al abrir Passes & Codes (lazy load).
  useEffect(() => {
    if (!showCouponPassesModal) {
      return;
    }

    void loadFullCouponInventory({ forceRefresh: true });
  }, [showCouponPassesModal, loadFullCouponInventory]);

  // Abre la vista unificada "Passes & Codes" (la carga de datos la hace el useEffect anterior).
  const openUnifiedPassesCodes = useCallback(() => {
    setUnifiedSystemFilter('coupon');
    setUnifiedKindFilter('all');
    setUnifiedStatusFilter('all');
    setCouponPassesSearchTerm('');
    setShowCouponPassesModal(true);
  }, []);

  // Cargar patrocinios y actualizar estado de concursos con patrocinadores
  useEffect(() => {
    void loadSponsorships();
  }, [loadSponsorships]);



  const stats = {
    total: contests.length,
    active: contests.filter(c => c.phase === 'submissions_open').length,
    pending: contests.filter(c => c.status === 'pending').length,
    completed: contests.filter(c => c.status === 'completed' || c.phase === 'completed').length,
    available: contests.filter(c => getContestAvailability(c).available).length,
    unavailable: contests.filter(c => !getContestAvailability(c).available).length,
    proposalLinked: contests.filter(c => Boolean(c.linkedProposal)).length,
    totalRevenue: contests.reduce((sum, c) => sum + (c.economics?.total_revenue || 0), 0),
    totalProfit: contests.reduce((sum, c) => sum + (c.economics?.noahs_ark_profit || 0), 0),
  };

  const filteredContests = contests.filter(contest => {
    // Filtro por estado/tipo
    if (filter === 'all') {
      // No filtrar por estado, solo por búsqueda
    } else if (filter === 'active') {
      // Filtrar por contest_state === 'active'
      if (contest.contest_state !== 'active') return false;
    } else if (filter === 'cancelled') {
      // Considerar cancelado si contest_state es 'cancelled' o 'cancel', o si status es 'cancelled'
      const contestState = contest.contest_state || '';
      const contestStatus = contest.status || '';
      const isCancelled = contestState === 'cancelled' ||
        contestState === 'cancel' ||
        contestStatus === 'cancelled';
      if (!isCancelled) return false;
    } else if (filter === 'available') {
      // Disponible según status/contest_state y fechas reales del concurso
      if (!getContestAvailability(contest).available) return false;
    } else if (filter === 'unavailable') {
      if (getContestAvailability(contest).available) return false;
    } else if (filter === 'sponsored') {
      if (contest.sponsorship === null && !contest.sponsor_id) return false;
    } else if (filter === 'proposal_linked') {
      if (!contest.linkedProposal) return false;
    } else if (filter === 'members_only') {
      if (!contest.members_only) return false;
    } else if (filter === 'subscribers_only') {
      if (!contest.subscribers_only) return false;
    }

    // Filtro por búsqueda (nombre, patrocinador, keywords, tema, descripción y meses)
    const searchTerm = contestsSearchTerm.trim();
    if (searchTerm && !contestMatchesSearch(contest, searchTerm)) {
      return false;
    }

    return true;
  });

  const handleApprove = async (contestId: string) => {
    try {
      // Hacer la petición para iniciar el concurso
      const response = await fetch(`${environment.apiBaseUrl}/contests/${contestId}/start`, {
        method: 'POST',
        headers: getApiHeaders(),
      });

      const result = await response.json();

      if (response.ok) {
        // Actualizar el estado local del concurso
        setContests(contests.map(c =>
          c.id === contestId ? {
            ...c,
            status: 'active',
            phase: 'submissions_open',
            current_participants: result.contest.total_participants
          } : c
        ));

        void appDialog.alert(
          t('contestcard.startSuccess', { count: result.contest.total_participants }),
          { type: 'success' }
        );
      } else if (result.code === 'MIN_PARTICIPANTS_NOT_MET') {
        void appDialog.alert(
          t('contestcard.startMinParticipantsNotice', {
            min: result.min_participants ?? 0,
            current: result.current_participants ?? 0,
          }),
          { type: 'info' }
        );
      } else if (response.status === 400) {
        void appDialog.alert(result.error || t('contestcard.startMinParticipantsNotice', { min: 0, current: 0 }), {
          type: 'info',
        });
      } else {
        void appDialog.alert(result.error || t('contestcard.startError'), { type: 'error' });
      }
    } catch (error) {
      console.error('Error starting contest:', error);
      void appDialog.alert(t('contestcard.startConnectionError'), { type: 'error' });
    }
  };

  const handleReject = async (contestId: string) => {
    try {
      // Obtener token de Petflix si está disponible
      const petflixToken = getPetflixAccessToken();
      const headers = getApiHeaders();

      // Agregar token de Petflix al header si está disponible
      if (petflixToken) {
        headers['X-Petflix-Access-Token'] = petflixToken;
      }

      // Hacer la petición para cancelar el concurso
      const response = await fetch(`${environment.apiBaseUrl}/contests/${contestId}/reject`, {
        method: 'POST',
        headers: headers,
      });

      const result = await response.json();

      if (response.ok) {
        // Actualizar el estado local del concurso
        setContests(contests.map(c =>
          c.id === contestId ? {
            ...c,
            contest_state: 'cancelled',
            status: 'cancelled'
          } : c
        ));

        // Mostrar mensaje de éxito
        void appDialog.alert(`¡Concurso cancelado exitosamente!`);
      } else {
        // Mostrar error
        void appDialog.alert(`Error al cancelar el concurso: ${result.error}`);
      }
    } catch (error) {
      console.error('Error cancelling contest:', error);
      void appDialog.alert('Error de conexión al cancelar el concurso');
    }
  };

  const handleReactivate = async (contestId: string) => {
    try {
      // Hacer la petición para reactivar el concurso
      const response = await fetch(`${environment.apiBaseUrl}/contests/${contestId}/reactivate`, {
        method: 'POST',
        headers: getApiHeaders(),
      });

      const result = await response.json();

      if (response.ok) {
        // Recargar todos los concursos desde el servidor para asegurar que el estado esté actualizado
        try {
          const updatedContests = await contestService.getAllContests();

          // Cargar contador de participantes y propuestas enlazadas para cada concurso
          const contestsWithParticipantCount = await attachProposalSummariesBulk(await attachParticipantCountsBulk(updatedContests));

          setContests(contestsWithParticipantCount);
        } catch (error) {
          console.error('Error recargando concursos:', error);
          // Si falla la recarga, al menos actualizar el estado local
          setContests(contests.map(c =>
            c.id === contestId ? {
              ...c,
              contest_state: 'active',
              status: 'pending'
            } : c
          ));
        }

        // Mostrar mensaje de éxito
        void appDialog.alert(`¡Concurso reactivado exitosamente!`);
      } else {
        // Mostrar error
        void appDialog.alert(`Error al reactivar el concurso: ${result.error}`);
      }
    } catch (error) {
      console.error('Error reactivating contest:', error);
      void appDialog.alert('Error de conexión al reactivar el concurso');
    }
  };

  // Función para editar concurso
  const handleEditContest = async (contest: any) => {
    console.log('Editando concurso:', contest);
    try {
      const freshContest = await contestService.getContestById(contest.id);
      setEditingContest(freshContest);
    } catch (error) {
      console.error('Error loading fresh contest for edit:', error);
      setEditingContest(contest);
    }
    setShowContestForm(true);
  };

  const handleDeleteContest = async (contest: any) => {
    // Si el concurso está ligado a una propuesta del Sponsorship Builder (NAPC/PetFlix),
    // se advierte explícitamente: la propuesta sigue existiendo allá, pero el enlace
    // local (badge, sponsor, premio sincronizado) se pierde para siempre al eliminar.
    const linkedProposal = contest.linkedProposal;
    const message = linkedProposal
      ? t('contestcard.confirmDeleteLinkedMessage', {
        name: contest.name,
        proposalNumber: linkedProposal.proposal_number || '—',
        sponsorName: linkedProposal.sponsor_name || t('contestcard.unnamedSponsor'),
      })
      : t('contestcard.confirmDeleteMessage', { name: contest.name });

    const confirmed = await appDialog.confirm(message, {
      destructive: true,
      title: t('contestcard.confirmDeleteTitle'),
    });
    if (!confirmed) return;

    try {
      await contestService.deleteContest(contest.id);
      setContests(prev => prev.filter(c => c.id !== contest.id));
      void appDialog.alert(t('contestcard.deleteSuccess'), { type: 'success' });
    } catch (error) {
      console.error('Error deleting contest:', error);
      void appDialog.alert(t('contestcard.deleteError'), { type: 'error' });
    }
  };

  const proceedToSponsorshipContestLink = async (contest: any, payload: any) => {
    setSponsorshipData(payload);
    setSelectedContestForSponsorship(contest);

    if (contest.sponsor_id) {
      try {
        const existing = await sponsorshipService.getSponsorshipById(contest.sponsor_id);
        setExistingSponsor(existing);
        setShowReplaceSponsorConfirmation(true);
      } catch (error) {
        console.error('Error obteniendo patrocinador existente:', error);
        setExistingSponsor(null);
        setShowContestConfirmation(true);
      }
    } else {
      setExistingSponsor(null);
      setShowContestConfirmation(true);
    }
  };

  const handleSponsorClick = async (contest: any) => {
    try {
      if (!contest.sponsor_id) {
        setSelectedContestForSponsorship(contest);
        setSponsorshipData(null);
        setShowSponsorshipForm(true);
        return;
      }

      // Buscar el patrocinio usando el sponsor_id
      const sponsorship = await sponsorshipService.getSponsorshipById(contest.sponsor_id);
      if (sponsorship) {
        setSelectedSponsor(sponsorship);
        setShowSponsorModal(true);
      } else {
        void appDialog.alert('No se encontró el patrocinio para este concurso');
      }
    } catch (error) {
      console.error('Error cargando patrocinio:', error);
      void appDialog.alert('Error al cargar los datos del patrocinador');
    }
  };

  // Función para cargar tags del sponsor
  const handleViewSponsorTags = async (sponsorId: string) => {
    try {
      setLoadingSponsorTags(true);
      const response = await fetch(`${environment.apiBaseUrl}/qr-tags/sponsor/${sponsorId}`, {
        headers: getApiHeadersForGet()
      });
      if (response.ok) {
        const tags = await response.json();
        setSponsorTags(tags);
        setShowSponsorTagsModal(true);
      } else {
        void appDialog.alert('Error al cargar los tags del patrocinador');
      }
    } catch (error) {
      console.error('Error cargando tags del sponsor:', error);
      void appDialog.alert('Error al cargar los tags del patrocinador');
    } finally {
      setLoadingSponsorTags(false);
    }
  };

  const handleViewAmbassadorAssignments = async (ambassador: any) => {
    try {
      setSelectedAmbassadorForAssignments(ambassador);
      setLoadingAmbassadorAssignments(true);
      setShowAmbassadorAssignmentsModal(true);
      const data = await ambassadorService.getContestAssignments(ambassador.id);
      setAmbassadorAssignments(data.assignments || []);
    } catch (error) {
      console.error('Error cargando asignaciones del embajador:', error);
      void appDialog.alert(t('ambassadormanagement.errorLoading'));
      setAmbassadorAssignments([]);
    } finally {
      setLoadingAmbassadorAssignments(false);
    }
  };

  const handleViewAmbassadorTags = async (ambassador: any) => {
    try {
      setSelectedAmbassadorForTags(ambassador);
      setLoadingAmbassadorTagsView(true);
      const response = await fetch(`${environment.apiBaseUrl}/qr-tags/ambassador/${ambassador.id}`, {
        headers: getApiHeadersForGet()
      });
      if (response.ok) {
        const tags = await response.json();
        setAmbassadorTagsView(tags);
        setShowAmbassadorTagsModal(true);
      } else {
        void appDialog.alert(t('ambassadortags.errorLoading'));
      }
    } catch (error) {
      console.error('Error cargando tags del embajador:', error);
      void appDialog.alert(t('ambassadortags.errorLoading'));
    } finally {
      setLoadingAmbassadorTagsView(false);
    }
  };

  // Función para abrir modal de digital passes de embajador
  const handleViewAmbassadorDigitalPasses = async (ambassador: any) => {
    try {
      setSelectedAmbassadorForDigitalPasses(ambassador);
      setLoadingAmbassadorDigitalPasses(true);
      const response = await fetch(`${environment.apiBaseUrl}/digital-passes/ambassador/${ambassador.id}`, {
        headers: getApiHeadersForGet()
      });
      if (response.ok) {
        const passes = await response.json();
        setAmbassadorDigitalPasses(passes);
        setShowAmbassadorDigitalPassesModal(true);
      } else {
        void appDialog.alert(t('ambassadordigitalpasses.errorLoading'));
      }
    } catch (error) {
      console.error('Error cargando digital passes del embajador:', error);
      void appDialog.alert(t('ambassadordigitalpasses.errorLoading'));
    } finally {
      setLoadingAmbassadorDigitalPasses(false);
    }
  };

  // Función para abrir modal de diagrama de flujo económico
  const handleViewEconomyFlowDiagram = async () => {
    try {
      setLoadingEconomyFlowData(true);
      // Pasar el idioma como query parameter
      const langParam = locale || 'en';
      const response = await fetch(`${environment.apiBaseUrl}/economy-flow?lang=${langParam}`, {
        headers: getApiHeadersForGet()
      });
      if (response.ok) {
        const data = await response.json();
        setEconomyFlowData(data);
        setShowEconomyFlowDiagramModal(true);
      } else {
        void appDialog.alert('Error al cargar el diagrama de flujo económico');
      }
    } catch (error) {
      console.error('Error cargando diagrama de flujo económico:', error);
      void appDialog.alert('Error al cargar el diagrama de flujo económico');
    } finally {
      setLoadingEconomyFlowData(false);
    }
  };

  // Función para abrir modal de reportes de sponsors por tier
  const handleViewSponsorTierReport = async () => {
    try {
      setLoadingSponsorTierStats(true);
      const response = await fetch(`${environment.apiBaseUrl}/sponsorships/stats/by-tier`, {
        headers: getApiHeadersForGet()
      });
      if (response.ok) {
        const stats = await response.json();
        setSponsorTierStats(stats);
        setShowSponsorTierReportModal(true);
      } else {
        void appDialog.alert('Error al cargar el reporte de sponsors por tier');
      }
    } catch (error) {
      console.error('Error cargando reporte de sponsors por tier:', error);
      void appDialog.alert('Error al cargar el reporte de sponsors por tier');
    } finally {
      setLoadingSponsorTierStats(false);
    }
  };

  // Función para abrir modal de gestión de bonuses
  const handleViewBonusManagement = async (ambassador: any) => {
    try {
      setSelectedAmbassadorForBonus(ambassador);
      setLoadingBonusHistory(true);

      // Cargar historial de bonuses
      const response = await fetch(`${environment.apiBaseUrl}/ambassadors/${ambassador.id}/bonus-history`, {
        headers: getApiHeadersForGet()
      });
      if (response.ok) {
        const history = await response.json();
        setBonusHistory(history);
      } else {
        setBonusHistory([]);
      }

      setShowBonusManagementModal(true);
    } catch (error) {
      console.error('Error cargando historial de bonuses:', error);
      setBonusHistory([]);
      setShowBonusManagementModal(true);
    } finally {
      setLoadingBonusHistory(false);
    }
  };

  // Función para asignar badge
  const handleAssignBadge = async (badgeType: string) => {
    if (!selectedAmbassadorForBonus) return;

    try {
      setAssigningBadge(true);
      const response = await fetch(`${environment.apiBaseUrl}/ambassadors/${selectedAmbassadorForBonus.id}/assign-badge`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({ badgeType })
      });

      if (response.ok) {
        const result = await response.json();
        void appDialog.alert(`Badge ${badgeType} asignado exitosamente`);
        // Recargar datos del ambassador
        const ambassadorsResponse = await fetch(`${environment.apiBaseUrl}/ambassadors`, {
          headers: getApiHeadersForGet()
        });
        if (ambassadorsResponse.ok) {
          const updatedAmbassadors = await ambassadorsResponse.json();
          setAmbassadors(updatedAmbassadors);
          // Actualizar el ambassador seleccionado
          const updatedAmbassador = updatedAmbassadors.find((a: any) => a.id === selectedAmbassadorForBonus.id);
          if (updatedAmbassador) {
            setSelectedAmbassadorForBonus(updatedAmbassador);
          }
        }
      } else {
        const error = await response.json();
        void appDialog.alert(`Error: ${error.error || 'Error al asignar badge'}`);
      }
    } catch (error) {
      console.error('Error asignando badge:', error);
      void appDialog.alert('Error al asignar badge');
    } finally {
      setAssigningBadge(false);
    }
  };

  // Función para otorgar bonus tags
  const handleGrantBonusTags = async () => {
    if (!selectedAmbassadorForBonus) return;

    const quantity = parseInt(bonusTagsQuantity);
    if (isNaN(quantity) || quantity <= 0) {
      void appDialog.alert('Por favor ingresa una cantidad válida de tags');
      return;
    }

    try {
      setGrantingTags(true);
      const response = await fetch(`${environment.apiBaseUrl}/ambassadors/${selectedAmbassadorForBonus.id}/grant-bonus-tags`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({ quantity })
      });

      if (response.ok) {
        const result = await response.json();
        void appDialog.alert(`${quantity} bonus tags otorgados exitosamente`);
        setBonusTagsQuantity('0');
        // Recargar datos del ambassador
        const ambassadorsResponse = await fetch(`${environment.apiBaseUrl}/ambassadors`, {
          headers: getApiHeadersForGet()
        });
        if (ambassadorsResponse.ok) {
          const updatedAmbassadors = await ambassadorsResponse.json();
          setAmbassadors(updatedAmbassadors);
          // Actualizar el ambassador seleccionado
          const updatedAmbassador = updatedAmbassadors.find((a: any) => a.id === selectedAmbassadorForBonus.id);
          if (updatedAmbassador) {
            setSelectedAmbassadorForBonus(updatedAmbassador);
          }
        }
        // Recargar historial
        const historyResponse = await fetch(`${environment.apiBaseUrl}/ambassadors/${selectedAmbassadorForBonus.id}/bonus-history`, {
          headers: getApiHeadersForGet()
        });
        if (historyResponse.ok) {
          const history = await historyResponse.json();
          setBonusHistory(history);
        }
      } else {
        const error = await response.json();
        void appDialog.alert(`Error: ${error.error || 'Error al otorgar bonus tags'}`);
      }
    } catch (error) {
      console.error('Error otorgando bonus tags:', error);
      void appDialog.alert('Error al otorgar bonus tags');
    } finally {
      setGrantingTags(false);
    }
  };

  // Función para abrir modal de digital passes de sponsor
  const handleViewSponsorDigitalPasses = async (sponsor: any) => {
    try {
      setSelectedSponsorForDigitalPasses(sponsor);
      setLoadingSponsorDigitalPasses(true);
      const response = await fetch(`${environment.apiBaseUrl}/digital-passes/sponsor/${sponsor.id}`, {
        headers: getApiHeadersForGet()
      });
      if (response.ok) {
        const passes = await response.json();
        setSponsorDigitalPasses(passes);
        setShowSponsorDigitalPassesModal(true);
      } else {
        void appDialog.alert('Error al cargar los digital passes del sponsor');
      }
    } catch (error) {
      console.error('Error cargando digital passes del sponsor:', error);
      void appDialog.alert('Error al cargar los digital passes del sponsor');
    } finally {
      setLoadingSponsorDigitalPasses(false);
    }
  };

  // Función helper para extraer el número del invoice_number (soporta formato numérico: 860007, INVC_XXXXXXX, INV_XXXXXXX)
  const extractInvoiceNumber = (invoiceNum: string): string => {
    if (!invoiceNum) return '';

    // Verificar si es solo un número (formato nuevo: 860007)
    const numberMatch = invoiceNum.match(/^(\d+)$/);
    if (numberMatch && numberMatch[1]) {
      return numberMatch[1].replace(/^0+/, '') || numberMatch[1];
    }

    // Verificar si tiene el formato INVC_XXXXXXX (para compatibilidad)
    const invcMatch = invoiceNum.match(/^INVC_(\d+)$/);
    if (invcMatch && invcMatch[1]) {
      return invcMatch[1].replace(/^0+/, '') || invcMatch[1];
    }

    // Verificar si tiene el formato INV_XXXXXXX (formato antiguo, para compatibilidad)
    const invMatch = invoiceNum.match(/^INV_(\d+)$/);
    if (invMatch && invMatch[1]) {
      return invMatch[1].replace(/^0+/, '') || invMatch[1];
    }

    // Fallback: devolver el número sin ceros iniciales
    return String(invoiceNum).replace(/^0+/, '') || '';
  };

  // Función para enviar datos del patrocinador al endpoint externo
  const handleSendSponsor = async (contest: any, eventType: string = 'GeneratePaylink', selectedInvoice?: any) => {
    try {
      // Verificar si el concurso tiene sponsor_id
      if (!contest.sponsor_id) {
        void appDialog.alert('Este concurso no tiene un patrocinador vinculado');
        return;
      }

      // Obtener datos completos del concurso
      const fullContest = await contestService.getContestById(contest.id);
      if (!fullContest) {
        void appDialog.alert('No se pudieron obtener los datos del concurso');
        return;
      }

      // Obtener datos del patrocinio
      const sponsorship = await sponsorshipService.getSponsorshipById(contest.sponsor_id);
      if (!sponsorship) {
        void appDialog.alert('No se encontró el patrocinio para este concurso');
        return;
      }

      const billToEmail = resolveSponsorBillToEmail(sponsorship);
      if (!billToEmail) {
        void appDialog.alert('El patrocinador no tiene email de contacto. Agregue contact_email antes de enviar el paylink.');
        return;
      }

      // Eliminar campos específicos del objeto contest (mantener los primeros 4 premios)
      const {
        prize_fifth_place,
        prize_sixth_place,
        prize_seventh_place,
        prize_eighth_place,
        organizer_id,
        ...cleanedContest
      } = fullContest;

      // Generar datos de invoice primero (necesarios para el PDF)
      const invoiceDate = new Date().toISOString();
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 15); // 15 días para pagar

      // Si hay un invoice seleccionado, usar su ID; de lo contrario, dejar que el servidor lo genere
      const invoiceId = selectedInvoice && selectedInvoice.id
        ? selectedInvoice.id
        : undefined; // El servidor generará el ID (número aleatorio de 6 cifras único)

      const invoiceData: any = {
        ...(invoiceId ? { id: invoiceId } : {}), // Solo incluir id si existe (invoice seleccionado)
        contest_id: contest.id,
        sponsorship_id: contest.sponsor_id,
        invoice_date: invoiceDate,
        due_date: dueDate.toISOString(),
        bill_to_name: sponsorship.sponsor_name || 'NN',
        bill_to_address: sponsorship.sponsor_website || 'NN',
        bill_to_city: 'NN',
        bill_to_state: 'NN',
        bill_to_zip: 'NN',
        bill_to_email: billToEmail,
        bill_to_phone: sponsorship.sponsor_phone || 'NN',
        company_name: 'Noah\'s Ark',
        company_address: 'NN',
        company_city: 'NN',
        company_state: 'NN',
        company_zip: 'NN',
        company_phone: 'NN',
        //company_email: 'main@ipaysolutions.co',
        company_email: 'alexis@ipaysolutions.co',
        job_number: 'NN', // Se actualizará después con el número del invoice
        job_site: fullContest.name || 'NN',
        origin: 'NN',
        batch_number: 'NN',
        terms: '30',
        status: 'pending',
        // items como arreglo de dos líneas con los campos solicitados
        items: (() => {
          const couponpassesAssigned = (fullContest as any).couponpasses_assigned;
          const maxCoupass = (fullContest as any).max_coupass ?? 0;
          const usesPerCoupon = (fullContest as any).uses_per_coupon ?? 1;
          const couponPassesTotalUses: number = (typeof couponpassesAssigned === 'number' && !isNaN(couponpassesAssigned))
            ? couponpassesAssigned
            : (maxCoupass * usesPerCoupon);
          const organizerBuyRate: number = (fullContest as any).organizer_buy_rate ?? 0;
          const rewardContribution: number = (sponsorship as any).reward_contribution ?? 0;

          // Precios de los tiers
          const tierPrices: Record<string, number> = {
            'gold': 1500,
            'silver': 1000,
            'bronze': 500
          };

          const sponsorTier = (sponsorship as any).sponsor_tier;
          const tierPrice = sponsorTier ? (tierPrices[sponsorTier.toLowerCase()] || 0) : 0;

          const itemsArray = [
            {
              description: 'CouponPasses',
              Quanty: couponPassesTotalUses,
              Rate: organizerBuyRate,
              Amount: couponPassesTotalUses * organizerBuyRate
            },
            {
              description: 'Sponsor Reward Contribution',
              Quanty: 1,
              Rate: rewardContribution,
              Amount: 1 * rewardContribution
            }
          ];

          // Agregar el tier del sponsor si existe
          if (sponsorTier && tierPrice > 0) {
            const tierName = sponsorTier.charAt(0).toUpperCase() + sponsorTier.slice(1).toLowerCase();
            itemsArray.push({
              description: `Sponsor Tag Tier - ${tierName}`,
              Quanty: 1,
              Rate: tierPrice,
              Amount: tierPrice
            });
          }

          return itemsArray;
        })(),
        activity_log: [{
          action: 'Opened',
          date: invoiceDate,
          user: 'System'
        }],
        invoice_pdf: '' // Se llenará después de generar el PDF
      };

      // Calcular el subtotal sumando todos los Amount de los items
      const itemsArray = invoiceData.items as any[];
      const subtotal = itemsArray.reduce((sum, item) => {
        const itemAmount = parseFloat(item.Amount || item.amount || item.total || 0);
        return sum + (isNaN(itemAmount) ? 0 : itemAmount);
      }, 0);

      // Obtener el tax (si existe en el invoice seleccionado, sino usar 0)
      const tax = selectedInvoice && selectedInvoice.tax ? parseFloat(selectedInvoice.tax) : 0;

      // Incluir el tax en el invoiceData si existe
      if (tax > 0) {
        (invoiceData as any).tax = tax;
      }

      // Calcular el total = subtotal + tax
      const total = subtotal + (isNaN(tax) ? 0 : tax);

      // Asignar el total a total_amount y balance_due
      (invoiceData as any).total_amount = total;
      (invoiceData as any).balance_due = total;

      // Variable para guardar el ID del invoice creado
      let createdInvoiceId: string | null = null;
      let createdInvoiceNumber: string | null = null;

      // Si hay un invoice seleccionado, usar sus datos completos de la base de datos
      if (selectedInvoice && selectedInvoice.invoice_number) {
        // Usar el ID del invoice seleccionado (ya está asignado en invoiceData.id arriba)
        createdInvoiceId = selectedInvoice.id;
        createdInvoiceNumber = selectedInvoice.invoice_number;

        // Actualizar invoiceData con los datos del invoice seleccionado
        (invoiceData as any).invoice_number = selectedInvoice.invoice_number;
        // Asegurarse de que el ID sea el correcto
        invoiceData.id = selectedInvoice.id;

        console.log('Usando invoice seleccionado - ID:', selectedInvoice.id, 'Invoice Number:', selectedInvoice.invoice_number);
      } else {
        // Guardar invoice en la base de datos primero para obtener el invoice_number
        try {
          const invoiceResponse = await fetch(`${environment.apiBaseUrl}/invoices`, {
            method: 'POST',
            headers: getApiHeaders(),
            body: JSON.stringify(invoiceData)
          });

          if (!invoiceResponse.ok) {
            console.error('Error guardando invoice:', await invoiceResponse.text());
            void appDialog.alert('Error al crear el invoice en la base de datos');
            return;
          } else {
            const savedInvoice = await invoiceResponse.json();
            // Asignar el ID generado por el servidor (número aleatorio de 6 cifras único)
            (invoiceData as any).id = savedInvoice.invoice.id;
            (invoiceData as any).invoice_number = savedInvoice.invoice.invoice_number;
            createdInvoiceId = savedInvoice.invoice.id;
            createdInvoiceNumber = savedInvoice.invoice.invoice_number;
            console.log('Invoice guardada exitosamente:', savedInvoice);

            // Verificar que el invoice esté disponible en la base de datos antes de continuar
            let invoiceVerified = false;
            let retryCount = 0;
            const maxRetries = 10; // Aumentar a 10 intentos
            const retryDelay = 300; // Aumentar a 300ms entre intentos

            console.log('Verificando que el invoice esté disponible en la base de datos...');
            while (!invoiceVerified && retryCount < maxRetries) {
              try {
                const verifyResponse = await fetch(`${environment.apiBaseUrl}/invoices/${createdInvoiceId}`, {
                  headers: getApiHeadersForGet()
                });
                if (verifyResponse.ok) {
                  const verifiedInvoice = await verifyResponse.json();
                  if (verifiedInvoice && verifiedInvoice.id) {
                    invoiceVerified = true;
                    console.log('Invoice verificado exitosamente en la base de datos:', verifiedInvoice.id);
                  } else {
                    retryCount++;
                    console.log(`Intento ${retryCount}/${maxRetries}: Invoice aún no disponible, reintentando...`);
                    if (retryCount < maxRetries) {
                      await new Promise(resolve => setTimeout(resolve, retryDelay));
                    }
                  }
                } else {
                  retryCount++;
                  console.log(`Intento ${retryCount}/${maxRetries}: Error al verificar invoice (${verifyResponse.status}), reintentando...`);
                  if (retryCount < maxRetries) {
                    await new Promise(resolve => setTimeout(resolve, retryDelay));
                  }
                }
              } catch (verifyError) {
                retryCount++;
                console.log(`Intento ${retryCount}/${maxRetries}: Error al verificar invoice, reintentando...`, verifyError);
                if (retryCount < maxRetries) {
                  await new Promise(resolve => setTimeout(resolve, retryDelay));
                }
              }
            }

            if (!invoiceVerified) {
              console.error('No se pudo verificar el invoice en la base de datos después de varios intentos');
              void appDialog.alert('Error: No se pudo verificar el invoice en la base de datos. No se enviarán los datos.');
              return;
            }
          }
        } catch (error) {
          console.error('Error guardando invoice:', error);
          void appDialog.alert('Error al crear el invoice en la base de datos');
          return;
        }
      }

      // Solo continuar si el invoice está creado y verificado
      if (!createdInvoiceId && !selectedInvoice) {
        void appDialog.alert('Error: No se pudo crear o verificar el invoice. No se enviarán los datos.');
        return;
      }

      // Extraer el número del invoice para generar los códigos CONTEST-XXXXX y NA-PC-XXXXX
      // El ID tiene formato invoice_XXXXXX donde XXXXXX es un número aleatorio de 6 cifras
      let invoiceNumber = '';
      if (createdInvoiceId) {
        // Extraer números del ID (ej: "invoice_200005" -> "200005")
        // El formato es invoice_ seguido de 6 dígitos
        const idMatch = createdInvoiceId.match(/invoice_(\d+)$/);
        if (idMatch && idMatch[1]) {
          invoiceNumber = idMatch[1];
        } else {
          // Fallback: si no coincide el formato, intentar extraer cualquier número al final
          const fallbackMatch = createdInvoiceId.match(/\d+$/);
          if (fallbackMatch) {
            invoiceNumber = fallbackMatch[0];
          }
        }
      }
      if (!invoiceNumber && createdInvoiceNumber) {
        // Si no se encontró en el ID, usar invoice_number directamente
        invoiceNumber = extractInvoiceNumber(String(createdInvoiceNumber));
      }
      if (!invoiceNumber && selectedInvoice) {
        // Si hay un invoice seleccionado, intentar extraer de su ID o invoice_number
        if (selectedInvoice.id) {
          // Extraer números del ID (ej: "invoice_200005" -> "200005")
          const idMatch = selectedInvoice.id.match(/invoice_(\d+)$/);
          if (idMatch && idMatch[1]) {
            invoiceNumber = idMatch[1];
          } else {
            // Fallback: intentar extraer cualquier número al final
            const fallbackMatch = selectedInvoice.id.match(/\d+$/);
            if (fallbackMatch) {
              invoiceNumber = fallbackMatch[0];
            }
          }
        }
        if (!invoiceNumber && selectedInvoice.invoice_number) {
          invoiceNumber = extractInvoiceNumber(String(selectedInvoice.invoice_number));
        }
      }

      // Formatear el ID del contest: CONTEST-XXXXX (donde XXXXX es el número del invoice)
      const formatContestId = (invoiceNum: string) => {
        if (!invoiceNum) return '';
        return `CONTEST-${invoiceNum}`;
      };

      const formattedContestId = formatContestId(invoiceNumber);

      // Actualizar job_number en invoiceData con el formattedContestId basado en el invoice
      invoiceData.job_number = formattedContestId || 'NN';

      // Generar PDF en el servidor usando los datos reales del invoice
      let invoicePdfBase64 = '';
      try {
        // Llamar al endpoint del servidor para generar el PDF con los datos del invoice
        const pdfResponse = await fetch(`${environment.apiBaseUrl}/invoices/generate-pdf`, {
          method: 'POST',
          headers: getApiHeaders(),
          body: JSON.stringify({ invoice: invoiceData })
        });

        if (pdfResponse.ok) {
          const pdfData = await pdfResponse.json();
          invoicePdfBase64 = pdfData.pdf_base64 || '';

          // Convertir base64 a blob para descargar
          const byteCharacters = atob(invoicePdfBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });

          // Descargar el PDF
          saveAs(pdfBlob, `invoice_${contest.id}_${Date.now()}.pdf`);

          // Actualizar invoiceData con el PDF generado
          invoiceData.invoice_pdf = invoicePdfBase64;
        } else {
          console.warn('Error al generar PDF en el servidor');
          invoicePdfBase64 = '';
        }
      } catch (error: any) {
        console.warn('Error al generar PDF:', error?.message || error);
        invoicePdfBase64 = '';
      }

      // Generar documento Word completo del "Download Contest Information" y convertirlo a base64
      let contestInfoDocBase64 = '';
      try {
        // Obtener datos económicos del concurso
        const economicsData = await contestService.getContestEconomics(contest.id);
        const economics = economicsData.economics || {};
        const totalRevenue = Number(economics.total_revenue) || 0;
        const totalCosts = Number(economics.total_costs) || 0;

        // Obtener todas las plataformas ordenadas según platform_order de la base de datos
        let orderedPlatforms: any[] = [];
        try {
          const platformsResponse = await fetch(`${environment.apiBaseUrl}/platforms`, {
            headers: getApiHeadersForGet()
          });
          if (platformsResponse.ok) {
            const allPlatforms = await platformsResponse.json();

            // Verificar si existe platform_order en el sponsorship (desde la BD)
            if (sponsorship.platform_order && Array.isArray(sponsorship.platform_order) && sponsorship.platform_order.length > 0) {
              // Ordenar el array por position
              const sortedPlatformOrder = [...sponsorship.platform_order].sort((a: any, b: any) => a.position - b.position);

              // Construir el array de plataformas según el orden de platform_order
              sortedPlatformOrder.forEach((orderItem: any) => {
                if (orderItem.type === 'voletaje') {
                  // Buscar plataforma voletaje
                  const voletajePlatform = allPlatforms.find((p: any) => p.platform_type === 'voletaje' || p.id === 'voletaje' || p.id === 'base-voletaje');
                  if (voletajePlatform) {
                    orderedPlatforms.push({
                      ...voletajePlatform,
                      isBase: true,
                      id: voletajePlatform.id,
                      name: orderItem.name || voletajePlatform.name,
                      image: voletajePlatform.image_url || voletajePlatform.image,
                      x: voletajePlatform.x || 50,
                      y: voletajePlatform.y || 50,
                      width: voletajePlatform.width || 200,
                      height: voletajePlatform.height || 150,
                      logo_url: voletajePlatform.logo_url || sponsorship.media_assets?.logo_url,
                      logo_width: voletajePlatform.logo_width || 60,
                      logo_height: voletajePlatform.logo_height || 60,
                      Image_form: voletajePlatform.Image_form || 'circular',
                      custom_text: voletajePlatform.custom_text || 'your ad goes here',
                      Text_size: voletajePlatform.Text_size || 0.9,
                      contest_text: voletajePlatform.contest_text || 'Nombre del concurso',
                      distancia_x: voletajePlatform.distancia_x || 60,
                      Fix_width: voletajePlatform.Fix_width,
                      Fix_height: voletajePlatform.Fix_height,
                      general_width: voletajePlatform.general_width,
                      general_height: voletajePlatform.general_height
                    });
                  }
                } else if (orderItem.type === 'base') {
                  // Buscar la plataforma base por platform_id
                  let platformId = orderItem.platform_id;
                  if (platformId.startsWith('base-')) {
                    platformId = platformId.replace('base-', '');
                  }
                  const platform = allPlatforms.find((p: any) => p.id === platformId);
                  if (platform && sponsorship.enabled_base_platforms && sponsorship.enabled_base_platforms.includes(platform.id)) {
                    orderedPlatforms.push({
                      ...platform,
                      isBase: true,
                      id: platform.id,
                      name: orderItem.name || platform.name,
                      image: platform.image_url || platform.image,
                      x: platform.x || 50,
                      y: platform.y || 50,
                      width: platform.width || 200,
                      height: platform.height || 150,
                      logo_url: platform.logo_url || sponsorship.media_assets?.logo_url,
                      logo_width: platform.logo_width || 60,
                      logo_height: platform.logo_height || 60,
                      Image_form: platform.Image_form || 'circular',
                      custom_text: platform.custom_text || 'your ad goes here',
                      Text_size: platform.Text_size || 0.9,
                      contest_text: platform.contest_text || 'Nombre del concurso',
                      distancia_x: platform.distancia_x || 60,
                      Fix_width: platform.Fix_width,
                      Fix_height: platform.Fix_height,
                      general_width: platform.general_width,
                      general_height: platform.general_height
                    });
                  }
                } else if (orderItem.type === 'custom') {
                  // Buscar plataforma personalizada
                  if (sponsorship.custom_platforms && Array.isArray(sponsorship.custom_platforms)) {
                    const customPlatform = sponsorship.custom_platforms.find((cp: any) => {
                      const customId = `custom-${cp.name}-${cp.image}`;
                      return orderItem.platform_id.includes(customId) || orderItem.platform_id === customId;
                    });
                    if (customPlatform) {
                      orderedPlatforms.push({
                        ...customPlatform,
                        isBase: false,
                        name: orderItem.name || customPlatform.name
                      });
                    }
                  }
                }
              });

              // Agregar plataformas que no están en platform_order pero están habilitadas
              // Plataformas base habilitadas que no están en platform_order
              if (sponsorship.enabled_base_platforms && Array.isArray(sponsorship.enabled_base_platforms)) {
                sponsorship.enabled_base_platforms.forEach((platformId: string) => {
                  const alreadyIncluded = orderedPlatforms.some((p: any) => p.isBase && p.id === platformId);
                  if (!alreadyIncluded) {
                    const platform = allPlatforms.find((p: any) => p.id === platformId);
                    if (platform) {
                      orderedPlatforms.push({
                        ...platform,
                        isBase: true,
                        image: platform.image_url || platform.image,
                        x: platform.x || 50,
                        y: platform.y || 50,
                        width: platform.width || 200,
                        height: platform.height || 150,
                        logo_url: platform.logo_url || sponsorship.media_assets?.logo_url,
                        logo_width: platform.logo_width || 60,
                        logo_height: platform.logo_height || 60,
                        Image_form: platform.Image_form || 'circular',
                        custom_text: platform.custom_text || 'your ad goes here',
                        Text_size: platform.Text_size || 0.9,
                        contest_text: platform.contest_text || 'Nombre del concurso',
                        distancia_x: platform.distancia_x || 60,
                        Fix_width: platform.Fix_width,
                        Fix_height: platform.Fix_height,
                        general_width: platform.general_width,
                        general_height: platform.general_height
                      });
                    }
                  }
                });
              }

              // Plataformas personalizadas que no están en platform_order
              if (sponsorship.custom_platforms && Array.isArray(sponsorship.custom_platforms)) {
                sponsorship.custom_platforms.forEach((cp: any) => {
                  const customId = `custom-${cp.name}-${cp.image}`;
                  const alreadyIncluded = orderedPlatforms.some((p: any) => !p.isBase && p.name === cp.name && p.image === cp.image);
                  if (!alreadyIncluded) {
                    orderedPlatforms.push({
                      ...cp,
                      isBase: false
                    });
                  }
                });
              }
            } else {
              // Si no hay platform_order, usar el orden por defecto (orden de enabled_base_platforms)
              // Procesar plataformas base habilitadas
              if (sponsorship.enabled_base_platforms && Array.isArray(sponsorship.enabled_base_platforms)) {
                for (const platformId of sponsorship.enabled_base_platforms) {
                  const platform = allPlatforms.find((p: any) => p.id === platformId);
                  if (platform) {
                    orderedPlatforms.push({
                      ...platform,
                      isBase: true,
                      image: platform.image_url || platform.image,
                      x: platform.x || 50,
                      y: platform.y || 50,
                      width: platform.width || 200,
                      height: platform.height || 150,
                      logo_url: platform.logo_url || sponsorship.media_assets?.logo_url,
                      logo_width: platform.logo_width || 60,
                      logo_height: platform.logo_height || 60,
                      Image_form: platform.Image_form || 'circular',
                      custom_text: platform.custom_text || 'your ad goes here',
                      Text_size: platform.Text_size || 0.9,
                      contest_text: platform.contest_text || 'Nombre del concurso',
                      distancia_x: platform.distancia_x || 60,
                      Fix_width: platform.Fix_width,
                      Fix_height: platform.Fix_height,
                      general_width: platform.general_width,
                      general_height: platform.general_height
                    });
                  }
                }
              }

              // Procesar plataformas personalizadas
              if (sponsorship.custom_platforms && Array.isArray(sponsorship.custom_platforms)) {
                orderedPlatforms.push(...sponsorship.custom_platforms.map((p: any) => ({
                  ...p,
                  isBase: false
                })));
              }
            }
          }
        } catch (error) {
          console.error('Error obteniendo plataformas:', error);
        }

        // Generar membership breakdown dinámico (usar el mismo que el botón Download Contest Information)
        const generateDynamicMembershipBreakdown = () => {
          const entranceFee = fullContest.entrance_fee || 0;
          const totalParticipants = fullContest.current_participants || 0;
          const passCodesUsed = economics.total_pass_codes_used || 0;

          const couponpassesUsed = fullContest.couponpasses_used || 0;
          const couponpassesAssigned = fullContest.couponpasses_assigned ?? (fullContest.max_coupass || 0);
          const couponpassesProgress = couponpassesAssigned > 0 ? (couponpassesUsed / couponpassesAssigned) * 100 : 0;

          const originalBreakdown = fullContest.membership_breakdown || [];

          const updatedBreakdown = originalBreakdown.map((memberType: any) => {
            if (memberType.membership_key === 'voucherFreePass') {
              return {
                ...memberType,
                discount_type: 'Couponpasses',
                membership_level: '',
                participant_count: couponpassesUsed,
                actual_paid: 0,
                total_revenue: 0,
                percentage_of_total: couponpassesProgress
              };
            }

            if (memberType.membership_key === 'membersFullPrice') {
              const maxAssigned = (fullContest.max_pass ?? fullContest.max_participants ?? fullContest.min_participants) || 100;
              const percentage = maxAssigned > 0 ? (passCodesUsed / maxAssigned) * 100 : 0;
              return {
                ...memberType,
                participant_count: passCodesUsed,
                actual_paid: entranceFee,
                total_revenue: passCodesUsed * entranceFee,
                percentage_of_total: percentage,
                total_assigned: maxAssigned,
                show_percentage: true
              };
            }

            if (memberType.membership_key === 'subscribersDiscounted') {
              return {
                ...memberType,
                participant_count: 0,
                actual_paid: 0,
                total_revenue: 0,
                percentage_of_total: 0
              };
            }

            if (memberType.membership_key === 'nonMembersFull') {
              return {
                ...memberType,
                participant_count: 0,
                actual_paid: 0,
                total_revenue: 0,
                percentage_of_total: 0
              };
            }

            return memberType;
          });

          const noahsArkSubscribers100 = {
            membership_key: 'noahsArkSubscribers100',
            discount_type: "Noah's Ark Subscribers (100% off)",
            membership_level: 'Paid Subscriber',
            discount_amount: entranceFee,
            discount_percentage: 100,
            participant_count: 0,
            actual_paid: 0,
            total_revenue: 0,
            percentage_of_total: 0
          };

          const result = [];
          let couponpassesSection = null;
          let membersFullPriceSection = null;
          let nonMembersFullSection = null;
          let noahsArk50Section = null;
          let otherSections = [];

          for (let i = 0; i < updatedBreakdown.length; i++) {
            const section = updatedBreakdown[i];

            if (section.membership_key === 'voucherFreePass') {
              couponpassesSection = section;
            } else if (section.membership_key === 'membersFullPrice') {
              membersFullPriceSection = section;
            } else if (section.membership_key === 'nonMembersFull') {
              nonMembersFullSection = section;
            } else if (section.discount_type && section.discount_type.includes("Noah's Ark Subscribers (50% off)")) {
              noahsArk50Section = section;
            } else {
              otherSections.push(section);
            }
          }

          if (couponpassesSection) result.push(couponpassesSection);
          if (membersFullPriceSection) result.push(membersFullPriceSection);
          if (nonMembersFullSection) result.push(nonMembersFullSection);
          if (noahsArk50Section) result.push(noahsArk50Section);
          result.push(noahsArkSubscribers100);
          result.push(...otherSections);

          return result;
        };

        const dynamicMembershipBreakdown = generateDynamicMembershipBreakdown();

        // Función helper para cargar imágenes correctamente para el documento Word (igual que CreateSponsorshipForm)
        const loadImageForWord = async (imageUrl: string | null | undefined): Promise<{ data: Uint8Array; width: number; height: number } | null> => {
          if (!imageUrl) {
            console.warn('loadImageForWord: No image URL provided');
            return null;
          }

          // Normalizar la URL usando getCompleteImagePath si es necesario
          let normalizedUrl = imageUrl;
          if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
            normalizedUrl = getCompleteImagePath(imageUrl) || imageUrl;
          }

          console.log('loadImageForWord: Loading image from URL:', normalizedUrl);

          // Función helper para cargar imagen usando proxy (siempre necesario para evitar canvas tainted)
          const loadImageViaProxy = async (url: string): Promise<{ data: Uint8Array; width: number; height: number } | null> => {
            try {
              const proxyUrl = `${environment.apiBaseUrl}/proxy/image?url=${encodeURIComponent(url)}`;
              console.log('loadImageForWord: Loading via proxy URL:', proxyUrl);

              const response = await fetch(proxyUrl);
              if (!response.ok) {
                const errorText = await response.text();
                console.error('loadImageForWord: Proxy request failed:', response.status, errorText);
                throw new Error(`Proxy request failed: ${response.status} - ${errorText}`);
              }

              const contentType = response.headers.get('content-type');
              if (!contentType || !contentType.startsWith('image/')) {
                const errorText = await response.text();
                console.error('loadImageForWord: Proxy returned non-image content:', contentType, errorText);
                throw new Error(`Proxy returned non-image content: ${contentType}`);
              }

              const blob = await response.blob();
              console.log('loadImageForWord: Blob received, size:', blob.size, 'type:', blob.type);

              if (blob.size === 0) {
                console.error('loadImageForWord: Received empty blob from proxy');
                throw new Error('Received empty blob from proxy');
              }

              const arrayBuffer = await blob.arrayBuffer();
              const uint8Array = new Uint8Array(arrayBuffer);
              console.log('loadImageForWord: ArrayBuffer created, size:', arrayBuffer.byteLength);

              // Crear imagen para obtener dimensiones
              const img = new window.Image();
              const imageUrl = URL.createObjectURL(blob);
              console.log('loadImageForWord: Created object URL from blob');

              await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                  URL.revokeObjectURL(imageUrl);
                  console.error('loadImageForWord: Image load timeout after 30 seconds');
                  reject(new Error('Image load timeout'));
                }, 30000);

                img.onload = () => {
                  clearTimeout(timeout);
                  URL.revokeObjectURL(imageUrl);
                  console.log('loadImageForWord: Image loaded successfully from blob, dimensions:', img.width, 'x', img.height);
                  resolve(null);
                };
                img.onerror = (error) => {
                  clearTimeout(timeout);
                  URL.revokeObjectURL(imageUrl);
                  console.error('loadImageForWord: Error loading image from blob:', error);
                  reject(new Error('Failed to load image from blob'));
                };
                img.src = imageUrl;
              });

              console.log('loadImageForWord: Image loaded via proxy successfully, dimensions:', img.width, 'x', img.height, 'data size:', uint8Array.length);
              return {
                data: uint8Array,
                width: img.width,
                height: img.height
              };
            } catch (error) {
              console.error('loadImageForWord: Error loading via proxy:', error);
              return null;
            }
          };

          // Para documentos Word, siempre usar el proxy para evitar problemas de canvas tainted
          return await loadImageViaProxy(normalizedUrl);
        };

        // Función para crear imagen compuesta con volante (igual que CreateSponsorshipForm)
        const createCompositeImage = async (platform: any): Promise<{ data: Uint8Array; width: number; height: number } | null> => {
          const logoUrl = sponsorship.media_assets?.logo_url || sponsorship.logo_url || '';
          const contestName = fullContest.name || 'NOMBRE DEL CONCURSO';
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) return null;

            // Cargar imagen de fondo
            const imageUrl = platform.image || platform.image_url;
            if (!imageUrl) {
              throw new Error('No image URL provided');
            }

            // Normalizar URL si es necesario
            let normalizedImageUrl = imageUrl;
            if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
              normalizedImageUrl = getCompleteImagePath(imageUrl) || imageUrl;
            }

            // Usar loadImageForWord para manejar CORS automáticamente
            const imageData = await loadImageForWord(normalizedImageUrl);
            if (!imageData) {
              throw new Error(`Failed to load background image: ${normalizedImageUrl}`);
            }

            // Crear un elemento Image desde los datos cargados
            const blob = new Blob([imageData.data as any], { type: 'image/png' });
            const imageUrlFromBlob = URL.createObjectURL(blob);
            const bgImage = new window.Image();

            await new Promise((resolve, reject) => {
              const timeout = setTimeout(() => {
                URL.revokeObjectURL(imageUrlFromBlob);
                reject(new Error('Background image load timeout'));
              }, 30000);

              bgImage.onload = () => {
                clearTimeout(timeout);
                URL.revokeObjectURL(imageUrlFromBlob);
                resolve(null);
              };
              bgImage.onerror = (error) => {
                clearTimeout(timeout);
                URL.revokeObjectURL(imageUrlFromBlob);
                reject(new Error(`Failed to load background image from blob`));
              };
              bgImage.src = imageUrlFromBlob;
            });

            // Usar las dimensiones reales de los datos cargados
            bgImage.width = imageData.width;
            bgImage.height = imageData.height;

            // Usar logo_url del platform si existe, sino del sponsorship
            const platformLogoUrl = platform.logo_url || logoUrl;

            // Calcular el tamaño final del canvas según general_width/general_height si están definidos
            let finalCanvasWidth = bgImage.width;
            let finalCanvasHeight = bgImage.height;
            let scaleX = 1;
            let scaleY = 1;

            // Aplicar tamaño general si está definido
            if (platform.general_width && platform.general_height) {
              finalCanvasWidth = platform.general_width;
              finalCanvasHeight = platform.general_height;
            } else if (platform.general_width) {
              scaleX = platform.general_width / bgImage.width;
              finalCanvasWidth = platform.general_width;
              finalCanvasHeight = bgImage.height * scaleX;
            } else if (platform.general_height) {
              scaleY = platform.general_height / bgImage.height;
              finalCanvasWidth = bgImage.width * scaleY;
              finalCanvasHeight = platform.general_height;
            } else {
              // Limitar el tamaño máximo del canvas para reducir el tamaño del archivo (reducido a 600px para menor peso)
              const maxCanvasDimension = 600;
              if (finalCanvasWidth > maxCanvasDimension || finalCanvasHeight > maxCanvasDimension) {
                const scale = Math.min(maxCanvasDimension / finalCanvasWidth, maxCanvasDimension / finalCanvasHeight);
                finalCanvasWidth = Math.round(finalCanvasWidth * scale);
                finalCanvasHeight = Math.round(finalCanvasHeight * scale);
              }
            }

            // Mantener la resolución original pero reducir la calidad de compresión
            let canvasWidth = finalCanvasWidth;
            let canvasHeight = finalCanvasHeight;

            canvas.width = canvasWidth;
            canvas.height = canvasHeight;

            // Rellenar el canvas con fondo blanco
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, canvasWidth, canvasHeight);

            // Dibujar la imagen de fondo escalada a la resolución reducida
            ctx.drawImage(bgImage, 0, 0, canvasWidth, canvasHeight);

            // Calcular el tamaño del volante basado en el tamaño del canvas (que ya tiene resolución reducida)
            // Usar el mismo enfoque que en CreateSponsorshipForm
            const previewMaxWidth = 800;
            const previewDisplayWidth = Math.min(canvasWidth, previewMaxWidth);
            const previewDisplayHeight = (canvasHeight / canvasWidth) * previewDisplayWidth;

            let flyerWidth: number;
            let flyerHeight: number;
            let tallImageScaleFactor = 1.0;

            if (platform.Fix_width && platform.Fix_height) {
              flyerWidth = platform.Fix_width;
              flyerHeight = platform.Fix_height;

              // Si se aplicó general_size, escalar Fix_width y Fix_height proporcionalmente
              if (platform.general_width || platform.general_height) {
                if (platform.general_width && platform.general_height) {
                  scaleX = canvasWidth / bgImage.width;
                  scaleY = canvasHeight / bgImage.height;
                } else if (platform.general_width) {
                  scaleX = canvasWidth / bgImage.width;
                  scaleY = scaleX;
                } else if (platform.general_height) {
                  scaleY = canvasHeight / bgImage.height;
                  scaleX = scaleY;
                }
                flyerWidth = flyerWidth * scaleX;
                flyerHeight = flyerHeight * scaleY;
              } else {
                // Si no hay general_size pero se redujo la resolución, aplicar el ratio de reducción
                const scaleRatioX = canvasWidth / bgImage.width;
                const scaleRatioY = canvasHeight / bgImage.height;
                flyerWidth = flyerWidth * scaleRatioX;
                flyerHeight = flyerHeight * scaleRatioY;
              }
            } else {
              // Calcular el tamaño del volante basado en porcentajes del canvas (que ya tiene resolución reducida)
              const flyerWidthPercent = ((platform.width || 200) / previewDisplayWidth) * 100;
              const flyerHeightPercent = ((platform.height || 150) / previewDisplayHeight) * 100;

              // Aplicar esos porcentajes al tamaño del canvas (que ya tiene resolución reducida)
              flyerWidth = (flyerWidthPercent / 100) * canvasWidth;
              flyerHeight = (flyerHeightPercent / 100) * canvasHeight;

              // Aplicar el factor de escala para imágenes altas (solo si no se aplicó general_size)
              if (!platform.general_width && !platform.general_height) {
                const maxDimension = Math.max(canvasWidth, canvasHeight);
                const minDimension = Math.min(canvasWidth, canvasHeight);
                const differencePercent = (minDimension / maxDimension) * 100;

                if (canvasHeight > canvasWidth && differencePercent < 100) {
                  if (differencePercent <= 40.18) {
                    tallImageScaleFactor = 0.4;
                  } else if (differencePercent <= 50) {
                    tallImageScaleFactor = 0.4 + ((differencePercent - 40.18) / (50 - 40.18)) * (0.65 - 0.5);
                  } else {
                    tallImageScaleFactor = 0.677 + ((differencePercent - 50) / 50) * 0.35;
                  }
                  flyerWidth = flyerWidth * tallImageScaleFactor;
                  flyerHeight = flyerHeight * tallImageScaleFactor;
                }
              }
            }

            // Aplicar el escalado a las posiciones del volante (usar canvasWidth/Height)
            const flyerX = ((platform.x || 50) / 100) * canvasWidth;
            const flyerY = ((platform.y || 50) / 100) * canvasHeight;

            // Dibujar fondo del volante
            ctx.fillStyle = '#f5f5dc';
            ctx.strokeStyle = '#ff6b35';
            ctx.lineWidth = 2;
            const radius = 8;
            ctx.beginPath();
            ctx.moveTo(flyerX + radius, flyerY);
            ctx.lineTo(flyerX + flyerWidth - radius, flyerY);
            ctx.quadraticCurveTo(flyerX + flyerWidth, flyerY, flyerX + flyerWidth, flyerY + radius);
            ctx.lineTo(flyerX + flyerWidth, flyerY + flyerHeight - radius);
            ctx.quadraticCurveTo(flyerX + flyerWidth, flyerY + flyerHeight, flyerX + flyerWidth - radius, flyerY + flyerHeight);
            ctx.lineTo(flyerX + radius, flyerY + flyerHeight);
            ctx.quadraticCurveTo(flyerX, flyerY + flyerHeight, flyerX, flyerY + flyerHeight - radius);
            ctx.lineTo(flyerX, flyerY + radius);
            ctx.quadraticCurveTo(flyerX, flyerY, flyerX + radius, flyerY);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();

            // Cargar y dibujar logo
            let logoLoaded = false;
            let logoBottom = flyerY + (platform.distancia_x || 60);
            if (platformLogoUrl) {
              try {
                // Normalizar URL del logo si es necesario
                let normalizedLogoUrl = platformLogoUrl;
                if (!platformLogoUrl.startsWith('http://') && !platformLogoUrl.startsWith('https://')) {
                  normalizedLogoUrl = getCompleteImagePath(platformLogoUrl) || platformLogoUrl;
                }

                // Usar loadImageForWord para cargar el logo a través del proxy (maneja CORS)
                const logoData = await loadImageForWord(normalizedLogoUrl);
                if (!logoData) {
                  throw new Error(`Failed to load logo image: ${normalizedLogoUrl}`);
                }

                // Crear un elemento Image desde los datos cargados
                const logoBlob = new Blob([logoData.data as any], { type: 'image/png' });
                const logoUrlFromBlob = URL.createObjectURL(logoBlob);
                const logoImage = new window.Image();

                await new Promise((resolve, reject) => {
                  const timeout = setTimeout(() => {
                    URL.revokeObjectURL(logoUrlFromBlob);
                    reject(new Error('Logo load timeout'));
                  }, 10000); // 10 segundos timeout

                  logoImage.onload = () => {
                    clearTimeout(timeout);
                    URL.revokeObjectURL(logoUrlFromBlob);
                    resolve(logoImage);
                  };
                  logoImage.onerror = (error) => {
                    clearTimeout(timeout);
                    URL.revokeObjectURL(logoUrlFromBlob);
                    reject(error);
                  };
                  logoImage.src = logoUrlFromBlob;
                });

                // Usar las dimensiones reales de los datos cargados
                logoImage.width = logoData.width;
                logoImage.height = logoData.height;

                // Calcular dimensiones máximas basadas en porcentajes (igual que CreateSponsorshipForm)
                const maxLogoWidth = (platform.logo_width / 100) * flyerWidth;
                const maxLogoHeight = (platform.logo_height / 100) * flyerHeight;

                // Calcular proporción de la imagen original
                const imageAspectRatio = logoImage.width / logoImage.height;
                const maxAspectRatio = maxLogoWidth / maxLogoHeight;

                // Calcular dimensiones finales manteniendo la proporción
                let logoWidth, logoHeight;
                if (imageAspectRatio > maxAspectRatio) {
                  // La imagen es más ancha que el espacio disponible
                  logoWidth = maxLogoWidth;
                  logoHeight = maxLogoWidth / imageAspectRatio;
                } else {
                  // La imagen es más alta que el espacio disponible
                  logoHeight = maxLogoHeight;
                  logoWidth = maxLogoHeight * imageAspectRatio;
                }

                const logoX = flyerX + (flyerWidth - logoWidth) / 2;
                const logoY = flyerY + (platform.distancia_x || 60); // Espacio desde la parte superior

                // Aplicar el escalado al tamaño de fuente si se aplicó general_size
                // Usar canvasWidth/Height en lugar de finalCanvasWidth/Height para mantener proporción con la resolución reducida
                let fontSizeScale = tallImageScaleFactor;
                if (platform.general_width || platform.general_height) {
                  if (platform.general_width && platform.general_height) {
                    fontSizeScale = Math.min(canvasWidth / bgImage.width, canvasHeight / bgImage.height);
                  } else if (platform.general_width) {
                    fontSizeScale = canvasWidth / bgImage.width;
                  } else if (platform.general_height) {
                    fontSizeScale = canvasHeight / bgImage.height;
                  }
                } else {
                  // Si no hay general_size, aplicar el ratio de reducción de resolución
                  fontSizeScale = Math.min(canvasWidth / bgImage.width, canvasHeight / bgImage.height) * tallImageScaleFactor;
                }

                // Dibujar el logo sin recorte circular (mostrar completo)
                ctx.drawImage(logoImage, logoX, logoY, logoWidth, logoHeight);

                logoBottom = logoY + logoHeight;
                logoLoaded = true;
                // Usar el nombre real del concurso primero, luego contest_text si no existe
                const contestNameText = (contestName || platform.contest_text || 'NOMBRE DEL CONCURSO').toUpperCase();
                const fontSize = (platform.Text_size || 0.9) * 16 * fontSizeScale;
                ctx.fillStyle = '#7c3aed';
                ctx.font = `bold ${fontSize}px "DejaVu Sans", "Liberation Sans", Arial, Helvetica, sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'top';
                // Espacio entre logo y primer texto
                const textMargin = Math.max(40, 40 * fontSizeScale);
                let contestY = logoBottom + textMargin;

                const drawMultilineText = (text: string, x: number, y: number, maxWidth: number, lineHeight: number) => {
                  const words = text.split(' ');
                  const lines: string[] = [];
                  let currentLine = words[0];

                  for (let i = 1; i < words.length; i++) {
                    const word = words[i];
                    const testLine = currentLine + ' ' + word;
                    const metrics = ctx.measureText(testLine);
                    if (metrics.width > maxWidth && currentLine.length > 0) {
                      lines.push(currentLine);
                      currentLine = word;
                    } else {
                      currentLine = testLine;
                    }
                  }
                  lines.push(currentLine);

                  lines.forEach((line, index) => {
                    ctx.fillText(line, x, y + (index * lineHeight));
                  });

                  return lines.length;
                };

                const textMaxWidth = flyerWidth * 0.9;
                const lineHeight = fontSize * 1.2;
                const lineCount = drawMultilineText(contestNameText, flyerX + flyerWidth / 2, contestY, textMaxWidth, lineHeight);
                contestY += (lineCount - 1) * lineHeight;

                const customText = platform.custom_text || 'your ad goes here';
                const customTextY = contestY + fontSize * 1.8;
                ctx.fillText(customText.toUpperCase(), flyerX + flyerWidth / 2, customTextY);
              } catch (logoError) {
                // Silenciosamente ignorar el error del logo y continuar sin él
                console.warn('Logo no se pudo cargar, continuando sin logo:', platformLogoUrl);
                logoLoaded = false;
                // Ajustar la posición inicial del texto si no hay logo
                logoBottom = flyerY + (platform.distancia_x || 60);
              }
            }

            // Dibujar texto del concurso si no hay logo o el logo no se cargó
            if (!logoLoaded || !platformLogoUrl) {
              // Si no hay logo, posicionar el texto desde arriba
              const contestNameText = (contestName || platform.contest_text || 'NOMBRE DEL CONCURSO').toUpperCase();
              // Aplicar el escalado al tamaño de fuente si se aplicó general_size
              // Usar canvasWidth/Height en lugar de finalCanvasWidth/Height para mantener proporción con la resolución reducida
              let fontSizeScale = tallImageScaleFactor;
              if (platform.general_width || platform.general_height) {
                if (platform.general_width && platform.general_height) {
                  fontSizeScale = Math.min(canvasWidth / bgImage.width, canvasHeight / bgImage.height);
                } else if (platform.general_width) {
                  fontSizeScale = canvasWidth / bgImage.width;
                } else if (platform.general_height) {
                  fontSizeScale = canvasHeight / bgImage.height;
                }
              } else {
                // Si no hay general_size, aplicar el ratio de reducción de resolución
                fontSizeScale = Math.min(canvasWidth / bgImage.width, canvasHeight / bgImage.height) * tallImageScaleFactor;
              }

              const fontSize = (platform.Text_size || 0.9) * 16 * fontSizeScale;
              ctx.fillStyle = '#7c3aed';
              ctx.font = `bold ${fontSize}px "DejaVu Sans", "Liberation Sans", Arial, Helvetica, sans-serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'top';
              const contestY = flyerY + 20;
              ctx.fillText(contestNameText, flyerX + flyerWidth / 2, contestY);

              const customText = platform.custom_text || 'your ad goes here';
              const customTextY = contestY + fontSize * 1.2;
              ctx.fillText(customText.toUpperCase(), flyerX + flyerWidth / 2, customTextY);
            }

            // Convertir canvas a Uint8Array
            return new Promise((resolve) => {
              canvas.toBlob((blob) => {
                if (!blob) {
                  resolve(null);
                  return;
                }
                blob.arrayBuffer().then((buffer) => {
                  resolve({
                    data: new Uint8Array(buffer),
                    width: canvas.width,
                    height: canvas.height
                  });
                });
              }, 'image/jpeg', 0.15); // Usar JPEG con calidad 0.15 (15%) para reducir significativamente el tamaño del archivo
            });
          } catch (error) {
            console.error('Error creating composite image:', error);
            return null;
          }
        };

        // Crear secciones para cada plataforma (cada una en su propia página) - igual que downloadAllPlatformsWord pero con calidad reducida
        const sections = await Promise.all(orderedPlatforms.map(async (platform: any, index: number) => {
          const children: any[] = [];

          // Título de la plataforma
          const platformName = platform.name || platform.platform_type || 'Platform';
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: platformName,
                  size: 36,
                  bold: true
                })
              ],
              alignment: AlignmentType.CENTER,
              spacing: { before: index === 0 ? 400 : 800, after: 400, line: 240 }
            })
          );

          // Obtener imagen compuesta usando el endpoint del servidor (igual que downloadAllPlatformsWord pero con calidad reducida)
          if (platform.image || platform.image_url) {
            try {
              // Preparar datos de la plataforma
              let platformData = { ...platform };

              // Si es voletaje, buscar los datos de la plataforma base
              if (platform.type === 'voletaje' || platform.platform_type === 'voletaje') {
                const allPlatformsResponse = await fetch(`${environment.apiBaseUrl}/platforms`, {
                  headers: getApiHeadersForGet()
                });
                if (allPlatformsResponse.ok) {
                  const allPlatforms = await allPlatformsResponse.json();
                  const voletajeBase = allPlatforms.find((bp: any) => bp.platform_type === 'voletaje');
                  if (voletajeBase) {
                    platformData = {
                      ...voletajeBase,
                      name: platformName,
                      image: voletajeBase.image_url || voletajeBase.image,
                      logo_url: voletajeBase.logo_url || sponsorship.media_assets?.logo_url || sponsorship.logo_url,
                      custom_text: voletajeBase.custom_text || 'your ad goes here'
                    };
                  }
                }
              }

              // Obtener URLs normalizadas
              const imageUrl = platformData.image || platformData.image_url;
              let normalizedImageUrl = imageUrl;
              if (imageUrl && !imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
                normalizedImageUrl = getCompleteImagePath(imageUrl) || imageUrl;
              }

              const logoUrl = platformData.logo_url || sponsorship.media_assets?.logo_url || sponsorship.logo_url;
              let normalizedLogoUrl = logoUrl;
              if (logoUrl && logoUrl.trim() !== '' && !logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
                normalizedLogoUrl = getCompleteImagePath(logoUrl) || logoUrl;
              }

              // Log para debugging
              if (logoUrl && logoUrl.trim() !== '') {
                console.log(`[Word Generation] Logo URL para plataforma ${platformName}:`, {
                  original: logoUrl,
                  normalized: normalizedLogoUrl,
                  fromPlatform: !!platformData.logo_url,
                  fromSponsorshipMediaAssets: !!sponsorship.media_assets?.logo_url,
                  fromSponsorshipDirect: !!sponsorship.logo_url
                });
              } else {
                console.warn(`[Word Generation] No se encontró logoUrl para plataforma ${platformName}`, {
                  platformDataLogoUrl: platformData.logo_url,
                  sponsorshipMediaAssetsLogoUrl: sponsorship.media_assets?.logo_url,
                  sponsorshipLogoUrl: sponsorship.logo_url,
                  sponsorshipKeys: Object.keys(sponsorship || {})
                });
              }

              const customText = platformData.custom_text || 'your ad goes here';
              const contestName = fullContest.name || 'CONTEST NAME';

              // Llamar al endpoint del servidor para obtener la imagen compuesta con calidad reducida
              const response = await fetch(`${environment.apiBaseUrl}/generar-preview-word`, {
                method: 'POST',
                headers: getApiHeaders(),
                body: JSON.stringify({
                  imageUrl: normalizedImageUrl,
                  logoUrl: (normalizedLogoUrl && normalizedLogoUrl.trim() !== '') ? normalizedLogoUrl : null,
                  customText: customText || null,
                  contestName: contestName || null,
                  platformName: platformName,
                  reduceQuality: true // Reducir calidad para reducir el peso del archivo
                }),
              });

              if (!response.ok) {
                let errorMessage = 'Error desconocido';
                let errorDetails = '';
                try {
                  const errorBody = await response.json();
                  errorMessage = errorBody.error || errorBody.message || `Error ${response.status}`;
                  errorDetails = errorBody.details || '';
                } catch (e) {
                  // Si no se puede parsear como JSON, usar el status text
                  errorMessage = response.status === 404
                    ? `Ruta de API no encontrada (404): /api/generar-preview-word`
                    : `Error ${response.status}: ${response.statusText}`;
                }

                console.error(`Error generando imagen para plataforma ${platformName}:`, errorMessage, errorDetails ? `- ${errorDetails}` : '');
                children.push(
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: `Error al cargar la imagen: ${platformName}${errorDetails ? ` - ${errorDetails}` : ''}`,
                        size: 24,
                        color: 'FF0000'
                      })
                    ],
                    spacing: { after: 200 }
                  })
                );
                // Retornar la sección con el error para mantener la estructura del documento
                return {
                  properties: {
                    type: SectionType.NEXT_PAGE,
                    page: {
                      size: {
                        width: 15840,
                        height: 20160,
                      },
                    },
                  },
                  children: children
                };
              } else {
                // Obtener la imagen como blob
                const imageBlob = await response.blob();
                const imageArrayBuffer = await imageBlob.arrayBuffer();
                const imageData = new Uint8Array(imageArrayBuffer);

                // Obtener dimensiones de la imagen
                const img = new window.Image();
                const imageUrlFromBlob = URL.createObjectURL(imageBlob);
                await new Promise((resolve, reject) => {
                  img.onload = () => {
                    URL.revokeObjectURL(imageUrlFromBlob);
                    resolve(null);
                  };
                  img.onerror = () => {
                    URL.revokeObjectURL(imageUrlFromBlob);
                    reject(new Error('Failed to load image'));
                  };
                  img.src = imageUrlFromBlob;
                });

                const imageWidth = img.width;
                const imageHeight = img.height;

                // Agregar la imagen al documento (usar 'jpeg' ya que el servidor devuelve JPEG cuando reduceQuality es true)
                children.push(
                  new Paragraph({
                    children: [
                      new ImageRun({
                        data: imageData,
                        transformation: {
                          width: imageWidth,
                          height: imageHeight,
                        },
                        type: 'jpeg',
                      } as any),
                    ],
                    alignment: AlignmentType.CENTER,
                    spacing: { before: 0, after: 300 }
                  })
                );
              }
            } catch (imgError) {
              console.error('Error loading image:', imgError);
              children.push(
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `Error al cargar la imagen: ${platform.image || platform.image_url}`,
                      size: 24,
                      color: 'FF0000'
                    })
                  ],
                  spacing: { after: 200 }
                })
              );
            }
          } else {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'No hay imagen disponible',
                    size: 24,
                    color: '666666'
                  })
                ],
                spacing: { after: 200 }
              })
            );
          }

          return {
            properties: {
              type: SectionType.NEXT_PAGE,
              page: {
                size: {
                  width: 15840,
                  height: 20160,
                },
                margin: {
                  top: 720,
                  right: 720,
                  bottom: 720,
                  left: 1080,
                },
              },
            },
            children: children
          };
        }));

        // Cargar logo de Noah's Ark (PNG)
        let noahsArkLogoData: Uint8Array | null = null;
        let noahsArkLogoWidth = 375;
        let noahsArkLogoHeight = 375;

        try {
          const noahsArkLogoUrl = '/Noah.logo.color5.png';
          const noahsArkLogoResponse = await fetch(noahsArkLogoUrl);
          if (noahsArkLogoResponse.ok) {
            const arrayBuffer = await noahsArkLogoResponse.arrayBuffer();
            noahsArkLogoData = new Uint8Array(arrayBuffer);

            const img = new window.Image();
            await new Promise<void>((resolve, reject) => {
              img.onload = () => {
                const maxDimensionPx = 12.5;
                let finalWidthPx = img.width;
                let finalHeightPx = img.height;

                if (finalWidthPx > maxDimensionPx || finalHeightPx > maxDimensionPx) {
                  const scale = Math.min(maxDimensionPx / finalWidthPx, maxDimensionPx / finalHeightPx);
                  finalWidthPx = finalWidthPx * scale;
                  finalHeightPx = finalHeightPx * scale;
                }

                noahsArkLogoWidth = Math.round(finalWidthPx * 15);
                noahsArkLogoHeight = Math.round(finalHeightPx * 15);
                resolve();
              };
              img.onerror = reject;
              img.src = noahsArkLogoUrl;
            });
          }
        } catch (noahsArkLogoError) {
          console.error('Error loading Noah\'s Ark logo:', noahsArkLogoError);
        }

        // Cargar logo de la empresa
        let logoImageData: Uint8Array | null = null;
        let logoWidth = 2000;
        let logoHeight = 2000;

        if (sponsorship.media_assets?.logo_url) {
          try {
            const logoResponse = await fetch(sponsorship.media_assets.logo_url);
            const logoArrayBuffer = await logoResponse.arrayBuffer();
            logoImageData = new Uint8Array(logoArrayBuffer);

            const img = new window.Image();
            const blob = new Blob([logoArrayBuffer], { type: 'image/png' });
            const imgUrl = URL.createObjectURL(blob);

            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
              img.src = imgUrl;
            });

            const maxHeightPx = 18.75;
            let finalWidthPx = img.width;
            let finalHeightPx = img.height;

            if (finalHeightPx > maxHeightPx) {
              const scale = maxHeightPx / finalHeightPx;
              finalWidthPx = finalWidthPx * scale;
              finalHeightPx = maxHeightPx;
            }

            logoWidth = Math.round(finalWidthPx * 15);
            logoHeight = Math.round(finalHeightPx * 15);

            const aspectRatio = img.width / img.height;
            if (aspectRatio >= 2) {
              logoWidth = Math.round(logoWidth * 0.5);
              logoHeight = Math.round(logoHeight * 0.5);
            }

            URL.revokeObjectURL(imgUrl);
          } catch (logoError) {
            console.error('Error loading logo:', logoError);
          }
        }

        // Formatear fechas
        const formatDate = (dateString: string) => {
          if (!dateString) return 'N/A';
          try {
            const date = new Date(dateString);
            return date.toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
          } catch {
            return dateString;
          }
        };

        // Primera sección: Logo, título, patrocinador, logo empresa, slogan
        const firstSectionChildren: any[] = [
          // Logo de Noah's Ark
          ...(noahsArkLogoData && noahsArkLogoData.length > 0 ? [
            new Paragraph({
              children: [
                new ImageRun({
                  data: noahsArkLogoData,
                  transformation: {
                    width: noahsArkLogoWidth,
                    height: noahsArkLogoHeight,
                  },
                  type: 'png',
                } as any),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 600 }
            })
          ] : []),

          // Título principal
          new Paragraph({
            children: [
              new TextRun({
                text: `Contest Information - ${fullContest.name || 'Contest'}`,
                size: 44,
                bold: true
              })
            ],
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: { after: 600 }
          }),

          // Nombre del patrocinador
          new Paragraph({
            children: [
              new TextRun({
                text: 'Sponsorship:',
                size: 32,
                bold: true
              })
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 100 }
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: sponsorship.sponsor_name || 'N/A',
                size: 32
              })
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 300 }
          }),

          // Logo de la empresa
          ...(logoImageData ? [
            new Paragraph({
              children: [
                new ImageRun({
                  data: logoImageData,
                  transformation: {
                    width: logoWidth,
                    height: logoHeight,
                  },
                  type: 'png',
                } as any),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 300 }
            })
          ] : []),

          // Slogan
          ...(sponsorship.media_assets?.slogan ? [
            new Paragraph({
              children: [
                new TextRun({
                  text: sponsorship.media_assets.slogan,
                  size: 28,
                  italics: true
                })
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 }
            })
          ] : [])
        ];

        // Función helper para generar contenido de datos del concurso (igual que CreateSponsorshipForm)
        const generateContestDataSection = (contest: any, economics: any, sponsorship: any, membershipBreakdown: any[]) => {
          const totalRevenue = Number(economics?.total_revenue) || 0;
          const totalCosts = Number(economics?.total_costs) || 0;
          const economicsObj = economics || {};

          return [
            // Título
            new Paragraph({
              children: [
                new TextRun({
                  text: `Economic Dashboard for the Contest ${contest?.name || 'Contest'}`,
                  size: 44,
                  bold: true
                })
              ],
              heading: HeadingLevel.HEADING_1,
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 }
            }),

            // Información General del Concurso
            new Paragraph({
              children: [
                new TextRun({
                  text: t('details_contest.ContestConfiguration'),
                  size: 36,
                  bold: true
                })
              ],
              heading: HeadingLevel.HEADING_2,
              alignment: AlignmentType.CENTER,
              spacing: { before: 400, after: 200 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.Status')}: `, bold: true, size: 28 }),
                new TextRun({ text: contest?.status || 'N/A', size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.Phase')}: `, bold: true, size: 28 }),
                new TextRun({ text: contest?.phase?.replace(/_/g, ' ') || 'N/A', size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.AccessLevel')}: `, bold: true, size: 28 }),
                new TextRun({ text: contest?.subscribers_only ? 'Subscribers Only' : contest?.members_only ? 'Members Only' : 'Public', size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.PhotoRewardsMode')}: `, bold: true, size: 28 }),
                new TextRun({ text: contest?.photo_rewards_mode || 'N/A', size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.PrizeType')}: `, bold: true, size: 28 }),
                new TextRun({ text: contest?.prize_type || 'N/A', size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Couponpasses: ', bold: true, size: 28 }),
                new TextRun({ text: String(contest?.coupon_quantity || contest?.max_coupass || 0), size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Pases Normales: ', bold: true, size: 28 }),
                new TextRun({ text: String(contest?.max_participants || contest?.max_pass || 0), size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),

            // Línea divisoria
            new Paragraph({
              border: {
                bottom: {
                  color: '000000',
                  size: 6,
                  style: BorderStyle.SINGLE
                }
              },
              spacing: { after: 300, before: 200 }
            }),

            // Rendimiento Económico
            new Paragraph({
              children: [
                new TextRun({
                  text: t('details_contest.economic_performance'),
                  size: 36,
                  bold: true
                })
              ],
              heading: HeadingLevel.HEADING_2,
              alignment: AlignmentType.CENTER,
              spacing: { before: 400, after: 200 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.total_revenue')}: `, bold: true, size: 28 }),
                new TextRun({ text: `$${totalRevenue.toFixed(2)}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `  ${t('details_contest.participant')}: `, bold: true, size: 28 }),
                new TextRun({ text: `$${(Number(economicsObj.participant_revenue) || 0).toFixed(2)}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `  ${t('details_contest.Sponsor')}: `, bold: true, size: 28 }),
                new TextRun({ text: `$${(Number(contest?.total_sponsor_cost) || 0).toFixed(2)}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.total_costs')}: `, bold: true, size: 28 }),
                new TextRun({ text: `$${totalCosts.toFixed(2)}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `  ${t('details_contest.Main_Prize')}: `, bold: true, size: 28 }),
                new TextRun({ text: `$${(Number(economicsObj.prize_costs) || 0).toFixed(2)}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `  ${t('details_contest.Secondary_Prize')}: `, bold: true, size: 28 }),
                new TextRun({ text: `$${(Number(economicsObj.ops_costs) || 0).toFixed(2)}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.net_profit')}: `, bold: true, size: 28 }),
                new TextRun({ text: `$${(Number(economicsObj.noahs_ark_profit) || 0).toFixed(2)}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `  ${t('details_contest.Margin')}: `, bold: true, size: 28 }),
                new TextRun({ text: `${(Number(economicsObj.profit_margin) || 0).toFixed(1)}%`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `ROI: `, bold: true, size: 28 }),
                new TextRun({ text: `${(Number(economicsObj.total_roi) || 0).toFixed(1)}%`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `  ${t('details_contest.Per_Participant')}: `, bold: true, size: 28 }),
                new TextRun({ text: `$${(Number(economicsObj.revenue_per_participant) || 0).toFixed(2)}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),

            // Línea divisoria
            new Paragraph({
              border: {
                bottom: {
                  color: '000000',
                  size: 6,
                  style: BorderStyle.SINGLE
                }
              },
              spacing: { after: 300, before: 200 }
            }),

            // Análisis de Ingresos
            new Paragraph({
              children: [
                new TextRun({
                  text: t('details_contest.RevenueAnalysis'),
                  size: 36,
                  bold: true
                })
              ],
              heading: HeadingLevel.HEADING_2,
              alignment: AlignmentType.CENTER,
              spacing: { before: 400, after: 200 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.FullPriceRevenue')}: `, bold: true, size: 28 }),
                new TextRun({ text: `$${(economicsObj.full_price_revenue || 0).toFixed(2)}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.DiscountedRevenue')}: `, bold: true, size: 28 }),
                new TextRun({ text: `$${(economicsObj.discounted_revenue || 0).toFixed(2)}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.VoucherValueUsed')}: `, bold: true, size: 28 }),
                new TextRun({ text: `$${(economicsObj.voucher_redemption_value || 0).toFixed(2)}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.TotalDiscountsGiven')}: `, bold: true, size: 28 }),
                new TextRun({ text: `$${(economicsObj.total_discount_given || 0).toFixed(2)}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.RevenueEfficiency')}: `, bold: true, size: 28 }),
                new TextRun({ text: `${(economicsObj.revenue_efficiency_percentage || 0).toFixed(1)}%`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.PotentialRevenue')}: `, bold: true, size: 28 }),
                new TextRun({ text: `$${(economicsObj.potential_revenue || 0).toFixed(2)}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),

            // Línea divisoria
            new Paragraph({
              border: {
                bottom: {
                  color: '000000',
                  size: 6,
                  style: BorderStyle.SINGLE
                }
              },
              spacing: { after: 300, before: 200 }
            }),

            // Premios y Patrocinio
            new Paragraph({
              children: [
                new TextRun({
                  text: t('details_contest.PrizeandSponsorship'),
                  size: 36,
                  bold: true
                })
              ],
              heading: HeadingLevel.HEADING_2,
              alignment: AlignmentType.CENTER,
              spacing: { before: 400, after: 200 }
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: t('details_contest.PrizePoolBreakdown'),
                  size: 32,
                  bold: true
                })
              ],
              heading: HeadingLevel.HEADING_3,
              alignment: AlignmentType.CENTER,
              spacing: { before: 200, after: 100 }
            }),
            ...(contest?.prize_first_place > 0 ? [new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.Frist_Place')}: `, bold: true, size: 28 }),
                new TextRun({ text: `$${contest.prize_first_place.toFixed(2)}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            })] : []),
            ...(contest?.prize_second_place > 0 ? [new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.Second_Place')}: `, bold: true, size: 28 }),
                new TextRun({ text: `$${contest.prize_second_place.toFixed(2)}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            })] : []),
            ...(contest?.prize_third_place > 0 ? [new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.Third_Place')}: `, bold: true, size: 28 }),
                new TextRun({ text: `$${contest.prize_third_place.toFixed(2)}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            })] : []),
            ...(contest?.prize_fourth_place > 0 ? [new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.Fourth_Place')}: `, bold: true, size: 28 }),
                new TextRun({ text: `$${contest.prize_fourth_place.toFixed(2)}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            })] : []),
            new Paragraph({
              children: [
                new TextRun({ text: 'Total: ', bold: true, size: 28 }),
                new TextRun({
                  text: `$${((Number(contest?.prize_first_place) || 0) +
                    (Number(contest?.prize_second_place) || 0) +
                    (Number(contest?.prize_third_place) || 0) +
                    (Number(contest?.prize_fourth_place) || 0) +
                    (Number(contest?.prize_fifth_place) || 0) +
                    (Number(contest?.prize_sixth_place) || 0) +
                    (Number(contest?.prize_seventh_place) || 0) +
                    (Number(contest?.prize_eighth_place) || 0)).toFixed(2)}`, size: 28
                })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.Secondary_Prize')}: `, bold: true, size: 28 }),
                new TextRun({
                  text: `$${(() => {
                    const dynamicPrizes = contest?.dynamic_prizes || {};
                    let secondaryTotal = 0;
                    if (typeof dynamicPrizes === 'object' && dynamicPrizes !== null) {
                      Object.values(dynamicPrizes).forEach((prize: any) => {
                        secondaryTotal += Number(prize) || 0;
                      });
                    }
                    return secondaryTotal.toFixed(2);
                  })()}`,
                  size: 28
                })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),

            // Detalles de Patrocinio
            ...((sponsorship || contest?.sponsor_name) ? [
              new Paragraph({
                children: [
                  new TextRun({
                    text: t('details_contest.Sponsorship_Details'),
                    size: 32,
                    bold: true
                  })
                ],
                heading: HeadingLevel.HEADING_3,
                alignment: AlignmentType.CENTER,
                spacing: { before: 200, after: 100 }
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: `${t('details_contest.Sponsor')}: `, bold: true, size: 28 }),
                  new TextRun({ text: sponsorship?.sponsor_name || contest?.sponsor_name || 'Noah\'s Ark', size: 28 })
                ],
                alignment: AlignmentType.LEFT,
                indent: { left: 720 }
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: `${t('details_contest.type')}: `, bold: true, size: 28 }),
                  new TextRun({ text: sponsorship?.sponsorship_type?.replace(/_/g, ' ') || contest?.sponsorship_type?.replace(/_/g, ' ') || 'N/A', size: 28 })
                ],
                alignment: AlignmentType.LEFT,
                indent: { left: 720 }
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: `${t('details_contest.contribution')}: `, bold: true, size: 28 }),
                  new TextRun({ text: `$${(sponsorship?.total_sponsor_cost || contest?.total_sponsor_cost || 0).toFixed(2)}`, size: 28 })
                ],
                alignment: AlignmentType.LEFT,
                indent: { left: 720 }
              }),
              ...(sponsorship?.impressions > 0 ? [new Paragraph({
                children: [
                  new TextRun({ text: `${t('details_contest.impressions')}: `, bold: true, size: 28 }),
                  new TextRun({ text: sponsorship.impressions.toLocaleString(), size: 28 })
                ],
                alignment: AlignmentType.LEFT,
                indent: { left: 720 }
              })] : []),
              ...(sponsorship?.engagements > 0 ? [new Paragraph({
                children: [
                  new TextRun({ text: `${t('details_contest.engagements')}: `, bold: true, size: 28 }),
                  new TextRun({ text: sponsorship.engagements.toLocaleString(), size: 28 })
                ],
                alignment: AlignmentType.LEFT,
                indent: { left: 720 }
              })] : []),
              ...(sponsorship?.contact_name ? [new Paragraph({
                children: [
                  new TextRun({ text: 'Contact: ', bold: true, size: 28 }),
                  new TextRun({ text: sponsorship.contact_name, size: 28 })
                ],
                alignment: AlignmentType.LEFT,
                indent: { left: 720 }
              })] : []),
              ...(sponsorship?.contact_email ? [new Paragraph({
                children: [
                  new TextRun({ text: 'Email: ', bold: true, size: 28 }),
                  new TextRun({ text: sponsorship.contact_email, size: 28 })
                ],
                alignment: AlignmentType.LEFT,
                indent: { left: 720 }
              })] : [])
            ] : []),

            // Photo Rewards Split
            ...(contest?.photo_rewards_mode !== 'disabled' ? [
              new Paragraph({
                children: [
                  new TextRun({
                    text: t('details_contest.PhotoRewardsSplit'),
                    size: 32,
                    bold: true
                  })
                ],
                heading: HeadingLevel.HEADING_3,
                alignment: AlignmentType.CENTER,
                spacing: { before: 200, after: 100 }
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: `Photo Rewards User Percentage: `, bold: true, size: 28 }),
                  new TextRun({ text: `${contest?.photo_rewards_user_percentage || 0}%`, size: 28 })
                ],
                alignment: AlignmentType.LEFT,
                indent: { left: 720 }
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: `Photo Rewards Organizer Percentage: `, bold: true, size: 28 }),
                  new TextRun({ text: `${contest?.photo_rewards_organizer_percentage || 0}%`, size: 28 })
                ],
                alignment: AlignmentType.LEFT,
                indent: { left: 720 }
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: `Photo Rewards Noah's Ark Percentage: `, bold: true, size: 28 }),
                  new TextRun({ text: `${contest?.photo_rewards_noahs_ark_percentage || 0}%`, size: 28 })
                ],
                alignment: AlignmentType.LEFT,
                indent: { left: 720 }
              })
            ] : []),

            // Línea divisoria
            new Paragraph({
              border: {
                bottom: {
                  color: '000000',
                  size: 6,
                  style: BorderStyle.SINGLE
                }
              },
              spacing: { after: 300, before: 200 }
            }),

            // Cronograma
            new Paragraph({
              children: [
                new TextRun({
                  text: t('details_contest.ScheduleandTimeline'),
                  size: 36,
                  bold: true
                })
              ],
              heading: HeadingLevel.HEADING_2,
              alignment: AlignmentType.CENTER,
              spacing: { before: 400, after: 200 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `Submission Period: `, bold: true, size: 28 }),
                new TextRun({ text: `${formatDate(contest?.submission_start)} - ${formatDate(contest?.submission_deadline)}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            ...(contest?.voting_start ? [new Paragraph({
              children: [
                new TextRun({ text: `Voting Period: `, bold: true, size: 28 }),
                new TextRun({ text: `${formatDate(contest?.voting_start)} - ${formatDate(contest?.voting_end)}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            })] : []),
            new Paragraph({
              children: [
                new TextRun({ text: `Winner Announcement: `, bold: true, size: 28 }),
                new TextRun({ text: formatDate(contest?.winner_announcement), size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),

            // Línea divisoria
            new Paragraph({
              border: {
                bottom: {
                  color: '000000',
                  size: 6,
                  style: BorderStyle.SINGLE
                }
              },
              spacing: { after: 300, before: 200 }
            }),

            // Requisitos de Medios
            new Paragraph({
              children: [
                new TextRun({
                  text: t('details_contest.MediaRequirements'),
                  size: 36,
                  bold: true
                })
              ],
              heading: HeadingLevel.HEADING_2,
              alignment: AlignmentType.CENTER,
              spacing: { before: 400, after: 200 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.Allowed_Types')}: `, bold: true, size: 28 }),
                new TextRun({ text: contest?.allowed_media_types || 'both', size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.Max_Images')}: `, bold: true, size: 28 }),
                new TextRun({ text: String(contest?.max_images || 0), size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.Max_Videos')}: `, bold: true, size: 28 }),
                new TextRun({ text: String(contest?.max_videos || 0), size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.Imagesize_limit')}: `, bold: true, size: 28 }),
                new TextRun({ text: `${contest?.max_file_size || 0}MB`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.Videosize_limit')}: `, bold: true, size: 28 }),
                new TextRun({ text: `${contest?.max_file_size || 0}MB`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.Video_duration')}: `, bold: true, size: 28 }),
                new TextRun({ text: `${contest?.max_video_duration_seconds || 0}s`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),
            ...(contest?.media_requirements ? [new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.Media_Requirements')}: `, bold: true, size: 28 }),
                new TextRun({ text: contest.media_requirements, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            })] : []),

            // Línea divisoria
            new Paragraph({
              border: {
                bottom: {
                  color: '000000',
                  size: 6,
                  style: BorderStyle.SINGLE
                }
              },
              spacing: { after: 300, before: 200 }
            }),

            // Progreso de Participación
            new Paragraph({
              children: [
                new TextRun({
                  text: t('details_contest.ParticipationProgress'),
                  size: 36,
                  bold: true
                })
              ],
              heading: HeadingLevel.HEADING_2,
              alignment: AlignmentType.CENTER,
              spacing: { before: 400, after: 200 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${t('details_contest.Current_Progress')}: `, bold: true, size: 28 }),
                new TextRun({ text: `${economicsObj.total_pass_codes_used || 0} / ${contest?.max_participants || contest?.min_participants} ${t('details_contest.participants')}`, size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 }
            }),

            // Línea divisoria
            new Paragraph({
              border: {
                bottom: {
                  color: '000000',
                  size: 6,
                  style: BorderStyle.SINGLE
                }
              },
              spacing: { after: 300, before: 200 }
            }),

            // Membership Breakdown
            new Paragraph({
              children: [
                new TextRun({
                  text: t('details_contest.MembershipAndPaymentBreakdown'),
                  size: 36,
                  bold: true
                })
              ],
              heading: HeadingLevel.HEADING_2,
              alignment: AlignmentType.CENTER,
              spacing: { before: 400, after: 200 }
            }),
            ...(membershipBreakdown.map((member: any) => [
              new Paragraph({
                children: [
                  new TextRun({ text: `${member.membership_level || member.discount_type}: `, bold: true, size: 28 }),
                  new TextRun({ text: `${member.participant_count || 0} participantes`, size: 28 })
                ],
                alignment: AlignmentType.LEFT,
                indent: { left: 720 }
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: `  Ingresos: `, bold: true, size: 28 }),
                  new TextRun({ text: `$${(member.total_revenue || 0).toFixed(2)}`, size: 28 })
                ],
                alignment: AlignmentType.LEFT,
                indent: { left: 720 }
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: `  Porcentaje: `, bold: true, size: 28 }),
                  new TextRun({ text: `${(member.percentage_of_total || 0).toFixed(1)}%`, size: 28 })
                ],
                alignment: AlignmentType.LEFT,
                indent: { left: 720 }
              })
            ]).flat())
          ];
        };

        // Función para generar tabla de contenido
        const generateTableOfContents = () => {
          return [
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Table of Contents',
                  size: 44,
                  bold: true
                })
              ],
              heading: HeadingLevel.HEADING_1,
              alignment: AlignmentType.CENTER,
              spacing: { after: 3000 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Basic Contest Data', size: 32, bold: true }),
                new TextRun({ text: ' ................................. ', size: 32 }),
                new TextRun({ text: '3', size: 32 })
              ],
              alignment: AlignmentType.LEFT,
              spacing: { after: 300 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `Economic Dashboard for the Contest ${fullContest.name || 'Contest'}`, size: 32, bold: true }),
                new TextRun({ text: ' ................................. ', size: 32 }),
                new TextRun({ text: '4', size: 32 })
              ],
              alignment: AlignmentType.LEFT,
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `  ${t('details_contest.ContestConfiguration')}`, size: 28 }),
                new TextRun({ text: ' ................................. ', size: 28 }),
                new TextRun({ text: '4', size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `  ${t('details_contest.economic_performance')}`, size: 28 }),
                new TextRun({ text: ' ................................. ', size: 28 }),
                new TextRun({ text: '4', size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `  ${t('details_contest.RevenueAnalysis')}`, size: 28 }),
                new TextRun({ text: ' ................................. ', size: 28 }),
                new TextRun({ text: '4', size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `  ${t('details_contest.PrizeandSponsorship')}`, size: 28 }),
                new TextRun({ text: ' ................................. ', size: 28 }),
                new TextRun({ text: '4', size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `  ${t('details_contest.ScheduleandTimeline')}`, size: 28 }),
                new TextRun({ text: ' ................................. ', size: 28 }),
                new TextRun({ text: '4', size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `  ${t('details_contest.MediaRequirements')}`, size: 28 }),
                new TextRun({ text: ' ................................. ', size: 28 }),
                new TextRun({ text: '4', size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `  ${t('details_contest.ParticipationProgress')}`, size: 28 }),
                new TextRun({ text: ' ................................. ', size: 28 }),
                new TextRun({ text: '4', size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `  ${t('details_contest.MembershipAndPaymentBreakdown')}`, size: 28 }),
                new TextRun({ text: ' ................................. ', size: 28 }),
                new TextRun({ text: '4', size: 28 })
              ],
              alignment: AlignmentType.LEFT,
              spacing: { after: 300 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Flyer Images', size: 32, bold: true }),
                new TextRun({ text: ' ................................. ', size: 32 }),
                new TextRun({ text: '5', size: 32 })
              ],
              alignment: AlignmentType.LEFT,
              spacing: { after: 200 }
            })
          ];
        };

        // Función para generar la tercera hoja con datos adicionales
        const generateAdditionalInfoSection = () => {
          return [
            new Paragraph({
              children: [],
              spacing: { before: 0, after: 3000 }
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Social Media:',
                  size: 32,
                  bold: true
                })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 },
              spacing: { after: 100 }
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: sponsorship.social_links || 'facebook.com instagram.com',
                  size: 32
                })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 },
              spacing: { after: 300 }
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Start Date:',
                  size: 32,
                  bold: true
                })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 },
              spacing: { after: 100 }
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: formatDate(sponsorship.schedule?.start || fullContest.submission_start),
                  size: 32
                })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 },
              spacing: { after: 300 }
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: 'End Date:',
                  size: 32,
                  bold: true
                })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 },
              spacing: { after: 100 }
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: formatDate(sponsorship.schedule?.end || fullContest.submission_deadline),
                  size: 32
                })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 },
              spacing: { after: 300 }
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Total Cost:',
                  size: 32,
                  bold: true
                })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 },
              spacing: { after: 100 }
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: `$${sponsorship.total_sponsor_cost?.toFixed(2) || '0.00'}`,
                  size: 32,
                  bold: true,
                  color: '2563EB'
                })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 },
              spacing: { after: 300 }
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Type of Sponsorship:',
                  size: 32,
                  bold: true
                })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 },
              spacing: { after: 100 }
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: t(`type_and_budget_info_sponsorship.${sponsorship.sponsorship_type}`) || sponsorship.sponsorship_type || 'N/A',
                  size: 32
                })
              ],
              alignment: AlignmentType.LEFT,
              indent: { left: 720 },
              spacing: { after: 300 }
            }),
            ...((sponsorship.reward_contribution && sponsorship.reward_contribution > 0) ? [
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'Reward Contribution:',
                    size: 32,
                    bold: true
                  })
                ],
                alignment: AlignmentType.LEFT,
                indent: { left: 720 },
                spacing: { after: 100 }
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `$${sponsorship.reward_contribution.toFixed(2)}`,
                    size: 32
                  })
                ],
                alignment: AlignmentType.LEFT,
                indent: { left: 720 },
                spacing: { after: 300 }
              })
            ] : []),
            ...((sponsorship.impressions && sponsorship.impressions > 0) ? [
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'Impressions Meta:',
                    size: 32,
                    bold: true
                  })
                ],
                alignment: AlignmentType.LEFT,
                indent: { left: 720 },
                spacing: { after: 100 }
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: sponsorship.impressions.toLocaleString(),
                    size: 32
                  })
                ],
                alignment: AlignmentType.LEFT,
                indent: { left: 720 },
                spacing: { after: 300 }
              })
            ] : []),
            ...((sponsorship.engagements && sponsorship.engagements > 0) ? [
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'Engagements Meta:',
                    size: 32,
                    bold: true
                  })
                ],
                alignment: AlignmentType.LEFT,
                indent: { left: 720 },
                spacing: { after: 100 }
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: sponsorship.engagements.toLocaleString(),
                    size: 32
                  })
                ],
                alignment: AlignmentType.LEFT,
                indent: { left: 720 },
                spacing: { after: 400 }
              })
            ] : [])
          ];
        };

        // Crear el documento Word (misma estructura completa que el botón Download Contest Information)
        const doc = new Document({
          sections: [
            // Primera sección con el título general e información del patrocinador
            {
              properties: {
                page: {
                  size: {
                    width: 15840,
                    height: 15000,
                  },
                  margins: {
                    top: 720,
                    right: 720,
                    bottom: 720,
                    left: 360,
                  },
                },
              },
              children: firstSectionChildren
            },
            // Segunda sección: Tabla de contenido
            {
              properties: {
                type: SectionType.NEXT_PAGE,
                page: {
                  size: {
                    width: 15840,
                    height: 20160,
                  },
                  margins: {
                    top: 720,
                    right: 720,
                    bottom: 720,
                    left: 360,
                  },
                },
              },
              children: generateTableOfContents()
            },
            // Tercera sección: Información adicional
            {
              properties: {
                type: SectionType.NEXT_PAGE,
                page: {
                  size: {
                    width: 15840,
                    height: 20160,
                  },
                  margins: {
                    top: 720,
                    right: 720,
                    bottom: 720,
                    left: 360,
                  },
                },
              },
              children: generateAdditionalInfoSection()
            },
            // Cuarta sección con datos del concurso (solo si hay datos disponibles)
            ...(fullContest && economics ? [{
              properties: {
                type: SectionType.NEXT_PAGE,
                page: {
                  size: {
                    width: 15840,
                    height: 20160,
                  },
                  margins: {
                    top: 720,
                    right: 720,
                    bottom: 720,
                    left: 360,
                  },
                },
              },
              children: generateContestDataSection(fullContest, economics, sponsorship, dynamicMembershipBreakdown)
            }] : []),
            // Secciones para cada plataforma (cada una en su propia página)
            ...sections.map((section: any) => ({
              ...section,
              properties: {
                ...section.properties,
                page: {
                  size: {
                    width: 15840,
                    height: 20160,
                  },
                  margins: {
                    top: 720,
                    right: 720,
                    bottom: 720,
                    left: 360,
                  },
                },
              },
            }))
          ]
        });

        // Generar el blob y descargarlo primero (como el PDF del invoice)
        const blob = await Packer.toBlob(doc);

        // Descargar el Word antes de convertirlo a base64
        const fileName = `Contest_Info_${fullContest.name.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.docx`;
        saveAs(blob, fileName);

        // Convertir a base64 para enviarlo
        const arrayBuffer = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        // Convertir a base64 de manera segura para archivos grandes
        let binary = '';
        const len = uint8Array.length;
        for (let i = 0; i < len; i++) {
          binary += String.fromCharCode(uint8Array[i]);
        }
        const base64String = btoa(binary);
        contestInfoDocBase64 = base64String;

        // Agregar al invoice
        // El documento Word se incluye en el payload
        if (contestInfoDocBase64) {
          const docSizeKB = (contestInfoDocBase64.length / 1024).toFixed(2);
          console.log(`[Word Doc] Documento generado: ${docSizeKB} KB - Incluido en el payload`);
          (invoiceData as any).contest_info_document = contestInfoDocBase64;
        }
      } catch (error: any) {
        console.warn('Error generando documento Word del concurso:', error?.message || error);
        contestInfoDocBase64 = '';
      }

      // Obtener datos de las plataformas
      const platformsArray: Array<{ name: string; description: string }> = [];

      try {
        // Obtener todas las plataformas base desde la API
        const platformsResponse = await fetch(`${environment.apiBaseUrl}/platforms`, {
          headers: getApiHeadersForGet()
        });
        if (platformsResponse.ok) {
          const allPlatforms = await platformsResponse.json();

          // Agregar plataformas base habilitadas
          if (sponsorship.enabled_base_platforms && Array.isArray(sponsorship.enabled_base_platforms)) {
            sponsorship.enabled_base_platforms.forEach((platformId: string) => {
              const platform = allPlatforms.find((p: any) => p.id === platformId);
              if (platform) {
                platformsArray.push({
                  name: platform.name || platformId,
                  description: platform.custom_text || platform.contest_text || platform.description || 'Sin descripción'
                });
              }
            });
          }
        }
      } catch (error) {
        console.error('Error obteniendo plataformas base:', error);
      }

      // Agregar plataformas personalizadas
      if (sponsorship.custom_platforms && Array.isArray(sponsorship.custom_platforms)) {
        sponsorship.custom_platforms.forEach((customPlatform: any) => {
          platformsArray.push({
            name: customPlatform.name || 'Plataforma Personalizada',
            description: customPlatform.description || 'Sin descripción'
          });
        });
      }

      // Preparar el body con los datos del concurso, patrocinio e invoice
      // Formatear el ID del sponsorship: NA-PC-XXXXX (donde XXXXX es el número del invoice)
      // El invoiceNumber ya fue extraído anteriormente después de crear el invoice
      const formatSponsorshipId = (invoiceNum: string) => {
        if (!invoiceNum) return '';
        return `NA-PCX-${invoiceNum}`;
      };

      // Modificar sponsorship para incluir sponsor_email fijo, plataformas y ID formateado
      const formattedSponsorshipId = formatSponsorshipId(invoiceNumber);

      const modifiedSponsorship = {
        ...sponsorship,
        id: formattedSponsorshipId,
        sponsor_id: formattedSponsorshipId,
        sponsor_email: billToEmail,
        platforms: platformsArray
      };

      //main@ipaysolutions.co

      const requestBody = {
        event_type: eventType,
        company_id: '5',
        submerchant_id: 5,
        contest: {
          ...cleanedContest,
          id: formattedContestId // Reemplazar el ID original con el formateado
        },
        sponsorship: modifiedSponsorship,
        invoice: invoiceData
      };

      // Enviar POST al endpoint proxy (que reenvía a la API externa)
      // Usar proxy para evitar problemas de CORS en producción
      // Nota: apiBaseUrl ya incluye /api, así que solo agregamos la ruta relativa
      const response = await fetch(`${environment.apiBaseUrl}/proxy/sponsorship/paylink`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Respuesta del servidor:', result);

        const paylink = extractPaylinkFromResponse(result);
        const invoiceNo = extractInvoiceNoFromPaylinkResponse(result);
        const costDescription = extractCostDescriptionFromPaylinkResponse(result);

        if (paylink) {
          // Actualizar el invoice usando el ID que guardamos al crearlo
          try {
            let invoiceIdToUpdate: string | null = null;

            // Si tenemos el ID del invoice creado, usarlo directamente
            if (createdInvoiceId) {
              invoiceIdToUpdate = createdInvoiceId;
            } else if (invoiceNo) {
              // Si no tenemos el ID, buscar por invoice_number (fallback)
              const normalizedInvoiceNo = extractInvoiceNumber(String(invoiceNo)) || '0';
              const findInvoiceResponse = await fetch(`${environment.apiBaseUrl}/invoices/contest/${contest.id}`, {
                headers: getApiHeadersForGet()
              });
              if (findInvoiceResponse.ok) {
                const invoices = await findInvoiceResponse.json();
                const invoiceToUpdate = Array.isArray(invoices)
                  ? invoices.find((inv: any) => {
                    const normalizedDbInvoiceNo = extractInvoiceNumber(String(inv.invoice_number || '')) || '0';
                    return normalizedDbInvoiceNo === normalizedInvoiceNo ||
                      inv.invoice_number === invoiceNo ||
                      inv.invoice_number === String(invoiceNo);
                  })
                  : null;
                if (invoiceToUpdate && invoiceToUpdate.id) {
                  invoiceIdToUpdate = invoiceToUpdate.id;
                }
              }
            }

            if (invoiceIdToUpdate) {
              // Actualizar el invoice en la base de datos con el paylink y cost_description usando el endpoint webhook
              const updateResponse = await fetch(`${environment.apiBaseUrl}/webhooks/paylink`, {
                method: 'POST',
                headers: getApiHeaders(),
                body: JSON.stringify({
                  invoice_id: invoiceIdToUpdate,
                  paylink: paylink,
                  cost_description: costDescription
                })
              });

              if (updateResponse.ok) {
                // Actualizar el invoice en el estado local
                setInvoiceData((prevData: any[]) => {
                  return prevData.map((inv: any) => {
                    // Si tenemos el ID creado, comparar por ID
                    if (createdInvoiceId && inv.id === createdInvoiceId) {
                      return { ...inv, paylink: paylink, cost_description: costDescription };
                    }
                    // Si no, comparar por invoice_number
                    if (invoiceNo) {
                      const normalizedInvoiceNo = extractInvoiceNumber(String(invoiceNo)) || '0';
                      const normalizedDbInvoiceNo = extractInvoiceNumber(String(inv.invoice_number || '')) || '0';
                      if (normalizedDbInvoiceNo === normalizedInvoiceNo ||
                        inv.invoice_number === invoiceNo ||
                        inv.invoice_number === String(invoiceNo)) {
                        return { ...inv, paylink: paylink, cost_description: costDescription };
                      }
                    }
                    return inv;
                  });
                });
                void appDialog.alert('Datos del patrocinador enviados exitosamente. Paylink y cost_description actualizados.');
              } else {
                console.error('Error actualizando paylink en la base de datos');
                void appDialog.alert('Datos del patrocinador enviados exitosamente, pero hubo un error al actualizar el paylink.');
              }
            } else {
              console.error('No se encontró el invoice para actualizar');
              void appDialog.alert('Datos del patrocinador enviados exitosamente, pero no se pudo encontrar el invoice para actualizar.');
            }
          } catch (updateError) {
            console.error('Error actualizando paylink:', updateError);
            void appDialog.alert('Datos del patrocinador enviados exitosamente, pero hubo un error al actualizar el paylink.');
          }
        } else {
          console.warn('Paylink no encontrado en la respuesta:', result);
          void appDialog.alert('La solicitud se envió, pero no se recibió un paylink en la respuesta. Revise los logs del servidor.');
        }
      } else {
        const errorText = await response.text();
        void appDialog.alert(`Error al enviar los datos: ${errorText}`);
        console.error('Error en la respuesta:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error enviando datos del patrocinador:', error);
      void appDialog.alert('Error al enviar los datos del patrocinador');
    }
  };

  // Función para enviar datos de ganadores de un concurso
  const handleSendWinners = async (contest: any) => {
    const confirmed = await appDialog.confirm('¿Está seguro de enviar los ganadores?', { type: 'warning' });
    if (!confirmed) return;

    try {
      // Obtener datos completos del concurso
      const fullContest = await contestService.getContestById(contest.id);
      if (!fullContest) {
        void appDialog.alert('No se pudieron obtener los datos del concurso');
        return;
      }

      // Obtener los ganadores del concurso
      const winnersResponse = await fetch(`${environment.apiBaseUrl}/contests/${contest.id}/winners`, {
        headers: getApiHeadersForGet()
      });
      if (!winnersResponse.ok) {
        throw new Error(`Error ${winnersResponse.status}: ${winnersResponse.statusText}`);
      }

      const winnersData = await winnersResponse.json();

      // Mapear ganadores al formato del webhook
      const webhookWinners = (winnersData.winners || []).map((winner: any) => {
        // Determinar badge_type: "pawtographer" para primeros 3 lugares, "pawticipant" para otros
        const badgeType = winner.place <= 3 ? 'pawtographer' : 'pawticipant';

        // Determinar prize_info.type: "primary" para primeros lugares, "secondary" para otros
        const prizeType = winner.place <= 3 ? 'primary' : 'secondary';

        // Crear descripción del premio
        const placeNames: { [key: number]: string } = {
          1: 'Grand Prize Winner',
          2: 'Second Place Winner',
          3: 'Third Place Winner',
          4: 'Fourth Place Winner'
        };
        const prizeDescription = placeNames[winner.place] || `${winner.place}${winner.place === 1 ? 'st' : winner.place === 2 ? 'nd' : winner.place === 3 ? 'rd' : 'th'} Place Winner`;

        // Construir objeto del ganador para el webhook
        const webhookWinner: any = {
          user_id: winner.participant?.user_id || null,
          badge_type: badgeType,
          place: winner.place,
          participant_name: winner.participant?.name || null,
          participant_id: winner.participant?.id || null
        };

        // Agregar winner_media si existe
        if (winner.participant?.photo_url) {
          webhookWinner.winner_media = winner.participant.photo_url;
        }

        // Agregar prize_info
        webhookWinner.prize_info = {
          type: prizeType,
          amount: winner.prize > 0 ? winner.prize : null,
          description: winner.prize > 0
            ? `${prizeDescription} - $${winner.prize.toFixed(2)}`
            : prizeDescription
        };

        // Agregar post_social_id si existe
        if (winner.participant?.post_social_id) {
          webhookWinner.post_social_id = winner.participant.post_social_id;
        }

        return webhookWinner;
      });

      // Validar que haya ganadores
      if (!webhookWinners || webhookWinners.length === 0) {
        void appDialog.alert('No se encontraron ganadores para enviar');
        return;
      }

      // Preparar los datos en el formato del webhook
      const webhookPayload = {
        contest_name: fullContest.name,
        contest_id: contest.id,
        winners: webhookWinners
      };

      // Log para debug
      console.log('=== PAYLOAD A ENVIAR ===');
      console.log('Contest Name:', webhookPayload.contest_name);
      console.log('Contest ID:', webhookPayload.contest_id);
      console.log('Winners Count:', webhookWinners.length);
      console.log('Winners:', webhookWinners);
      console.log('Full Payload:', JSON.stringify(webhookPayload, null, 2));

      // El API key se maneja en el servidor, no es necesario enviarlo desde el frontend
      const requestBody = webhookPayload;

      // El API key del backend (PET_CONTEST_ACCESS_KEY) va en el header.
      // El webhook externo lo autentica el servidor con CONTEST_WINNERS_WEBHOOK_API_KEY.
      const webhookConfigResponse = await fetch(`${environment.apiBaseUrl}/contests/${contest.id}/send-winners`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify(requestBody)
      });

      // Imprimir headers de respuesta del servidor
      console.log('=== HEADERS DE RESPUESTA DEL SERVIDOR ===');
      console.log('Status:', webhookConfigResponse.status, webhookConfigResponse.statusText);
      const responseHeaders: { [key: string]: string } = {};
      webhookConfigResponse.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });
      console.log('Response Headers:', responseHeaders);
      console.log('Response Headers (JSON):', JSON.stringify(responseHeaders, null, 2));

      if (webhookConfigResponse.ok) {
        const result = await webhookConfigResponse.json();
        console.log('Datos de ganadores enviados exitosamente:', result);
        void appDialog.alert('Datos de ganadores enviados exitosamente al webhook');
      } else {
        let errorData: any;
        try {
          errorData = await webhookConfigResponse.json();
        } catch (e) {
          const errorText = await webhookConfigResponse.text();
          errorData = { error: errorText };
        }

        // Intentar extraer el mensaje del webhook
        let errorMessage = 'Error desconocido';
        try {
          if (errorData.webhookResponse?.body) {
            const webhookBody = typeof errorData.webhookResponse.body === 'string'
              ? JSON.parse(errorData.webhookResponse.body)
              : errorData.webhookResponse.body;
            errorMessage = webhookBody.message || webhookBody.error || JSON.stringify(webhookBody);
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          errorMessage = errorData.webhookResponse?.body || errorData.error || 'Error al procesar la respuesta';
        }

        void appDialog.alert(`Error al enviar los datos al webhook:\n\n${errorMessage}`);
        console.error('Error en la respuesta:', webhookConfigResponse.status, errorData);
      }
    } catch (error) {
      console.error('Error enviando datos de ganadores:', error);
      void appDialog.alert('Error al enviar los datos de ganadores');
    }
  };

  // Función para mostrar modal de tipo de participante
  const handleSimulateParticipant = (contestId: string, entranceFee: number, contestName: string) => {
    setSelectedContestForParticipant({
      id: contestId,
      entranceFee,
      name: contestName
    });
    setShowParticipantModal(true);
  };

  // Función para mostrar el podio Live con los top 4 participantes
  const handleViewLive = async (contestId: string, contestName: string) => {
    try {
      setSelectedContestForLive({
        id: contestId,
        name: contestName
      });

      // Cargar participantes del concurso
      const response = await fetch(`${environment.apiBaseUrl}/contest-started/${contestId}`, {
        headers: getApiHeadersForGet()
      });
      if (response.ok) {
        const participants = await response.json();

        // Ordenar por votos (descendente) y tomar los primeros 4
        const sortedParticipants = [...participants].sort((a: any, b: any) => (b.votes || 0) - (a.votes || 0));
        const top4 = sortedParticipants.slice(0, 4);

        console.log('Top 4 participantes:', top4.map((p: any) => ({ name: p.participant_name, votes: p.votes })));

        // Mapear participantes usando directamente photo_url de la base de datos
        const top4WithImages = top4.map((participant: any) => {
          const mediaItems: Array<{ url: string; type: 'image' | 'video' }> = [];

          // Usar photo_url directamente de la base de datos
          if (participant.photo_url) {
            let mediaType: 'image' | 'video' = 'image';
            const photoUrlLower = participant.photo_url.toLowerCase();
            // Determinar si es video basado en la extensión o parámetros de la URL
            if (photoUrlLower.includes('.mp4') || photoUrlLower.includes('.mov') ||
              photoUrlLower.includes('.avi') || photoUrlLower.includes('.webm') ||
              photoUrlLower.includes('video') || photoUrlLower.includes('type=video')) {
              mediaType = 'video';
            }
            // Usar getCompleteImagePath para manejar URLs completas o relativas
            const photoUrl = getCompleteImagePath(participant.photo_url) || participant.photo_url;
            mediaItems.push({ url: photoUrl, type: mediaType });
          }

          const firstMedia = mediaItems.length > 0 ? mediaItems[0] : null;

          return {
            ...participant,
            imageUrl: firstMedia?.url || null,
            mediaType: firstMedia?.type || null,
            ownerName: participant.participant_name || '',
            ownerCountry: ''
          };
        });

        setLiveTopParticipants(top4WithImages);
        setShowLiveModal(true);
      } else {
        void appDialog.alert('Error al cargar los participantes del concurso');
      }
    } catch (error) {
      console.error('Error loading live participants:', error);
      void appDialog.alert('Error de conexión al cargar participantes');
    }
  };

  // Función para mostrar participantes del concurso iniciado
  const handleViewContestParticipants = async (contestId: string, contestName: string) => {
    try {
      setSelectedContestForParticipants({
        id: contestId,
        name: contestName
      });

      // Cargar información del concurso para obtener su estado
      try {
        const contestResponse = await fetch(`${environment.apiBaseUrl}/contests/${contestId}`, {
          headers: getApiHeadersForGet()
        });
        if (contestResponse.ok) {
          const contestData = await contestResponse.json();
          setSelectedContestStatus(contestData.status);
        }
      } catch (error) {
        console.error('Error obteniendo estado del concurso:', error);
      }

      // Cargar participantes del concurso
      const response = await fetch(`${environment.apiBaseUrl}/contest-started/${contestId}`, {
        headers: getApiHeadersForGet()
      });
      if (response.ok) {
        const participants = await response.json();
        console.log(`Total de participantes obtenidos del concurso ${contestId}:`, participants.length);
        console.log('Participantes:', participants.map((p: any) => ({ name: p.participant_name, id: p.id, votes: p.votes })));

        // Mapear participantes usando directamente photo_url de la base de datos
        const participantsWithImages = participants.map((participant: any) => {
          const mediaItems: Array<{ url: string; type: 'image' | 'video' }> = [];

          // Usar photo_url directamente de la base de datos
          if (participant.photo_url) {
            let mediaType: 'image' | 'video' = 'image';
            const photoUrlLower = participant.photo_url.toLowerCase();
            // Determinar si es video basado en la extensión o parámetros de la URL
            if (photoUrlLower.includes('.mp4') || photoUrlLower.includes('.mov') ||
              photoUrlLower.includes('.avi') || photoUrlLower.includes('.webm') ||
              photoUrlLower.includes('video') || photoUrlLower.includes('type=video')) {
              mediaType = 'video';
            }
            // Usar getCompleteImagePath para manejar URLs completas o relativas
            const photoUrl = getCompleteImagePath(participant.photo_url) || participant.photo_url;
            mediaItems.push({ url: photoUrl, type: mediaType });
          }

          // Mantener compatibilidad con código existente (primer media)
          const firstMedia = mediaItems.length > 0 ? mediaItems[0] : null;

          return {
            ...participant,
            imageUrl: firstMedia?.url || null,
            mediaType: firstMedia?.type || null,
            mediaItems: mediaItems
          };
        });

        setContestParticipants(participantsWithImages);

        // Cargar estadísticas mejoradas de participantes y votos
        try {
          setLoadingParticipantVoteStats(true);
          const statsResponse = await fetch(`${environment.apiBaseUrl}/contest-started/${contestId}/participant-vote-stats`, {
            headers: getApiHeadersForGet()
          });
          if (statsResponse.ok) {
            const stats = await statsResponse.json();
            setParticipantVoteStats(stats);
          }
        } catch (statsError) {
          console.error('Error cargando estadísticas de participantes:', statsError);
        } finally {
          setLoadingParticipantVoteStats(false);
        }

        setShowContestParticipantsModal(true);
      } else {
        void appDialog.alert('Error al cargar los participantes del concurso');
      }
    } catch (error) {
      console.error('Error loading contest participants:', error);
      void appDialog.alert('Error de conexión al cargar participantes');
    }
  };

  // Función para votar por un participante
  const handleVoteForParticipant = async (participantId: string) => {
    try {
      const response = await fetch(`${environment.apiBaseUrl}/contest-started/participant/${participantId}/vote`, {
        method: 'POST',
        headers: getApiHeaders(),
      });

      if (response.ok) {
        // Actualizar la lista de participantes localmente
        setContestParticipants(prev =>
          prev.map(p =>
            p.id === participantId
              ? { ...p, votes: p.votes + 1 }
              : p
          )
        );
      } else {
        const result = await response.json();
        void appDialog.alert(`Error al votar: ${result.error}`);
      }
    } catch (error) {
      console.error('Error voting for participant:', error);
      void appDialog.alert('Error de conexión al votar');
    }
  };

  // Función para resetear un concurso (eliminar participantes y resetear pass codes)
  const handleResetContest = async (contestId: string) => {
    if (!(await appDialog.confirm('¿Estás seguro de que quieres resetear este concurso? Esto eliminará todos los participantes, reseteará los pass codes y couponpasses usados, y eliminará todos los votos (tanto pagos como normales) del concurso.', { destructive: true }))) {
      return;
    }

    try {
      const response = await fetch(`${environment.apiBaseUrl}/contests/${contestId}/reset`, {
        method: 'POST',
        headers: getApiHeaders(),
      });

      const result = await response.json();

      if (response.ok) {
        // Actualizar el estado local del concurso
        setContests(contests.map(c =>
          c.id === contestId ? {
            ...c,
            current_participants: 0
          } : c
        ));

        // Limpiar la lista de participantes
        setContestParticipants([]);

        // Mostrar mensaje de éxito
        void appDialog.alert('¡Concurso reseteado exitosamente!');

        // Recargar los participantes (ahora vacío)
        if (selectedContestForParticipants) {
          await handleViewContestParticipants(contestId, selectedContestForParticipants.name);
        }
      } else {
        // Mostrar error
        void appDialog.alert(`Error al resetear el concurso: ${result.error || result.message}`);
      }
    } catch (error) {
      console.error('Error resetting contest:', error);
      void appDialog.alert('Error de conexión al resetear el concurso');
    }
  };

  const handleResetVotes = async (contestId: string) => {
    if (!(await appDialog.confirm('¿Estás seguro de que quieres resetear los votos de este concurso? Esto eliminará todos los votos (tanto pagos como normales) y pondrá los votos de todos los participantes a 0.', { destructive: true }))) {
      return;
    }

    try {
      const response = await fetch(`${environment.apiBaseUrl}/contests/${contestId}/reset-votes`, {
        method: 'POST',
        headers: getApiHeaders(),
      });

      const result = await response.json();

      if (response.ok) {
        // Mostrar mensaje de éxito
        void appDialog.alert('¡Votos reseteados exitosamente!');

        // Recargar los participantes para ver los votos actualizados
        if (selectedContestForParticipants) {
          await handleViewContestParticipants(contestId, selectedContestForParticipants.name);
        }
      } else {
        // Mostrar error
        void appDialog.alert(`Error al resetear los votos: ${result.error || result.message}`);
      }
    } catch (error) {
      console.error('Error resetting votes:', error);
      void appDialog.alert('Error de conexión al resetear los votos');
    }
  };

  // Función para terminar un concurso
  const handleFinishContest = async (contestId: string) => {
    try {
      const response = await fetch(`${environment.apiBaseUrl}/contests/${contestId}/finish`, {
        method: 'POST',
        headers: getApiHeaders(),
      });

      const result = await response.json();

      if (response.ok) {
        // Actualizar el estado local del concurso
        setContests(contests.map(c =>
          c.id === contestId ? {
            ...c,
            status: 'completed',
            phase: 'completed'
          } : c
        ));

        // Cerrar el modal de participantes
        setShowContestParticipantsModal(false);

        // Mostrar mensaje de éxito
        void appDialog.alert('¡Concurso terminado exitosamente!');
      } else {
        // Mostrar error
        void appDialog.alert(`Error al terminar el concurso: ${result.error}`);
      }
    } catch (error) {
      console.error('Error finishing contest:', error);
      void appDialog.alert('Error de conexión al terminar el concurso');
    }
  };

  // Función para ver ganadores del concurso
  const handleViewWinners = async (contestId: string, contestName: string) => {
    try {
      setSelectedContestForWinners({
        id: contestId,
        name: contestName
      });

      // Cargar ganadores del concurso
      const response = await fetch(`${environment.apiBaseUrl}/contests/${contestId}/winners`, {
        headers: getApiHeadersForGet()
      });
      if (response.ok) {
        const data = await response.json();
        setContestWinners(data.winners);
        setShowWinnersModal(true);
      } else {
        void appDialog.alert('Error al cargar los ganadores del concurso');
      }
    } catch (error) {
      console.error('Error loading contest winners:', error);
      void appDialog.alert('Error de conexión al cargar ganadores');
    }
  };

  // Función para ver datos de invoice del concurso
  const handleViewInvoice = async (contestId: string, contestName: string) => {
    try {
      setLoadingInvoice(true);
      setSelectedContestForInvoice({
        id: contestId,
        name: contestName
      });

      // Cargar invoices del concurso
      const response = await fetch(`${environment.apiBaseUrl}/invoices/contest/${contestId}`, {
        headers: getApiHeadersForGet()
      });
      if (response.ok) {
        const data = await response.json();
        setInvoiceData(Array.isArray(data) ? data : []);
        setShowInvoiceModal(true);
      } else {
        void appDialog.alert(t('invoicedata.errorLoading'));
      }
    } catch (error) {
      console.error('Error loading invoice data:', error);
      void appDialog.alert(t('invoicedata.errorConnection'));
    } finally {
      setLoadingInvoice(false);
    }
  };

  // Función para abrir modal de ambassadors
  const handleViewAmbassadors = async () => {
    try {
      setLoadingAmbassadors(true);
      const data = await ambassadorService.getAllAmbassadors();
      setAmbassadors(data);
      setShowAmbassadorsModal(true);
    } catch (error) {
      console.error('Error loading ambassadors:', error);
      void appDialog.alert(t('ambassadormanagement.errorLoading'));
    } finally {
      setLoadingAmbassadors(false);
    }
  };

  // Función para abrir dashboard global de compensación
  const handleViewGlobalCompensationDashboard = async () => {
    try {
      setLoadingGlobalCompensationStats(true);
      const response = await fetch(`${environment.apiBaseUrl}/ambassador-compensation/global-stats`, {
        headers: getApiHeadersForGet()
      });
      if (response.ok) {
        const stats = await response.json();
        setGlobalCompensationStats(stats);
        setShowGlobalCompensationDashboard(true);
      } else {
        void appDialog.alert('Error al cargar las estadísticas globales de compensación');
      }
    } catch (error) {
      console.error('Error loading global compensation stats:', error);
      void appDialog.alert('Error de conexión al cargar estadísticas');
    } finally {
      setLoadingGlobalCompensationStats(false);
    }
  };

  // Cargar concursos y sponsorships una vez al inicio (para usar en múltiples lugares)
  useEffect(() => {
    const loadContestsAndSponsorships = async () => {
      try {
        // Cargar concursos
        const contestsResponse = await fetch(`${environment.apiBaseUrl}/contests`, {
          headers: getApiHeadersForGet()
        });
        if (contestsResponse.ok) {
          const contestsData = await contestsResponse.json();
          setContestsForTags(contestsData);
        }

        // Cargar sponsorships
        const sponsorshipsResponse = await fetch(`${environment.apiBaseUrl}/sponsorships`, {
          headers: getApiHeadersForGet()
        });
        if (sponsorshipsResponse.ok) {
          const sponsorshipsData = await sponsorshipsResponse.json();
          setSponsorshipsForTags(sponsorshipsData);
        }
      } catch (error: any) {
        const isNetworkError = error?.message === 'Failed to fetch' || error?.name === 'TypeError';
        console.error('Error cargando concursos y sponsorships:', error);
        if (isNetworkError) {
          console.warn(
            'No se pudo conectar al servidor API. Asegúrate de que el backend esté en ejecución en el puerto 3030 o usa npm run dev.'
          );
        }
      }
    };
    loadContestsAndSponsorships();
  }, []);

  // Cargar tags del embajador cuando se abre el formulario de venta
  useEffect(() => {
    if (showTagSaleForm && selectedAmbassadorForSale) {
      const loadData = async () => {
        setLoadingAmbassadorTags(true);
        try {
          // Cargar tags del embajador
          const tagsResponse = await fetch(`${environment.apiBaseUrl}/qr-tags/ambassador/${selectedAmbassadorForSale.id}`, {
            headers: getApiHeadersForGet()
          });
          if (tagsResponse.ok) {
            const tags = await tagsResponse.json();
            setAmbassadorTags(tags);

            // Agrupar tags por concurso y sponsor
            const groups: any = {};
            tags
              .filter((tag: any) => !tag.sold_at)
              .forEach((tag: any) => {
                const key = `${tag.contest_id || 'none'}_${tag.sponsor_id || 'none'}`;
                if (!groups[key]) {
                  groups[key] = {
                    contestId: tag.contest_id || null,
                    sponsorId: tag.sponsor_id || null,
                    tags: []
                  };
                }
                groups[key].tags.push(tag);
              });

            const groupsArray = Object.values(groups);
            setTagGroups(groupsArray as any[]);
          } else {
            console.error('Error cargando tags del embajador');
            setAmbassadorTags([]);
            setTagGroups([]);
          }
        } catch (error) {
          console.error('Error cargando datos:', error);
          setAmbassadorTags([]);
          setTagGroups([]);
        } finally {
          setLoadingAmbassadorTags(false);
        }
      };
      loadData();
      setSelectedContestSponsor(null);
      setSelectedTagForSale(null);
      setSalePrice('');
    }
  }, [showTagSaleForm, selectedAmbassadorForSale]);

  // Cargar compensaciones cuando se abre el dashboard
  useEffect(() => {
    if (showCompensationDashboard && selectedAmbassadorForCompensation) {
      const loadCompensations = async () => {
        setLoadingCompensations(true);
        try {
          const response = await fetch(`${environment.apiBaseUrl}/ambassador-compensation/ambassador/${selectedAmbassadorForCompensation.id}`, {
            headers: getApiHeadersForGet()
          });
          if (response.ok) {
            const comps = await response.json();
            setCompensations(comps);
          } else {
            console.error('Error cargando compensaciones');
            setCompensations([]);
          }
        } catch (error) {
          console.error('Error cargando compensaciones:', error);
          setCompensations([]);
        } finally {
          setLoadingCompensations(false);
        }
      };
      loadCompensations();
    }
  }, [showCompensationDashboard, selectedAmbassadorForCompensation]);

  // Función para crear/editar ambassador
  const handleSaveAmbassador = async (ambassadorData: any) => {
    try {
      if (editingAmbassador) {
        await ambassadorService.updateAmbassador(editingAmbassador.id, ambassadorData);
        void appDialog.alert('Embajador actualizado exitosamente');
      } else {
        await ambassadorService.createAmbassador(ambassadorData);
        void appDialog.alert('Embajador creado exitosamente');
      }

      // Recargar lista
      const data = await ambassadorService.getAllAmbassadors();
      setAmbassadors(data);
      setShowAmbassadorForm(false);
      setEditingAmbassador(null);
    } catch (error) {
      console.error('Error saving ambassador:', error);
      void appDialog.alert('Error al guardar el embajador');
    }
  };

  // Función para abrir modal de vote boosters
  const handlePurchaseVoteBooster = async (participantId: string, contestId: string, participantName: string) => {
    try {
      setSelectedParticipantForBooster({
        id: participantId,
        contest_id: contestId,
        participant_name: participantName
      });
      setShowVoteBoosterModal(true);
    } catch (error) {
      console.error('Error opening vote booster modal:', error);
      void appDialog.alert('Error al abrir el modal de vote boosters');
    }
  };

  // Función para exportar reporte de Vote Boosters a CSV
  const exportVoteBoosterReportCSV = () => {
    if (!voteBoosterStats || !selectedContestForReport) return;

    setExportingVoteBoosterReport(true);
    try {
      const rows: string[] = [];

      // Encabezado
      rows.push(t('voteboosterreport.reportTitle'));
      rows.push(`${t('voteboosterreport.contest')}: ${selectedContestForReport.name}`);
      rows.push(`${t('voteboosterreport.generationDate')}: ${new Date().toLocaleString('en-US')}`);
      rows.push('');

      // Estadísticas Generales
      rows.push(t('voteboosterreport.generalStats'));
      rows.push(`${t('voteboosterreport.totalRevenue')},${(voteBoosterStats.generalStats?.total_revenue || 0).toFixed(2)}`);
      rows.push(`${t('voteboosterreport.totalPurchases')},${voteBoosterStats.generalStats?.total_purchases || 0}`);
      rows.push(`${t('voteboosterreport.uniqueBuyers')},${voteBoosterStats.generalStats?.unique_buyers || 0}`);
      rows.push(`${t('voteboosterreport.conversionRate')},${(voteBoosterStats.conversionRate || 0).toFixed(2)}%`);
      rows.push('');

      // Revenue por Tier
      if (voteBoosterStats.revenueByTier && voteBoosterStats.revenueByTier.length > 0) {
        rows.push(t('voteboosterreport.revenueByTierCSV'));
        rows.push(`${t('voteboosterreport.tierCSV')},${t('voteboosterreport.quantityCSV')},${t('voteboosterreport.totalRevenueCSV')},${t('voteboosterreport.totalVotesCSV')},${t('voteboosterreport.averagePriceCSV')}`);
        voteBoosterStats.revenueByTier.forEach((tier: any) => {
          rows.push(`${tier.booster_tier},${tier.count || 0},${(tier.total_revenue || 0).toFixed(2)},${tier.total_votes || 0},${(tier.avg_price || 0).toFixed(2)}`);
        });
        rows.push('');
      }

      // Ventas por Día
      if (voteBoosterStats.salesByDay && voteBoosterStats.salesByDay.length > 0) {
        rows.push(t('voteboosterreport.salesByDayCSV'));
        rows.push(`${t('voteboosterreport.dateCSV')},${t('voteboosterreport.purchasesCSV')},${t('voteboosterreport.revenueCSV')},${t('voteboosterreport.votesTotalCSV')}`);
        voteBoosterStats.salesByDay.forEach((day: any) => {
          const fecha = new Date(day.date).toLocaleDateString('en-US');
          rows.push(`${fecha},${day.count || 0},${(day.total_revenue || 0).toFixed(2)},${day.total_votes || 0}`);
        });
        rows.push('');
      }

      // Top Participantes
      if (voteBoosterStats.topParticipants && voteBoosterStats.topParticipants.length > 0) {
        rows.push(t('voteboosterreport.topParticipantsCSV'));
        rows.push(`#,${t('voteboosterreport.participantCSV')},${t('voteboosterreport.purchasesCSV')},${t('voteboosterreport.votesTotalParticipantCSV')},${t('voteboosterreport.totalSpentCSV')}`);
        voteBoosterStats.topParticipants.forEach((participant: any, index: number) => {
          rows.push(`${index + 1},${participant.participant_name || participant.participant_id},${participant.purchases || 0},${participant.total_votes || 0},${(participant.total_spent || 0).toFixed(2)}`);
        });
      }

      const csvContent = rows.join('\n');
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `reporte_vote_boosters_${selectedContestForReport.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().getTime()}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exportando reporte:', error);
      void appDialog.alert(t('voteboosterreport.errorExporting'));
    } finally {
      setExportingVoteBoosterReport(false);
    }
  };

  // Función para abrir dashboard de reportes de vote boosters
  const handleViewVoteBoosterReport = async (contestId: string, contestName: string) => {
    try {
      setLoadingVoteBoosterStats(true);
      setSelectedContestForReport({ id: contestId, name: contestName });

      // Actualizar registros 'pending' a 'paid' antes de cargar estadísticas
      // Esto asegura que las compras realizadas desde el dashboard se muestren
      try {
        const updateResponse = await fetch(`${environment.apiBaseUrl}/vote-boosters/contest/${contestId}/update-pending-to-paid`, {
          method: 'POST',
          headers: getApiHeaders()
        });
        if (updateResponse.ok) {
          const updateResult = await updateResponse.json();
          if (updateResult.updated > 0) {
            console.log(`${updateResult.updated} registros actualizados de 'pending' a 'paid'`);
          }
        }
      } catch (updateError) {
        console.error('Error actualizando registros pending:', updateError);
        // No fallar si hay error en la actualización, continuar cargando estadísticas
      }

      const response = await fetch(`${environment.apiBaseUrl}/vote-boosters/stats/contest/${contestId}`, {
        headers: getApiHeadersForGet()
      });
      if (response.ok) {
        const stats = await response.json();
        setVoteBoosterStats(stats);
        setShowVoteBoosterReportModal(true);
      } else {
        void appDialog.alert('Error al cargar las estadísticas de vote boosters');
      }
    } catch (error) {
      console.error('Error loading vote booster stats:', error);
      void appDialog.alert('Error de conexión al cargar estadísticas');
    } finally {
      setLoadingVoteBoosterStats(false);
    }
  };

  // Función para abrir modal de estadísticas de tags por concurso
  const handleViewTagStatistics = async (contestId: string, contestName: string) => {
    try {
      setLoadingTagStatistics(true);
      setSelectedContestForTagStats({ id: contestId, name: contestName });

      const response = await fetch(`${environment.apiBaseUrl}/qr-tags/contest/${contestId}/statistics`, {
        headers: getApiHeadersForGet()
      });
      if (response.ok) {
        const stats = await response.json();
        setTagStatistics(stats);
        setShowTagStatisticsModal(true);
      } else {
        void appDialog.alert(t('tagstatistics.errorLoadingStats'));
      }
    } catch (error) {
      console.error('Error cargando estadísticas de tags:', error);
      void appDialog.alert(t('tagstatistics.errorLoadingStats'));
    } finally {
      setLoadingTagStatistics(false);
    }
  };

  // Función para comprar vote booster
  const handleBuyVoteBooster = async (tier: string, userEmail: string) => {
    if (!selectedParticipantForBooster) return;

    try {
      const boosterData = {
        contest_id: selectedParticipantForBooster.contest_id,
        participant_id: selectedParticipantForBooster.id,
        user_email: userEmail,
        booster_tier: tier
      };

      const result = await voteBoosterService.purchaseVoteBooster(boosterData);

      if (result.success) {
        void appDialog.alert(`¡${result.votes_added} votos agregados exitosamente!`);
        setShowVoteBoosterModal(false);
        setSelectedParticipantForBooster(null);

        // Recargar participantes si es necesario
        if (selectedContestForParticipants) {
          const response = await fetch(`${environment.apiBaseUrl}/contests/${selectedContestForParticipants.id}/participants`, {
            headers: getApiHeadersForGet()
          });
          if (response.ok) {
            const data = await response.json();
            setContestParticipants(data);
          }
        }
      }
    } catch (error: any) {
      console.error('Error purchasing vote booster:', error);
      void appDialog.alert(error.message || 'Error al comprar vote booster');
    }
  };

  // Función para manejar el éxito de creación de participante
  const handleParticipantSuccess = async (result: any) => {
    if (!selectedContestForParticipant) return;

    try {
      // Actualizar el contador de participantes para este concurso específico
      const participantCountData = await participantService.getParticipantCountByContest(selectedContestForParticipant.id);

      // Actualizar el estado de concursos con el nuevo contador
      setContests(prevContests =>
        prevContests.map(contest =>
          contest.id === selectedContestForParticipant.id
            ? { ...contest, actualParticipantCount: participantCountData.participantCount }
            : contest
        )
      );

      // Mensaje de éxito removido - se muestra en el modal personalizado

    } catch (error) {
      console.error('Error actualizando contador de participantes:', error);
      // Error actualizando contador - se maneja silenciosamente
    }
  };

  // Función para manejar errores de creación de participante
  const handleParticipantError = (error: string) => {
    console.error('Error creando participante:', error);
    // Error se muestra en el modal personalizado
  };


  // Función para verificar si un concurso tiene patrocinios
  const hasSponsorship = async (contest: any) => {
    try {
      // Verificar si el concurso tiene sponsor_id
      return contest.sponsor_id !== null && contest.sponsor_id !== undefined;
    } catch (error) {
      console.error('Error verificando patrocinios:', error);
      return false;
    }
  };

  return (
    <div className="napc-page napc-dashboard min-h-screen">
      {/* Header Sticky - Mejorado para mejor navegación */}
      <div className="napc-topbar sticky top-0 z-50">
        <div
          className="mx-auto"
          style={{
            width: 'min(100%, 1280px)',
            marginLeft: 'auto',
            marginRight: 'auto',
            paddingLeft: isMobile ? '16px' : '48px',
            paddingRight: isMobile ? '16px' : '48px'
          }}
        >
          <div className="napc-builder-topline">
            <div className="napc-builder-brand">
              <div className="napc-mini-logo" aria-hidden="true">
                <svg viewBox="0 0 64 64" fill="currentColor">
                  <path d="M17.6 29.7c4.2-2.6 8.7-3.9 14.4-3.9s10.2 1.3 14.4 3.9c4.7 2.9 7.2 7.4 6.3 11.4-1.1 5.1-6.5 7.4-12 5.2-3.2-1.3-5.5-2-8.7-2s-5.5.7-8.7 2c-5.5 2.2-10.9-.1-12-5.2-.9-4 1.6-8.5 6.3-11.4Zm-5-2.5c-3.5 0-6.3-3.5-6.3-7.8s2.8-7.8 6.3-7.8 6.3 3.5 6.3 7.8-2.8 7.8-6.3 7.8Zm12.2-5.9c-3.5 0-6.3-3.7-6.3-8.2s2.8-8.2 6.3-8.2 6.3 3.7 6.3 8.2-2.8 8.2-6.3 8.2Zm14.4 0c-3.5 0-6.3-3.7-6.3-8.2s2.8-8.2 6.3-8.2 6.3 3.7 6.3 8.2-2.8 8.2-6.3 8.2Zm12.2 5.9c-3.5 0-6.3-3.5-6.3-7.8s2.8-7.8 6.3-7.8 6.3 3.5 6.3 7.8-2.8 7.8-6.3 7.8Z" />
                </svg>
              </div>
              <div>
                <h1>{t('app.title')}</h1>
                <p>NAPC · Internal contest builder</p>
              </div>
            </div>



            <div className="napc-builder-controls">
              <Tooltip title="Configuración">
                <IconButton
                  onClick={handleConfigMenuOpen}
                  sx={{
                    color: '#6B6F7B',
                    '&:hover': {
                      color: '#B01F8E',
                      backgroundColor: 'rgba(237, 23, 144, 0.08)'
                    }
                  }}
                >
                  <Settings className="w-5 h-5" />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={configMenuAnchor}
                open={configMenuOpen}
                onClose={handleConfigMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem
                  onClick={() => handleModeChange('normal')}
                  selected={apiMode === 'normal'}
                >
                  Normal
                </MenuItem>
                <MenuItem
                  onClick={() => handleModeChange('test')}
                  selected={apiMode === 'test'}
                >
                  Test
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleConfigMenuClose();
                    navigate(routes.seoConfig);
                  }}
                >
                  Configuración SEO
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleConfigMenuClose();
                    setS3UploadModalOpen(true);
                  }}
                >
                  Subir Imagen a S3
                </MenuItem>
              </Menu>
              {onLogout && (
                <button
                  onClick={onLogout}
                  className="napc-logout-button"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 40 30">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              )}
            </div>

            {/* Botones Flotantes */}
            <Box className="napc-action-pills" sx={{
              position: 'relative',
              marginTop: isMobile ? '8px' : '24px',
              marginBottom: isMobile ? '8px' : '24px',
              display: 'flex',
              gap: 2,
              justifyContent: 'flex-start',
              flexWrap: 'wrap',
              width: isMobile ? '100%' : 'auto'
            }}>
              {/* Botón Flotante 1: Create Contest */}
              <Box sx={{ position: 'relative' }} data-floating-button>
                <Button
                  onClick={() => setExpandedFloatingButton1(!expandedFloatingButton1)}
                  variant="contained"
                  startIcon={<Plus className="w-5 h-5" />}
                  sx={{
                    backgroundColor: '#ED1790',
                    color: 'white',
                    px: 3,
                    py: 1.5,
                    borderRadius: 999,
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: isMobile ? '12px' : '14px',
                    minWidth: '280px',
                    boxShadow: '0 18px 34px -22px rgba(237, 23, 144, .95)',
                    zIndex: 10,
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: '#B01F8E',
                      boxShadow: '0 22px 38px -24px rgba(237, 23, 144, .95)',
                    },
                    '&:active': {
                      boxShadow: '0 14px 28px -22px rgba(237, 23, 144, .9)',
                    }
                  }}
                >
                  {t('buttonsforms.floatingButtonCreate')}
                </Button>

                {expandedFloatingButton1 && (
                  <Box className="napc-floating-popup" sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    mt: 1.25,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    zIndex: 20,
                    minWidth: '220px',
                    backgroundColor: '#ffffff',
                    padding: 1.5,
                    borderRadius: '12px',
                    boxShadow: '0 1px 0 rgba(14,15,18,.04), 0 18px 44px -28px rgba(14,15,18,.28)',
                    border: '1px solid #E7E5EA'
                  }}>
                    <Button
                      onClick={() => {
                        setShowCreateForm(true);
                        setExpandedFloatingButton1(false);
                      }}
                      variant="contained"
                      startIcon={<Plus className="w-5 h-5" />}
                      sx={{
                        backgroundColor: '#ED1790',
                        color: 'white',
                        px: 2.5,
                        py: 1,
                        borderRadius: 999,
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        fontSize: isMobile ? '12px' : '14px',
                        width: '100%',
                        boxShadow: '0 14px 28px -20px rgba(237, 23, 144, .85)',
                        '&:hover': {
                          backgroundColor: '#B01F8E',
                          boxShadow: '0 18px 34px -22px rgba(237, 23, 144, .95)',
                        },
                      }}
                    >
                      {t('buttonsforms.createContest')}
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Botón Flotante 2: List Coupon Passes y List Pass Codes */}
              <Box sx={{ position: 'relative' }} data-floating-button>
                <Button
                  onClick={() => setExpandedFloatingButton2(!expandedFloatingButton2)}
                  variant="contained"
                  startIcon={<Gift className="w-5 h-5" />}
                  sx={{
                    backgroundColor: '#F26B42',
                    color: 'white',
                    px: 3,
                    py: 1.5,
                    borderRadius: 999,
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: isMobile ? '12px' : '14px',
                    minWidth: '280px',
                    boxShadow: '0 18px 34px -22px rgba(242, 107, 66, .9)',
                    zIndex: 10,
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: '#D9552F',
                      boxShadow: '0 22px 38px -24px rgba(242, 107, 66, .95)',
                    },
                    '&:active': {
                      boxShadow: '0 14px 28px -22px rgba(242, 107, 66, .9)',
                    }
                  }}
                >
                  {t('buttonsforms.floatingButtonLists')}
                </Button>

                {expandedFloatingButton2 && (
                  <Box className="napc-floating-popup" sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    mt: 1.25,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    zIndex: 20,
                    minWidth: '220px',
                    backgroundColor: '#ffffff',
                    padding: 1.5,
                    borderRadius: '12px',
                    boxShadow: '0 1px 0 rgba(14,15,18,.04), 0 18px 44px -28px rgba(14,15,18,.28)',
                    border: '1px solid #E7E5EA'
                  }}>
                    <Button
                      onClick={() => {
                        setExpandedFloatingButton2(false);
                        openUnifiedPassesCodes();
                      }}
                      variant="contained"
                      startIcon={<Gift className="w-5 h-5" />}
                      sx={{
                        backgroundColor: '#f97316',
                        color: 'white',
                        px: 2.5,
                        py: 1,
                        borderRadius: 2,
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        fontSize: isMobile ? '12px' : '14px',
                        width: '100%',
                        boxShadow: '0 4px 8px rgba(249, 115, 22, 0.3)',
                        '&:hover': {
                          backgroundColor: '#ea580c',
                          boxShadow: '0 6px 12px rgba(249, 115, 22, 0.4)',
                        },
                      }}
                    >
                      {t('buttonsforms.floatingButtonLists')}
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Botón Flotante 3: Más opciones */}
              <Box sx={{ position: 'relative' }} data-floating-button>
                <Button
                  onClick={() => setExpandedFloatingButton3(!expandedFloatingButton3)}
                  variant="contained"
                  startIcon={<Settings className="w-5 h-5" />}
                  sx={{
                    backgroundColor: 'white',
                    color: '#374151',
                    px: 3,
                    py: 1.5,
                    borderRadius: 3,
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: isMobile ? '12px' : '14px',
                    minWidth: '280px',
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
                    border: '1px solid #e5e7eb',
                    zIndex: 10,
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: '#f9fafb',
                      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
                    },
                    '&:active': {
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    }
                  }}
                >
                  {t('buttonsforms.floatingButtonMoreOptions')}
                </Button>

                {expandedFloatingButton3 && (
                  <Box className="napc-floating-popup" sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    mt: 1.25,
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                    gap: 1.5,
                    zIndex: 20,
                    minWidth: isMobile ? '400px' : '800px',
                    maxWidth: '1200px',
                    backgroundColor: '#ffffff',
                    padding: 1.5,
                    borderRadius: '12px',
                    boxShadow: '0 1px 0 rgba(14,15,18,.04), 0 18px 44px -28px rgba(14,15,18,.28)',
                    border: '1px solid #E7E5EA'
                  }}>
                    <Button
                      onClick={handleViewAmbassadors}
                      variant="contained"
                      startIcon={<Users className="w-5 h-5" />}
                      sx={{
                        backgroundColor: '#9333ea',
                        color: 'white',
                        px: 2.5,
                        py: 1,
                        borderRadius: 2,
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        fontSize: isMobile ? '12px' : '14px',
                        width: '100%',
                        boxShadow: '0 4px 8px rgba(147, 51, 234, 0.3)',
                        '&:hover': {
                          backgroundColor: '#7e22ce',
                          boxShadow: '0 6px 12px rgba(147, 51, 234, 0.4)',
                        },
                        '&:active': {
                          boxShadow: '0 2px 4px rgba(147, 51, 234, 0.3)',
                        }
                      }}
                    >
                      {t('buttonsforms.ambassadorManagement')}
                    </Button>
                    <Button
                      onClick={async () => {
                        setShowImportsModal(true);
                        setLoadingImports(true);
                        try {
                          const importsData = await importService.getAllImports();
                          setImports(importsData);
                        } catch (error) {
                          console.error('Error loading imports:', error);
                          void appDialog.alert('Error al cargar las planillas importadas');
                        } finally {
                          setLoadingImports(false);
                        }
                      }}
                      variant="contained"
                      startIcon={<FileText className="w-5 h-5" />}
                      sx={{
                        backgroundColor: '#6366f1',
                        color: 'white',
                        px: 2.5,
                        py: 1,
                        borderRadius: 2,
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        fontSize: isMobile ? '12px' : '14px',
                        width: '100%',
                        boxShadow: '0 4px 8px rgba(99, 102, 241, 0.3)',
                        '&:hover': {
                          backgroundColor: '#4f46e5',
                          boxShadow: '0 6px 12px rgba(99, 102, 241, 0.4)',
                        },
                        '&:active': {
                          boxShadow: '0 2px 4px rgba(99, 102, 241, 0.3)',
                        }
                      }}
                    >
                      Índice de Planillas
                    </Button>
                    <Button
                      onClick={handleViewSponsorTierReport}
                      variant="contained"
                      startIcon={<Building2 className="w-5 h-5" />}
                      disabled={loadingSponsorTierStats}
                      sx={{
                        backgroundColor: '#dc2626',
                        color: 'white',
                        px: 2.5,
                        py: 1,
                        borderRadius: 2,
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        fontSize: isMobile ? '12px' : '14px',
                        width: '100%',
                        boxShadow: '0 4px 8px rgba(220, 38, 38, 0.3)',
                        '&:hover': {
                          backgroundColor: '#b91c1c',
                          boxShadow: '0 6px 12px rgba(220, 38, 38, 0.4)',
                        },
                        '&:active': {
                          boxShadow: '0 2px 4px rgba(220, 38, 38, 0.3)',
                        }
                      }}
                    >
                      {loadingSponsorTierStats ? 'Cargando...' : t('buttonsforms.reportManagement')}
                    </Button>
                    <Button
                      onClick={handleViewEconomyFlowDiagram}
                      variant="contained"
                      startIcon={<TrendingUp className="w-5 h-5" />}
                      disabled={loadingEconomyFlowData}
                      sx={{
                        backgroundColor: '#10b981',
                        color: 'white',
                        px: 2.5,
                        py: 1,
                        borderRadius: 2,
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        fontSize: isMobile ? '12px' : '14px',
                        width: '100%',
                        boxShadow: '0 4px 8px rgba(16, 185, 129, 0.3)',
                        '&:hover': {
                          backgroundColor: '#059669',
                          boxShadow: '0 6px 12px rgba(16, 185, 129, 0.4)',
                        },
                        '&:active': {
                          boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
                        }
                      }}
                    >
                      {loadingEconomyFlowData ? t('economyflow.loadingButton') : t('economyflow.diagramButton')}
                    </Button>
                    <Button
                      onClick={() => setShowWordDocModal(true)}
                      variant="contained"
                      startIcon={<FileText className="w-5 h-5" />}
                      sx={{
                        backgroundColor: '#8b5cf6',
                        color: 'white',
                        px: 2.5,
                        py: 1,
                        borderRadius: 2,
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        fontSize: isMobile ? '12px' : '14px',
                        width: '100%',
                        boxShadow: '0 4px 8px rgba(139, 92, 246, 0.3)',
                        '&:hover': {
                          backgroundColor: '#7c3aed',
                          boxShadow: '0 6px 12px rgba(139, 92, 246, 0.4)',
                        },
                        '&:active': {
                          boxShadow: '0 2px 4px rgba(139, 92, 246, 0.3)',
                        }
                      }}
                    >
                      {t('buttonsforms.generateWordDoc')}
                    </Button>
                    <Button
                      onClick={async () => {
                        setShowPlatformsModal(true);
                        setLoadingPlatforms(true);
                        try {
                          const platformsData = await fetch(`${environment.apiBaseUrl}/platforms`, {
                            headers: getApiHeadersForGet()
                          }).then(r => r.json());
                          setPlatforms(platformsData);
                        } catch (error) {
                          console.error('Error loading platforms:', error);
                        } finally {
                          setLoadingPlatforms(false);
                        }
                      }}
                      startIcon={<Settings className="w-5 h-5" />}
                      sx={{
                        backgroundColor: '#7c3aed',
                        color: 'white',
                        px: 2.5,
                        py: 1,
                        borderRadius: 2,
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        fontSize: isMobile ? '12px' : '14px',
                        width: '100%',
                        boxShadow: '0 4px 8px rgba(124, 58, 237, 0.3)',
                        '&:hover': {
                          backgroundColor: '#6d28d9',
                          boxShadow: '0 6px 12px rgba(124, 58, 237, 0.4)',
                        },
                        '&:active': {
                          boxShadow: '0 2px 4px rgba(124, 58, 237, 0.3)',
                        }
                      }}
                    >
                      {t('buttonsforms.ManagePlatforms')}
                    </Button>
                    <Button
                      onClick={() => {
                        setExpandedFloatingButton3(false);
                        setShowDeleteTagsModal(true);
                      }}
                      variant="contained"
                      startIcon={<Tag className="w-5 h-5" />}
                      sx={{
                        backgroundColor: '#dc2626',
                        color: 'white',
                        px: 2.5,
                        py: 1,
                        borderRadius: 2,
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        fontSize: isMobile ? '12px' : '14px',
                        width: '100%',
                        boxShadow: '0 4px 8px rgba(220, 38, 38, 0.3)',
                        '&:hover': {
                          backgroundColor: '#b91c1c',
                          boxShadow: '0 6px 12px rgba(220, 38, 38, 0.4)',
                        },
                        '&:active': {
                          boxShadow: '0 2px 4px rgba(220, 38, 38, 0.3)',
                        }
                      }}
                    >
                      {t('buttonsforms.deleteTag')}
                    </Button>
                    {apiMode === 'test' && (
                      <Button
                        onClick={async () => {
                          setShowTestImagesModal(true);

                          // Verificar si hay token configurado
                          let accessToken = getPetflixAccessToken();

                          if (!accessToken) {
                            // Si no hay token, mostrar input para ingresarlo
                            setShowTokenInput(true);
                            setTokenInputValue('');
                            return;
                          }

                          // Si hay token, cargar las imágenes
                          await loadTestImages(accessToken);
                        }}
                        variant="contained"
                        sx={{
                          backgroundColor: '#dc2626',
                          color: 'white',
                          px: 2.5,
                          py: 1,
                          borderRadius: 2,
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          fontSize: isMobile ? '12px' : '14px',
                          width: '100%',
                          boxShadow: '0 4px 8px rgba(220, 38, 38, 0.3)',
                          '&:hover': {
                            backgroundColor: '#b91c1c',
                            boxShadow: '0 6px 12px rgba(220, 38, 38, 0.4)',
                          },
                          '&:active': {
                            boxShadow: '0 2px 4px rgba(220, 38, 38, 0.3)',
                          }
                        }}
                      >
                        {t('buttonsforms.TestImages')}
                      </Button>
                    )}
                    {apiMode === 'test' && (
                      <Button
                        onClick={async () => {
                          setShowGeoIpModal(true);
                          await fetchGeoIpData();
                        }}
                        variant="contained"
                        startIcon={<MapPin className="w-5 h-5" />}
                        sx={{
                          backgroundColor: '#7c3aed',
                          color: 'white',
                          px: 2.5,
                          py: 1,
                          borderRadius: 2,
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          fontSize: isMobile ? '12px' : '14px',
                          width: '100%',
                          boxShadow: '0 4px 8px rgba(124, 58, 237, 0.3)',
                          '&:hover': {
                            backgroundColor: '#6d28d9',
                            boxShadow: '0 6px 12px rgba(124, 58, 237, 0.4)',
                          },
                          '&:active': {
                            boxShadow: '0 2px 4px rgba(124, 58, 237, 0.3)',
                          }
                        }}
                      >
                        GEO-IP
                      </Button>
                    )}
                    {apiMode === 'test' && (
                      <Button
                        onClick={async () => {
                          try {
                            const confirmed = await appDialog.confirm(
                              '¿Estás seguro de que deseas procesar los participantes pendientes de contest_participante_petflix?\n\n' +
                              'Esto procesará todos los participantes con:\n' +
                              '- user_id con longitud mayor o igual a 3 dígitos\n' +
                              '- contest_id no null\n\n' +
                              'Se obtendrán los posts de PetFlix y se crearán los participantes automáticamente.'
                            );

                            if (!confirmed) {
                              return;
                            }

                            // Obtener token de PetFlix para autenticación
                            let accessToken = getPetflixAccessToken();
                            if (!accessToken) {
                              // Intentar obtener del servicio de auth
                              accessToken = petflixAuthService.getAccessToken();
                            }

                            if (!accessToken) {
                              void appDialog.alert('No se encontró token de autenticación. Por favor, configura el token de PetFlix.');
                              return;
                            }

                            const headers: any = {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${accessToken}`
                            };

                            console.log('✓ Token de autenticación obtenido');

                            // Primero, obtener los participantes pendientes para obtener las imágenes
                            // Hacer una llamada al backend para obtener la lista de participantes pendientes
                            // Luego, para cada participante, obtener el post y la imagen
                            console.log('Obteniendo participantes pendientes y sus imágenes...');

                            // Obtener participantes pendientes (necesitamos un endpoint para esto o hacerlo en el frontend)
                            // Por ahora, vamos a modificar el flujo para obtener las imágenes antes de procesar
                            // Primero llamamos al endpoint de procesamiento que nos dará los resultados
                            // Pero antes, vamos a obtener las imágenes para cada participante pendiente

                            // Función helper para obtener la imagen de un post
                            const getImageUrlFromPost = async (postId: string): Promise<string | null> => {
                              try {
                                // 1. Obtener el post
                                const postResponse = await fetch(`https://dev.petflix.io/posts/${postId}`, {
                                  headers: {
                                    'Authorization': `Bearer ${accessToken}`,
                                    'Content-Type': 'application/json'
                                  }
                                });

                                if (!postResponse.ok) {
                                  console.error(`Error obteniendo post ${postId}: ${postResponse.status}`);
                                  return null;
                                }

                                const postData = await postResponse.json();
                                const post = postData.data || postData;

                                // 2. Obtener el array media del post
                                if (!post.media || !Array.isArray(post.media) || post.media.length === 0) {
                                  console.warn(`Post ${postId} no tiene media`);
                                  return null;
                                }

                                // 3. Obtener el primer elemento de media (o todos si es necesario)
                                const mediaId = post.media[0];
                                if (!mediaId) {
                                  console.warn(`Post ${postId} tiene array media vacío`);
                                  return null;
                                }

                                // 4. Llamar a /media/:id para obtener el objeto media con el path
                                const mediaResponse = await fetch(`https://dev.petflix.io/media/${mediaId}`, {
                                  headers: {
                                    'Authorization': `Bearer ${accessToken}`,
                                    'Content-Type': 'application/json'
                                  }
                                });

                                if (!mediaResponse.ok) {
                                  console.error(`Error obteniendo media ${mediaId}: ${mediaResponse.status}`);
                                  return null;
                                }

                                const mediaData = await mediaResponse.json();
                                const media = mediaData.data || mediaData;

                                // 5. Usar getImageUrlFromMedia para obtener la URL de la imagen (igual que Test Images)
                                const imageUrl = getImageUrlFromMedia(media);
                                console.log(`✓ Imagen obtenida para post ${postId}: ${imageUrl}`);
                                return imageUrl;
                              } catch (error: any) {
                                console.error(`Error obteniendo imagen para post ${postId}:`, error);
                                return null;
                              }
                            };

                            // Obtener la lista de participantes pendientes
                            console.log('Obteniendo lista de participantes pendientes...');
                            const pendingResponse = await fetch(`${environment.apiBaseUrl}/petflix/pending-participants`, {
                              method: 'GET',
                              headers: getApiHeadersForGet()
                            });

                            if (!pendingResponse.ok) {
                              const errorData = await pendingResponse.json().catch(() => ({}));
                              throw new Error(errorData.error || `Error ${pendingResponse.status}: ${pendingResponse.statusText}`);
                            }

                            const pendingData = await pendingResponse.json();
                            const pendingParticipants = pendingData.participants || [];

                            if (pendingParticipants.length === 0) {
                              void appDialog.alert('No hay participantes pendientes para procesar');
                              return;
                            }

                            console.log(`Total de participantes pendientes: ${pendingParticipants.length}`);

                            // Procesar cada participante usando /api/contests/join-by-name
                            const results: any[] = [];
                            let successful = 0;
                            let failed = 0;
                            let skipped = 0;

                            for (let i = 0; i < pendingParticipants.length; i++) {
                              const participant = pendingParticipants[i];
                              console.log(`\n[${i + 1}/${pendingParticipants.length}] Procesando participante: ${participant.id}`);
                              console.log(`- Concurso: ${participant.contest_name}`);
                              console.log(`- User ID: ${participant.user_id}`);
                              console.log(`- Post Social ID: ${participant.post_social_id}`);

                              try {
                                // Llamar a /api/contests/join-by-name para este participante específico
                                const joinResponse = await fetch(`${environment.apiBaseUrl}/contests/join-by-name`, {
                                  method: 'POST',
                                  headers: {
                                    ...getApiHeaders(),
                                    'Authorization': `Bearer ${accessToken}`
                                  },
                                  body: JSON.stringify({
                                    contest_name: participant.contest_name,
                                    user_id: participant.user_id,
                                    post_social_id: participant.post_social_id,
                                    coupon_code: participant.coupon_code || null
                                  })
                                });

                                const joinData = await joinResponse.json();

                                if (joinResponse.ok && joinData.success) {
                                  // Obtener la URL de la imagen para este participante
                                  let imageUrl = null;
                                  if (participant.post_social_id) {
                                    try {
                                      imageUrl = await getImageUrlFromPost(participant.post_social_id);
                                    } catch (imgError: any) {
                                      console.warn(`No se pudo obtener imagen para participante ${participant.id}:`, imgError);
                                    }
                                  }

                                  if (joinData.participant_creation) {
                                    // El participante fue creado exitosamente
                                    console.log(`✓ Participante creado exitosamente: ${joinData.participant_creation.participantName || 'N/A'}`);
                                    results.push({
                                      participant_id: participant.id,
                                      contest_name: participant.contest_name,
                                      status: 'success',
                                      participantId: joinData.participant_creation.participantId,
                                      participantName: joinData.participant_creation.participantName,
                                      passCode: joinData.participant_creation.passCode,
                                      imageUrl: imageUrl,
                                      message: joinData.participant_creation.message || 'Participante creado exitosamente'
                                    });
                                    successful++;
                                  } else {
                                    // El participante ya existe o fue omitido
                                    console.log(`⊘ Participante ya existe o fue omitido`);
                                    results.push({
                                      participant_id: participant.id,
                                      contest_name: participant.contest_name,
                                      status: 'skipped',
                                      imageUrl: imageUrl,
                                      message: 'El participante ya existe o fue omitido'
                                    });
                                    skipped++;
                                  }
                                } else {
                                  // Error al procesar el participante
                                  console.error(`✗ Error procesando participante: ${joinData.error || joinData.message || 'Error desconocido'}`);
                                  results.push({
                                    participant_id: participant.id,
                                    contest_name: participant.contest_name,
                                    status: 'failed',
                                    error: joinData.error || joinData.message || 'Error desconocido',
                                    post_social_id: participant.post_social_id
                                  });
                                  failed++;
                                }
                              } catch (error: any) {
                                console.error(`✗ Error procesando participante ${participant.id}:`, error);
                                results.push({
                                  participant_id: participant.id,
                                  contest_name: participant.contest_name,
                                  status: 'failed',
                                  error: error.message || 'Error desconocido',
                                  post_social_id: participant.post_social_id
                                });
                                failed++;
                              }
                            }

                            // Construir mensaje detallado con errores
                            let message = `Procesamiento completado:
- Total de participantes procesados: ${pendingParticipants.length}
- Exitosos: ${successful}
- Fallidos: ${failed}
- Omitidos: ${skipped}`;

                            // Agregar detalles de errores si los hay
                            if (failed > 0) {
                              const failedResults = results.filter((r: any) => r.status === 'failed');
                              if (failedResults.length > 0) {
                                message += `\n\nErrores encontrados:\n`;
                                failedResults.forEach((error: any, index: number) => {
                                  message += `${index + 1}. ${error.contest_name || 'Concurso desconocido'}: ${error.error || 'Error desconocido'}\n`;
                                });
                              }
                            }

                            void appDialog.alert(message);
                            console.log('\n=== Resultados del procesamiento ===');
                            console.log(`Total: ${pendingParticipants.length}, Exitosos: ${successful}, Fallidos: ${failed}, Omitidos: ${skipped}`);

                            // Mostrar detalles completos en consola
                            if (results.length > 0) {
                              console.log('\n=== Detalles de cada resultado ===');
                              results.forEach((result: any, index: number) => {
                                console.log(`\n${index + 1}. Participante ${result.participant_id || 'N/A'}:`);
                                console.log(`   - Concurso: ${result.contest_name || 'N/A'}`);
                                console.log(`   - Estado: ${result.status || 'N/A'}`);
                                if (result.imageUrl) {
                                  console.log(`   - URL de Imagen: ${result.imageUrl}`);
                                }
                                if (result.status === 'failed') {
                                  console.log(`   - Error: ${result.error || 'Error desconocido'}`);
                                } else if (result.status === 'success') {
                                  console.log(`   - Participante creado: ${result.participantName || 'N/A'}`);
                                  console.log(`   - ID: ${result.participantId || 'N/A'}`);
                                  console.log(`   - Pass Code: ${result.passCode || 'N/A'}`);
                                } else if (result.status === 'skipped') {
                                  console.log(`   - Razón: ${result.message || 'Omitido'}`);
                                }
                              });
                            }

                            // Recargar concursos para actualizar contadores
                            const contestsData = await contestService.getAllContests();
                            const contestsWithParticipantCount = await attachProposalSummariesBulk(await attachParticipantCountsBulk(contestsData));
                            setContests(contestsWithParticipantCount);

                          } catch (error: any) {
                            console.error('Error procesando participantes pendientes:', error);
                            void appDialog.alert(`Error al procesar participantes: ${error.message || 'Error desconocido'}`);
                          }
                        }}
                        variant="contained"
                        sx={{
                          backgroundColor: '#7c3aed',
                          color: 'white',
                          px: 2.5,
                          py: 1,
                          borderRadius: 2,
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          fontSize: isMobile ? '12px' : '14px',
                          width: '100%',
                          boxShadow: '0 4px 8px rgba(124, 58, 237, 0.3)',
                          '&:hover': {
                            backgroundColor: '#6d28d9',
                            boxShadow: '0 6px 12px rgba(124, 58, 237, 0.4)',
                          },
                          '&:active': {
                            boxShadow: '0 2px 4px rgba(124, 58, 237, 0.3)',
                          }
                        }}
                      >
                        Procesar Participantes Pendientes
                      </Button>
                    )}
                    {apiMode === 'test' && (
                      <Button
                        onClick={fetchPetflixUsers}
                        variant="contained"
                        sx={{
                          backgroundColor: '#059669',
                          color: 'white',
                          px: 2.5,
                          py: 1,
                          borderRadius: 2,
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          fontSize: isMobile ? '12px' : '14px',
                          width: '100%',
                          boxShadow: '0 4px 8px rgba(5, 150, 105, 0.3)',
                          '&:hover': {
                            backgroundColor: '#047857',
                            boxShadow: '0 6px 12px rgba(5, 150, 105, 0.4)',
                          },
                          '&:active': {
                            boxShadow: '0 2px 4px rgba(5, 150, 105, 0.3)',
                          }
                        }}
                      >
                        Obtener Usuarios Petflix
                      </Button>
                    )}
                    {apiMode === 'test' && (
                      <Button
                        onClick={fetchPetflixAmbassadorUsers}
                        variant="contained"
                        disabled={loadingPetflixUsers}
                        sx={{
                          backgroundColor: '#9333ea',
                          color: 'white',
                          px: 2.5,
                          py: 1,
                          borderRadius: 2,
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          fontSize: isMobile ? '12px' : '14px',
                          width: '100%',
                          boxShadow: '0 4px 8px rgba(147, 51, 234, 0.3)',
                          '&:hover': {
                            backgroundColor: '#7e22ce',
                            boxShadow: '0 6px 12px rgba(147, 51, 234, 0.4)',
                          },
                          '&:active': {
                            boxShadow: '0 2px 4px rgba(147, 51, 234, 0.3)',
                          },
                          '&:disabled': {
                            backgroundColor: '#9ca3af',
                            opacity: 0.6
                          }
                        }}
                      >
                        {loadingPetflixUsers ? 'Obteniendo...' : 'Obtener desde Petflix'}
                      </Button>
                    )}
                    {apiMode === 'test' && (
                      <Button
                        onClick={showCurrentToken}
                        variant="contained"
                        sx={{
                          backgroundColor: '#7c3aed',
                          color: 'white',
                          px: 2.5,
                          py: 1,
                          borderRadius: 2,
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          fontSize: isMobile ? '12px' : '14px',
                          width: '100%',
                          boxShadow: '0 4px 8px rgba(124, 58, 237, 0.3)',
                          '&:hover': {
                            backgroundColor: '#6d28d9',
                            boxShadow: '0 6px 12px rgba(124, 58, 237, 0.4)',
                          },
                          '&:active': {
                            boxShadow: '0 2px 4px rgba(124, 58, 237, 0.3)',
                          }
                        }}
                      >
                        Generar Token
                      </Button>
                    )}
                    {apiMode === 'test' && (
                      <Button
                        onClick={showUserData}
                        variant="contained"
                        sx={{
                          backgroundColor: '#059669',
                          color: 'white',
                          px: 2.5,
                          py: 1,
                          borderRadius: 2,
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          fontSize: isMobile ? '12px' : '14px',
                          width: '100%',
                          boxShadow: '0 4px 8px rgba(5, 150, 105, 0.3)',
                          '&:hover': {
                            backgroundColor: '#047857',
                            boxShadow: '0 6px 12px rgba(5, 150, 105, 0.4)',
                          },
                          '&:active': {
                            boxShadow: '0 2px 4px rgba(5, 150, 105, 0.3)',
                          }
                        }}
                      >
                        Datos de Usuario
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </div>
        </div>
      </div>

      <div className="napc-builder-shell">
        <aside className="napc-builder-sidebar" aria-label="Inventory">
          <span className="napc-sidebar-label">Inventory</span>
          <button type="button" className="active" onClick={() => setExpandedFloatingButton1(!expandedFloatingButton1)}>
            <Award className="w-4 h-4" />
            <span>Pet Contests</span>
            <b>{stats.total}</b>
          </button>
          <button type="button" onClick={() => openUnifiedPassesCodes()}>
            <Gift className="w-4 h-4" />
            <span>Passes & Codes</span>
            <b>{inventorySummary.couponPassCount + inventorySummary.passCodeCount}</b>
          </button>
          <button type="button" onClick={() => setExpandedFloatingButton3(!expandedFloatingButton3)}>
            <Settings className="w-4 h-4" />
            <span>Operations</span>
          </button>
          <button type="button" onClick={() => navigate(routes.contestVote)}>
            <Radio className="w-4 h-4" />
            <span>Voting</span>
            <b>{stats.active}</b>
          </button>
          <button type="button" onClick={() => navigate(routes.payoutsCenter)}>
            <Trophy className="w-4 h-4" />
            <span>Payouts</span>
            <b>{stats.completed}</b>
          </button>
        </aside>

        <section className="napc-builder-hero">
          <span className="napc-step-label">Step 01 · Build a contest package</span>
          <h2>Select the activations that fit this contest.</h2>
          <p>
            Review contest inventory, sponsor packages, voting tools, passes and reporting from one consistent NAPC surface.
          </p>
        </section>

        <aside className="napc-current-package">
          <div className="napc-package-head">
            <h3>Current package</h3>
            <span>{filteredContests.length} items</span>
          </div>
          <ul>
            <li><strong>Active contests</strong><span>{stats.active} live</span></li>
            <li><strong>Contest revenue</strong><span>${stats.totalRevenue.toFixed(2)}</span></li>
            <li><strong>Net profit</strong><span>${stats.totalProfit.toFixed(2)}</span></li>
          </ul>
          <div className="napc-package-score">
            <span>Impact score</span>
            <b>{Math.max(0, Math.round(stats.totalProfit / 25))}</b>
          </div>
        </aside>
      </div>

      <div
        className="mx-auto py-8"
        style={{
          width: 'min(100%, 1280px)',
          marginLeft: 'auto',
          marginRight: 'auto',
          paddingLeft: isMobile ? '16px' : '48px',
          paddingRight: isMobile ? '16px' : '48px'
        }}
      >
        <div
          className="flex gap-6 mb-8"
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(5, minmax(0, 1fr))',
            gap: isMobile ? '10px' : '24px',
            overflowX: 'visible',
            marginLeft: isMobile ? '0' : '0',
            marginRight: isMobile ? '0' : '0',
            paddingLeft: 0,
            paddingRight: 0,
            width: '100%',
            maxWidth: '100%',
            justifyContent: 'center'
          }}
        >
          <div className="bg-white rounded-lg shadow-sm" style={{
            minWidth: 0,
            flex: 'none',
            border: '1px solid #E7E5EA',
            borderRadius: '14px',
            boxShadow: '0 1px 0 rgba(14,15,18,.04), 0 18px 44px -28px rgba(14,15,18,.28)',
            padding: isMobile ? '14px' : '18px 22px',
            maxWidth: isMobile ? '150px' : 'none',
            width: isMobile ? '100%' : 'auto'
          }}>
            <div className="flex items-center" style={{ display: 'flex', alignItems: 'center', marginLeft: 0 }}>
              <Award className={isMobile ? "w-6 h-6" : "w-6 h-6"} style={{ color: '#306FC8', marginRight: isMobile ? '8px' : '14px' }} />
              <div className="flex flex-col">
                <p className="text-sm text-gray-500 mb-1" style={{ textAlign: 'left', fontSize: isMobile ? '11px' : '14px', marginBottom: isMobile ? '4px' : '2px' }}>{t('dashboard.totalContests')}</p>
                <p className="text-2xl font-bold text-gray-900" style={{ fontSize: isMobile ? '18px' : '20px', lineHeight: '1.2' }}>{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm" style={{
            minWidth: 0,
            flex: 'none',
            border: '1px solid #E7E5EA',
            borderRadius: '14px',
            boxShadow: '0 1px 0 rgba(14,15,18,.04), 0 18px 44px -28px rgba(14,15,18,.28)',
            padding: isMobile ? '14px' : '18px 22px',
            maxWidth: isMobile ? '150px' : 'none',
            width: isMobile ? '100%' : 'auto'
          }}>
            <div className="flex items-center" style={{ display: 'flex', alignItems: 'center', marginLeft: 0 }}>
              <Clock className={isMobile ? "w-6 h-6" : "w-6 h-6"} style={{ color: '#43A878', marginRight: isMobile ? '8px' : '14px' }} />
              <div className="flex flex-col">
                <p className="text-sm text-gray-500 mb-1" style={{ textAlign: 'left', fontSize: isMobile ? '11px' : '14px', marginBottom: isMobile ? '4px' : '2px' }}>{t('dashboard.activeContests')}</p>
                <p className="text-2xl font-bold text-gray-900" style={{ fontSize: isMobile ? '18px' : '20px', lineHeight: '1.2' }}>{stats.active}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm" style={{
            minWidth: 0,
            flex: 'none',
            border: '1px solid #E7E5EA',
            borderRadius: '14px',
            boxShadow: '0 1px 0 rgba(14,15,18,.04), 0 18px 44px -28px rgba(14,15,18,.28)',
            padding: isMobile ? '14px' : '18px 22px',
            maxWidth: isMobile ? '150px' : 'none',
            width: isMobile ? '100%' : 'auto'
          }}>
            <div className="flex items-center" style={{ display: 'flex', alignItems: 'center', marginLeft: 0 }}>
              <Settings className={isMobile ? "w-6 h-6" : "w-6 h-6"} style={{ color: '#F26B42', marginRight: isMobile ? '8px' : '14px' }} />
              <div className="flex flex-col">
                <p className="text-sm text-gray-500 mb-1" style={{ textAlign: 'left', fontSize: isMobile ? '11px' : '14px', marginBottom: isMobile ? '4px' : '2px' }}>{t('dashboard.pendingContests')}</p>
                <p className="text-2xl font-bold text-gray-900" style={{ fontSize: isMobile ? '18px' : '20px', lineHeight: '1.2' }}>{stats.pending}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm" style={{
            minWidth: 0,
            flex: 'none',
            border: '1px solid #E7E5EA',
            borderRadius: '14px',
            boxShadow: '0 1px 0 rgba(14,15,18,.04), 0 18px 44px -28px rgba(14,15,18,.28)',
            padding: isMobile ? '14px' : '18px 22px',
            maxWidth: isMobile ? '150px' : 'none',
            width: isMobile ? '100%' : 'auto'
          }}>
            <div className="flex items-center" style={{ display: 'flex', alignItems: 'center', marginLeft: 0 }}>
              <CheckCircle className={isMobile ? "w-6 h-6" : "w-6 h-6"} style={{ color: '#047857', marginRight: isMobile ? '8px' : '14px' }} />
              <div className="flex flex-col">
                <p className="text-sm text-gray-500 mb-1" style={{ textAlign: 'left', fontSize: isMobile ? '11px' : '14px', marginBottom: isMobile ? '4px' : '2px' }}>{t('menufilters.AvailableContests')}</p>
                <p className="text-2xl font-bold text-gray-900" style={{ fontSize: isMobile ? '18px' : '20px', lineHeight: '1.2', color: '#047857' }}>{stats.available}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm" style={{
            minWidth: 0,
            flex: 'none',
            border: '1px solid #E7E5EA',
            borderRadius: '14px',
            boxShadow: '0 1px 0 rgba(14,15,18,.04), 0 18px 44px -28px rgba(14,15,18,.28)',
            padding: isMobile ? '14px' : '18px 22px',
            maxWidth: isMobile ? '150px' : 'none',
            width: isMobile ? '100%' : 'auto'
          }}>
            <div className="flex items-center" style={{ display: 'flex', alignItems: 'center', marginLeft: 0 }}>
              <XCircle className={isMobile ? "w-6 h-6" : "w-6 h-6"} style={{ color: '#B91C1C', marginRight: isMobile ? '8px' : '14px' }} />
              <div className="flex flex-col">
                <p className="text-sm text-gray-500 mb-1" style={{ textAlign: 'left', fontSize: isMobile ? '11px' : '14px', marginBottom: isMobile ? '4px' : '2px' }}>{t('menufilters.UnavailableContests')}</p>
                <p className="text-2xl font-bold text-gray-900" style={{ fontSize: isMobile ? '18px' : '20px', lineHeight: '1.2', color: '#B91C1C' }}>{stats.unavailable}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm" style={{
            minWidth: 0,
            flex: 'none',
            border: '1px solid #E7E5EA',
            borderRadius: '14px',
            boxShadow: '0 1px 0 rgba(14,15,18,.04), 0 18px 44px -28px rgba(14,15,18,.28)',
            padding: isMobile ? '14px' : '18px 22px',
            maxWidth: isMobile ? '150px' : 'none',
            width: isMobile ? '100%' : 'auto'
          }}>
            <div className="flex items-center" style={{ display: 'flex', alignItems: 'center', marginLeft: 0 }}>
              <DollarSign className={isMobile ? "w-6 h-6" : "w-6 h-6"} style={{ color: '#ED1790', marginRight: isMobile ? '8px' : '14px' }} />
              <div className="flex flex-col">
                <p className="text-sm text-gray-500 mb-1" style={{ textAlign: 'left', fontSize: isMobile ? '11px' : '14px', marginBottom: isMobile ? '4px' : '2px' }}>{t('dashboard.revenue')}</p>
                <p className="text-2xl font-bold text-gray-900" style={{ fontSize: isMobile ? '18px' : '20px', lineHeight: '1.2' }}>${stats.totalRevenue.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm" style={{
            minWidth: 0,
            flex: 'none',
            border: '1px solid #E7E5EA',
            borderRadius: '14px',
            boxShadow: '0 1px 0 rgba(14,15,18,.04), 0 18px 44px -28px rgba(14,15,18,.28)',
            padding: isMobile ? '14px' : '18px 22px',
            maxWidth: isMobile ? '150px' : 'none',
            width: isMobile ? '100%' : 'auto'
          }}>
            <div className="flex items-center" style={{ display: 'flex', alignItems: 'center', marginLeft: 0 }}>
              <TrendingUp className={isMobile ? "w-6 h-6" : "w-6 h-6"} style={{ color: '#43A878', marginRight: isMobile ? '8px' : '14px' }} />
              <div className="flex flex-col">
                <p className="text-sm text-gray-500 mb-1" style={{ textAlign: 'left', fontSize: isMobile ? '11px' : '14px', marginBottom: isMobile ? '4px' : '2px' }}>{t('dashboard.profit')}</p>
                <p className="text-2xl font-bold text-green-600" style={{ fontSize: isMobile ? '18px' : '20px', lineHeight: '1.2', color: '#059669' }}>${stats.totalProfit.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sección de Filtros y Búsqueda Mejorada */}
        <div
          style={{
            width: '100%',
            marginTop: '24px',
            marginBottom: '24px'
          }}
        >
          {/* Buscador de concursos - Ahora más prominente */}
          <div
            style={{
              marginBottom: '20px',
              paddingLeft: isMobile ? '8px' : '0',
              paddingRight: isMobile ? '8px' : '0'
            }}
          >
            <TextField
              fullWidth
              placeholder={t('buttonsforms.searchPlaceholder')}
              value={contestsSearchTerm}
              onChange={(e) => setContestsSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Filter className="w-5 h-5" style={{ color: '#306FC8' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: '100%',
                backgroundColor: 'rgba(255,255,255,.78)',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '18px',
                  boxShadow: '0 1px 0 rgba(14,15,18,.04), 0 18px 44px -28px rgba(14,15,18,.28)',
                  '&:hover fieldset': {
                    borderColor: '#ED1790',
                    borderWidth: '2px',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#ED1790',
                    borderWidth: '2px',
                  },
                },
              }}
            />
          </div>

          {/* Filtros mejorados con mejor diseño visual */}
          <div
            style={{
              backgroundColor: 'rgba(255,255,255,.85)',
              border: '1px solid #E7E5EA',
              borderRadius: '14px',
              boxShadow: '0 1px 0 rgba(14,15,18,.04), 0 18px 44px -28px rgba(14,15,18,.28)',
              padding: isMobile ? '12px 8px' : '16px'
            }}
          >
            <div
              className="flex items-center space-x-4"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                marginBottom: '12px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Filter className="w-5 h-5" style={{ color: '#6B6F7B' }} />
                <span style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '700', color: '#1B1D22' }}>
                  {t('menufilters.Status')}:
                </span>
              </div>
              <Tooltip title={refreshingContests ? t('buttonsforms.refreshingContests') : t('buttonsforms.refreshContests')}>
                <span>
                  <IconButton
                    onClick={() => void fetchContests({ refresh: true })}
                    disabled={refreshingContests || loading}
                    aria-label={t('buttonsforms.refreshContests')}
                    sx={{
                      border: '1px solid #E7E5EA',
                      backgroundColor: 'rgba(255,255,255,.72)',
                      '&:hover': {
                        backgroundColor: 'rgba(237, 23, 144, .06)',
                        borderColor: '#ED1790',
                      },
                    }}
                  >
                    {refreshingContests ? (
                      <CircularProgress size={18} sx={{ color: '#ED1790' }} />
                    ) : (
                      <RefreshCw className="w-4 h-4" style={{ color: '#306FC8' }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </div>
            <div className="flex flex-wrap gap-2" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {[
                { key: 'active', label: t('menufilters.Active') },
                { key: 'all', label: t('menufilters.Allcontests') },
                { key: 'available', label: `${t('menufilters.AvailableContests')} (${stats.available})` },
                { key: 'unavailable', label: `${t('menufilters.UnavailableContests')} (${stats.unavailable})` },
                { key: 'sponsored', label: t('menufilters.Sponsored') },
                { key: 'proposal_linked', label: `${t('menufilters.ProposalLinked')} (${stats.proposalLinked})` },
                { key: 'members_only', label: t('menufilters.Membersonly') },
                { key: 'subscribers_only', label: t('menufilters.Subscribersonly') },
                { key: 'cancelled', label: t('menufilters.Cancel') }
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${filter === key
                      ? 'bg-napc-pink text-white shadow-soft'
                      : 'bg-white text-napc-ink hover:bg-napc-cream border border-napc-line'
                    }`}
                  style={{
                    borderRadius: '20px',
                    padding: '8px 18px',
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: filter === key ? '600' : '500',
                    border: filter === key ? 'none' : '1px solid #E7E5EA',
                    backgroundColor: filter === key ? '#ED1790' : 'rgba(255,255,255,.72)',
                    color: filter === key ? '#ffffff' : '#1B1D22',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: filter === key ? '0 14px 28px -20px rgba(237, 23, 144, .85)' : 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (filter !== key) {
                      e.currentTarget.style.backgroundColor = 'rgba(237, 23, 144, .06)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (filter !== key) {
                      e.currentTarget.style.backgroundColor = 'rgba(255,255,255,.72)';
                    }
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 mx-auto mb-4" style={{ borderBottom: '2px solid #ED1790' }}></div>
              <p style={{ color: '#6B6F7B' }}>Cargando concursos...</p>
            </div>
          </div>
        ) : (
          <div
            style={{
              width: '100%',
              paddingLeft: 0,
              paddingRight: 0
            }}
          >
            {/* Contador de resultados */}
            <div
              style={{
                marginBottom: '20px',
                padding: '12px 16px',
                backgroundColor: 'rgba(255,255,255,.85)',
                borderRadius: '14px',
                border: '1px solid #E7E5EA',
              }}
            >
              <p style={{
                fontSize: isMobile ? '13px' : '14px',
                color: '#6B6F7B',
                margin: 0,
                fontWeight: '500'
              }}>
                {filteredContests.length === 0
                  ? t('buttonsforms.noContestsFound')
                  : filteredContests.length === 1
                    ? `1 ${t('buttonsforms.contestFound')}`
                    : `${filteredContests.length} ${t('buttonsforms.contestsFound')}`
                }
                {contestsSearchTerm && (
                  <span style={{ color: '#ED1790', marginLeft: '8px' }}>
                    {t('buttonsforms.searchResultsFor', { term: contestsSearchTerm })}
                  </span>
                )}
              </p>
            </div>

            {/* Grid de concursos mejorado */}
            <div
              className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
              style={{
                width: '100%',
                justifyItems: 'stretch',
                rowGap: '24px',
                columnGap: '24px'
              }}
            >
              {filteredContests.map(contest => (
                <ContestCard
                  key={`contest-${contest.id}-${locale}`}
                  contest={contest}
                  onViewDetails={setSelectedContest}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onReactivate={handleReactivate}
                  onSponsorClick={handleSponsorClick}
                  onEditContest={handleEditContest}
                  onDeleteContest={handleDeleteContest}
                  onSimulateParticipant={apiMode === 'test' ? ((contestId, entranceFee, contestName) => handleSimulateParticipant(contestId, entranceFee, contestName)) : undefined}
                  onViewContestParticipants={(contestId, contestName) => handleViewContestParticipants(contestId, contestName)}
                  onViewLive={(contestId, contestName) => handleViewLive(contestId, contestName)}
                  onViewWinners={(contestId, contestName) => handleViewWinners(contestId, contestName)}
                  onViewInvoice={(contestId, contestName) => handleViewInvoice(contestId, contestName)}
                  onViewVoteBoosterReport={(contestId, contestName) => handleViewVoteBoosterReport(contestId, contestName)}
                  onViewTagStatistics={(contestId, contestName) => handleViewTagStatistics(contestId, contestName)}
                  onSendSponsor={handleSendSponsor}
                  onSendWinners={handleSendWinners}
                  isMobile={isMobile}
                  hasSponsor={(() => {
                    const hasSponsor = contest.sponsor_id !== null && contest.sponsor_id !== undefined;
                    return hasSponsor;
                  })()}
                />
              ))}
            </div>
          </div>
        )}

        {filteredContests.length === 0 && !loading && (
          <div
            className="text-center py-12"
            style={{
              marginLeft: isMobile ? '0' : '200px',
              marginRight: isMobile ? '0' : '200px',
              paddingLeft: isMobile ? '8px' : '0',
              paddingRight: isMobile ? '8px' : '0'
            }}
          >
            <Award className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No se encontraron concursos que coincidan con los filtros</p>
            {(contestsSearchTerm || filter !== 'all') && (
              <p className="text-gray-400 text-sm mt-2">
                Intenta ajustar los filtros o el término de búsqueda
              </p>
            )}
          </div>
        )}
      </div>

      {selectedContest && (
        <ContestDetailsModal
          contest={selectedContest}
          onClose={() => setSelectedContest(null)}
          onUpdateSponsorship={handleSponsorClick}
        />
      )}

      {showCreateForm && (
        <Box
          component="section"
          aria-label="Formulario fijo para crear concurso"
          sx={{
            position: 'fixed',
            inset: 0,
            height: '100vh',
            zIndex: 1300,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            backgroundColor: '#f5f5f5'
          }}
        >
          <Box sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 20,
            px: isMobile ? 1.5 : 3,
            py: 1,
            m: 0,
            minHeight: 'auto',
            backgroundColor: '#f5f5f5'
          }}>
            <IconButton
              onClick={handleCreateFormClose}
              aria-label="Cerrar formulario de concurso"
              sx={{
                color: '#6b7280',
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                boxShadow: '0 8px 24px -18px rgba(14, 15, 18, 0.45)',
                '&:hover': {
                  color: '#374151',
                  backgroundColor: '#f3f4f6'
                },
                p: 0.5
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{
            position: 'relative',
            px: isMobile ? 0 : 2,
            pb: isMobile ? 0 : 2,
            ...(isMobile && {
              width: '100%',
              maxWidth: '100%',
              margin: 0
            })
          }}>
            {isCreatingContest && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.85)',
                  zIndex: 10,
                  borderRadius: 1
                }}
              >
                <CircularProgress size={48} sx={{ mb: 2 }} />
                <Box component="span" sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                  Creando concurso...
                </Box>
              </Box>
            )}
            <CreateContestForm
              ref={createFormRef}
              key={`create-form-${locale}`}
              sponsorshipData={sponsorshipData}
              onCreated={async (contestData) => {
                setIsCreatingContest(true);
                const newContest = {
                  ...contestData,
                  id: 'contest_' + Date.now(),
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  weighted_average_price: contestData.entrance_fee || 0,
                  current_participants: 0,
                  economics: {
                    total_revenue: 0,
                    participant_revenue: 0,
                    sponsor_cost: 0,
                    profit_margin: 0,
                    total_roi: 0
                  },
                  sponsorship: null
                };

                const maxAttempts = 3;
                const delayMs = 2000;
                let lastError: any = null;
                for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                  try {
                    await contestService.createContest(newContest);
                    lastError = null;
                    break;
                  } catch (err: any) {
                    lastError = err;
                    if (err.isDuplicateName) {
                      setIsCreatingContest(false);
                      void appDialog.alert('El nombre del concurso ya está registrado. Por favor, cambie el nombre.');
                      return;
                    }
                    if (attempt < maxAttempts) {
                      await new Promise(r => setTimeout(r, delayMs));
                    }
                  }
                }
                if (lastError) {
                  setIsCreatingContest(false);
                  void appDialog.alert('Error al crear el concurso. Por favor, inténtalo de nuevo.');
                  return;
                }

                try {
                  console.log('Nuevo concurso creado y guardado:', newContest);


                  // Si está habilitado CouponPasses, generar códigos inmediatamente
                  if (contestData.coupon_passes_enabled) {
                    // Solo se generan los códigos de los tipos de pase seleccionados.
                    const selectedPassTypes = Array.isArray(contestData.pass_types) ? contestData.pass_types : [];
                    const wantCoupon = selectedPassTypes.includes('coupon_pass');
                    const wantContestPass = selectedPassTypes.includes('entry_ticket');
                    // Si la opción es "add_to_existing", asignar QR tags a coupon passes existentes
                    if (wantCoupon && contestData.petflix_qr_integration === 'add_to_existing') {
                      console.log('Asignando QR tags a coupon passes existentes sin QR tag (nuevo concurso)...');

                      try {
                        const assignResult = await couponService.assignQRToExistingCouponPasses(
                          newContest.id,
                          contestData.individual_qr || false,
                          contestData.use_existing_codes || false,
                          contestData.tagType || 'standard',
                          contestData.petflix_qr_type || null,
                          true // petflix_qr_integration está habilitado
                        );

                        console.log('QR tags asignados a coupon passes existentes:', assignResult);

                        // Recargar datos después de asignar QR tags
                        const { couponPasses: couponPassesData, couponCodes: couponCodesData, passCodes: passCodesData, passCodesError } = await loadCouponDashboardData({
                          couponService,
                          forceCouponPassRefresh: true,
                        });
                        if (passCodesError) {
                          console.warn('No se pudieron cargar Pass_Codes después de asignar QR tags:', passCodesError);
                        }

                        setCouponPasses(couponPassesData);
                        setCouponCodes(couponCodesData);
                        setPassCodes(passCodesData);

                        // Recargar links de QR solo para los coupon passes afectados
                        const idsToRefresh = assignResult.debug?.couponPassIdsWithoutQR?.length
                          ? assignResult.debug.couponPassIdsWithoutQR
                          : couponPassesData.filter((cp: any) => cp.contest_id === newContest.id).map((cp: any) => cp.id);
                        const qrData = await fetchCouponPassQRLinksBulk(idsToRefresh);
                        setCouponPassQRLinks(prev => ({ ...prev, ...qrData.linksMap }));
                        setCouponPassQRClaimed(prev => ({ ...prev, ...qrData.claimedMap }));
                        setCouponPassQRTagTypes(prev => ({ ...prev, ...qrData.tagTypesMap }));
                        setCouponPassQRTagCodes(prev => ({ ...prev, ...qrData.tagCodesMap }));

                        console.log(`Se asignaron QR tags a ${assignResult.assignedCount} coupon passes existentes`);
                      } catch (error) {
                        console.error('Error asignando QR tags a coupon passes existentes:', error);
                      }
                    } else if (wantCoupon) {
                      console.log('Generando códigos de cupón para el nuevo concurso...');
                      console.log('ContestData.generatedCouponCodes:', contestData.generatedCouponCodes);
                      console.log('Length:', contestData.generatedCouponCodes?.length);

                      // Si no hay códigos generados, generar algunos por defecto
                      let specificCodes = contestData.generatedCouponCodes || [];
                      if (specificCodes.length === 0) {
                        console.log('No hay códigos generados, creando códigos aleatorios...');
                        const quantity = contestData.coupon_quantity || 10;

                        // Función para generar códigos aleatorios únicos
                        const generateRandomCodes = (count: number): string[] => {
                          const codes: string[] = [];
                          const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

                          while (codes.length < count) {
                            let code = '';
                            // Generar código de 20 caracteres
                            for (let i = 0; i < 20; i++) {
                              code += chars.charAt(Math.floor(Math.random() * chars.length));
                            }

                            // Verificar que no esté duplicado
                            if (!codes.includes(code)) {
                              codes.push(code);
                            }
                          }
                          return codes;
                        };

                        specificCodes = generateRandomCodes(quantity);
                        console.log('Códigos aleatorios generados:', specificCodes);
                      }

                      try {
                        const couponConfig = {
                          specificCodes: specificCodes,
                          maxUsesPerCode: contestData.coupon_uses_per_code || 3
                        };

                        console.log('CouponConfig:', couponConfig);

                        // Esperar un momento para asegurar que el concurso esté en la base de datos
                        await new Promise(resolve => setTimeout(resolve, 1000));

                        const result = await couponService.generateSpecificCouponCodesForContest(
                          newContest.id,
                          specificCodes,
                          couponConfig.maxUsesPerCode,
                          contestData.individual_qr || false,
                          contestData.use_existing_codes || false,
                          contestData.tagType || 'standard',
                          contestData.petflix_qr_type || null,
                          contestData.petflix_qr_integration === 'enabled'
                        );

                        console.log('Códigos de cupón específicos generados exitosamente:', result);

                        // Esperar un momento para asegurar que la caché se haya invalidado en el servidor
                        await new Promise(resolve => setTimeout(resolve, 500));

                        // Recargar datos de cupones y Pass_Codes (forzar actualización sin caché)
                        console.log('=== RECARGANDO DATOS DESPUÉS DE GENERAR CÓDIGOS ===');
                        const { couponPasses: couponPassesData, couponCodes: couponCodesData, passCodes: passCodesData, passCodesError } = await loadCouponDashboardData({
                          couponService,
                          forceCouponPassRefresh: true,
                        });
                        if (passCodesError) {
                          console.warn('No se pudieron cargar Pass_Codes después de generar códigos:', passCodesError);
                        }

                        console.log('CouponPasses recargados:', couponPassesData.length);
                        console.log('CouponCodes recargados:', couponCodesData.length);
                        console.log('Pass_Codes recargados:', passCodesData.length);

                        setCouponPasses(couponPassesData);
                        setCouponCodes(couponCodesData);
                        setPassCodes(passCodesData);

                        // Recargar links de QR solo para los coupon passes recién creados
                        const idsToRefresh = result.couponPassIds?.length
                          ? result.couponPassIds
                          : couponPassesData.filter((cp: any) => cp.contest_id === newContest.id).map((cp: any) => cp.id);
                        console.log('=== RECARGANDO QR LINKS PARA NUEVOS COUPON PASSES ===', idsToRefresh.length);
                        const qrData = await fetchCouponPassQRLinksBulk(idsToRefresh);
                        setCouponPassQRLinks(prev => ({ ...prev, ...qrData.linksMap }));
                        setCouponPassQRClaimed(prev => ({ ...prev, ...qrData.claimedMap }));
                        setCouponPassQRTagTypes(prev => ({ ...prev, ...qrData.tagTypesMap }));
                        setCouponPassQRTagCodes(prev => ({ ...prev, ...qrData.tagCodesMap }));
                        console.log('QR Links recargados para', idsToRefresh.length, 'CouponPasses nuevos');

                        console.log(`Se generaron ${result.totalCodes} códigos específicos para el concurso ${contestData.name}`);

                        // Forzar actualización del modal si está abierto
                        // Incrementar el key para forzar re-render del modal
                        setCouponPassesUpdateKey(prev => prev + 1);
                        console.log('Datos de CouponPasses actualizados. Si el modal está abierto, debería actualizarse automáticamente.');

                        if (showPassCodesModal) {
                          console.log('Modal de Pass_Codes está abierto, forzando actualización...');
                          // Pequeño delay para asegurar que los datos se actualicen
                          setTimeout(() => {
                            setPassCodes([...passCodesData]);
                          }, 500);
                        }

                      } catch (error) {
                        console.error('Error generando códigos de cupón específicos:', error);
                      }
                    } else {
                      console.log('Coupon Pass no seleccionado: se omite la generación de cupones.');
                    }

                    // Generar códigos de Contest Pass (Pass_Codes) solo si el tipo fue seleccionado
                    if (wantContestPass && contestData.generatedPassCodes && contestData.generatedPassCodes.length > 0) {
                      console.log('Generando códigos de Pass_Codes para el nuevo concurso...');
                      console.log('ContestData.generatedPassCodes:', contestData.generatedPassCodes);
                      console.log('Length:', contestData.generatedPassCodes.length);

                      try {
                        const passConfig = {
                          specificCodes: contestData.generatedPassCodes,
                          maxUsesPerCode: contestData.coupon_uses_per_code || 3
                        };

                        console.log('PassConfig:', passConfig);

                        // Esperar un momento para asegurar que el concurso esté en la base de datos
                        await new Promise(resolve => setTimeout(resolve, 1000));

                        const passResult = await couponService.generateSpecificPassCodesForContest(
                          newContest.id,
                          contestData.generatedPassCodes,
                          passConfig.maxUsesPerCode
                        );

                        console.log('Códigos de Pass_Codes específicos generados exitosamente:', passResult);
                        console.log(`Se generaron ${passResult.totalCodes} códigos de Pass_Codes para el concurso ${contestData.name}`);

                      } catch (error) {
                        console.error('Error generando códigos de Pass_Codes específicos:', error);
                      }
                    } else {
                      console.log('No hay códigos de Pass_Codes para generar');
                    }
                  }

                  // Pequeña pausa para evitar 429 (Too Many Requests) en producción tras varias llamadas
                  await new Promise(resolve => setTimeout(resolve, 800));
                  // Recargar la lista de concursos desde el servidor para mostrar todos los datos actualizados
                  console.log('=== RECARGANDO LISTA DE CONCURSOS DESPUÉS DE CREAR ===');
                  const updatedContests = await attachParticipantCounts(await contestService.getAllContests());
                  setContests(updatedContests);
                  console.log('Lista de concursos recargada:', updatedContests.length, 'concursos');

                  if (sponsorshipData) {
                    const createdContest =
                      updatedContests.find((c: any) => c.id === newContest.id) || newContest;
                    setShowCreateForm(false);
                    await proceedToSponsorshipContestLink(createdContest, sponsorshipData);
                    void appDialog.alert('Concurso creado. Confirma la vinculación del patrocinio.');
                    return;
                  }

                  // Cerrar el formulario
                  setShowCreateForm(false);
                  void appDialog.alert('Concurso creado exitosamente');
                } catch (error) {
                  console.error('Error after creating contest (coupons/reload):', error);
                  void appDialog.alert('Error al crear el concurso. Por favor, inténtalo de nuevo.');
                } finally {
                  setIsCreatingContest(false);
                }
              }}
              onCreatedWithCoupons={async (contestData, couponConfig) => {
                // Esta función ya no es necesaria ya que la lógica está en onCreated
                console.log('onCreatedWithCoupons llamado, pero la lógica está en onCreated');
              }}
              onCancel={() => setShowCreateForm(false)}
            />
          </Box>
        </Box>
      )}

      {/* Edit Contest Modal */}
      {showContestForm && (
        <Dialog
          open={showContestForm}
          onClose={handleEditFormClose}
          maxWidth={isMobile ? false : "md"}
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              ...(isMobile && {
                width: '100%',
                maxWidth: '100%',
                margin: 0
              })
            }
          }}
          sx={{
            ...(isMobile && {
              '& .MuiDialog-container': {
                padding: 0,
                margin: 0
              },
              '& .MuiDialog-paper': {
                margin: 0
              }
            })
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            pb: 0,
            pt: 0,
            m: 0,
            minHeight: 'auto'
          }}>
            <IconButton
              onClick={handleEditFormClose}
              sx={{
                color: '#666',
                '&:hover': { backgroundColor: '#f5f5f5' },
                p: 0.5
              }}
            >
              <X size={20} />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            <CreateContestForm
              ref={editFormRef}
              key={`edit-form-${locale}-${editingContest?.id || 'new'}`}
              onCreated={async (contestData) => {
                try {
                  console.log('=== EDITANDO CONCURSO ===');
                  console.log('Datos del concurso:', contestData);
                  console.log('ID del concurso a editar:', editingContest?.id);

                  // Actualizar el concurso en la base de datos
                  if (editingContest?.id) {
                    await contestService.updateContest(editingContest.id, contestData);
                    console.log('Concurso actualizado exitosamente en la base de datos');

                    // Si está habilitado CouponPasses, añadir nuevos coupon passes sin eliminar los existentes
                    if (contestData.coupon_passes_enabled) {
                      // Si la opción es "add_to_existing", asignar QR tags a coupon passes existentes
                      if (contestData.petflix_qr_integration === 'add_to_existing') {
                        console.log('Asignando QR tags a coupon passes existentes sin QR tag...');

                        try {
                          console.log('\n=== INICIANDO ASIGNACIÓN DE QR TAGS A COUPON PASSES EXISTENTES ===');
                          console.log('Contest ID:', editingContest.id);
                          console.log('Individual QR:', contestData.individual_qr || false);
                          console.log('Use Existing Codes:', contestData.use_existing_codes || false);
                          console.log('Tag Type:', contestData.tagType || 'standard');
                          console.log('Owner (petflix_qr_type):', contestData.petflix_qr_type || null);
                          console.log('===============================================================\n');

                          const assignResult = await couponService.assignQRToExistingCouponPasses(
                            editingContest.id,
                            contestData.individual_qr || false,
                            contestData.use_existing_codes || false,
                            contestData.tagType || 'standard',
                            contestData.petflix_qr_type || null,
                            true // petflix_qr_integration está habilitado
                          );

                          console.log('\n=== RESULTADO DE ASIGNACIÓN ===');
                          console.log('QR tags asignados a coupon passes existentes:', JSON.stringify(assignResult, null, 2));
                          console.log('================================\n');

                          // Recargar datos después de asignar QR tags
                          const { couponPasses: couponPassesData, couponCodes: couponCodesData, passCodes: passCodesData, passCodesError } = await loadCouponDashboardData({
                            couponService,
                          });
                          if (passCodesError) {
                            console.warn('No se pudieron cargar Pass_Codes después de asignar QR tags:', passCodesError);
                          }

                          setCouponPasses(couponPassesData);
                          setCouponCodes(couponCodesData);
                          setPassCodes(passCodesData);

                          // Recargar links de QR solo para los coupon passes afectados
                          const idsToRefresh = assignResult.debug?.couponPassIdsWithoutQR?.length
                            ? assignResult.debug.couponPassIdsWithoutQR
                            : couponPassesData.filter((cp: any) => cp.contest_id === editingContest.id).map((cp: any) => cp.id);
                          const qrData = await fetchCouponPassQRLinksBulk(idsToRefresh);
                          setCouponPassQRLinks(prev => ({ ...prev, ...qrData.linksMap }));
                          setCouponPassQRClaimed(prev => ({ ...prev, ...qrData.claimedMap }));
                          setCouponPassQRTagTypes(prev => ({ ...prev, ...qrData.tagTypesMap }));
                          setCouponPassQRTagCodes(prev => ({ ...prev, ...qrData.tagCodesMap }));

                          console.log(`Se asignaron QR tags a ${assignResult.assignedCount} coupon passes existentes`);
                        } catch (error) {
                          console.error('Error asignando QR tags a coupon passes existentes:', error);
                          void appDialog.alert('Concurso actualizado, pero hubo un error al asignar QR tags a coupon passes existentes. Por favor, verifica manualmente.');
                        }
                      } else {
                        console.log('CouponPasses habilitado - añadiendo nuevos coupon passes a los existentes...');

                        try {
                          // NO eliminar los coupon passes existentes, solo añadir los nuevos
                          console.log('Manteniendo coupon passes existentes y añadiendo nuevos...');

                          // Generar nuevos códigos de cupón
                          console.log('Generando nuevos códigos de cupón para el concurso editado...');
                          console.log('ContestData.generatedCouponCodes:', contestData.generatedCouponCodes);
                          console.log('Length:', contestData.generatedCouponCodes?.length);

                          // Si no hay códigos generados, generar algunos por defecto
                          let specificCodes = contestData.generatedCouponCodes || [];
                          if (specificCodes.length === 0) {
                            console.log('No hay códigos generados, creando códigos aleatorios...');
                            const quantity = contestData.coupon_quantity || 10;

                            // Función para generar códigos aleatorios únicos
                            const generateRandomCodes = (count: number): string[] => {
                              const codes: string[] = [];
                              const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

                              while (codes.length < count) {
                                let code = '';
                                // Generar código de 20 caracteres
                                for (let i = 0; i < 20; i++) {
                                  code += chars.charAt(Math.floor(Math.random() * chars.length));
                                }

                                // Verificar que no esté duplicado
                                if (!codes.includes(code)) {
                                  codes.push(code);
                                }
                              }
                              return codes;
                            };

                            specificCodes = generateRandomCodes(quantity);
                            console.log('Códigos aleatorios generados:', specificCodes);
                          }

                          const couponConfig = {
                            specificCodes: specificCodes,
                            maxUsesPerCode: contestData.coupon_uses_per_code || 3
                          };

                          console.log('CouponConfig:', couponConfig);

                          // Esperar un momento para asegurar que el concurso esté actualizado en la base de datos
                          await new Promise(resolve => setTimeout(resolve, 1000));

                          const result = await couponService.generateSpecificCouponCodesForContest(
                            editingContest.id,
                            specificCodes,
                            couponConfig.maxUsesPerCode,
                            contestData.individual_qr || false,
                            contestData.use_existing_codes || false,
                            contestData.tagType || 'standard',
                            contestData.petflix_qr_type || null,
                            contestData.petflix_qr_integration === 'enabled'
                          );

                          console.log('Códigos de cupón específicos generados exitosamente:', result);

                          // Recargar datos de cupones y Pass_Codes
                          console.log('=== RECARGANDO DATOS DESPUÉS DE GENERAR CÓDIGOS ===');
                          const { couponPasses: couponPassesData, couponCodes: couponCodesData, passCodes: passCodesData, passCodesError } = await loadCouponDashboardData({
                            couponService,
                          });
                          if (passCodesError) {
                            console.warn('No se pudieron cargar Pass_Codes después de generar códigos:', passCodesError);
                          }

                          console.log('CouponPasses recargados:', couponPassesData.length);
                          console.log('CouponCodes recargados:', couponCodesData.length);
                          console.log('Pass_Codes recargados:', passCodesData.length);

                          setCouponPasses(couponPassesData);
                          setCouponCodes(couponCodesData);
                          setPassCodes(passCodesData);

                          // Recargar links de QR solo para los coupon passes recién creados
                          const idsToRefresh = result.couponPassIds?.length
                            ? result.couponPassIds
                            : couponPassesData.filter((cp: any) => cp.contest_id === editingContest.id).map((cp: any) => cp.id);
                          console.log('=== RECARGANDO QR LINKS PARA NUEVOS COUPON PASSES ===', idsToRefresh.length);
                          const qrData = await fetchCouponPassQRLinksBulk(idsToRefresh);
                          setCouponPassQRLinks(prev => ({ ...prev, ...qrData.linksMap }));
                          setCouponPassQRClaimed(prev => ({ ...prev, ...qrData.claimedMap }));
                          setCouponPassQRTagTypes(prev => ({ ...prev, ...qrData.tagTypesMap }));
                          setCouponPassQRTagCodes(prev => ({ ...prev, ...qrData.tagCodesMap }));
                          console.log('QR Links recargados para', idsToRefresh.length, 'CouponPasses nuevos');

                          console.log(`Se generaron ${result.totalCodes} códigos específicos para el concurso editado ${contestData.name}`);

                        } catch (error) {
                          console.error('Error eliminando/generando coupon passes:', error);
                          // No fallar la edición si hay error con los coupon passes
                          void appDialog.alert('Concurso actualizado, pero hubo un error al regenerar los coupon passes. Por favor, verifica manualmente.');
                        }
                      }
                    } else {
                      console.log('CouponPasses deshabilitado - no se modifican los coupon passes existentes');
                    }
                  } else {
                    throw new Error('No se encontró el ID del concurso a editar');
                  }

                  // Recargar la lista de concursos desde el servidor para mostrar todos los datos actualizados
                  console.log('=== RECARGANDO LISTA DE CONCURSOS DESPUÉS DE EDITAR ===');
                  const updatedContests = await attachParticipantCounts(await contestService.getAllContests(true));
                  setContests(updatedContests);
                  console.log('Lista de concursos recargada:', updatedContests.length, 'concursos');

                  // Cerrar el modal
                  setShowContestForm(false);
                  setEditingContest(null);
                  void appDialog.alert('Concurso actualizado exitosamente');
                } catch (error) {
                  console.error('Error updating contest:', error);
                  void appDialog.alert('Error al actualizar el concurso. Por favor, inténtalo de nuevo.');
                }
              }}
              onCancel={() => {
                setShowContestForm(false);
                setEditingContest(null);
              }}
              existingContest={editingContest}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Sponsorship Options Modal */}
      {showSponsorshipOptions && (
        <Dialog
          open={showSponsorshipOptions}
          onClose={() => setShowSponsorshipOptions(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
            borderBottom: '1px solid #e5e7eb'
          }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#111827' }}>
                Create Sponsorship
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280', mt: 1 }}>
                Select an existing contest or create a new one
              </Typography>
            </Box>
            <IconButton
              onClick={() => setShowSponsorshipOptions(false)}
              sx={{
                color: '#6b7280',
                '&:hover': {
                  color: '#374151',
                  backgroundColor: '#f3f4f6'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'semibold', color: '#111827', mb: 3 }}>
              Available Contests
            </Typography>

            <TableContainer component={Paper} sx={{ maxHeight: 300, mb: 3 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Contest</TableCell>
                    <TableCell align="center">Phase</TableCell>
                    <TableCell align="center">Prize Pool</TableCell>
                    <TableCell align="center">Participants</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contests.map((contest) => (
                    <TableRow
                      key={contest.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={async () => {
                        setSelectedContestForSponsorship(contest);

                        // Verificar si el concurso ya tiene un patrocinador
                        if (contest.sponsor_id) {
                          try {
                            // Obtener información del patrocinador existente
                            const existingSponsorship = await sponsorshipService.getSponsorshipById(contest.sponsor_id);
                            setExistingSponsor(existingSponsorship);
                            setShowReplaceSponsorConfirmation(true);
                          } catch (error) {
                            console.error('Error obteniendo patrocinador existente:', error);
                            // Si hay error, proceder con el flujo normal
                            setShowContestConfirmation(true);
                          }
                        } else {
                          // Si no tiene patrocinador, mostrar confirmación normal
                          setShowContestConfirmation(true);
                        }
                      }}
                    >
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {contest.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={contest.phase}
                          size="small"
                          color={contest.phase === 'submissions_open' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'success.main' }}>
                          ${(contest.prize_pool_total || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="text.secondary">
                          {contest.current_participants || contest.min_participants}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                startIcon={<Plus />}
                onClick={() => {
                  console.log('=== CREANDO NUEVO CONCURSO ===');
                  console.log('sponsorshipData actual:', sponsorshipData);
                  setShowSponsorshipOptions(false);
                  setShowCreateForm(true);
                }}
                sx={{
                  backgroundColor: '#2563eb',
                  color: 'white',
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  fontSize: isMobile ? '12px' : '14px',
                  minWidth: 'auto',
                  boxShadow: '0 4px 8px rgba(37, 99, 235, 0.3)',
                  '&:hover': {
                    backgroundColor: '#1d4ed8',
                    boxShadow: '0 6px 12px rgba(37, 99, 235, 0.4)',
                  },
                  '&:active': {
                    boxShadow: '0 2px 4px rgba(37, 99, 235, 0.3)',
                  }
                }}
              >
                Create New Contest
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {/* Contest Confirmation Modal */}
      {showContestConfirmation && selectedContestForSponsorship && (
        <Dialog
          open={showContestConfirmation}
          onClose={() => {
            setShowContestConfirmation(false);
            setSelectedContestForSponsorship(null);
            setExistingSponsor(null);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }
          }}
        >
          <DialogContent sx={{ p: 4, textAlign: 'center' }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: '#dbeafe',
              mx: 'auto',
              mb: 3
            }}>
              <Building2 className="w-8 h-8" style={{ color: '#2563eb' }} />
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 'medium', color: '#111827', mb: 2 }}>
              {sponsorshipData?.id ? 'Actualizar Patrocinio' : (existingSponsor ? 'Reemplazar Patrocinador' : 'Confirm Sponsorship')}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              {sponsorshipData?.id
                ? `¿Estás seguro de que quieres actualizar el patrocinio para el concurso ${selectedContestForSponsorship?.name}?`
                : existingSponsor
                  ? `¿Estás seguro de que deseas reemplazar el patrocinador existente y crear un nuevo patrocinio para el concurso ${selectedContestForSponsorship?.name}?`
                  : `Are you sure you want to create a sponsorship for the contest ${selectedContestForSponsorship?.name}?`
              }
            </Typography>

            <DialogActions sx={{ justifyContent: 'center', gap: 3 }}>
              <Button
                onClick={() => {
                  setShowContestConfirmation(false);
                  setSelectedContestForSponsorship(null);
                  setExistingSponsor(null);
                }}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  borderColor: '#6b7280',
                  color: '#6b7280',
                  px: 3,
                  py: 1.5,
                  minWidth: 'auto',
                  '&:hover': {
                    borderColor: '#374151',
                    backgroundColor: '#f9fafb'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  try {
                    if (sponsorshipData && selectedContestForSponsorship) {
                      // Verificar si es una actualización (tiene ID) o creación nueva
                      const isUpdate = sponsorshipData.id && sponsorshipData.id.trim() !== '';

                      // Mapear los datos del frontend a la estructura de la base de datos
                      const finalSponsorshipData = {
                        // Si es actualización, mantener el ID existente; si no, generar uno nuevo
                        ...(isUpdate ? { id: sponsorshipData.id } : { id: 'sponsorship_' + Date.now() }),
                        contest_id: selectedContestForSponsorship.id,
                        sponsor_name: sponsorshipData.sponsor_name,
                        sponsor_short_name: sponsorshipData.sponsor_short_name || '',
                        industry: sponsorshipData.industry || '',
                        sponsor_email: sponsorshipData.contact_email || '',
                        sponsor_phone: sponsorshipData.sponsor_phone || '', // Mantener si existe
                        sponsor_website: sponsorshipData.media_assets?.website_url || '',
                        sponsorship_type: sponsorshipData.sponsorship_type,
                        budget_range: sponsorshipData.budget_range || '', // Mantener si existe
                        target_audience: sponsorshipData.target_audience || '', // Mantener si existe
                        marketing_goals: sponsorshipData.marketing_goals || '', // Mantener si existe
                        // Los campos de media_assets se envían en la estructura original
                        media_assets: sponsorshipData.media_assets || {},
                        social_links: sponsorshipData.social_links || '',
                        media_requirements: sponsorshipData.media_requirements || '', // Mantener si existe
                        visibility_level: sponsorshipData.visibility_level || '', // Mantener si existe
                        // Los campos de schedule se envían en la estructura original
                        schedule: sponsorshipData.schedule || {},
                        total_sponsor_cost: sponsorshipData.total_sponsor_cost || 0,
                        reward_contribution: sponsorshipData.reward_contribution || 0,
                        notes: sponsorshipData.notes || '',
                        status: sponsorshipData.status || t('menufilters.Pending'), // Mantener estado existente
                        // Solo agregar created_at si es creación nueva
                        ...(isUpdate ? {} : { created_at: new Date().toISOString() }),
                        updated_at: new Date().toISOString(),
                        // Campos de contacto
                        contact_name: sponsorshipData.contact_name || '',
                        contact_email: sponsorshipData.contact_email || '',
                        // Los campos de placements se envían en la estructura original
                        placements: sponsorshipData.placements || {},
                        // Campos adicionales
                        brand_guidelines_url: sponsorshipData.media_assets?.brand_guidelines_url || '',
                        landing_url: sponsorshipData.media_assets?.landing_url || '',
                        impressions: sponsorshipData.impressions || 0,
                        engagements: sponsorshipData.engagements || 0,
                        entrance_coverage_percentage: sponsorshipData.entrance_coverage_percentage || 0,
                        // Plataformas personalizadas - asignar contest_id a todas las plataformas que no lo tengan
                        custom_platforms: (sponsorshipData.custom_platforms || []).map((platform: any) => ({
                          ...platform,
                          contest_id: platform.contest_id || selectedContestForSponsorship.id
                        })),
                        // Plataformas base habilitadas
                        enabled_base_platforms: sponsorshipData.enabled_base_platforms || [],
                        // Orden de plataformas
                        platform_order: sponsorshipData.platform_order || [],
                        // Campos de economy diagram
                        sponsor_tier: sponsorshipData.sponsor_tier || null,
                        ambassador_id: sponsorshipData.ambassador_id || null
                      };

                      // Guardar en la base de datos (actualizar o crear según corresponda)
                      let result;
                      if (isUpdate) {
                        result = await sponsorshipService.updateSponsorship(sponsorshipData.id, finalSponsorshipData);
                        void appDialog.alert('Patrocinio actualizado exitosamente');
                      } else {
                        result = await sponsorshipService.createSponsorship(finalSponsorshipData);

                        // Actualizar el sponsor_id del concurso solo si es creación nueva
                        if (result && result.id && finalSponsorshipData.contest_id) {
                          try {
                            await contestService.updateContestSponsor(finalSponsorshipData.contest_id, result.id);
                          } catch (error) {
                            console.error('Error actualizando sponsor del concurso:', error);
                          }
                        }
                        const successMessage = existingSponsor
                          ? 'Patrocinador reemplazado exitosamente'
                          : 'Patrocinio creado exitosamente';
                        void appDialog.alert(
                          finalSponsorshipData.ambassador_id
                            ? `${successMessage}\n${t('ambassadormanagement.ambassadorNotifiedOnAssign')}`
                            : successMessage
                        );
                      }

                      // Recargar concursos y patrocinios para mostrar los cambios
                      try {
                        const updatedContests = await attachParticipantCounts(await contestService.getAllContests());
                        setContests(updatedContests);

                        // Recargar patrocinios
                        const sponsorships = await sponsorshipService.getAllSponsorships();
                        const contestIdsWithSponsors = new Set(sponsorships.map((s: any) => s.contest_id)) as Set<string>;
                        setContestsWithSponsors(contestIdsWithSponsors);

                      } catch (error) {
                        console.error('Error recargando concursos y patrocinios:', error);
                      }
                    } else {
                      void appDialog.alert('Error: No se encontraron los datos del patrocinio o del concurso seleccionado');
                      // NO cerrar los modales si hay error
                      return;
                    }

                    // Solo cerrar todos los modales si la operación fue exitosa
                    setShowContestConfirmation(false);
                    setShowReplaceSponsorConfirmation(false);
                    setShowSponsorshipOptions(false);
                    setShowSponsorshipForm(false); // Cerrar formulario de patrocinio
                    setSelectedContestForSponsorship(null);
                    setSponsorshipData(null);
                    setExistingSponsor(null);
                  } catch (error) {
                    console.error('Error saving sponsorship:', error);
                    void appDialog.alert(`Error al ${sponsorshipData?.id ? 'actualizar' : 'crear'} el patrocinio. Por favor, inténtalo de nuevo.`);
                  }
                }}
                variant="contained"
                sx={{
                  backgroundColor: '#059669',
                  color: 'white',
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  fontSize: isMobile ? '12px' : '14px',
                  minWidth: 'auto',
                  boxShadow: '0 4px 8px rgba(5, 150, 105, 0.3)',
                  '&:hover': {
                    backgroundColor: '#047857',
                    boxShadow: '0 6px 12px rgba(5, 150, 105, 0.4)',
                  },
                  '&:active': {
                    boxShadow: '0 2px 4px rgba(5, 150, 105, 0.3)',
                  }
                }}
              >
                {sponsorshipData?.id ? 'Actualizar' : 'Confirm'}
              </Button>
            </DialogActions>
          </DialogContent>
        </Dialog>
      )}

      {/* Replace Sponsor Confirmation Modal */}
      {showReplaceSponsorConfirmation && selectedContestForSponsorship && existingSponsor && (
        <Dialog
          open={showReplaceSponsorConfirmation}
          onClose={() => {
            setShowReplaceSponsorConfirmation(false);
            setSelectedContestForSponsorship(null);
            setExistingSponsor(null);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }
          }}
        >
          <DialogContent sx={{ p: 4, textAlign: 'center' }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: '#fef3c7',
              mx: 'auto',
              mb: 3
            }}>
              <Building2 className="w-8 h-8" style={{ color: '#f59e0b' }} />
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 'medium', color: '#111827', mb: 2 }}>
              {t('create_sponsorship_form.contest_already_has_sponsor')}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('create_sponsorship_form.contest_already_has_sponsor_assigned', { contestName: selectedContestForSponsorship?.name || '' })}
            </Typography>

            <Box sx={{
              p: 2,
              mb: 3,
              backgroundColor: '#f9fafb',
              borderRadius: 1,
              border: '1px solid #e5e7eb'
            }}>
              <Typography variant="body1" sx={{ fontWeight: 'semibold', color: '#111827' }}>
                {existingSponsor?.sponsor_name || 'Patrocinador sin nombre'}
              </Typography>
              {existingSponsor?.sponsor_short_name && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {existingSponsor.sponsor_short_name}
                </Typography>
              )}
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              {t('create_sponsorship_form.replace_existing_sponsor_question')}
            </Typography>

            <DialogActions sx={{ justifyContent: 'center', gap: 3 }}>
              <Button
                onClick={() => {
                  setShowReplaceSponsorConfirmation(false);
                  setSelectedContestForSponsorship(null);
                  setExistingSponsor(null);
                }}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  borderColor: '#6b7280',
                  color: '#6b7280',
                  px: 3,
                  py: 1.5,
                  minWidth: 'auto',
                  '&:hover': {
                    borderColor: '#374151',
                    backgroundColor: '#f9fafb'
                  }
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  // Cerrar el diálogo de reemplazo y mostrar el diálogo de confirmación normal
                  setShowReplaceSponsorConfirmation(false);
                  setShowContestConfirmation(true);
                }}
                variant="contained"
                sx={{
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  fontSize: isMobile ? '12px' : '14px',
                  minWidth: 'auto',
                  boxShadow: '0 4px 8px rgba(245, 158, 11, 0.3)',
                  '&:hover': {
                    backgroundColor: '#d97706',
                    boxShadow: '0 6px 12px rgba(245, 158, 11, 0.4)',
                  },
                  '&:active': {
                    boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                  }
                }}
              >
                Reemplazar
              </Button>
            </DialogActions>
          </DialogContent>
        </Dialog>
      )}

      {/* Sponsorship Form Modal */}
      {showSponsorshipForm && (
        <Dialog
          open={showSponsorshipForm}
          onClose={() => setShowSponsorshipForm(false)}
          maxWidth={isMobile ? false : "md"}
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              ...(isMobile && {
                width: '99%',
                maxWidth: '99%',
                margin: 0
              })
            }
          }}
          sx={{
            ...(isMobile && {
              '& .MuiDialog-container': {
                padding: 0,
                margin: 0
              },
              '& .MuiDialog-paper': {
                margin: 0
              }
            })
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            pb: 0,
            pt: 0,
            m: 0,
            minHeight: 'auto'
          }}>
            <IconButton
              onClick={() => setShowSponsorshipForm(false)}
              sx={{
                color: '#6b7280',
                '&:hover': {
                  color: '#374151',
                  backgroundColor: '#f3f4f6'
                },
                p: 0.5
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{
            p: 3,
            ...(isMobile && {
              width: '100%',
              maxWidth: '100%',
              margin: 0,
              paddingLeft: '16px',
              paddingRight: '16px'
            })
          }}>
            <CreateSponsorshipForm
              onCancel={() => setShowSponsorshipForm(false)}
              onCreated={async (payload) => {
                try {
                  // Cast a any para acceder a propiedades adicionales como id, platform_order, etc.
                  const payloadWithId = payload as any;
                  const isUpdate = payloadWithId.id && payloadWithId.id.trim() !== '';

                  if (isUpdate) {
                    // Actualizar directamente el patrocinador existente
                    if (!selectedContestForSponsorship) {
                      void appDialog.alert('Error: No se encontró el concurso asociado');
                      return;
                    }

                    // Mapear los datos del frontend a la estructura de la base de datos
                    // Usar payloadWithId para todas las propiedades ya que puede tener campos adicionales
                    const finalSponsorshipData = {
                      id: payloadWithId.id,
                      contest_id: selectedContestForSponsorship.id,
                      sponsor_name: payload.sponsor_name,
                      sponsor_short_name: payload.sponsor_short_name || '',
                      industry: payload.industry || '',
                      sponsor_email: payload.contact_email || '',
                      sponsor_phone: (payloadWithId as any).sponsor_phone || '',
                      sponsor_website: payload.media_assets?.website_url || '',
                      sponsorship_type: payload.sponsorship_type,
                      budget_range: (payloadWithId as any).budget_range || '',
                      target_audience: (payloadWithId as any).target_audience || '',
                      marketing_goals: (payloadWithId as any).marketing_goals || '',
                      media_assets: payload.media_assets || {},
                      social_links: payload.social_links || '',
                      media_requirements: (payloadWithId as any).media_requirements || '',
                      visibility_level: (payloadWithId as any).visibility_level || '',
                      schedule: payload.schedule || {},
                      total_sponsor_cost: payload.total_sponsor_cost || 0,
                      reward_contribution: payload.reward_contribution || 0,
                      notes: payload.notes || '',
                      status: (payloadWithId as any).status || t('menufilters.Pending'),
                      updated_at: new Date().toISOString(),
                      contact_name: payload.contact_name || '',
                      contact_email: payload.contact_email || '',
                      placements: payload.placements || {},
                      brand_guidelines_url: payload.media_assets?.brand_guidelines_url || '',
                      landing_url: payload.media_assets?.landing_url || '',
                      impressions: payload.impressions || 0,
                      engagements: payload.engagements || 0,
                      entrance_coverage_percentage: payload.entrance_coverage_percentage || 0,
                      custom_platforms: (payload.custom_platforms || []).map((platform: any) => ({
                        ...platform,
                        contest_id: platform.contest_id || selectedContestForSponsorship.id
                      })),
                      enabled_base_platforms: payload.enabled_base_platforms || [],
                      platform_order: (payloadWithId as any).platform_order || [],
                      sponsor_tier: payload.sponsor_tier || null,
                      ambassador_id: payload.ambassador_id || null
                    };

                    // Actualizar el patrocinador
                    await sponsorshipService.updateSponsorship(payloadWithId.id, finalSponsorshipData);
                    void appDialog.alert('Patrocinio actualizado exitosamente');

                    // Recargar concursos y patrocinios
                    try {
                      const updatedContests = await attachParticipantCounts(await contestService.getAllContests());
                      setContests(updatedContests);

                      const sponsorships = await sponsorshipService.getAllSponsorships();
                      const contestIdsWithSponsors = new Set(sponsorships.map((s: any) => s.contest_id)) as Set<string>;
                      setContestsWithSponsors(contestIdsWithSponsors);
                    } catch (error) {
                      console.error('Error recargando concursos y patrocinios:', error);
                    }

                    // Cerrar el formulario después de actualizar
                    setShowSponsorshipForm(false);
                    setSelectedContestForSponsorship(null);
                    setSponsorshipData(null);
                  } else {
                    const linkedContest = selectedContestForSponsorship || selectedContest;
                    if (linkedContest) {
                      await proceedToSponsorshipContestLink(linkedContest, payload);
                    } else {
                      setSponsorshipData(payload);
                      setShowSponsorshipOptions(true);
                    }
                  }

                } catch (error) {
                  console.error('Error processing sponsorship:', error);
                  const payloadWithId = payload as any;
                  void appDialog.alert(`Error al ${payloadWithId.id ? 'actualizar' : 'procesar'} el patrocinio. Por favor, inténtalo de nuevo.`);
                }
              }}
              onShowContestOptions={() => {
                if (!selectedContestForSponsorship && !selectedContest) {
                  setShowSponsorshipOptions(true);
                }
              }}
              selectedContest={selectedContestForSponsorship || selectedContest}
              existingSponsorship={sponsorshipData}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Sponsor Modal */}
      {showSponsorModal && selectedSponsor && (
        <Dialog
          open={showSponsorModal}
          onClose={() => setShowSponsorModal(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
            borderBottom: '1px solid #e5e7eb'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Building2 className="w-5 h-5" />
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#111827' }}>
                {t('sponsordata.title')}
              </Typography>
            </Box>
            <IconButton
              onClick={() => setShowSponsorModal(false)}
              sx={{
                color: '#6b7280',
                '&:hover': { backgroundColor: '#f3f4f6' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Información básica */}
              <Card sx={{ backgroundColor: '#f8fafc' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: '#1f2937', fontWeight: 'bold' }}>
                    {t('sponsordata.sponsorInformation')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {t('sponsordata.sponsorName')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {selectedSponsor.sponsor_name || '—'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {t('sponsordata.industry')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {selectedSponsor.industry || '—'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {t('sponsordata.sponsorshipType')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium', textTransform: 'capitalize' }}>
                        {selectedSponsor.sponsorship_type?.replace('_', ' ') || '—'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Información financiera */}
              <Card sx={{ backgroundColor: '#f0f9ff' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: '#1f2937', fontWeight: 'bold' }}>
                    {t('sponsordata.financialInformation')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {t('sponsordata.rewardContribution')}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#059669' }}>
                        ${selectedSponsor.reward_contribution?.toFixed(2) || '0.00'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {t('sponsordata.totalSponsorCost')}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#dc2626' }}>
                        ${selectedSponsor.total_sponsor_cost?.toFixed(2) || '0.00'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Información de contacto */}
              <Card sx={{ backgroundColor: '#fef3c7' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: '#1f2937', fontWeight: 'bold' }}>
                    {t('sponsordata.contactInformation')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {t('sponsordata.email')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {selectedSponsor.sponsor_email || '—'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {t('sponsordata.phone')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {selectedSponsor.sponsor_phone || '—'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {t('sponsordata.website')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {selectedSponsor.sponsor_website || '—'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Información de Tags */}
              <Card sx={{ backgroundColor: '#fef3c7' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ color: '#1f2937', fontWeight: 'bold' }}>
                        {t('sponsordata.physicalTagsAssigned')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('sponsordata.physicalTagsDescription')}
                      </Typography>
                    </Box>
                    <Button
                      onClick={() => selectedSponsor?.id && handleViewSponsorTags(selectedSponsor.id)}
                      variant="outlined"
                      startIcon={<QrCode className="w-4 h-4" />}
                      disabled={loadingSponsorTags || !selectedSponsor?.id}
                      sx={{
                        borderColor: '#f59e0b',
                        color: '#f59e0b',
                        '&:hover': {
                          borderColor: '#d97706',
                          backgroundColor: '#fef3c7'
                        }
                      }}
                    >
                      {loadingSponsorTags ? t('sponsordata.loading') : t('sponsordata.viewTags')}
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedSponsor.sponsor_tier ? (
                      <>
                        {(() => {
                          const tierConfig: { [key: string]: { sponsorTags: number; ambassadorTags: number } } = {
                            'gold': { sponsorTags: 50, ambassadorTags: 100 },
                            'silver': { sponsorTags: 30, ambassadorTags: 50 },
                            'bronze': { sponsorTags: 12, ambassadorTags: 20 }
                          };
                          const config = tierConfig[selectedSponsor.sponsor_tier.toLowerCase()] || { sponsorTags: 0, ambassadorTags: 0 };
                          return (
                            <>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('sponsordata.tagsSponsor')}
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                  {config.sponsorTags}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('sponsordata.tagsAmbassador')}
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                  {config.ambassadorTags}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('sponsordata.tier')}
                                </Typography>
                                <Chip
                                  label={selectedSponsor.sponsor_tier.toUpperCase()}
                                  size="small"
                                  sx={{
                                    backgroundColor: selectedSponsor.sponsor_tier === 'gold' ? '#fbbf24' :
                                      selectedSponsor.sponsor_tier === 'silver' ? '#94a3b8' : '#cd7f32',
                                    color: 'white',
                                    fontWeight: 'bold'
                                  }}
                                />
                              </Box>
                            </>
                          );
                        })()}
                      </>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('sponsordata.tagsReceived')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {selectedSponsor.physical_tags_received || 0}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {t('sponsordata.digitalPasses')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {selectedSponsor.digital_passes_received ? t('sponsordata.unlimited') : 'N/A'}
                        </Typography>
                      </Box>
                      <Button
                        onClick={() => selectedSponsor?.id && handleViewSponsorDigitalPasses(selectedSponsor)}
                        variant="outlined"
                        startIcon={<Ticket className="w-4 h-4" />}
                        disabled={loadingSponsorDigitalPasses || !selectedSponsor?.id}
                        size="small"
                        sx={{
                          borderColor: '#9c27b0',
                          color: '#9c27b0',
                          '&:hover': {
                            borderColor: '#7b1fa2',
                            backgroundColor: '#f3e5f5'
                          }
                        }}
                      >
                        {loadingSponsorDigitalPasses ? t('sponsordata.loading') : t('sponsordata.viewDigitalPasses')}
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Notas adicionales */}
              {selectedSponsor.notes && (
                <Card sx={{ backgroundColor: '#f3f4f6' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: '#1f2937', fontWeight: 'bold' }}>
                      {t('sponsordata.additionalNotes')}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#4b5563' }}>
                      {selectedSponsor.notes}
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          </DialogContent>

          <DialogActions sx={{ justifyContent: 'space-between', p: 3, gap: 2 }}>
            <Button
              onClick={async () => {
                try {
                  // Cerrar el modal de vista
                  setShowSponsorModal(false);
                  // Configurar el concurso seleccionado para el formulario
                  const contest = contests.find(c => c.sponsor_id === selectedSponsor.id);
                  if (contest) {
                    setSelectedContestForSponsorship(contest);
                  }
                  // Configurar los datos del patrocinio existente
                  setSponsorshipData(selectedSponsor);
                  // Abrir el formulario de crear patrocinio en modo edición
                  setShowSponsorshipForm(true);
                  setShowSponsorshipOptions(false);
                } catch (error) {
                  console.error('Error abriendo formulario de edición:', error);
                  void appDialog.alert(t('sponsordata.errorOpeningForm'));
                }
              }}
              variant="contained"
              startIcon={<Edit className="w-4 h-4" />}
              sx={{
                backgroundColor: '#2563eb',
                color: 'white',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#1d4ed8'
                }
              }}
            >
              {t('sponsordata.editSponsor')}
            </Button>
            <Button
              onClick={() => setShowSponsorModal(false)}
              variant="contained"
              sx={{
                backgroundColor: '#6b7280',
                color: 'white',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#4b5563'
                }
              }}
            >
              {t('buttonsforms.Close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para Digital Passes de Sponsor */}
      {showSponsorDigitalPassesModal && selectedSponsorForDigitalPasses && (
        <Dialog
          open={showSponsorDigitalPassesModal}
          onClose={() => {
            setShowSponsorDigitalPassesModal(false);
            setSelectedSponsorForDigitalPasses(null);
            setSponsorDigitalPasses([]);
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Ticket className="w-5 h-5" style={{ color: '#9c27b0' }} />
              Digital Passes - {selectedSponsorForDigitalPasses.sponsor_name || selectedSponsorForDigitalPasses.name}
            </Box>
            <IconButton onClick={() => {
              setShowSponsorDigitalPassesModal(false);
              setSelectedSponsorForDigitalPasses(null);
              setSponsorDigitalPasses([]);
            }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {loadingSponsorDigitalPasses ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#6b7280' }}>
                  Cargando digital passes...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2, mb: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Sponsor:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedSponsorForDigitalPasses.sponsor_name || selectedSponsorForDigitalPasses.name || 'N/A'}
                    {selectedSponsorForDigitalPasses.sponsor_email && (
                      <span style={{ color: '#6b7280', marginLeft: '8px' }}>
                        ({selectedSponsorForDigitalPasses.sponsor_email})
                      </span>
                    )}
                  </Typography>
                </Box>

                {sponsorDigitalPasses.length === 0 ? (
                  <Alert severity="info">
                    Este sponsor no tiene digital passes asignados.
                  </Alert>
                ) : (
                  <>
                    {/* Resumen */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Card sx={{ flex: '1 1 calc(33% - 16px)', minWidth: '200px', p: 2, backgroundColor: '#f3e8ff' }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                          Total Digital Passes
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                          {(() => {
                            // Agrupar por concurso para evitar duplicar códigos del mismo concurso
                            const contestCodesMap = new Map();
                            sponsorDigitalPasses.forEach((pass: any) => {
                              if (!contestCodesMap.has(pass.contest_id)) {
                                contestCodesMap.set(pass.contest_id, pass.digital_total_codes || 0);
                              }
                            });
                            return Array.from(contestCodesMap.values()).reduce((sum: number, codes: number) => sum + codes, 0);
                          })()}
                        </Typography>
                      </Card>
                      <Card sx={{ flex: '1 1 calc(33% - 16px)', minWidth: '200px', p: 2, backgroundColor: '#ecfdf5' }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                          Códigos Generados
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                          {(() => {
                            // Agrupar por concurso para evitar duplicar códigos del mismo concurso
                            const contestCodesMap = new Map();
                            sponsorDigitalPasses.forEach((pass: any) => {
                              if (!contestCodesMap.has(pass.contest_id)) {
                                contestCodesMap.set(pass.contest_id, pass.total_codes || 0);
                              }
                            });
                            return Array.from(contestCodesMap.values()).reduce((sum: number, codes: number) => sum + codes, 0);
                          })()}
                        </Typography>
                      </Card>
                      <Card sx={{ flex: '1 1 calc(33% - 16px)', minWidth: '200px', p: 2, backgroundColor: '#fef3c7' }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                          Códigos Usados
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#d97706' }}>
                          {(() => {
                            // Agrupar por concurso para evitar duplicar códigos del mismo concurso
                            const contestCodesMap = new Map();
                            sponsorDigitalPasses.forEach((pass: any) => {
                              if (!contestCodesMap.has(pass.contest_id)) {
                                contestCodesMap.set(pass.contest_id, pass.used_codes || 0);
                              }
                            });
                            return Array.from(contestCodesMap.values()).reduce((sum: number, codes: number) => sum + codes, 0);
                          })()}
                        </Typography>
                      </Card>
                    </Box>

                    {/* Lista de Digital Passes */}
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2, mb: 2 }}>
                      Digital Passes Asignados
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Concurso</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Nombre</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Tipo</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Códigos Generados</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Códigos Usados</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Códigos Disponibles</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Fecha de Creación</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sponsorDigitalPasses.map((pass: any) => (
                            <TableRow key={pass.id}>
                              <TableCell>{pass.contest_name || pass.contest_id || 'N/A'}</TableCell>
                              <TableCell>{pass.name || 'N/A'}</TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                <Chip
                                  label={pass.is_unlimited ? 'Ilimitado' : 'Limitado'}
                                  size="small"
                                  color={pass.is_unlimited ? 'success' : 'default'}
                                />
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>{pass.total_codes || 0}</TableCell>
                              <TableCell sx={{ textAlign: 'center', color: '#d97706', fontWeight: 'medium' }}>
                                {pass.used_codes || 0}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center', color: '#10b981', fontWeight: 'medium' }}>
                                {pass.available_codes || 0}
                              </TableCell>
                              <TableCell>
                                {pass.created_at ? new Date(pass.created_at).toLocaleDateString() : 'N/A'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={() => {
                setShowSponsorDigitalPassesModal(false);
                setSelectedSponsorForDigitalPasses(null);
                setSponsorDigitalPasses([]);
              }}
              variant="outlined"
            >
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para mostrar lista de CouponPasses */}
      {showCouponPassesModal && (
        <Dialog
          key={`coupon-passes-modal-${couponPassesUpdateKey}`}
          open={showCouponPassesModal}
          onClose={() => {
            setShowCouponPassesModal(false);
            setCouponPassesSearchTerm('');
          }}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            borderBottom: '1px solid #e5e7eb',
            m: 0,
            minHeight: 'auto'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#111827' }}>
                Passes &amp; Codes
              </Typography>
              <Button
                onClick={async () => {
                  try {
                    console.log('=== ACTUALIZANDO DATOS MANUALMENTE ===');
                    const [couponPassesData, couponCodesData] = await Promise.all([
                      couponService.getAllCouponPasses(true), // forzar actualización sin caché
                      couponService.getAllCouponCodes()
                    ]);

                    setCouponPasses(couponPassesData);
                    setCouponCodes(couponCodesData);

                    // Cargar links de QR con endpoint bulk (evita 429)
                    const qrData = await fetchCouponPassQRLinksBulk(couponPassesData.map((cp: any) => cp.id));
                    setCouponPassQRLinks(qrData.linksMap);
                    setCouponPassQRClaimed(qrData.claimedMap);
                    setCouponPassQRTagTypes(qrData.tagTypesMap);
                    setCouponPassQRTagCodes(qrData.tagCodesMap);
                    try {
                      const passCodesData = await couponService.getAllPassCodes();
                      setPassCodes(passCodesData);
                      // Recargar tokens incluyendo concursos con Contest Pass
                      await loadContestTokensForCouponPasses(couponPassesData, passCodesData);
                    } catch (passError) {
                      console.warn('No se pudieron actualizar Pass_Codes:', passError);
                    }
                    console.log('Datos actualizados manualmente');
                  } catch (error) {
                    console.error('Error actualizando datos:', error);
                  }
                }}
                variant="outlined"
                size="small"
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  py: 0.5,
                  fontSize: '0.75rem'
                }}
              >
                {t('buttonsforms.Update')}
              </Button>
            </Box>
            <IconButton
              onClick={() => {
                setShowCouponPassesModal(false);
                setExpandedCouponPass(null);
                setExpandedContest(null);
                setCouponPassesSearchTerm('');
              }}
              sx={{
                color: '#6b7280',
                '&:hover': {
                  backgroundColor: '#f3f4f6'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {(couponPasses.length === 0 && passCodes.length === 0) ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Gift className="w-16 h-16 text-gray-300 mb-4" />
                <Typography variant="h6" sx={{ color: '#6b7280', mb: 1 }}>
                  No codes available
                </Typography>
                <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                  Generate codes (CouponPass or Contest Pass) for a contest to see them here
                </Typography>
              </Box>
            ) : (
              <Box>
                {/* Buscador de CouponPasses */}
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder={t('buttonsforms.searchPlaceholder')}
                    value={couponPassesSearchTerm}
                    onChange={(e) => setCouponPassesSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Filter className="w-5 h-5" style={{ color: '#6b7280' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#2563eb',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2563eb',
                        },
                      },
                    }}
                  />
                </Box>

                {/* System tabs: CouponPass vs Contest Pass (fast switching) */}
                <Tabs
                  value={unifiedSystemFilter}
                  onChange={(_e, val) => setUnifiedSystemFilter(val)}
                  TabIndicatorProps={{
                    sx: {
                      height: 3,
                      backgroundColor: unifiedSystemFilter === 'coupon' ? '#9a3412' : '#065f46',
                    },
                  }}
                  sx={{ mb: 2, borderBottom: '1px solid #e5e7eb', minHeight: 'auto' }}
                >
                  <Tab
                    value="coupon"
                    icon={<Tag className="w-4 h-4" />}
                    iconPosition="start"
                    label={`CouponPass (${couponCodes.length})`}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 'bold',
                      minHeight: 48,
                      gap: 0.75,
                      '&.Mui-selected': { color: '#9a3412' },
                    }}
                  />
                  <Tab
                    value="pass"
                    icon={<Ticket className="w-4 h-4" />}
                    iconPosition="start"
                    label={`Contest Pass (${passCodes.length})`}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 'bold',
                      minHeight: 48,
                      gap: 0.75,
                      '&.Mui-selected': { color: '#065f46' },
                    }}
                  />
                </Tabs>

                {/* Secondary filters: Type / Status */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 'bold', textTransform: 'uppercase' }}>Type</Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={unifiedKindFilter}
                      onChange={(_e, val) => { if (val) setUnifiedKindFilter(val); }}
                    >
                      <ToggleButton value="all">All</ToggleButton>
                      <ToggleButton value="printed">Printed</ToggleButton>
                      <ToggleButton value="digital">Digital</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 'bold', textTransform: 'uppercase' }}>Status</Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={unifiedStatusFilter}
                      onChange={(_e, val) => { if (val) setUnifiedStatusFilter(val); }}
                    >
                      <ToggleButton value="all">All</ToggleButton>
                      <ToggleButton value="available">Available</ToggleButton>
                      <ToggleButton value="used">Used</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>

                {unifiedSystemFilter !== 'pass' && (
                  <Box sx={{ mb: 3, p: 2, borderRadius: 2, border: '1px solid #f3e8df', borderLeft: '4px solid #9a3412', backgroundColor: 'rgba(154,52,18,0.03)' }}>
                    <PassSystemSectionHeader
                      variant="coupon"
                      count={couponCodes.length}
                      contestCount={new Set(couponPasses.map((cp: any) => cp.contest_id)).size}
                    />
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e5e7eb' }}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                            <TableCell sx={{ fontWeight: 'bold', color: '#374151', textAlign: 'center' }}>{t('menufilters.Allcontests')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#374151', textAlign: 'center' }}>{t('menufilters.Codes')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#374151', textAlign: 'center' }}>{t('menufilters.Status')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#374151', textAlign: 'center' }}>{t('menufilters.CreatedAt')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(() => {
                            // Función para verificar si el término de búsqueda es una subsecuencia del texto
                            const isSubsequence = (searchTerm: string, text: string): boolean => {
                              if (!text || !searchTerm) return false;
                              searchTerm = searchTerm.toLowerCase().trim();
                              text = text.toLowerCase().trim();
                              if (searchTerm.length === 0) return true;
                              if (searchTerm.length > text.length) return false;

                              let searchIndex = 0;
                              for (let i = 0; i < text.length && searchIndex < searchTerm.length; i++) {
                                if (text[i] === searchTerm[searchIndex]) {
                                  searchIndex++;
                                }
                              }
                              return searchIndex === searchTerm.length;
                            };

                            // Filtrar CouponPasses según el término de búsqueda
                            const filteredCouponPasses = couponPasses.filter((couponPass) => {
                              // Primero filtrar solo los que tienen códigos de cupón
                              const contestCodes = couponCodes.filter(code => code.coupon_pass_id === couponPass.id);
                              if (contestCodes.length === 0) return false;

                              const searchTerm = couponPassesSearchTerm.trim();
                              if (!searchTerm) return true;

                              const contest = contests.find(c => c.id === couponPass.contest_id);
                              const contestName = (contest ? contest.name : couponPass.contest_id || '').trim();

                              // Buscar en el nombre del concurso
                              if (isSubsequence(searchTerm, contestName)) return true;

                              // Buscar en los códigos de cupón
                              return contestCodes.some(code => {
                                const codeValue = (code.code || '').trim();
                                return isSubsequence(searchTerm, codeValue);
                              });
                            });

                            // Agrupar CouponPasses por contest_id
                            const groupedByContest = filteredCouponPasses.reduce((acc, couponPass) => {
                              const contestId = couponPass.contest_id;
                              if (!acc[contestId]) {
                                const contest = contests.find(c => c.id === contestId);
                                acc[contestId] = {
                                  contest_id: contestId,
                                  contest_name: contest ? contest.name : contestId,
                                  couponPasses: []
                                };
                              }
                              acc[contestId].couponPasses.push(couponPass);
                              return acc;
                            }, {} as Record<string, { contest_id: string; contest_name: string; couponPasses: typeof couponPasses }>);

                            // Función para obtener todos los códigos y QRs de un concurso
                            const getAllCodesAndQRs = (contestGroup: { contest_id: string; contest_name: string; couponPasses: typeof couponPasses }) => {
                              const allCodes: Array<{ code: typeof couponCodes[0]; qr_link: string | null; contest_name: string; contest_id: string; contest_token: string | null; qr_is_claimed: boolean; qr_tag_type: string; qr_activated: boolean | null }> = [];
                              const contestPetCodes = petCodesByContest[contestGroup.contest_id] || {};
                              const contestToken = contestTokens[contestGroup.contest_id]?.token
                                || contestGroup.couponPasses.find((couponPass: any) => couponPass.contest_token)?.contest_token
                                || null;
                              contestGroup.couponPasses.forEach(couponPass => {
                                const contestCodes = couponCodes
                                  .filter(code => code.coupon_pass_id === couponPass.id)
                                  .filter(code => unifiedKindFilter === 'all' || (code.code_kind || '') === unifiedKindFilter)
                                  .filter(code => {
                                    if (unifiedStatusFilter === 'all') return true;
                                    const used = Boolean(code.used_at) || (code.uses_remaining != null && Number(code.uses_remaining) <= 0);
                                    return unifiedStatusFilter === 'used' ? used : !used;
                                  })
                                  .sort((a, b) => {
                                    const orderA = a.claim_order ?? Number.MAX_SAFE_INTEGER;
                                    const orderB = b.claim_order ?? Number.MAX_SAFE_INTEGER;
                                    if (orderA !== orderB) return orderA - orderB;
                                    return String(a.created_at || '').localeCompare(String(b.created_at || ''));
                                  });
                                contestCodes.forEach(code => {
                                  // Buscar el estado de activación en pet-codes usando el código
                                  const petCodeData = contestPetCodes[code.code];
                                  const activated = petCodeData ? petCodeData.activated : null;
                                  const tagCode = couponPassQRTagCodes[couponPass.id];
                                  const fallbackLink = couponPassQRLinks[couponPass.id]
                                    || (tagCode ? `https://internal.petflix.io/pet-found?code=${encodeURIComponent(tagCode)}` : null);
                                  const qrLink = buildContestTokenQrLink({
                                    token: contestToken,
                                    fallbackLink,
                                  });
                                  allCodes.push({
                                    code,
                                    qr_link: qrLink,
                                    contest_name: contestGroup.contest_name,
                                    contest_id: contestGroup.contest_id,
                                    contest_token: contestToken,
                                    qr_is_claimed: couponPassQRClaimed[couponPass.id] || false,
                                    qr_tag_type: couponPassQRTagTypes[couponPass.id] || 'standard',
                                    qr_activated: activated
                                  });
                                });
                              });
                              return allCodes;
                            };

                            if (Object.keys(groupedByContest).length === 0) {
                              return (
                                <TableRow key="no-results">
                                  <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body1" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
                                      {couponPasses.filter((couponPass) => {
                                        const contestCodes = couponCodes.filter(code => code.coupon_pass_id === couponPass.id);
                                        return contestCodes.length > 0;
                                      }).length === 0
                                        ? 'No contests with coupon codes available'
                                        : 'No CouponPasses match your search'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              );
                            }

                            return (Object.values(groupedByContest) as Array<{ contest_id: string; contest_name: string; couponPasses: typeof couponPasses }>).map((contestGroup) => {
                              const allCodesAndQRs = getAllCodesAndQRs(contestGroup);
                              if (allCodesAndQRs.length === 0) return null;
                              const isExpanded = expandedContest === contestGroup.contest_id;
                              const contestTokenValue = contestTokens[contestGroup.contest_id]?.token
                                || contestGroup.couponPasses.find((couponPass: any) => couponPass.contest_token)?.contest_token
                                || null;
                              const isRotatingToken = Boolean(rotatingContestTokens[contestGroup.contest_id]);

                              return (
                                <React.Fragment key={contestGroup.contest_id}>
                                  <TableRow hover>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                        {contestGroup.contest_name}
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#6b7280', fontFamily: 'monospace' }}>
                                        Token: {contestTokenValue || 'Loading...'}
                                      </Typography>
                                      {contestTokenValue && (
                                        <Button
                                          size="small"
                                          variant="text"
                                          disabled={isRotatingToken}
                                          onClick={async () => {
                                            try {
                                              setRotatingContestTokens(prev => ({ ...prev, [contestGroup.contest_id]: true }));
                                              const result = await couponService.rotateContestToken(contestGroup.contest_id);
                                              setContestTokens(prev => ({
                                                ...prev,
                                                [contestGroup.contest_id]: result.token
                                              }));
                                            } catch (error: any) {
                                              console.error('Error actualizando token de concurso:', error);
                                              void appDialog.alert(`Error actualizando token: ${error.message || 'Error desconocido'}`);
                                            } finally {
                                              setRotatingContestTokens(prev => ({ ...prev, [contestGroup.contest_id]: false }));
                                            }
                                          }}
                                          sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 'auto', p: 0.25 }}
                                        >
                                          {isRotatingToken ? 'Updating...' : 'Update token'}
                                        </Button>
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                      {allCodesAndQRs.length > 0 ? (
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          onClick={async () => {
                                            if (isExpanded) {
                                              setExpandedContest(null);
                                            } else {
                                              setExpandedContest(contestGroup.contest_id);
                                              // Consultar pet-codes solo cuando se expande
                                              if (!petCodesByContest[contestGroup.contest_id] && !loadingPetCodes[contestGroup.contest_id]) {
                                                try {
                                                  setLoadingPetCodes(prev => ({ ...prev, [contestGroup.contest_id]: true }));
                                                  // Obtener token de Petflix para la autenticación
                                                  const petflixToken = getPetflixAccessToken();
                                                  const headers: Record<string, string> = {
                                                    'Content-Type': 'application/json'
                                                  };
                                                  if (petflixToken) {
                                                    headers['Authorization'] = `Bearer ${petflixToken}`;
                                                  }

                                                  const response = await fetch(`https://dev.petflix.io/pet-codes/${contestGroup.contest_id}`, {
                                                    headers
                                                  });
                                                  if (response.ok) {
                                                    const petCodesData = await response.json();
                                                    // Crear un mapa por código: { code: { activated: boolean } }
                                                    const codesMap: Record<string, { activated: boolean }> = {};
                                                    if (Array.isArray(petCodesData)) {
                                                      petCodesData.forEach((petCode: any) => {
                                                        if (petCode.code) {
                                                          codesMap[petCode.code] = { activated: petCode.activated || false };
                                                        }
                                                      });
                                                    }
                                                    setPetCodesByContest(prev => ({
                                                      ...prev,
                                                      [contestGroup.contest_id]: codesMap
                                                    }));
                                                  } else {
                                                    console.error(`Error obteniendo pet-codes para concurso ${contestGroup.contest_id}:`, response.statusText);
                                                  }
                                                } catch (error) {
                                                  console.error(`Error consultando pet-codes para concurso ${contestGroup.contest_id}:`, error);
                                                } finally {
                                                  setLoadingPetCodes(prev => ({ ...prev, [contestGroup.contest_id]: false }));
                                                }
                                              }
                                            }
                                          }}
                                          sx={{
                                            textTransform: 'none',
                                            color: '#2563eb',
                                            borderColor: '#2563eb',
                                            '&:hover': {
                                              borderColor: '#1d4ed8',
                                              backgroundColor: '#eff6ff'
                                            }
                                          }}
                                        >
                                          {isExpanded ? 'Hide code list' : 'View code list'}
                                        </Button>
                                      ) : (
                                        <Typography variant="body2" sx={{ color: '#9ca3af', fontStyle: 'italic' }}>
                                          No codes
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                      <Chip
                                        label={t('menufilters.Active')}
                                        color="success"
                                        size="small"
                                      />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                        {contestGroup.couponPasses[0]?.created_at
                                          ? new Date(contestGroup.couponPasses[0].created_at).toLocaleDateString()
                                          : '-'}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                  {isExpanded && allCodesAndQRs.length > 0 && (
                                    <TableRow>
                                      <TableCell colSpan={4} sx={{ py: 2, backgroundColor: '#f9fafb' }}>
                                        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>Contest</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>Order</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>Code</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>Uses</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>Type</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>Tag</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>QR Code</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>QR Status</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {allCodesAndQRs.map((item, index) => (
                                                <TableRow key={`${item.code.id}-${index}`} hover>
                                                  <TableCell sx={{ fontSize: '0.75rem', py: 1, textAlign: 'center' }}>
                                                    {item.contest_name}
                                                  </TableCell>
                                                  <TableCell sx={{ fontSize: '0.75rem', py: 1, textAlign: 'center' }}>
                                                    {item.code.claim_order || index + 1}
                                                  </TableCell>
                                                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>
                                                    {item.code.code}
                                                  </TableCell>
                                                  <TableCell sx={{ fontSize: '0.75rem', py: 1, textAlign: 'center' }}>
                                                    {item.code.uses_remaining || 0} / {item.code.max_uses || 0}
                                                  </TableCell>
                                                  <TableCell sx={{ fontSize: '0.75rem', py: 1, textAlign: 'center' }}>
                                                    {item.code.code_kind === 'printed' ? 'Printed' : item.code.code_kind === 'digital' ? 'Digital' : 'NA'}
                                                  </TableCell>
                                                  <TableCell sx={{ fontSize: '0.75rem', py: 1, textAlign: 'center' }}>
                                                    {item.qr_tag_type || 'standard'}
                                                  </TableCell>
                                                  <TableCell sx={{ py: 1, textAlign: 'center' }}>
                                                    {item.qr_link ? (
                                                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                        <img
                                                          src={`https://api.qrserver.com/v1/create-qr-code/?size=60x60&data=${encodeURIComponent(item.qr_link)}`}
                                                          alt="QR Code"
                                                          loading="lazy"
                                                          style={{
                                                            width: '60px',
                                                            height: '60px',
                                                            cursor: 'pointer',
                                                            border: '1px solid #e5e7eb',
                                                            borderRadius: '4px'
                                                          }}
                                                          onClick={() => setSelectedQRCode(item.qr_link!)}
                                                          title={item.qr_link}
                                                        />
                                                      </Box>
                                                    ) : (
                                                      <Typography variant="caption" sx={{ color: '#9ca3af', fontStyle: 'italic' }}>

                                                      </Typography>
                                                    )}
                                                  </TableCell>
                                                  <TableCell sx={{ py: 1, textAlign: 'center' }}>
                                                    {loadingPetCodes[item.contest_id] ? (
                                                      <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem' }}>
                                                        Loading...
                                                      </Typography>
                                                    ) : item.qr_activated !== null ? (
                                                      <Typography
                                                        variant="caption"
                                                        sx={{
                                                          fontWeight: 'bold',
                                                          color: item.qr_activated ? '#10b981' : '#ef4444',
                                                          fontSize: '0.75rem'
                                                        }}
                                                      >
                                                        {item.qr_activated ? 'Activated' : 'Not activated'}
                                                      </Typography>
                                                    ) : (
                                                      <Typography
                                                        variant="caption"
                                                        sx={{
                                                          fontWeight: 'bold',
                                                          color: item.qr_is_claimed ? '#10b981' : '#ef4444',
                                                          fontSize: '0.75rem'
                                                        }}
                                                      >
                                                        {item.qr_is_claimed ? 'Claimed' : 'Unclaimed'}
                                                      </Typography>
                                                    )}
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </React.Fragment>
                              );
                            });
                          })()}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {unifiedSystemFilter !== 'coupon' && (
                  <Box sx={{ mb: 1, p: 2, borderRadius: 2, border: '1px solid #d8efe6', borderLeft: '4px solid #065f46', backgroundColor: 'rgba(6,95,70,0.03)' }}>
                    <PassSystemSectionHeader
                      variant="pass"
                      count={passCodes.length}
                      contestCount={new Set(passCodes.map((pc: any) => pc.pass_id)).size}
                    />
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e5e7eb' }}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                            <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>{t('menufilters.Allcontests')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>{t('menufilters.Codes')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#374151', textAlign: 'center' }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>{t('menufilters.Status')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>{t('menufilters.CreatedAt')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>{t('buttonsforms.Actions')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(() => {
                            const term = couponPassesSearchTerm.trim().toLowerCase();
                            const filteredPassCodes = passCodes.filter((pc: any) => {
                              if (unifiedKindFilter !== 'all' && (pc.code_kind || '') !== unifiedKindFilter) return false;
                              if (unifiedStatusFilter !== 'all') {
                                const used = Boolean(pc.used_at) || (pc.uses_remaining != null && Number(pc.uses_remaining) <= 0);
                                if (unifiedStatusFilter === 'used' ? !used : used) return false;
                              }
                              if (term) {
                                const contest = contests.find(c => c.id === pc.pass_id);
                                const cname = (contest ? contest.name : pc.pass_id || '').toLowerCase();
                                if (!cname.includes(term) && !String(pc.code || '').toLowerCase().includes(term)) return false;
                              }
                              return true;
                            });

                            const groupedPassCodes = filteredPassCodes.reduce((acc: Record<string, any[]>, pc: any) => {
                              const contestId = pc.pass_id;
                              if (!acc[contestId]) acc[contestId] = [];
                              acc[contestId].push(pc);
                              return acc;
                            }, {} as Record<string, any[]>);

                            const contestGroups = Object.entries(groupedPassCodes);

                            if (contestGroups.length === 0) {
                              return (
                                <TableRow>
                                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body1" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
                                      No Contest Pass codes match the filters
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              );
                            }

                            return contestGroups.map(([contestId, contestPassCodes]) => {
                              const contest = contests.find(c => c.id === contestId);
                              const passCodeList = contestPassCodes as any[];
                              const firstPassCode = passCodeList[0];
                              const kinds = Array.from(new Set(passCodeList.map((c: any) => c.code_kind || '')));
                              const groupKind = kinds.length === 1 ? kinds[0] : 'mixed';
                              const kindLabel = groupKind === 'printed' ? 'Printed' : groupKind === 'digital' ? 'Digital' : groupKind === 'mixed' ? 'Mixed' : 'NA';
                              const isExpanded = expandedPassCode === contestId;
                              // El QR de Contest Pass usa el MISMO formato que CouponPass:
                              // `noahsark://contest-coupon?token=<contestToken>`. El backend
                              // resuelve por token cuál código canjear (claim-next): si el
                              // concurso no tiene cupones disponibles hace fallback a
                              // Pass_Codes. Así el deep link abre la app y queda "disponible"
                              // igual que los CouponPass (el formato `?code=...&contestId=...`
                              // no era reconocido por la app). El link por código solo se
                              // mantiene como fallback cuando el concurso aún no tiene token.
                              const contestTokenValue = contestTokens[contestId]?.token || null;
                              const contestTokenQrLink = buildContestTokenQrLink({ token: contestTokenValue, fallbackLink: null });
                              const sortedPassCodes = [...passCodeList].sort((a: any, b: any) =>
                                String(a.created_at || '').localeCompare(String(b.created_at || ''))
                              );

                              return (
                                <React.Fragment key={contestId}>
                                  <TableRow hover>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                        {contest ? contest.name : contestId}
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#6b7280', fontFamily: 'monospace' }}>
                                        Token: {contestTokenValue || 'Loading...'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setExpandedPassCode(isExpanded ? null : contestId)}
                                        sx={{
                                          textTransform: 'none',
                                          color: '#059669',
                                          borderColor: '#059669',
                                          '&:hover': { borderColor: '#047857', backgroundColor: '#f0fdf4' }
                                        }}
                                      >
                                        {isExpanded ? 'Hide code list' : 'View code list'}
                                      </Button>
                                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#6b7280' }}>
                                        {passCodeList.length} {passCodeList.length === 1 ? 'code' : 'codes'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                      <Typography variant="body2">{kindLabel}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                      <Chip
                                        label={firstPassCode.is_active ? t('menufilters.Active') : t('menufilters.Inactive')}
                                        color={firstPassCode.is_active ? 'success' : 'default'}
                                        size="small"
                                      />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                        {new Date(firstPassCode.created_at).toLocaleDateString()}
                                      </Typography>
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                      <Button
                                        onClick={() => downloadPassCodesCSV(contestId, contest ? contest.name : contestId)}
                                        variant="outlined"
                                        size="small"
                                        startIcon={<FileText className="w-4 h-4" />}
                                        sx={{ color: '#059669', borderColor: '#059669', '&:hover': { backgroundColor: '#f0fdf4', borderColor: '#047857' } }}
                                      >
                                        CSV
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                  {isExpanded && (
                                    <TableRow>
                                      <TableCell colSpan={6} sx={{ py: 2, backgroundColor: '#f9fafb' }}>
                                        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>Order</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>Code</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>Entries</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>Value</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>Type</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>QR Code</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>Status</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {sortedPassCodes.map((passCode: any, index: number) => {
                                                const used = Boolean(passCode.used_at) || (passCode.uses_remaining != null && Number(passCode.uses_remaining) <= 0);
                                                // QR ÚNICO por código (mismo valor que Pet Contest devuelve a PetFlix y
                                                // que PetFlix imprime). Cada Contest Pass debe tener un QR distinto:
                                                // noahsark://contest-coupon?code=<code>&contestId=<id>. El token del
                                                // concurso solo se usa como fallback si aún no hay código/contestId.
                                                const passQrLink =
                                                  buildContestCouponQrLink({ couponCode: passCode.code, contestId, fallbackLink: null })
                                                  || contestTokenQrLink;
                                                // Valor (precio) del Contest Pass. Se define en el pass builder de PetFlix
                                                // y se guarda por código; si falta, cae al entrance_fee del concurso.
                                                const passValueRaw =
                                                  passCode.value != null && passCode.value !== ''
                                                    ? Number(passCode.value)
                                                    : (contest && (contest as any).entrance_fee != null && (contest as any).entrance_fee !== ''
                                                      ? Number((contest as any).entrance_fee)
                                                      : null);
                                                const passValueLabel =
                                                  passValueRaw == null || Number.isNaN(passValueRaw)
                                                    ? '—'
                                                    : `$${passValueRaw.toFixed(2)}`;
                                                return (
                                                  <TableRow key={passCode.id} hover>
                                                    <TableCell sx={{ fontSize: '0.75rem', py: 1, textAlign: 'center' }}>
                                                      {index + 1}
                                                    </TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>
                                                      {passCode.code}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.75rem', py: 1, textAlign: 'center' }}>
                                                      {(passCode.max_uses ?? 0)}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.75rem', py: 1, textAlign: 'center', fontWeight: 'bold', color: '#059669' }}>
                                                      {passValueLabel}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.75rem', py: 1, textAlign: 'center' }}>
                                                      {passCode.code_kind === 'printed' ? 'Printed' : passCode.code_kind === 'digital' ? 'Digital' : 'NA'}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1, textAlign: 'center' }}>
                                                      {passQrLink ? (
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                          <img
                                                            src={`https://api.qrserver.com/v1/create-qr-code/?size=60x60&data=${encodeURIComponent(passQrLink)}`}
                                                            alt="QR Code"
                                                            loading="lazy"
                                                            style={{ width: '60px', height: '60px', cursor: 'pointer', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                                                            onClick={() => setSelectedQRCode(passQrLink)}
                                                            title={passQrLink}
                                                          />
                                                        </Box>
                                                      ) : (
                                                        <Typography variant="caption" sx={{ color: '#9ca3af', fontStyle: 'italic' }}>
                                                          QR pending
                                                        </Typography>
                                                      )}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1, textAlign: 'center' }}>
                                                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: used ? '#ef4444' : '#10b981', fontSize: '0.75rem' }}>
                                                        {used ? t('menufilters.Used') : t('menufilters.Available')}
                                                      </Typography>
                                                    </TableCell>
                                                  </TableRow>
                                                );
                                              })}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </React.Fragment>
                              );
                            });
                          })()}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ justifyContent: 'center', p: 3 }}>
            <Button
              onClick={() => {
                setShowCouponPassesModal(false);
                setExpandedCouponPass(null);
                setExpandedContest(null);
                setExpandedPassCode(null);
                setCouponPassesSearchTerm('');
              }}
              variant="contained"
              sx={{
                backgroundColor: '#6b7280',
                color: 'white',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#4b5563'
                }
              }}
            >
              {t('buttonsforms.Close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para mostrar QR Code ampliado */}
      {selectedQRCode && (
        <Dialog
          open={!!selectedQRCode}
          onClose={() => setSelectedQRCode(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <DialogTitle sx={{
            backgroundColor: '#2563eb',
            color: 'white',
            textAlign: 'center',
            py: 2,
            m: 0,
            minHeight: 'auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
              QR Code
            </Typography>
            <IconButton
              onClick={() => setSelectedQRCode(null)}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
              width: '100%'
            }}>
              <Box sx={{
                p: 3,
                backgroundColor: '#ffffff',
                borderRadius: 2,
                border: '1px solid #e5e7eb',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                maxWidth: '500px'
              }}>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(selectedQRCode)}`}
                  alt="QR Code"
                  style={{
                    width: '100%',
                    maxWidth: '400px',
                    height: 'auto',
                    display: 'block'
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', maxWidth: '400px', wordBreak: 'break-all', px: 2 }}>
                {selectedQRCode}
              </Typography>
            </Box>
          </DialogContent>

          <DialogActions sx={{ justifyContent: 'center', p: 3 }}>
            <Button
              onClick={() => setSelectedQRCode(null)}
              variant="contained"
              sx={{
                backgroundColor: '#6b7280',
                color: 'white',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#4b5563'
                }
              }}
            >
              {t('buttonsforms.Close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para mostrar lista de Pass_Codes */}
      {showPassCodesModal && (
        <Dialog
          open={showPassCodesModal}
          onClose={() => setShowPassCodesModal(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              borderRadius: 2,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <DialogTitle sx={{
            backgroundColor: '#059669',
            color: 'white',
            textAlign: 'center',
            py: 2,
            m: 0,
            minHeight: 'auto'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
                {t('buttonsforms.ListPassCodes')}
              </Typography>
              <Button
                onClick={async () => {
                  try {
                    console.log('=== ACTUALIZANDO DATOS DE PASS_CODES MANUALMENTE ===');
                    const passCodesData = await couponService.getAllPassCodes();
                    setPassCodes(passCodesData);
                    console.log('Datos de Pass_Codes actualizados manualmente');
                  } catch (error) {
                    console.error('Error actualizando Pass_Codes:', error);
                  }
                }}
                variant="outlined"
                size="small"
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                {t('buttonsforms.Update')}
              </Button>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {passCodes.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Gift className="w-16 h-16 text-gray-300 mb-4" />
                <Typography variant="h6" sx={{ color: '#6b7280', mb: 1 }}>
                  No hay Pass_Codes disponibles
                </Typography>
                <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                  Los Pass_Codes aparecerán aquí cuando se creen concursos
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e5e7eb' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                      <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>{t('menufilters.Allcontests')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>{t('menufilters.Name')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>{t('menufilters.Codes')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>{t('menufilters.Status')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>{t('menufilters.CreatedAt')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>{t('buttonsforms.Actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      // Agrupar Pass_Codes por concurso
                      const groupedPassCodes = passCodes.reduce((acc, passCode) => {
                        const contestId = passCode.pass_id;
                        if (!acc[contestId]) {
                          acc[contestId] = [];
                        }
                        acc[contestId].push(passCode);
                        return acc;
                      }, {} as Record<string, any[]>);

                      const contestGroups = Object.entries(groupedPassCodes);

                      if (contestGroups.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                              <Typography variant="body1" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
                                No hay concursos con Pass_Codes disponibles
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      }

                      return contestGroups.map(([contestId, contestPassCodes]) => {
                        const contest = contests.find(c => c.id === contestId);
                        const firstPassCode = (contestPassCodes as any[])[0];

                        return (
                          <TableRow key={contestId} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {contest ? contest.name : contestId}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                Pass_Codes del Concurso {contestId}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {(expandedPassCode === contestId ? (contestPassCodes as any[]) : (contestPassCodes as any[]).slice(0, 3)).map((passCode: any) => (
                                  <Box
                                    key={passCode.id}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      backgroundColor: '#f3f4f6',
                                      padding: '4px 8px',
                                      borderRadius: 1,
                                      fontSize: '12px'
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: 'monospace',
                                        flex: 1
                                      }}
                                    >
                                      {passCode.code}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: passCode.used_at ? '#ef4444' : '#059669',
                                        fontWeight: 'bold',
                                        minWidth: 'fit-content'
                                      }}
                                    >
                                      {passCode.used_at ? t('menufilters.Active') : t('menufilters.Available')}
                                    </Typography>
                                  </Box>
                                ))}
                                {(contestPassCodes as any[]).length > 3 && expandedPassCode !== contestId && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#059669',
                                      cursor: 'pointer',
                                      fontWeight: 'bold',
                                      '&:hover': {
                                        color: '#047857',
                                        textDecoration: 'underline'
                                      }
                                    }}
                                    onClick={() => setExpandedPassCode(contestId)}
                                  >
                                    +{(contestPassCodes as any[]).length - 3} {t('buttonsforms.More')}
                                  </Typography>
                                )}
                                {expandedPassCode === contestId && (contestPassCodes as any[]).length > 3 && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#6b7280',
                                      cursor: 'pointer',
                                      '&:hover': {
                                        color: '#374151',
                                        textDecoration: 'underline'
                                      }
                                    }}
                                    onClick={() => setExpandedPassCode(null)}
                                  >
                                    {t('buttonsforms.Less')}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={firstPassCode.is_active ? t('menufilters.Active') : t('menufilters.Inactive')}
                                color={firstPassCode.is_active ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                {new Date(firstPassCode.created_at).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Button
                                onClick={() => downloadPassCodesCSV(contestId, contest ? contest.name : contestId)}
                                variant="outlined"
                                size="small"
                                startIcon={<FileText className="w-4 h-4" />}
                                sx={{
                                  color: '#059669',
                                  borderColor: '#059669',
                                  '&:hover': {
                                    backgroundColor: '#f0fdf4',
                                    borderColor: '#047857'
                                  }
                                }}
                              >
                                CSV
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>

          <DialogActions sx={{ justifyContent: 'center', p: 3 }}>
            <Button
              onClick={() => {
                setShowPassCodesModal(false);
                setExpandedPassCode(null);
              }}
              variant="contained"
              sx={{
                backgroundColor: '#6b7280',
                color: 'white',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#4b5563'
                }
              }}
            >
              {t('buttonsforms.Close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal de tipo de participante */}
      {selectedContestForParticipant && (
        <ParticipantTypeModal
          isOpen={showParticipantModal}
          onClose={() => {
            setShowParticipantModal(false);
            setSelectedContestForParticipant(null);
          }}
          contestId={selectedContestForParticipant.id}
          entranceFee={selectedContestForParticipant.entranceFee}
          contestName={selectedContestForParticipant.name}
          onSuccess={handleParticipantSuccess}
          onError={handleParticipantError}
        />
      )}

      {/* Modal de Participantes del Concurso Iniciado */}
      {showContestParticipantsModal && selectedContestForParticipants && (
        <Dialog
          open={true}
          onClose={() => {
            setShowContestParticipantsModal(false);
            setParticipantVoteStats(null);
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Users className="w-5 h-5" style={{ color: '#6b7280' }} />
            {t('contestparticipants.title')}: {selectedContestForParticipants.name}
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {/* Sección de Estadísticas Mejoradas */}
            {participantVoteStats && (
              <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Estadísticas Generales */}
                {participantVoteStats.general_stats && (
                  <Card sx={{ p: 2, backgroundColor: '#f9fafb' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                      {t('contestparticipants.generalStats')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">{t('contestparticipants.totalParticipants')}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {participantVoteStats.general_stats.total_participants || 0}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">{t('contestparticipants.totalVotes')}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2563eb' }}>
                          {participantVoteStats.general_stats.total_votes || 0}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">{t('contestparticipants.averageVotes')}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {Math.round(participantVoteStats.general_stats.avg_votes || 0)}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                )}

                {/* Participantes por Tipo de Entrada */}
                {participantVoteStats.participants_by_entry_type && participantVoteStats.participants_by_entry_type.length > 0 && (
                  <Card sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                      {t('contestparticipants.participantsByEntryType')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      {participantVoteStats.participants_by_entry_type.map((entry: any, index: number) => (
                        <Box key={index} sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: entry.entry_type === 'Physical Tag' ? '#eff6ff' : '#f0fdf4', borderRadius: 2 }}>
                          <Typography variant="body2" color="text.secondary">{entry.entry_type}</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: entry.entry_type === 'Physical Tag' ? '#2563eb' : '#059669' }}>
                            {entry.participant_count || 0}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Card>
                )}

                {/* Conversión a Boosters */}
                {participantVoteStats.conversion_to_boosters && (
                  <Card sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                      {t('contestparticipants.conversionToBoosters')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">{t('contestparticipants.totalParticipants')}</Typography>
                          <Tooltip
                            title={
                              <Box sx={{ p: 1 }}>
                                <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                                  {t('contestparticipants.totalParticipantsTooltip')}
                                </Typography>
                              </Box>
                            }
                            arrow
                            placement="top"
                            PopperProps={{
                              sx: {
                                '& .MuiTooltip-tooltip': {
                                  maxWidth: '350px',
                                  backgroundColor: '#1f2937',
                                  fontSize: '0.875rem'
                                }
                              }
                            }}
                          >
                            <IconButton
                              size="small"
                              sx={{
                                p: 0,
                                color: '#6b7280',
                                '&:hover': { color: '#2563eb' }
                              }}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {participantVoteStats.conversion_to_boosters.total_participants || 0}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">{t('contestparticipants.boosterBuyers')}</Typography>
                          <Tooltip
                            title={
                              <Box sx={{ p: 1 }}>
                                <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                                  {t('contestparticipants.boosterBuyersTooltip')}
                                </Typography>
                              </Box>
                            }
                            arrow
                            placement="top"
                            PopperProps={{
                              sx: {
                                '& .MuiTooltip-tooltip': {
                                  maxWidth: '350px',
                                  backgroundColor: '#1f2937',
                                  fontSize: '0.875rem'
                                }
                              }
                            }}
                          >
                            <IconButton
                              size="small"
                              sx={{
                                p: 0,
                                color: '#6b7280',
                                '&:hover': { color: '#2563eb' }
                              }}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f59e0b' }}>
                          {participantVoteStats.conversion_to_boosters.booster_buyers || 0}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">{t('contestparticipants.conversionRate')}</Typography>
                          <Tooltip
                            title={
                              <Box sx={{ p: 1 }}>
                                <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                                  {t('contestparticipants.conversionRateTooltip')}
                                </Typography>
                              </Box>
                            }
                            arrow
                            placement="top"
                            PopperProps={{
                              sx: {
                                '& .MuiTooltip-tooltip': {
                                  maxWidth: '350px',
                                  backgroundColor: '#1f2937',
                                  fontSize: '0.875rem'
                                }
                              }
                            }}
                          >
                            <IconButton
                              size="small"
                              sx={{
                                p: 0,
                                color: '#6b7280',
                                '&:hover': { color: '#2563eb' }
                              }}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#059669' }}>
                          {participantVoteStats.conversion_to_boosters.conversion_rate?.toFixed(1) || 0}%
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                )}

                {/* Top Votados */}
                {participantVoteStats.top_voted && participantVoteStats.top_voted.length > 0 && (
                  <Card sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                      {t('contestparticipants.top10MostVoted')}
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('contestparticipants.rank')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('contestparticipants.name')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('contestparticipants.votesHeader')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {participantVoteStats.top_voted.map((participant: any, index: number) => (
                            <TableRow key={participant.id}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>{participant.participant_name}</TableCell>
                              <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>{participant.votes || 0}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Card>
                )}

                {/* Gráfico de Votos Diarios (simplificado) */}
                {participantVoteStats.daily_votes && participantVoteStats.daily_votes.length > 0 && (
                  <Card sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                      {t('contestparticipants.votesPerDay')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {participantVoteStats.daily_votes.map((day: any, index: number) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="body2" sx={{ minWidth: '100px' }}>
                            {new Date(day.vote_date).toLocaleDateString()}
                          </Typography>
                          <Box sx={{ flex: 1, height: '24px', backgroundColor: '#e5e7eb', borderRadius: 1, position: 'relative', overflow: 'hidden' }}>
                            <Box
                              sx={{
                                height: '100%',
                                backgroundColor: '#2563eb',
                                width: `${Math.min((day.votes_count / Math.max(...participantVoteStats.daily_votes.map((d: any) => d.votes_count))) * 100, 100)}%`
                              }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ minWidth: '50px', textAlign: 'right', fontWeight: 'bold' }}>
                            {day.votes_count}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Card>
                )}
              </Box>
            )}

            {contestParticipants.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
                  {t('contestparticipants.noParticipants')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 3,
                p: 2
              }}>
                {contestParticipants.map((participant, index) => {
                  const firstMedia = participant.mediaItems && participant.mediaItems.length > 0
                    ? participant.mediaItems[0]
                    : null;

                  // Función para formatear votos (ej: 87300 -> "87.3K+")
                  const formatVotes = (votes: number): string => {
                    if (votes >= 1000000) {
                      const millions = votes / 1000000;
                      return `${millions.toFixed(1)}M+`;
                    } else if (votes >= 1000) {
                      const thousands = votes / 1000;
                      return `${thousands.toFixed(1)}K+`;
                    }
                    return `${votes}+`;
                  };

                  return (
                    <Box
                      key={participant.id}
                      sx={{
                        position: 'relative',
                        width: '100%',
                        aspectRatio: '1 / 1',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        backgroundColor: '#f3f4f6',
                        cursor: firstMedia ? 'pointer' : 'default',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)'
                        }
                      }}
                      onClick={() => {
                        if (firstMedia) {
                          setGalleryMediaItems(participant.mediaItems || []);
                          setCurrentGalleryIndex(0);
                          setShowGalleryModal(true);
                        }
                      }}
                    >
                      {/* Imagen/Video del participante */}
                      {firstMedia ? (
                        firstMedia.type === 'video' ? (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              position: 'relative',
                              backgroundColor: '#000',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <video
                              src={firstMedia.url}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block'
                              }}
                              muted
                              playsInline
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                pointerEvents: 'none'
                              }}
                            >
                              <Video className="w-5 h-5" style={{ color: '#000' }} />
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            component="img"
                            src={firstMedia.url}
                            alt={participant.participant_name}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              display: 'block'
                            }}
                            onError={(e) => {
                              console.error('Error cargando imagen:', firstMedia.url);
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        )
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            backgroundColor: '#f3f4f6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Image className="w-12 h-12" style={{ color: '#9ca3af' }} />
                        </Box>
                      )}

                      {/* Gradiente oscuro en la parte inferior para mejor legibilidad del texto */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '40%',
                          background: 'linear-gradient(to top, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.3), transparent)',
                          pointerEvents: 'none'
                        }}
                      />

                      {/* Botón para comprar Vote Booster (arriba del nombre) */}
                      {apiMode !== 'normal' && (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 60,
                            left: 16,
                            pointerEvents: 'auto'
                          }}
                        >
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<Star className="w-4 h-4" />}
                            onClick={() => handlePurchaseVoteBooster(
                              participant.id,
                              selectedContestForParticipants.id,
                              participant.participant_name
                            )}
                            sx={{
                              backgroundColor: '#f59e0b',
                              color: '#ffffff',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                              '&:hover': {
                                backgroundColor: '#d97706',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
                              }
                            }}
                          >
                            {t('contestparticipants.buyVotes')}
                          </Button>
                        </Box>
                      )}

                      {/* Nombre del participante (inferior izquierda) */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 16,
                          left: 16,
                          pointerEvents: 'none'
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 'bold',
                            color: '#ffffff',
                            fontSize: '1.25rem',
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                            lineHeight: 1.2,
                            mb: 0.5
                          }}
                        >
                          {participant.participant_name}
                        </Typography>
                      </Box>

                      {/* Votos con icono de urna (inferior derecha) */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 16,
                          right: 16,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          pointerEvents: 'none'
                        }}
                      >
                        {/* Icono de urna de votación */}
                        <Typography
                          component="span"
                          sx={{
                            fontSize: '1.2rem',
                            lineHeight: 1,
                            filter: 'drop-shadow(0 2px 2px rgba(0, 0, 0, 0.3))'
                          }}
                        >
                          🗳️
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: '600',
                            color: '#ffffff',
                            fontSize: '1rem',
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
                          }}
                        >
                          {formatVotes(participant.votes || 0)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {apiMode !== 'normal' && (
                <>
                  <Button
                    onClick={() => handleResetVotes(selectedContestForParticipants.id)}
                    variant="outlined"
                    sx={{
                      borderColor: '#ef4444',
                      color: '#ef4444',
                      '&:hover': {
                        borderColor: '#dc2626',
                        backgroundColor: '#fee2e2'
                      }
                    }}
                    startIcon={<RotateCcw />}
                  >
                    Resetear Votos
                  </Button>
                  <Button
                    onClick={() => handleResetContest(selectedContestForParticipants.id)}
                    variant="outlined"
                    sx={{
                      borderColor: '#f59e0b',
                      color: '#f59e0b',
                      '&:hover': {
                        borderColor: '#d97706',
                        backgroundColor: '#fef3c7'
                      }
                    }}
                    startIcon={<RotateCcw />}
                  >
                    Resetear Concurso
                  </Button>
                </>
              )}
              {selectedContestStatus === 'active' && (
                <Button
                  onClick={() => handleFinishContest(selectedContestForParticipants.id)}
                  variant="contained"
                  sx={{
                    backgroundColor: '#dc2626',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#b91c1c'
                    }
                  }}
                  startIcon={<XCircle />}
                >
                  {t('contestparticipants.endContest')}
                </Button>
              )}
            </Box>
            <Button
              onClick={() => {
                setShowContestParticipantsModal(false);
                setParticipantVoteStats(null);
              }}
              variant="outlined"
              sx={{
                borderColor: '#d1d5db',
                color: '#6b7280',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: '#f9fafb'
                }
              }}
            >
              {t('buttonsforms.Close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal Live - Top 4 Simple */}
      {showLiveModal && selectedContestForLive && (
        <Dialog
          open={showLiveModal}
          onClose={() => setShowLiveModal(false)}
          maxWidth={false}
          PaperProps={{
            sx: {
              width: isMobile ? '100%' : '90%',
              maxWidth: isMobile ? '100%' : '1200px',
              maxHeight: '95vh',
              margin: isMobile ? 0 : 'auto',
              borderRadius: 2,
              backgroundColor: 'transparent',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: 'url("/fondo ganadores.png")',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                zIndex: 0,
                borderRadius: '8px'
              }
            }
          }}
        >
          <DialogContent sx={{
            p: 4,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-end',
            minHeight: isMobile ? '400px' : '500px',
            gap: isMobile ? 2 : 3,
            backgroundColor: 'transparent',
            position: 'relative',
            zIndex: 2
          }}>
            {/* Contenedor de los 4 lugares */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-end',
              gap: isMobile ? 2 : 3,
              width: '100%',
              flexWrap: 'wrap',
              position: 'relative',
              zIndex: 2
            }}>
              {/* 1er Lugar - Imagen más alta (más margen inferior) */}
              {liveTopParticipants[0] && (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  marginBottom: isMobile ? '80px' : '120px'
                }}>
                  {/* Imagen */}
                  <Box sx={{
                    width: isMobile ? '120px' : '150px',
                    height: isMobile ? '120px' : '150px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    backgroundColor: '#f3f4f6',
                    border: '3px solid #e5e7eb'
                  }}>
                    {liveTopParticipants[0].imageUrl ? (
                      <OptimizedImage
                        src={liveTopParticipants[0].imageUrl}
                        alt={liveTopParticipants[0].participant_name}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <Box sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#e5e7eb'
                      }}>
                        <Camera className="w-10 h-10" style={{ color: '#9ca3af' }} />
                      </Box>
                    )}
                  </Box>
                  {/* Nombre */}
                  <Box sx={{ width: '100%', position: 'relative' }}>
                    <Typography sx={{
                      fontSize: isMobile ? '16px' : '18px',
                      fontWeight: 'bold',
                      color: '#ffffff',
                      textAlign: 'center',
                      backgroundColor: '#2563eb',
                      border: '2px solid #1e40af',
                      borderRadius: '12px',
                      padding: isMobile ? '8px 12px' : '10px 16px',
                      width: '100%'
                    }}>
                      {liveTopParticipants[0].participant_name || 'N/A'}
                    </Typography>
                    {/* Cuadro azul con votos */}
                    <Box sx={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '60%',
                      height: isMobile ? '80px' : '120px',
                      backgroundColor: '#2563eb',
                      borderLeft: '2px solid #1e40af',
                      borderRight: '2px solid #1e40af',
                      borderBottom: '2px solid #1e40af',
                      borderBottomLeftRadius: '12px',
                      borderBottomRightRadius: '12px',
                      zIndex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gap: 0.5
                    }}>
                      <Typography sx={{
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: 'bold',
                        color: '#ffffff'
                      }}>
                        {liveTopParticipants[0].votes || 0}
                      </Typography>
                      <Typography sx={{
                        fontSize: isMobile ? '10px' : '12px',
                        color: '#ffffff',
                        opacity: 0.9
                      }}>
                        {t('contestparticipants.votes')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* 2do Lugar */}
              {liveTopParticipants[1] && (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  marginBottom: isMobile ? '50px' : '80px'
                }}>
                  {/* Imagen */}
                  <Box sx={{
                    width: isMobile ? '120px' : '150px',
                    height: isMobile ? '120px' : '150px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    backgroundColor: '#f3f4f6',
                    border: '3px solid #e5e7eb'
                  }}>
                    {liveTopParticipants[1].imageUrl ? (
                      <OptimizedImage
                        src={liveTopParticipants[1].imageUrl}
                        alt={liveTopParticipants[1].participant_name}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <Box sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#e5e7eb'
                      }}>
                        <Camera className="w-10 h-10" style={{ color: '#9ca3af' }} />
                      </Box>
                    )}
                  </Box>
                  {/* Nombre */}
                  <Box sx={{ width: '100%', position: 'relative' }}>
                    <Typography sx={{
                      fontSize: isMobile ? '16px' : '18px',
                      fontWeight: 'bold',
                      color: '#ffffff',
                      textAlign: 'center',
                      backgroundColor: '#2563eb',
                      border: '2px solid #1e40af',
                      borderRadius: '12px',
                      padding: isMobile ? '8px 12px' : '10px 16px',
                      width: '100%'
                    }}>
                      {liveTopParticipants[1].participant_name || 'N/A'}
                    </Typography>
                    {/* Cuadro azul con votos */}
                    <Box sx={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '60%',
                      height: isMobile ? '50px' : '80px',
                      backgroundColor: '#2563eb',
                      borderLeft: '2px solid #1e40af',
                      borderRight: '2px solid #1e40af',
                      borderBottom: '2px solid #1e40af',
                      borderBottomLeftRadius: '12px',
                      borderBottomRightRadius: '12px',
                      zIndex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gap: 0.5
                    }}>
                      <Typography sx={{
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: 'bold',
                        color: '#ffffff'
                      }}>
                        {liveTopParticipants[1].votes || 0}
                      </Typography>
                      <Typography sx={{
                        fontSize: isMobile ? '10px' : '12px',
                        color: '#ffffff',
                        opacity: 0.9
                      }}>
                        {t('contestparticipants.votes')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* 3er Lugar */}
              {liveTopParticipants[2] && (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  marginBottom: isMobile ? '30px' : '50px'
                }}>
                  {/* Imagen */}
                  <Box sx={{
                    width: isMobile ? '120px' : '150px',
                    height: isMobile ? '120px' : '150px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    backgroundColor: '#f3f4f6',
                    border: '3px solid #e5e7eb'
                  }}>
                    {liveTopParticipants[2].imageUrl ? (
                      <OptimizedImage
                        src={liveTopParticipants[2].imageUrl}
                        alt={liveTopParticipants[2].participant_name}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <Box sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#e5e7eb'
                      }}>
                        <Camera className="w-10 h-10" style={{ color: '#9ca3af' }} />
                      </Box>
                    )}
                  </Box>
                  {/* Nombre */}
                  <Box sx={{ width: '100%', position: 'relative' }}>
                    <Typography sx={{
                      fontSize: isMobile ? '16px' : '18px',
                      fontWeight: 'bold',
                      color: '#ffffff',
                      textAlign: 'center',
                      backgroundColor: '#2563eb',
                      border: '2px solid #1e40af',
                      borderRadius: '12px',
                      padding: isMobile ? '8px 12px' : '10px 16px',
                      width: '100%'
                    }}>
                      {liveTopParticipants[2].participant_name || 'N/A'}
                    </Typography>
                    {/* Cuadro azul con votos */}
                    <Box sx={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '60%',
                      height: isMobile ? '30px' : '50px',
                      backgroundColor: '#2563eb',
                      borderLeft: '2px solid #1e40af',
                      borderRight: '2px solid #1e40af',
                      borderBottom: '2px solid #1e40af',
                      borderBottomLeftRadius: '12px',
                      borderBottomRightRadius: '12px',
                      zIndex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gap: 0.5
                    }}>
                      <Typography sx={{
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: 'bold',
                        color: '#ffffff'
                      }}>
                        {liveTopParticipants[2].votes || 0}
                      </Typography>
                      <Typography sx={{
                        fontSize: isMobile ? '10px' : '12px',
                        color: '#ffffff',
                        opacity: 0.9
                      }}>
                        {t('contestparticipants.votes')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* 4to Lugar - Imagen más baja (sin margen inferior) */}
              {liveTopParticipants[3] && (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  marginBottom: 0
                }}>
                  {/* Imagen */}
                  <Box sx={{
                    width: isMobile ? '120px' : '150px',
                    height: isMobile ? '120px' : '150px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    backgroundColor: '#f3f4f6',
                    border: '3px solid #e5e7eb'
                  }}>
                    {liveTopParticipants[3].imageUrl ? (
                      <OptimizedImage
                        src={liveTopParticipants[3].imageUrl}
                        alt={liveTopParticipants[3].participant_name}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <Box sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#e5e7eb'
                      }}>
                        <Camera className="w-10 h-10" style={{ color: '#9ca3af' }} />
                      </Box>
                    )}
                  </Box>
                  {/* Nombre */}
                  <Box sx={{ width: '100%', position: 'relative' }}>
                    <Typography sx={{
                      fontSize: isMobile ? '16px' : '18px',
                      fontWeight: 'bold',
                      color: '#ffffff',
                      textAlign: 'center',
                      backgroundColor: '#2563eb',
                      border: '2px solid #1e40af',
                      borderRadius: '12px',
                      padding: isMobile ? '8px 12px' : '10px 16px',
                      width: '100%'
                    }}>
                      {liveTopParticipants[3].participant_name || 'N/A'}
                    </Typography>
                    {/* Cuadro azul con votos */}
                    <Box sx={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '60%',
                      height: isMobile ? '30px' : '50px',
                      backgroundColor: '#2563eb',
                      borderLeft: '2px solid #1e40af',
                      borderRight: '2px solid #1e40af',
                      borderBottom: '2px solid #1e40af',
                      borderBottomLeftRadius: '12px',
                      borderBottomRightRadius: '12px',
                      zIndex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gap: 0.5
                    }}>
                      <Typography sx={{
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: 'bold',
                        color: '#ffffff'
                      }}>
                        {liveTopParticipants[3].votes || 0}
                      </Typography>
                      <Typography sx={{
                        fontSize: isMobile ? '10px' : '12px',
                        color: '#ffffff',
                        opacity: 0.9
                      }}>
                        {t('contestparticipants.votes')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </DialogContent>

          <DialogActions sx={{
            p: 2,
            borderTop: '1px solid rgba(255,255,255,0.3)',
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: 'transparent',
            position: 'relative',
            zIndex: 2
          }}>
            <Button
              onClick={() => setShowLiveModal(false)}
              variant="outlined"
              sx={{
                borderColor: 'rgba(255,255,255,0.8)',
                color: '#1e3a8a',
                backgroundColor: 'rgba(255,255,255,0.7)',
                fontWeight: 'bold',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,1)',
                  backgroundColor: 'rgba(255,255,255,0.9)'
                }
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para mostrar imagen o video en tamaño completo */}
      {showImageModal && selectedImageUrl && (
        <Dialog
          open={true}
          onClose={() => {
            setShowImageModal(false);
            setSelectedImageUrl(null);
            setSelectedMediaType(null);
            setIsVideoExpanded(false);
          }}
          maxWidth="sm"
          fullWidth={false}
        >
          <DialogTitle sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1
          }}>
            <Typography variant="h6">
              {selectedMediaType === 'video' ? 'Video del Participante' : 'Imagen del Participante'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {selectedMediaType === 'video' && (
                <IconButton
                  onClick={() => setIsVideoExpanded(!isVideoExpanded)}
                  size="small"
                  sx={{ color: '#6b7280' }}
                  title={isVideoExpanded ? 'Reducir video' : 'Agrandar video'}
                >
                  {isVideoExpanded ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                </IconButton>
              )}
              <IconButton
                onClick={() => {
                  setShowImageModal(false);
                  setSelectedImageUrl(null);
                  setSelectedMediaType(null);
                  setIsVideoExpanded(false);
                }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {selectedMediaType === 'video' ? (
              <Box
                component="video"
                src={selectedImageUrl}
                controls
                style={{
                  width: isVideoExpanded ? '640px' : undefined,
                  height: isVideoExpanded ? '640px' : undefined,
                  maxWidth: isVideoExpanded ? '640px' : undefined,
                  maxHeight: isVideoExpanded ? '640px' : undefined,
                }}
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                  transition: 'width 0.3s ease, height 0.3s ease'
                }}
                onError={(e) => {
                  console.error('Error cargando video en modal:', selectedImageUrl);
                }}
              />
            ) : (
              <Box
                component="img"
                src={selectedImageUrl}
                alt="Imagen del participante"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  height: 'auto',
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                }}
                onError={(e) => {
                  console.error('Error cargando imagen en modal:', selectedImageUrl);
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Modal de Galería para mostrar todas las fotos/videos del participante */}
      {showGalleryModal && galleryMediaItems.length > 0 && (
        <Dialog
          open={true}
          onClose={() => {
            setShowGalleryModal(false);
            setGalleryMediaItems([]);
            setCurrentGalleryIndex(0);
            setIsVideoExpanded(false);
          }}
          maxWidth="lg"
          fullWidth={true}
          PaperProps={{
            sx: {
              backgroundColor: '#ffffff',
              color: '#111827'
            }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
            color: '#111827'
          }}>
            <Typography variant="h6" sx={{ color: '#111827' }}>
              Galería ({currentGalleryIndex + 1} / {galleryMediaItems.length})
            </Typography>
            <IconButton
              onClick={() => {
                setShowGalleryModal(false);
                setGalleryMediaItems([]);
                setCurrentGalleryIndex(0);
                setIsVideoExpanded(false);
              }}
              size="small"
              sx={{ color: '#6b7280' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{
            p: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            minHeight: '60vh',
            pb: 16
          }}>
            {/* Botón anterior */}
            {galleryMediaItems.length > 1 && (
              <IconButton
                onClick={() => {
                  const newIndex = currentGalleryIndex > 0
                    ? currentGalleryIndex - 1
                    : galleryMediaItems.length - 1;
                  setCurrentGalleryIndex(newIndex);
                  setIsVideoExpanded(false);
                }}
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  color: '#111827',
                  zIndex: 10,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.2)'
                  }
                }}
              >
                <ChevronLeft size={32} />
              </IconButton>
            )}

            {/* Media actual */}
            {galleryMediaItems[currentGalleryIndex] && (
              galleryMediaItems[currentGalleryIndex].type === 'video' ? (
                <Box sx={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center' }}>
                  <Box
                    component="video"
                    src={galleryMediaItems[currentGalleryIndex].url}
                    controls
                    style={{
                      width: isVideoExpanded ? '640px' : '100%',
                      height: isVideoExpanded ? '640px' : 'auto',
                      maxWidth: '90%',
                      maxHeight: '80vh',
                    }}
                    sx={{
                      borderRadius: 2,
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                      transition: 'width 0.3s ease, height 0.3s ease'
                    }}
                    onError={(e) => {
                      console.error('Error cargando video en galería:', galleryMediaItems[currentGalleryIndex].url);
                    }}
                  />
                  <IconButton
                    onClick={() => setIsVideoExpanded(!isVideoExpanded)}
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      color: '#111827',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.2)'
                      }
                    }}
                    title={isVideoExpanded ? 'Reducir video' : 'Agrandar video'}
                  >
                    {isVideoExpanded ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                  </IconButton>
                </Box>
              ) : (
                <Box
                  component="img"
                  src={galleryMediaItems[currentGalleryIndex].url}
                  alt={`Galería ${currentGalleryIndex + 1}`}
                  sx={{
                    maxWidth: '90%',
                    maxHeight: '80vh',
                    height: 'auto',
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                  }}
                  onError={(e) => {
                    console.error('Error cargando imagen en galería:', galleryMediaItems[currentGalleryIndex].url);
                  }}
                />
              )
            )}

            {/* Botón siguiente */}
            {galleryMediaItems.length > 1 && (
              <IconButton
                onClick={() => {
                  const newIndex = currentGalleryIndex < galleryMediaItems.length - 1
                    ? currentGalleryIndex + 1
                    : 0;
                  setCurrentGalleryIndex(newIndex);
                  setIsVideoExpanded(false);
                }}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  color: '#111827',
                  zIndex: 10,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.2)'
                  }
                }}
              >
                <ChevronRight size={32} />
              </IconButton>
            )}

            {/* Miniaturas en la parte inferior (opcional) */}
            {galleryMediaItems.length > 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 1,
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  padding: '8px',
                  borderRadius: '8px',
                  maxWidth: '90%',
                  overflowX: 'auto'
                }}
              >
                {galleryMediaItems.map((item, index) => (
                  <Box
                    key={index}
                    onClick={() => {
                      setCurrentGalleryIndex(index);
                      setIsVideoExpanded(false);
                    }}
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '4px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: currentGalleryIndex === index ? '2px solid #3b82f6' : '2px solid transparent',
                      opacity: currentGalleryIndex === index ? 1 : 0.7,
                      '&:hover': {
                        opacity: 1
                      },
                      flexShrink: 0
                    }}
                  >
                    {item.type === 'video' ? (
                      <Box
                        component="video"
                        src={item.url}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        muted
                        playsInline
                      />
                    ) : (
                      <Box
                        component="img"
                        src={item.url}
                        alt={`Miniatura ${index + 1}`}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Modal de Ganadores del Concurso */}
      {showWinnersModal && selectedContestForWinners && (
        <Dialog
          open={true}
          onClose={() => setShowWinnersModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Crown className="w-5 h-5" style={{ color: '#f59e0b' }} />
            {t('contestparticipants.winnersTitle')}: {selectedContestForWinners.name}
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {contestWinners.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Trophy className="w-12 h-12 mx-auto mb-4" style={{ color: '#d1d5db' }} />
                <Typography variant="body1" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
                  {t('contestparticipants.noWinners')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {contestWinners.map((winner, index) => {
                  const WinnerCard = ({ winner, index }: { winner: any; index: number }) => {
                    const [imageError, setImageError] = useState(false);
                    const hasPhoto = winner.participant.photo_url && !imageError;
                    const circleBgColor = index === 0 ? '#f59e0b' : index === 1 ? '#6b7280' : index === 2 ? '#f97316' : index === 3 ? '#f7a01e' : '#3b82f6';

                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 3,
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          backgroundColor: index === 0 ? '#fef3c7' : index === 1 ? '#f3f4f6' : index === 2 ? '#fef7ed' : index === 3 ? '#faf1e8' : '#f9fafb',
                          position: 'relative'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            backgroundColor: hasPhoto ? 'transparent' : circleBgColor,
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '36px',
                            overflow: 'hidden',
                            border: hasPhoto ? '3px solid' : 'none',
                            borderColor: circleBgColor,
                            position: 'relative'
                          }}>
                            {hasPhoto ? (
                              <OptimizedImage
                                src={winner.participant.photo_url}
                                alt={winner.participant.name}
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '50%'
                                }}
                                onError={() => {
                                  setImageError(true);
                                }}
                              />
                            ) : (
                              <span>{winner.place}</span>
                            )}
                          </Box>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#111827' }}>
                              {winner.participant.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                              {winner.participant.votes} {t('contestparticipants.votes')}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#059669' }}>
                              ${winner.prize.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280', display: 'flex', alignItems: 'center', gap: 1 }}>
                              {index === 0 ? (
                                <>
                                  <Crown className="w-4 h-4" style={{ color: '#f59e0b' }} />
                                  🥇 {t('contestparticipants.firstPlace')}
                                </>
                              ) : index === 1 ? (
                                <>
                                  <Award className="w-4 h-4" style={{ color: '#6b7280' }} />
                                  🥈 {t('contestparticipants.secondPlace')}
                                </>
                              ) : index === 2 ? (
                                <>
                                  <Award className="w-4 h-4" style={{ color: '#f97316' }} />
                                  🥉 {t('contestparticipants.thirdPlace')}
                                </>
                              ) : index === 3 ? (
                                <>
                                  <Award className="w-4 h-4" style={{ color: '#fab382' }} />
                                  🎖️ {t('contestparticipants.fourthPlace')}
                                </>
                              ) : (
                                <>
                                  <Star className="w-4 h-4" style={{ color: '#3b82f6' }} />
                                  {winner.place}° {t('contestparticipants.place')}
                                </>
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  };

                  return <WinnerCard key={winner.participant.id} winner={winner} index={index} />;
                })}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={() => setShowWinnersModal(false)}
              variant="outlined"
              sx={{
                borderColor: '#d1d5db',
                color: '#6b7280',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: '#f9fafb'
                }
              }}
            >
              {t('buttonsforms.Close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal de Invoice del Concurso */}
      {showInvoiceModal && selectedContestForInvoice && (
        <Dialog
          open={true}
          onClose={() => setShowInvoiceModal(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <FileText className="w-5 h-5" style={{ color: '#9333ea' }} />
            {t('invoicedata.title')}: {selectedContestForInvoice.name}
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {loadingInvoice ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#6b7280' }}>
                  {t('invoicedata.loading')}
                </Typography>
              </Box>
            ) : invoiceData.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <FileText className="w-12 h-12 mx-auto mb-4" style={{ color: '#d1d5db' }} />
                <Typography variant="body1" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
                  {t('invoicedata.noData')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {invoiceData.map((invoice, index) => {
                  // Calcular subtotal sumando todos los totales de los items
                  let subtotal = 0;
                  const itemsData = invoice.cost_description || invoice.items;

                  if (itemsData) {
                    let itemsArray: any[] = [];

                    if (typeof itemsData === 'string') {
                      try {
                        const parsed = JSON.parse(itemsData);
                        itemsArray = Array.isArray(parsed) ? parsed : [];
                      } catch (e) {
                        // Si no se puede parsear, usar array vacío
                      }
                    } else if (Array.isArray(itemsData)) {
                      itemsArray = itemsData;
                    }

                    // Sumar todos los totales de los items
                    subtotal = itemsArray.reduce((sum, item) => {
                      const itemTotal = parseFloat(item.total || item.amount || 0);
                      return sum + (isNaN(itemTotal) ? 0 : itemTotal);
                    }, 0);
                  }

                  const tax = parseFloat(invoice.tax || 0);
                  const total = subtotal + (isNaN(tax) ? 0 : tax);

                  return (
                    <Card key={invoice.id || index} sx={{ border: '1px solid #e5e7eb', borderRadius: '12px' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#111827', mb: 2 }}>
                          {t('invoicedata.invoiceNumber')}{invoice.invoice_number || 'N/A'}
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableBody>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: '200px' }}>{t('invoicedata.id')}</TableCell>
                                <TableCell>{invoice.id || 'N/A'}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('invoicedata.invoiceNumberLabel')}</TableCell>
                                <TableCell>{invoice.invoice_number || 'N/A'}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('invoicedata.contestId')}</TableCell>
                                <TableCell>
                                  {(() => {
                                    if (!invoice.contest_id) return 'N/A';
                                    // Extraer los primeros 4 dígitos del ID
                                    const digits = invoice.contest_id.match(/\d/g);
                                    if (digits && digits.length >= 4) {
                                      const firstFourDigits = digits.slice(0, 4).join('');
                                      return `CONTEST-${firstFourDigits}`;
                                    }
                                    return invoice.contest_id;
                                  })()}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('invoicedata.sponsorId')}</TableCell>
                                <TableCell>
                                  {(() => {
                                    if (!invoice.sponsor_id) return 'N/A';
                                    // Extraer los primeros 4 dígitos del ID
                                    const digits = invoice.sponsor_id.match(/\d/g);
                                    if (digits && digits.length >= 4) {
                                      const firstFourDigits = digits.slice(0, 4).join('');
                                      return `NA-PC-${firstFourDigits}`;
                                    }
                                    return invoice.sponsor_id;
                                  })()}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('invoicedata.invoiceDate')}</TableCell>
                                <TableCell>
                                  {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleString() : 'N/A'}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('invoicedata.dueDate')}</TableCell>
                                <TableCell>
                                  {invoice.due_date ? new Date(invoice.due_date).toLocaleString() : 'N/A'}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('invoicedata.subtotal')}</TableCell>
                                <TableCell>${subtotal.toFixed(2)}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('invoicedata.tax')}</TableCell>
                                <TableCell>${invoice.tax ? parseFloat(invoice.tax).toFixed(2) : '0.00'}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('invoicedata.total')}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#059669' }}>
                                  ${total.toFixed(2)}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('invoicedata.status')}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={invoice.status || 'N/A'}
                                    color={invoice.status === 'paid' ? 'success' : invoice.status === 'pending' ? 'warning' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('invoicedata.paylink')}</TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                      {invoice.paylink ? (
                                        <a
                                          href={invoice.paylink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ color: '#2563eb', textDecoration: 'none' }}
                                          title={invoice.paylink}
                                        >
                                          {invoice.paylink.length > 40
                                            ? `${invoice.paylink.substring(0, 40)}...`
                                            : invoice.paylink}
                                        </a>
                                      ) : 'N/A'}
                                    </Box>
                                    <Button
                                      onClick={async () => {
                                        try {
                                          // Obtener el concurso completo desde el contest_id del invoice
                                          const contest = await contestService.getContestById(invoice.contest_id);
                                          if (contest) {
                                            await handleSendSponsor(contest, 'UpdatedPaylink', invoice);
                                          } else {
                                            void appDialog.alert(t('invoicedata.errorGettingContest'));
                                          }
                                        } catch (error) {
                                          console.error('Error obteniendo concurso:', error);
                                          void appDialog.alert(t('invoicedata.errorGettingContestGeneric'));
                                        }
                                      }}
                                      variant="contained"
                                      size="small"
                                      disabled={!invoice.contest_id}
                                      sx={{
                                        backgroundImage: invoice.contest_id ? 'linear-gradient(90deg, #2563eb, #3b82f6)' : 'linear-gradient(90deg, #9ca3af, #d1d5db)',
                                        color: '#ffffff',
                                        minWidth: '160px',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        boxShadow: invoice.contest_id ? '0 2px 6px rgba(0,0,0,0.12)' : 'none',
                                        border: invoice.contest_id ? '1px solid #3b82f6' : '1px solid #d1d5db',
                                        '&:hover': {
                                          backgroundImage: invoice.contest_id ? 'linear-gradient(90deg, #1d4ed8, #2563eb)' : 'linear-gradient(90deg, #9ca3af, #d1d5db)',
                                        },
                                        '&.Mui-disabled': {
                                          backgroundImage: 'linear-gradient(90deg, #9ca3af, #d1d5db)',
                                          color: '#ffffff',
                                          opacity: 0.6
                                        }
                                      }}
                                    >
                                      <Building2 className="w-4 h-4" style={{ marginRight: '8px' }} />
                                      {t('contestcard.sendsponsorship')}
                                    </Button>
                                  </Box>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('invoicedata.createdAt')}</TableCell>
                                <TableCell>
                                  {invoice.created_at ? new Date(invoice.created_at).toLocaleString() : 'N/A'}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('invoicedata.updatedAt')}</TableCell>
                                <TableCell>
                                  {invoice.updated_at ? new Date(invoice.updated_at).toLocaleString() : 'N/A'}
                                </TableCell>
                              </TableRow>
                              {(invoice.cost_description || invoice.items) && (
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'top' }}>{t('invoicedata.items')}:</TableCell>
                                  <TableCell>
                                    {(() => {
                                      // Priorizar cost_description sobre items
                                      const itemsData = invoice.cost_description || invoice.items;

                                      if (typeof itemsData === 'string') {
                                        try {
                                          const parsed = JSON.parse(itemsData);
                                          if (Array.isArray(parsed)) {
                                            return (
                                              <Box>
                                                {parsed.map((item: any, itemIndex: number) => (
                                                  <Box key={itemIndex} sx={{ mb: 1, p: 1, bgcolor: '#f9fafb', borderRadius: '4px' }}>
                                                    <Typography variant="body2">
                                                      <strong>{t('invoicedata.description')}:</strong> {item.description || item.name || item.item || 'N/A'}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                      <strong>{t('invoicedata.quantity')}:</strong> {item.quantity || 'N/A'}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                      <strong>{t('invoicedata.unitPrice')}:</strong> ${item.unit_price || item.rate ? parseFloat(item.unit_price || item.rate || 0).toFixed(2) : '0.00'}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                      <strong>{t('invoicedata.totalItem')}:</strong> ${item.total || item.amount ? parseFloat(item.total || item.amount || 0).toFixed(2) : '0.00'}
                                                    </Typography>
                                                  </Box>
                                                ))}
                                              </Box>
                                            );
                                          }
                                        } catch (e) {
                                          return (
                                            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
                                              {itemsData}
                                            </pre>
                                          );
                                        }
                                      }

                                      if (Array.isArray(itemsData)) {
                                        return (
                                          <Box>
                                            {itemsData.map((item: any, itemIndex: number) => (
                                              <Box key={itemIndex} sx={{ mb: 1, p: 1, bgcolor: '#f9fafb', borderRadius: '4px' }}>
                                                <Typography variant="body2">
                                                  <strong>{t('invoicedata.description')}:</strong> {item.description || item.name || item.item || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2">
                                                  <strong>{t('invoicedata.quantity')}:</strong> {item.quantity || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2">
                                                  <strong>{t('invoicedata.unitPrice')}:</strong> ${item.unit_price || item.rate ? parseFloat(item.unit_price || item.rate || 0).toFixed(2) : '0.00'}
                                                </Typography>
                                                <Typography variant="body2">
                                                  <strong>{t('invoicedata.totalItem')}:</strong> ${item.total || item.amount ? parseFloat(item.total || item.amount || 0).toFixed(2) : '0.00'}
                                                </Typography>
                                              </Box>
                                            ))}
                                          </Box>
                                        );
                                      }

                                      return (
                                        <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
                                          {JSON.stringify(itemsData, null, 2)}
                                        </pre>
                                      );
                                    })()}
                                  </TableCell>
                                </TableRow>
                              )}
                              {invoice.activity_log && (
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'top' }}>{t('invoicedata.activityLog')}:</TableCell>
                                  <TableCell>
                                    {typeof invoice.activity_log === 'string' ? (
                                      <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
                                        {invoice.activity_log}
                                      </pre>
                                    ) : Array.isArray(invoice.activity_log) ? (
                                      <Box>
                                        {invoice.activity_log.map((log: any, logIndex: number) => (
                                          <Box key={logIndex} sx={{ mb: 1, p: 1, bgcolor: '#f9fafb', borderRadius: '4px' }}>
                                            <Typography variant="body2">
                                              <strong>{t('invoicedata.date')}:</strong> {log.date ? new Date(log.date).toLocaleString() : 'N/A'}
                                            </Typography>
                                            <Typography variant="body2">
                                              <strong>{t('invoicedata.action')}:</strong> {log.action || 'N/A'}
                                            </Typography>
                                            {log.description && (
                                              <Typography variant="body2">
                                                <strong>{t('invoicedata.description')}:</strong> {log.description}
                                              </Typography>
                                            )}
                                          </Box>
                                        ))}
                                      </Box>
                                    ) : (
                                      <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
                                        {JSON.stringify(invoice.activity_log, null, 2)}
                                      </pre>
                                    )}
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={() => setShowInvoiceModal(false)}
              variant="outlined"
              sx={{
                borderColor: '#d1d5db',
                color: '#6b7280',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: '#f9fafb'
                }
              }}
            >
              {t('invoicedata.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para invitar Embajador por email */}
      {showInviteAmbassadorModal && (
        <Dialog
          open={showInviteAmbassadorModal}
          onClose={() => {
            setShowInviteAmbassadorModal(false);
            setInviteEmail('');
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Users className="w-5 h-5" style={{ color: '#9333ea' }} />
              {t('inviteambassador.title')}
            </Box>
            <IconButton onClick={() => {
              setShowInviteAmbassadorModal(false);
              setInviteEmail('');
            }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label={t('inviteambassador.emailLabel')}
                type="email"
                fullWidth
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t('inviteambassador.emailPlaceholder')}
                disabled={sendingInvite}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#9333ea',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#9333ea',
                    },
                  },
                }}
              />
              <Typography variant="body2" sx={{ color: '#6b7280', mt: 1 }}>
                {t('inviteambassador.description')}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={() => {
                setShowInviteAmbassadorModal(false);
                setInviteEmail('');
              }}
              variant="outlined"
              disabled={sendingInvite}
            >
              {t('inviteambassador.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleInviteAmbassador}
              disabled={sendingInvite || !inviteEmail.trim()}
              sx={{
                backgroundColor: '#9333ea',
                '&:hover': { backgroundColor: '#7e22ce' },
                '&:disabled': {
                  backgroundColor: '#9ca3af',
                  opacity: 0.6
                }
              }}
            >
              {sendingInvite ? t('inviteambassador.sending') : t('inviteambassador.send')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para gestionar Ambassadors */}
      {showAmbassadorsModal && (
        <Dialog
          open={showAmbassadorsModal}
          onClose={() => setShowAmbassadorsModal(false)}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: {
              maxWidth: '95vw',
              width: '100%'
            }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Users className="w-5 h-5" style={{ color: '#9333ea' }} />
              {t('ambassadormanagement.title')}
            </Box>
            <IconButton onClick={() => setShowAmbassadorsModal(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {loadingAmbassadors ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#6b7280' }}>
                  {t('ambassadormanagement.loading')}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 2, gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<DollarSign className="w-5 h-5" />}
                    onClick={handleViewGlobalCompensationDashboard}
                    sx={{
                      backgroundColor: '#10b981',
                      color: 'white',
                      '&:hover': { backgroundColor: '#059669' }
                    }}
                  >
                    {t('ambassadormanagement.ambassadorReports')}
                  </Button>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Users className="w-5 h-5" />}
                      onClick={fetchPetflixAmbassadorUsers}
                      disabled={loadingPetflixUsers}
                      sx={{
                        borderColor: '#2563eb',
                        color: '#2563eb',
                        '&:hover': {
                          borderColor: '#1d4ed8',
                          backgroundColor: '#eff6ff'
                        },
                        '&:disabled': {
                          borderColor: '#9ca3af',
                          color: '#9ca3af',
                          opacity: 0.6
                        }
                      }}
                    >
                      {loadingPetflixUsers ? t('ambassadormanagement.updating') : t('ambassadormanagement.updateAmbassadors')}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Users className="w-5 h-5" />}
                      onClick={() => {
                        setInviteEmail('');
                        setShowInviteAmbassadorModal(true);
                      }}
                      sx={{
                        backgroundColor: '#9333ea',
                        '&:hover': { backgroundColor: '#7e22ce' }
                      }}
                    >
                      {t('ambassadormanagement.inviteAmbassador')}
                    </Button>
                  </Box>
                </Box>

                {/* Buscador de embajadores */}
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder={t('ambassadormanagement.searchPlaceholder')}
                    value={ambassadorSearchTerm}
                    onChange={(e) => setAmbassadorSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Filter className="w-5 h-5" style={{ color: '#6b7280' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#9333ea',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#9333ea',
                        },
                      },
                    }}
                  />
                </Box>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadormanagement.name')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadormanagement.email')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadormanagement.phone')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadormanagement.signedSponsors')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadormanagement.tagsSold')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadormanagement.revenue')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadormanagement.badges')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadormanagement.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        // Función para verificar si el término de búsqueda es una subsecuencia del texto
                        // (los caracteres deben aparecer en el mismo orden, pero no necesariamente consecutivos)
                        const isSubsequence = (searchTerm: string, text: string): boolean => {
                          // Validaciones iniciales
                          if (!text || !searchTerm) return false;

                          searchTerm = searchTerm.toLowerCase().trim();
                          text = text.toLowerCase().trim();

                          // Si el término de búsqueda está vacío, retornar true
                          if (searchTerm.length === 0) return true;

                          // Si el término es más largo que el texto, no puede ser subsecuencia
                          if (searchTerm.length > text.length) return false;

                          // Verificar que todos los caracteres del término aparezcan en orden
                          let searchIndex = 0;
                          for (let i = 0; i < text.length && searchIndex < searchTerm.length; i++) {
                            if (text[i] === searchTerm[searchIndex]) {
                              searchIndex++;
                            }
                          }

                          // Solo retornar true si encontramos TODOS los caracteres en orden
                          return searchIndex === searchTerm.length;
                        };

                        // Filtrar embajadores según el término de búsqueda
                        const filteredAmbassadors = ambassadors.filter((ambassador: any) => {
                          const searchTerm = ambassadorSearchTerm.trim();
                          if (!searchTerm) return true;

                          // Buscar en cada campo individualmente - solo si el campo existe
                          const name = (ambassador.name || '').trim();
                          const email = (ambassador.email || '').trim();
                          const phone = (ambassador.phone || '').trim();

                          const matchesName = name.length > 0 ? isSubsequence(searchTerm, name) : false;
                          const matchesEmail = email.length > 0 ? isSubsequence(searchTerm, email) : false;
                          const matchesPhone = phone.length > 0 ? isSubsequence(searchTerm, phone) : false;

                          // Para teléfono, también buscar solo números si el término contiene números
                          let matchesPhoneNumeric = false;
                          const numericSearch = searchTerm.replace(/\D/g, '');
                          if (numericSearch.length > 0 && phone.length > 0) {
                            const numericPhone = phone.replace(/\D/g, '');
                            matchesPhoneNumeric = isSubsequence(numericSearch, numericPhone);
                          }

                          return matchesName || matchesEmail || matchesPhone || matchesPhoneNumeric;
                        });

                        if (filteredAmbassadors.length === 0) {
                          return (
                            <TableRow key="no-results">
                              <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                  {ambassadors.length === 0
                                    ? t('ambassadormanagement.noAmbassadorsRegistered')
                                    : t('ambassadormanagement.noAmbassadorsFound')}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        }

                        return filteredAmbassadors.map((ambassador: any) => (
                          <TableRow key={ambassador.id}>
                            <TableCell sx={{ textAlign: 'center' }}>{ambassador.name}</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>{ambassador.email}</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>{ambassador.phone || 'N/A'}</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>{ambassador.total_sponsors_signed || 0}</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>{ambassador.total_tags_sold || 0}</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>${(ambassador.total_revenue || 0).toFixed(2)}</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                {ambassador.recruiter_badge && (
                                  <Chip label={t('ambassadormanagement.recruiter')} size="small" color="primary" />
                                )}
                                {ambassador.senior_badge && (
                                  <Chip label={t('ambassadormanagement.senior')} size="small" color="secondary" />
                                )}
                                {ambassador.elite_badge && (
                                  <Chip label={t('ambassadormanagement.elite')} size="small" sx={{ backgroundColor: '#fbbf24', color: '#fff' }} />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: 1,
                                width: '100%',
                                minWidth: '300px'
                              }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  startIcon={<Award className="w-4 h-4" />}
                                  onClick={() => handleViewAmbassadorAssignments(ambassador)}
                                  disabled={loadingAmbassadorAssignments}
                                  sx={{ width: '100%' }}
                                >
                                  {t('ambassadormanagement.viewAssignments')}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Edit className="w-4 h-4" />}
                                  onClick={() => {
                                    setEditingAmbassador(ambassador);
                                    setAmbassadorFormStatus(ambassador.status || 'active');
                                    setShowAmbassadorForm(true);
                                  }}
                                  sx={{ width: '100%' }}
                                >
                                  {t('ambassadormanagement.edit')}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                  startIcon={<QrCode className="w-4 h-4" />}
                                  onClick={() => handleViewAmbassadorTags(ambassador)}
                                  disabled={loadingAmbassadorTagsView}
                                  sx={{ width: '100%' }}
                                >
                                  {t('ambassadormanagement.viewTags')}
                                </Button>
                                {/* Botón Vender Tag - Oculto pero función disponible para uso futuro */}
                                {/* <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  startIcon={<Tag className="w-4 h-4" />}
                                  onClick={() => {
                                    setSelectedAmbassadorForSale(ambassador);
                                    setShowTagSaleForm(true);
                                  }}
                                  sx={{ width: '100%' }}
                                >
                                  Vender Tag
                                </Button> */}
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  startIcon={<DollarSign className="w-4 h-4" />}
                                  onClick={() => {
                                    setSelectedAmbassadorForCompensation(ambassador);
                                    setShowCompensationDashboard(true);
                                  }}
                                  sx={{ width: '100%' }}
                                >
                                  {t('ambassadormanagement.viewCompensation')}
                                </Button>
                                {/* Botón Ver Digital Passes - Oculto pero función disponible para uso futuro */}
                                {/* <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  startIcon={<Ticket className="w-4 h-4" />}
                                  onClick={() => handleViewAmbassadorDigitalPasses(ambassador)}
                                  disabled={loadingAmbassadorDigitalPasses}
                                  sx={{ width: '100%' }}
                                >
                                  Ver Digital Passes
                                </Button> */}
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  startIcon={<Award className="w-4 h-4" />}
                                  onClick={() => handleViewBonusManagement(ambassador)}
                                  disabled={loadingBonusHistory}
                                  sx={{ width: '100%' }}
                                >
                                  {t('ambassadormanagement.bonusManagement')}
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ));
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button onClick={() => setShowAmbassadorsModal(false)} variant="outlined">
              {t('ambassadormanagement.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para Digital Passes de Embajador */}
      {showAmbassadorDigitalPassesModal && selectedAmbassadorForDigitalPasses && (
        <Dialog
          open={showAmbassadorDigitalPassesModal}
          onClose={() => {
            setShowAmbassadorDigitalPassesModal(false);
            setSelectedAmbassadorForDigitalPasses(null);
            setAmbassadorDigitalPasses([]);
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Ticket className="w-5 h-5" style={{ color: '#9c27b0' }} />
              {t('ambassadordigitalpasses.title')} - {selectedAmbassadorForDigitalPasses.name}
            </Box>
            <IconButton onClick={() => {
              setShowAmbassadorDigitalPassesModal(false);
              setSelectedAmbassadorForDigitalPasses(null);
              setAmbassadorDigitalPasses([]);
            }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {loadingAmbassadorDigitalPasses ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#6b7280' }}>
                  {t('ambassadordigitalpasses.loading')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2, mb: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('ambassadordigitalpasses.ambassador')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedAmbassadorForDigitalPasses.name} ({selectedAmbassadorForDigitalPasses.email})
                  </Typography>
                </Box>

                {ambassadorDigitalPasses.length === 0 ? (
                  <Alert severity="info">
                    {t('ambassadordigitalpasses.noPassesAssigned')}
                  </Alert>
                ) : (
                  <>
                    {/* Resumen */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Card sx={{ flex: '1 1 calc(33% - 16px)', minWidth: '200px', p: 2, backgroundColor: '#f3e8ff' }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                          {t('ambassadordigitalpasses.totalDigitalPasses')}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                          {(() => {
                            // Agrupar por concurso para evitar duplicar códigos del mismo concurso
                            const contestCodesMap = new Map();
                            ambassadorDigitalPasses.forEach((pass: any) => {
                              if (!contestCodesMap.has(pass.contest_id)) {
                                contestCodesMap.set(pass.contest_id, pass.digital_total_codes || 0);
                              }
                            });
                            return Array.from(contestCodesMap.values()).reduce((sum: number, codes: number) => sum + codes, 0);
                          })()}
                        </Typography>
                      </Card>
                      <Card sx={{ flex: '1 1 calc(33% - 16px)', minWidth: '200px', p: 2, backgroundColor: '#ecfdf5' }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                          {t('ambassadordigitalpasses.codesGenerated')}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                          {(() => {
                            // Agrupar por concurso para evitar duplicar códigos del mismo concurso
                            const contestCodesMap = new Map();
                            ambassadorDigitalPasses.forEach((pass: any) => {
                              if (!contestCodesMap.has(pass.contest_id)) {
                                contestCodesMap.set(pass.contest_id, pass.total_codes || 0);
                              }
                            });
                            return Array.from(contestCodesMap.values()).reduce((sum: number, codes: number) => sum + codes, 0);
                          })()}
                        </Typography>
                      </Card>
                      <Card sx={{ flex: '1 1 calc(33% - 16px)', minWidth: '200px', p: 2, backgroundColor: '#fef3c7' }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                          {t('ambassadordigitalpasses.codesUsed')}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#d97706' }}>
                          {(() => {
                            // Agrupar por concurso para evitar duplicar códigos del mismo concurso
                            const contestCodesMap = new Map();
                            ambassadorDigitalPasses.forEach((pass: any) => {
                              if (!contestCodesMap.has(pass.contest_id)) {
                                contestCodesMap.set(pass.contest_id, pass.used_codes || 0);
                              }
                            });
                            return Array.from(contestCodesMap.values()).reduce((sum: number, codes: number) => sum + codes, 0);
                          })()}
                        </Typography>
                      </Card>
                    </Box>

                    {/* Lista de Digital Passes */}
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2, mb: 2 }}>
                      {t('ambassadordigitalpasses.assignedPasses')}
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('ambassadordigitalpasses.contest')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('ambassadordigitalpasses.name')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadordigitalpasses.type')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadordigitalpasses.codesGeneratedHeader')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadordigitalpasses.codesUsedHeader')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadordigitalpasses.availableCodes')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('ambassadordigitalpasses.creationDate')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {ambassadorDigitalPasses.map((pass: any) => (
                            <TableRow key={pass.id}>
                              <TableCell>{pass.contest_name || pass.contest_id || 'N/A'}</TableCell>
                              <TableCell>{pass.name || 'N/A'}</TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                <Chip
                                  label={pass.is_unlimited ? t('ambassadordigitalpasses.unlimited') : t('ambassadordigitalpasses.limited')}
                                  size="small"
                                  color={pass.is_unlimited ? 'success' : 'default'}
                                />
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>{pass.total_codes || 0}</TableCell>
                              <TableCell sx={{ textAlign: 'center', color: '#d97706', fontWeight: 'medium' }}>
                                {pass.used_codes || 0}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center', color: '#10b981', fontWeight: 'medium' }}>
                                {pass.available_codes || 0}
                              </TableCell>
                              <TableCell>
                                {pass.created_at ? new Date(pass.created_at).toLocaleDateString() : 'N/A'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={() => {
                setShowAmbassadorDigitalPassesModal(false);
                setSelectedAmbassadorForDigitalPasses(null);
                setAmbassadorDigitalPasses([]);
              }}
              variant="outlined"
            >
              {t('ambassadordigitalpasses.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para crear/editar Ambassador */}
      {showAmbassadorForm && (
        <Dialog
          open={showAmbassadorForm}
          onClose={() => {
            setShowAmbassadorForm(false);
            setEditingAmbassador(null);
            setAmbassadorFormStatus('active');
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Users className="w-5 h-5" style={{ color: '#9333ea' }} />
              {editingAmbassador ? t('editambassador.title') : t('editambassador.newTitle')}
            </Box>
            <IconButton onClick={() => {
              setShowAmbassadorForm(false);
              setEditingAmbassador(null);
              setAmbassadorFormStatus('active');
            }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                sx={{ mt: 5 }}
                label={t('editambassador.name')}
                fullWidth
                defaultValue={editingAmbassador?.name || ''}
                disabled={!!editingAmbassador}
                inputRef={(input: any) => {
                  if (input && !input.dataset.initialized) {
                    input.dataset.initialized = 'true';
                    input.value = editingAmbassador?.name || '';
                  }
                }}
                InputProps={{
                  readOnly: !!editingAmbassador,
                }}
              />
              <TextField
                label={t('editambassador.email')}
                type="email"
                fullWidth
                defaultValue={editingAmbassador?.email || ''}
                disabled={!!editingAmbassador}
                inputRef={(input: any) => {
                  if (input && !input.dataset.initialized) {
                    input.dataset.initialized = 'true';
                    input.value = editingAmbassador?.email || '';
                  }
                }}
                InputProps={{
                  readOnly: !!editingAmbassador,
                }}
              />
              <TextField
                label={t('editambassador.phone')}
                fullWidth
                defaultValue={editingAmbassador?.phone || ''}
                inputRef={(input: any) => {
                  if (input && !input.dataset.initialized) {
                    input.dataset.initialized = 'true';
                    input.value = editingAmbassador?.phone || '';
                  }
                }}
              />
              <FormControl fullWidth>
                <InputLabel>{t('editambassador.statusLabel')}</InputLabel>
                <Select
                  value={ambassadorFormStatus}
                  onChange={(e) => setAmbassadorFormStatus(e.target.value)}
                  label={t('editambassador.statusLabel')}
                >
                  <MenuItem value="active">{t('editambassador.active')}</MenuItem>
                  <MenuItem value="inactive">{t('editambassador.inactive')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={() => {
                setShowAmbassadorForm(false);
                setEditingAmbassador(null);
                setAmbassadorFormStatus('active');
              }}
              variant="outlined"
            >
              {t('editambassador.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                const form = document.querySelector('form') || document.activeElement?.closest('.MuiDialog-root');
                const nameInput = form?.querySelector('input[type="text"]') as HTMLInputElement;
                const emailInput = form?.querySelector('input[type="email"]') as HTMLInputElement;
                const phoneInputs = Array.from(form?.querySelectorAll('input[type="text"]') || []) as HTMLInputElement[];
                const phoneInput = phoneInputs.find((el: any) => el.value !== nameInput?.value && !el.disabled);

                const ambassadorData: any = {
                  ...(editingAmbassador && { id: editingAmbassador.id }),
                  name: editingAmbassador ? editingAmbassador.name : (nameInput?.value || ''),
                  email: editingAmbassador ? editingAmbassador.email : (emailInput?.value || ''),
                  phone: phoneInput?.value || '',
                  status: ambassadorFormStatus
                };

                if (!ambassadorData.name || !ambassadorData.email) {
                  void appDialog.alert(t('editambassador.nameEmailRequired'));
                  return;
                }

                await handleSaveAmbassador(ambassadorData);
                setShowAmbassadorForm(false);
                setEditingAmbassador(null);
                setAmbassadorFormStatus('active');
              }}
              sx={{
                backgroundColor: '#9333ea',
                '&:hover': { backgroundColor: '#7e22ce' }
              }}
            >
              {editingAmbassador ? t('editambassador.update') : t('editambassador.create')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para comprar Vote Boosters */}
      {showVoteBoosterModal && selectedParticipantForBooster && (
        <Dialog
          open={showVoteBoosterModal}
          onClose={() => {
            setShowVoteBoosterModal(false);
            setSelectedParticipantForBooster(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Star className="w-5 h-5" style={{ color: '#f59e0b' }} />
              Comprar Vote Boosters
            </Box>
            <IconButton onClick={() => {
              setShowVoteBoosterModal(false);
              setSelectedParticipantForBooster(null);
            }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ mb: 3, color: '#6b7280' }}>
              Participante: <strong>{selectedParticipantForBooster.participant_name}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: '#6b7280' }}>
              Selecciona un tier de vote booster para agregar votos a este participante:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {voteBoosterService.getAllTiers().map((tier: any) => (
                <Card key={tier.tier} sx={{ border: '1px solid #e5e7eb', '&:hover': { borderColor: '#9333ea' } }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#111827' }}>
                          {tier.tier} = {tier.votes} votos
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          ${tier.price.toFixed(2)}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        onClick={async () => {
                          const userEmail = await appDialog.prompt('Ingresa tu email para la compra:', {
                            inputLabel: 'Email',
                            placeholder: 'ejemplo@correo.com',
                          });
                          if (userEmail) {
                            await handleBuyVoteBooster(tier.tier, userEmail);
                          }
                        }}
                        sx={{
                          backgroundColor: '#9333ea',
                          '&:hover': { backgroundColor: '#7e22ce' }
                        }}
                      >
                        Comprar
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Nota:</strong> Los votos se agregarán inmediatamente después de la compra.
                El pago se procesará según el método de pago configurado.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={() => {
                setShowVoteBoosterModal(false);
                setSelectedParticipantForBooster(null);
              }}
              variant="outlined"
            >
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para Dashboard de Reportes de Vote Boosters */}
      {showVoteBoosterReportModal && selectedContestForReport && (
        <Dialog
          open={showVoteBoosterReportModal}
          onClose={() => {
            setShowVoteBoosterReportModal(false);
            setSelectedContestForReport(null);
            setVoteBoosterStats(null);
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Star className="w-5 h-5" style={{ color: '#f59e0b' }} />
              {t('voteboosterreport.title')} - {selectedContestForReport.name}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileText className="w-4 h-4" />}
                onClick={exportVoteBoosterReportCSV}
                disabled={exportingVoteBoosterReport || !voteBoosterStats}
                sx={{ mr: 1 }}
              >
                {exportingVoteBoosterReport ? t('voteboosterreport.exporting') : t('voteboosterreport.exportCSV')}
              </Button>
              <IconButton onClick={() => {
                setShowVoteBoosterReportModal(false);
                setSelectedContestForReport(null);
                setVoteBoosterStats(null);
              }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {loadingVoteBoosterStats ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#6b7280' }}>
                  {t('voteboosterreport.loadingStats')}
                </Typography>
              </Box>
            ) : voteBoosterStats ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2, mb: 2 }}>
                {/* Estadísticas Generales */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                    <Card sx={{ p: 2, backgroundColor: '#f0f9ff' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          {t('voteboosterreport.totalRevenue')}
                        </Typography>
                        <Tooltip
                          title={
                            <Box sx={{ p: 1 }}>
                              <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                                {t('voteboosterreport.totalRevenueTooltip')}
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="top"
                          PopperProps={{
                            sx: {
                              '& .MuiTooltip-tooltip': {
                                maxWidth: '350px',
                                backgroundColor: '#1f2937',
                                fontSize: '0.875rem'
                              }
                            }
                          }}
                        >
                          <IconButton
                            size="small"
                            sx={{
                              p: 0,
                              color: '#6b7280',
                              '&:hover': { color: '#2563eb' }
                            }}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#059669' }}>
                        ${(voteBoosterStats.generalStats?.total_revenue || 0).toFixed(2)}
                      </Typography>
                    </Card>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                    <Card sx={{ p: 2, backgroundColor: '#fef3c7' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          {t('voteboosterreport.totalPurchases')}
                        </Typography>
                        <Tooltip
                          title={
                            <Box sx={{ p: 1 }}>
                              <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                                {t('voteboosterreport.totalPurchasesTooltip')}
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="top"
                          PopperProps={{
                            sx: {
                              '& .MuiTooltip-tooltip': {
                                maxWidth: '350px',
                                backgroundColor: '#1f2937',
                                fontSize: '0.875rem'
                              }
                            }
                          }}
                        >
                          <IconButton
                            size="small"
                            sx={{
                              p: 0,
                              color: '#6b7280',
                              '&:hover': { color: '#2563eb' }
                            }}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#d97706' }}>
                        {voteBoosterStats.generalStats?.total_purchases || 0}
                      </Typography>
                    </Card>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                    <Card sx={{ p: 2, backgroundColor: '#f3e8ff' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          {t('voteboosterreport.uniqueBuyers')}
                        </Typography>
                        <Tooltip
                          title={
                            <Box sx={{ p: 1 }}>
                              <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                                {t('voteboosterreport.uniqueBuyersTooltip')}
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="top"
                          PopperProps={{
                            sx: {
                              '& .MuiTooltip-tooltip': {
                                maxWidth: '350px',
                                backgroundColor: '#1f2937',
                                fontSize: '0.875rem'
                              }
                            }
                          }}
                        >
                          <IconButton
                            size="small"
                            sx={{
                              p: 0,
                              color: '#6b7280',
                              '&:hover': { color: '#2563eb' }
                            }}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#9333ea' }}>
                        {voteBoosterStats.generalStats?.unique_buyers || 0}
                      </Typography>
                    </Card>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                    <Card sx={{ p: 2, backgroundColor: '#ecfdf5' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          {t('voteboosterreport.conversionRate')}
                        </Typography>
                        <Tooltip
                          title={
                            <Box sx={{ p: 1 }}>
                              <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                                {t('voteboosterreport.conversionRateTooltip')}
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="top"
                          PopperProps={{
                            sx: {
                              '& .MuiTooltip-tooltip': {
                                maxWidth: '350px',
                                backgroundColor: '#1f2937',
                                fontSize: '0.875rem'
                              }
                            }
                          }}
                        >
                          <IconButton
                            size="small"
                            sx={{
                              p: 0,
                              color: '#6b7280',
                              '&:hover': { color: '#2563eb' }
                            }}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                        {voteBoosterStats.conversionRate || 0}%
                      </Typography>
                    </Card>
                  </Box>
                </Box>

                {/* Revenue por Tier */}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {t('voteboosterreport.revenueByTier')}
                  </Typography>

                  {/* Gráfico de Barras - Revenue por Tier (versión simple) */}
                  {voteBoosterStats.revenueByTier && voteBoosterStats.revenueByTier.length > 0 && (
                    <Card sx={{ p: 2, mb: 3, backgroundColor: '#ffffff' }}>
                      <Typography variant="subtitle2" sx={{ mb: 2, color: '#6b7280' }}>
                        {t('voteboosterreport.revenueDistributionByTier')}
                      </Typography>
                      <SimpleBarChart
                        data={voteBoosterStats.revenueByTier.map((tier: any) => ({
                          label: tier.booster_tier,
                          value: Number(tier.total_revenue || 0),
                          maxValue: Math.max(...voteBoosterStats.revenueByTier.map((t: any) => Number(t.total_revenue || 0)))
                        }))}
                      />
                    </Card>
                  )}

                  {/* Gráfico de Pastel - Distribución de Compras por Tier (versión simple) */}
                  {voteBoosterStats.revenueByTier && voteBoosterStats.revenueByTier.length > 0 && (
                    <Card sx={{ p: 2, mb: 3, backgroundColor: '#ffffff' }}>
                      <Typography variant="subtitle2" sx={{ mb: 2, color: '#6b7280' }}>
                        {t('voteboosterreport.purchasesDistributionByTier')}
                      </Typography>
                      <SimplePieChart
                        data={voteBoosterStats.revenueByTier.map((tier: any, index: number) => ({
                          label: tier.booster_tier,
                          value: Number(tier.count || 0),
                          color: ['#059669', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6366f1'][index % 8]
                        }))}
                      />
                    </Card>
                  )}

                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('voteboosterreport.tier')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('voteboosterreport.quantity')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('voteboosterreport.revenue')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('voteboosterreport.totalVotes')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('voteboosterreport.averagePrice')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {voteBoosterStats.revenueByTier && voteBoosterStats.revenueByTier.length > 0 ? (
                          voteBoosterStats.revenueByTier.map((tier: any, index: number) => (
                            <TableRow key={index}>
                              <TableCell>{tier.booster_tier}</TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>{tier.count}</TableCell>
                              <TableCell sx={{ textAlign: 'right', color: '#059669', fontWeight: 'medium' }}>
                                ${(tier.total_revenue || 0).toFixed(2)}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>{tier.total_votes || 0}</TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>
                                ${(tier.avg_price || 0).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                {t('voteboosterreport.noPurchases')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* Top Participantes */}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {t('voteboosterreport.topParticipantsByVotesBought')}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('voteboosterreport.participant')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('voteboosterreport.purchases')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('voteboosterreport.totalVotes')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('voteboosterreport.totalSpent')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {voteBoosterStats.topParticipants && voteBoosterStats.topParticipants.length > 0 ? (
                          voteBoosterStats.topParticipants.map((participant: any, index: number) => (
                            <TableRow key={participant.participant_id}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>{participant.participant_name || participant.participant_id}</TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>{participant.purchases}</TableCell>
                              <TableCell sx={{ textAlign: 'right', fontWeight: 'medium' }}>
                                {participant.total_votes || 0}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right', color: '#059669', fontWeight: 'medium' }}>
                                ${(participant.total_spent || 0).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                {t('voteboosterreport.noParticipantsWithPurchases')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* Ventas por Día */}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {t('voteboosterreport.salesByDay')}
                  </Typography>

                  {/* Gráfico de Barras - Ventas por Día (versión simple) */}
                  {voteBoosterStats.salesByDay && voteBoosterStats.salesByDay.length > 0 && (
                    <Card sx={{ p: 2, mb: 3, backgroundColor: '#ffffff' }}>
                      <Typography variant="subtitle2" sx={{ mb: 2, color: '#6b7280' }}>
                        {t('voteboosterreport.dailySalesTrend')}
                      </Typography>
                      <SimpleBarChart
                        data={voteBoosterStats.salesByDay.slice(-10).map((day: any) => ({
                          label: new Date(day.date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }),
                          value: Number(day.total_revenue || 0),
                          maxValue: Math.max(...voteBoosterStats.salesByDay.map((d: any) => Number(d.total_revenue || 0)))
                        }))}
                      />
                    </Card>
                  )}

                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('voteboosterreport.date')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('voteboosterreport.purchases')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('voteboosterreport.revenue')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('voteboosterreport.votes')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {voteBoosterStats.salesByDay && voteBoosterStats.salesByDay.length > 0 ? (
                          voteBoosterStats.salesByDay.map((day: any, index: number) => (
                            <TableRow key={index}>
                              <TableCell>{new Date(day.date).toLocaleDateString()}</TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>{day.count}</TableCell>
                              <TableCell sx={{ textAlign: 'right', color: '#059669', fontWeight: 'medium' }}>
                                ${(day.total_revenue || 0).toFixed(2)}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>{day.total_votes || 0}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} sx={{ textAlign: 'center', py: 3 }}>
                              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                {t('voteboosterreport.noSalesRecorded')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            ) : (
              <Alert severity="info">
                {t('voteboosterreport.errorLoadingStats')}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={() => {
                setShowVoteBoosterReportModal(false);
                setSelectedContestForReport(null);
                setVoteBoosterStats(null);
              }}
              variant="outlined"
            >
              {t('voteboosterreport.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para Formulario de Venta de Tag */}
      {showTagSaleForm && selectedAmbassadorForSale && (
        <Dialog
          open={showTagSaleForm}
          onClose={() => {
            setShowTagSaleForm(false);
            setSelectedAmbassadorForSale(null);
            setSelectedTagForSale(null);
            setSalePrice('');
            setSelectedContestSponsor(null);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Tag className="w-5 h-5" style={{ color: '#059669' }} />
              Registrar Venta de Tag
            </Box>
            <IconButton onClick={() => {
              setShowTagSaleForm(false);
              setSelectedAmbassadorForSale(null);
              setSelectedTagForSale(null);
              setSalePrice('');
              setSelectedContestSponsor(null);
            }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {loadingAmbassadorTags ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#6b7280' }}>
                  Cargando tags disponibles...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Embajador:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedAmbassadorForSale.name} ({selectedAmbassadorForSale.email})
                  </Typography>
                </Box>

                {!selectedContestSponsor ? (
                  <>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2, mb: 2 }}>
                      Seleccionar Concurso y Sponsor
                    </Typography>
                    {tagGroups.length === 0 ? (
                      <Alert severity="warning">
                        Este embajador no tiene tags disponibles para vender.
                      </Alert>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Concurso</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Sponsor</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Tags Disponibles</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Acción</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {tagGroups.map((group: any, index: number) => {
                              const contest = contestsForTags.find((c: any) => c.id === group.contestId);
                              const sponsor = sponsorshipsForTags.find((s: any) => s.id === group.sponsorId);

                              return (
                                <TableRow
                                  key={index}
                                  hover
                                  sx={{ cursor: 'pointer' }}
                                >
                                  <TableCell>
                                    {contest ? contest.name : group.contestId || 'N/A'}
                                  </TableCell>
                                  <TableCell>
                                    {sponsor ? sponsor.sponsor_name : group.sponsorId || 'N/A'}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'center' }}>
                                    <Chip
                                      label={group.tags.length}
                                      color="success"
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'center' }}>
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      size="small"
                                      onClick={() => {
                                        setSelectedContestSponsor({
                                          contestId: group.contestId || '',
                                          sponsorId: group.sponsorId || ''
                                        });
                                      }}
                                    >
                                      Seleccionar
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          Tags Disponibles
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(() => {
                            const contest = contestsForTags.find((c: any) => c.id === selectedContestSponsor.contestId);
                            const sponsor = sponsorshipsForTags.find((s: any) => s.id === selectedContestSponsor.sponsorId);
                            return `Concurso: ${contest ? contest.name : selectedContestSponsor.contestId || 'N/A'} | Sponsor: ${sponsor ? sponsor.sponsor_name : selectedContestSponsor.sponsorId || 'N/A'}`;
                          })()}
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setSelectedContestSponsor(null);
                          setSelectedTagForSale(null);
                          setSalePrice('');
                        }}
                      >
                        Volver
                      </Button>
                    </Box>

                    <FormControl fullWidth>
                      <InputLabel>Seleccionar Tag</InputLabel>
                      <Select
                        value={selectedTagForSale?.id || ''}
                        label="Seleccionar Tag"
                        onChange={(e) => {
                          const tag = ambassadorTags.find((t: any) => t.id === e.target.value);
                          setSelectedTagForSale(tag || null);
                          if (tag?.price) {
                            setSalePrice(tag.price.toString());
                          }
                        }}
                      >
                        {ambassadorTags
                          .filter((tag: any) =>
                            !tag.sold_at &&
                            tag.contest_id === selectedContestSponsor.contestId &&
                            tag.sponsor_id === selectedContestSponsor.sponsorId
                          )
                          .length === 0 ? (
                          <MenuItem disabled>No hay tags disponibles</MenuItem>
                        ) : (
                          ambassadorTags
                            .filter((tag: any) =>
                              !tag.sold_at &&
                              tag.contest_id === selectedContestSponsor.contestId &&
                              tag.sponsor_id === selectedContestSponsor.sponsorId
                            )
                            .map((tag: any) => (
                              <MenuItem key={tag.id} value={tag.id}>
                                {tag.tag_code} - Precio sugerido: ${tag.price?.toFixed(2) || '20.00'}
                              </MenuItem>
                            ))
                        )}
                      </Select>
                      <FormHelperText>
                        Solo se muestran tags disponibles para este concurso y sponsor
                      </FormHelperText>
                    </FormControl>

                    {selectedTagForSale && (
                      <TextField
                        label="Precio de Venta ($)"
                        type="number"
                        fullWidth
                        value={salePrice}
                        onChange={(e) => setSalePrice(e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        helperText={`Precio sugerido: $${selectedTagForSale.price?.toFixed(2) || '20.00'}`}
                      />
                    )}
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={() => {
                if (selectedContestSponsor) {
                  setSelectedContestSponsor(null);
                  setSelectedTagForSale(null);
                  setSalePrice('');
                } else {
                  setShowTagSaleForm(false);
                  setSelectedAmbassadorForSale(null);
                  setSelectedTagForSale(null);
                  setSalePrice('');
                  setSelectedContestSponsor(null);
                }
              }}
              variant="outlined"
            >
              {selectedContestSponsor ? 'Volver' : 'Cancelar'}
            </Button>
            <Button
              variant="contained"
              color="success"
              disabled={!selectedContestSponsor || !selectedTagForSale || !salePrice || parseFloat(salePrice) <= 0 || loadingAmbassadorTags}
              onClick={async () => {
                if (!selectedTagForSale || !salePrice) return;

                try {
                  const response = await fetch(`${environment.apiBaseUrl}/qr-tags/${selectedTagForSale.id}/sale`, {
                    method: 'PUT',
                    headers: getApiHeaders(),
                    body: JSON.stringify({
                      sold_by_ambassador: true,
                      sold_at: new Date().toISOString(),
                      sale_price: parseFloat(salePrice),
                    }),
                  });

                  if (response.ok) {
                    // Actualizar compensación del embajador
                    try {
                      const compensationResponse = await fetch(`${environment.apiBaseUrl}/ambassador-compensation`, {
                        method: 'POST',
                        headers: getApiHeaders(),
                        body: JSON.stringify({
                          ambassador_id: selectedAmbassadorForSale.id,
                          sponsor_id: selectedTagForSale.sponsor_id || null,
                          tags_sold: 1,
                          revenue_generated: parseFloat(salePrice),
                          compensation_type: 'tag_sale',
                          amount: parseFloat(salePrice), // 100% para el embajador
                          status: 'pending',
                        }),
                      });

                      if (compensationResponse.ok) {
                        // Actualizar totales del embajador
                        await ambassadorService.updateAmbassador(selectedAmbassadorForSale.id, {
                          ...selectedAmbassadorForSale,
                          total_tags_sold: (selectedAmbassadorForSale.total_tags_sold || 0) + 1,
                          total_revenue: (selectedAmbassadorForSale.total_revenue || 0) + parseFloat(salePrice),
                        });
                      }
                    } catch (compError) {
                      console.error('Error creando compensación:', compError);
                    }

                    // Recargar tags y embajadores
                    const tagsResponse = await fetch(`${environment.apiBaseUrl}/qr-tags/ambassador/${selectedAmbassadorForSale.id}`, {
                      headers: getApiHeadersForGet()
                    });
                    if (tagsResponse.ok) {
                      const updatedTags = await tagsResponse.json();
                      setAmbassadorTags(updatedTags);
                    }

                    const ambassadorsResponse = await fetch(`${environment.apiBaseUrl}/ambassadors`, {
                      headers: getApiHeadersForGet()
                    });
                    if (ambassadorsResponse.ok) {
                      const updatedAmbassadors = await ambassadorsResponse.json();
                      setAmbassadors(updatedAmbassadors);
                    }

                    void appDialog.alert('Venta registrada exitosamente');
                    setShowTagSaleForm(false);
                    setSelectedAmbassadorForSale(null);
                    setSelectedTagForSale(null);
                    setSalePrice('');
                    setSelectedContestSponsor(null);
                  } else {
                    const error = await response.json();
                    void appDialog.alert(`Error al registrar la venta: ${error.error || 'Error desconocido'}`);
                  }
                } catch (error) {
                  console.error('Error registrando venta:', error);
                  void appDialog.alert('Error al registrar la venta. Por favor, inténtalo de nuevo.');
                }
              }}
              sx={{
                backgroundColor: '#059669',
                '&:hover': { backgroundColor: '#047857' }
              }}
            >
              Registrar Venta
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para Dashboard de Compensación Individual */}
      {showCompensationDashboard && selectedAmbassadorForCompensation && (
        <Dialog
          open={showCompensationDashboard}
          onClose={() => {
            setShowCompensationDashboard(false);
            setSelectedAmbassadorForCompensation(null);
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <DollarSign className="w-5 h-5" style={{ color: '#9333ea' }} />
              {t('compensationdashboard.individualTitle')} - {selectedAmbassadorForCompensation.name}
            </Box>
            <IconButton onClick={() => {
              setShowCompensationDashboard(false);
              setSelectedAmbassadorForCompensation(null);
            }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {loadingCompensations ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#6b7280' }}>
                  {t('compensationdashboard.loading')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Resumen de Métricas */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Card sx={{ flex: '1 1 250px', minWidth: '200px', backgroundColor: '#f0fdf4', border: '1px solid #86efac' }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t('compensationdashboard.totalRevenue')}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#059669' }}>
                        ${(selectedAmbassadorForCompensation.total_revenue || 0).toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card sx={{ flex: '1 1 250px', minWidth: '200px', backgroundColor: '#fef3c7', border: '1px solid #fde047' }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t('compensationdashboard.tagsSold')}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#d97706' }}>
                        {selectedAmbassadorForCompensation.total_tags_sold || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card sx={{ flex: '1 1 250px', minWidth: '200px', backgroundColor: '#dbeafe', border: '1px solid #93c5fd' }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t('compensationdashboard.signedSponsors')}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2563eb' }}>
                        {selectedAmbassadorForCompensation.total_sponsors_signed || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card sx={{ flex: '1 1 250px', minWidth: '200px', backgroundColor: '#f3e8ff', border: '1px solid #c4b5fd' }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t('compensationdashboard.pendingCompensations')}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#9333ea' }}>
                        ${compensations
                          .filter((c: any) => c.status === 'pending')
                          .reduce((sum: number, c: any) => sum + (c.amount || 0), 0)
                          .toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>

                {/* Tabla de Compensaciones */}
                <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2, mb: 2 }}>
                  {t('compensationdashboard.compensationHistory')}
                </Typography>
                {compensations.length === 0 ? (
                  <Alert severity="info">
                    {t('compensationdashboard.noCompensations')}
                  </Alert>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('compensationdashboard.type')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('compensationdashboard.amount')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('compensationdashboard.tagsSoldHeader')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('compensationdashboard.revenueGenerated')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('compensationdashboard.status')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('compensationdashboard.date')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {compensations.map((compensation: any) => (
                          <TableRow key={compensation.id}>
                            <TableCell>
                              {compensation.compensation_type === 'tag_sale' ? t('compensationdashboard.tagSale') :
                                compensation.compensation_type === 'sponsor_signing' ? t('compensationdashboard.sponsorSigning') :
                                  compensation.compensation_type === 'bonus' ? t('compensationdashboard.bonus') :
                                    compensation.compensation_type}
                            </TableCell>
                            <TableCell>${(compensation.amount || 0).toFixed(2)}</TableCell>
                            <TableCell>{compensation.tags_sold || 0}</TableCell>
                            <TableCell>${(compensation.revenue_generated || 0).toFixed(2)}</TableCell>
                            <TableCell>
                              <Chip
                                label={compensation.status === 'pending' ? t('compensationdashboard.pending') :
                                  compensation.status === 'paid' ? t('compensationdashboard.paid') :
                                    compensation.status}
                                size="small"
                                color={compensation.status === 'paid' ? 'success' : 'warning'}
                              />
                            </TableCell>
                            <TableCell>
                              {compensation.created_at ? new Date(compensation.created_at).toLocaleDateString() : 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={() => {
                setShowCompensationDashboard(false);
                setSelectedAmbassadorForCompensation(null);
              }}
              variant="outlined"
            >
              {t('compensationdashboard.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para Dashboard Global de Compensación de Embajadores */}
      {showGlobalCompensationDashboard && (
        <Dialog
          open={showGlobalCompensationDashboard}
          onClose={() => {
            setShowGlobalCompensationDashboard(false);
            setGlobalCompensationStats(null);
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <DollarSign className="w-5 h-5" style={{ color: '#10b981' }} />
              {t('compensationdashboard.globalTitle')}
            </Box>
            <IconButton onClick={() => {
              setShowGlobalCompensationDashboard(false);
              setGlobalCompensationStats(null);
            }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {loadingGlobalCompensationStats ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#6b7280' }}>
                  {t('compensationdashboard.loadingStats')}
                </Typography>
              </Box>
            ) : globalCompensationStats ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2, mb: 2 }}>
                {/* Estadísticas Generales */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                    <Card sx={{ p: 2, backgroundColor: '#f0f9ff' }}>
                      <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                        {t('compensationdashboard.totalCompensations')}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#059669' }}>
                        {globalCompensationStats.generalStats?.total_compensations || 0}
                      </Typography>
                    </Card>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                    <Card sx={{ p: 2, backgroundColor: '#fef3c7' }}>
                      <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                        {t('compensationdashboard.totalRevenueGenerated')}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#d97706' }}>
                        ${(globalCompensationStats.generalStats?.total_revenue_generated || 0).toFixed(2)}
                      </Typography>
                    </Card>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                    <Card sx={{ p: 2, backgroundColor: '#ecfdf5' }}>
                      <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                        {t('compensationdashboard.totalPaid')}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                        ${(globalCompensationStats.generalStats?.paid_amount || 0).toFixed(2)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        {globalCompensationStats.generalStats?.paid_count || 0} {t('compensationdashboard.compensations')}
                      </Typography>
                    </Card>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                    <Card sx={{ p: 2, backgroundColor: '#fef3c7' }}>
                      <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                        {t('compensationdashboard.totalPending')}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#f59e0b' }}>
                        ${(globalCompensationStats.generalStats?.pending_amount || 0).toFixed(2)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        {globalCompensationStats.generalStats?.pending_count || 0} {t('compensationdashboard.compensations')}
                      </Typography>
                    </Card>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                    <Card sx={{ p: 2, backgroundColor: '#f3e8ff' }}>
                      <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                        {t('compensationdashboard.totalTagsSold')}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#9333ea' }}>
                        {globalCompensationStats.generalStats?.total_tags_sold || 0}
                      </Typography>
                    </Card>
                  </Box>
                </Box>

                {/* Top Embajadores por Revenue */}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {t('compensationdashboard.topByRevenue')}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('compensationdashboard.ambassador')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('compensationdashboard.email')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('compensationdashboard.revenueGeneratedHeader')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('compensationdashboard.tagsSoldHeader')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('compensationdashboard.compensationsHeader')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {globalCompensationStats.topByRevenue && globalCompensationStats.topByRevenue.length > 0 ? (
                          globalCompensationStats.topByRevenue.map((ambassador: any, index: number) => (
                            <TableRow key={ambassador.id}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>{ambassador.name || 'N/A'}</TableCell>
                              <TableCell>{ambassador.email || 'N/A'}</TableCell>
                              <TableCell sx={{ textAlign: 'right', color: '#059669', fontWeight: 'medium' }}>
                                ${(ambassador.total_revenue || 0).toFixed(2)}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>{ambassador.total_tags || 0}</TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>{ambassador.compensations_count || 0}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                {t('compensationdashboard.noCompensationData')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* Top Embajadores por Tags Vendidos */}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {t('compensationdashboard.topByTags')}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('compensationdashboard.ambassador')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('compensationdashboard.email')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('compensationdashboard.tagsSoldHeader')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('compensationdashboard.revenueGeneratedHeader')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('compensationdashboard.compensationsHeader')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {globalCompensationStats.topByTags && globalCompensationStats.topByTags.length > 0 ? (
                          globalCompensationStats.topByTags.map((ambassador: any, index: number) => (
                            <TableRow key={ambassador.id}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>{ambassador.name || 'N/A'}</TableCell>
                              <TableCell>{ambassador.email || 'N/A'}</TableCell>
                              <TableCell sx={{ textAlign: 'center', fontWeight: 'medium' }}>
                                {ambassador.total_tags || 0}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right', color: '#059669', fontWeight: 'medium' }}>
                                ${(ambassador.total_revenue || 0).toFixed(2)}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>{ambassador.compensations_count || 0}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                {t('compensationdashboard.noCompensationData')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* Top Embajadores por Sponsors Firmados */}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {t('compensationdashboard.topBySponsors')}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('compensationdashboard.ambassador')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('compensationdashboard.email')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('compensationdashboard.signedSponsors')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('compensationdashboard.tagsSoldHeader')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('compensationdashboard.totalRevenue')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {globalCompensationStats.topBySponsors && globalCompensationStats.topBySponsors.length > 0 ? (
                          globalCompensationStats.topBySponsors.map((ambassador: any, index: number) => (
                            <TableRow key={ambassador.id}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>{ambassador.name || 'N/A'}</TableCell>
                              <TableCell>{ambassador.email || 'N/A'}</TableCell>
                              <TableCell sx={{ textAlign: 'center', fontWeight: 'medium' }}>
                                {ambassador.total_sponsors_signed || 0}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>{ambassador.total_tags_sold || 0}</TableCell>
                              <TableCell sx={{ textAlign: 'right', color: '#059669', fontWeight: 'medium' }}>
                                ${(ambassador.total_revenue || 0).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                {t('compensationdashboard.noAmbassadorsWithSponsors')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* Distribución por Tipo de Compensación */}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {t('compensationdashboard.distributionByType')}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('compensationdashboard.type')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('compensationdashboard.count')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('compensationdashboard.totalAmount')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {globalCompensationStats.distributionByType && globalCompensationStats.distributionByType.length > 0 ? (
                          globalCompensationStats.distributionByType.map((item: any, index: number) => (
                            <TableRow key={index}>
                              <TableCell>
                                {item.compensation_type === 'tag_sale' ? t('compensationdashboard.tagSale') :
                                  item.compensation_type === 'sponsor_signing' ? t('compensationdashboard.sponsorSigning') :
                                    item.compensation_type === 'bonus' ? t('compensationdashboard.bonus') :
                                      item.compensation_type || 'N/A'}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>{item.count || 0}</TableCell>
                              <TableCell sx={{ textAlign: 'right', color: '#059669', fontWeight: 'medium' }}>
                                ${(item.total_amount || 0).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3 }}>
                              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                {t('compensationdashboard.noCompensationsRegistered')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            ) : (
              <Alert severity="info">
                {t('compensationdashboard.errorLoadingStats')}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={() => {
                setShowGlobalCompensationDashboard(false);
                setGlobalCompensationStats(null);
              }}
              variant="outlined"
            >
              {t('compensationdashboard.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para mostrar Tags del Sponsor */}
      {showSponsorTagsModal && (
        <Dialog
          open={showSponsorTagsModal}
          onClose={() => setShowSponsorTagsModal(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Tag className="w-5 h-5" style={{ color: '#f59e0b' }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Tags Físicos Asignados al Sponsor
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (Physical Entry Tags)
              </Typography>
            </Box>
            <IconButton onClick={() => setShowSponsorTagsModal(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {loadingSponsorTags ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <Typography variant="body1" color="text.secondary">
                  Cargando tags...
                </Typography>
              </Box>
            ) : sponsorTags.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body1">
                  No hay tags asignados a este sponsor todavía.
                </Typography>
              </Alert>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#6b7280' }}>
                    Total de tags: <strong>{sponsorTags.length}</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      label={`Disponibles: ${sponsorTags.filter((t: any) => !t.sold_by_ambassador).length}`}
                      color="success"
                      size="small"
                    />
                    <Chip
                      label={`Vendidos: ${sponsorTags.filter((t: any) => t.sold_by_ambassador).length}`}
                      color="warning"
                      size="small"
                    />
                  </Box>
                </Box>
                <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Tag Code</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Estado</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Tipo</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Precio</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Fecha de Asignación</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Vendido Por</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Fecha de Venta</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Precio de Venta</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sponsorTags.map((tag: any) => (
                        <TableRow key={tag.id} hover>
                          <TableCell sx={{ textAlign: 'center', fontWeight: 'medium' }}>
                            {tag.tag_code || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Chip
                              label={tag.sold_by_ambassador ? 'Vendido' : 'Disponible'}
                              color={tag.sold_by_ambassador ? 'warning' : 'success'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Chip
                              label={tag.tag_type === 'physical' ? 'Físico' : 'Digital'}
                              color="primary"
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            ${tag.price ? tag.price.toFixed(2) : '20.00'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {tag.created_at ? new Date(tag.created_at).toLocaleDateString() : 'N/A'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {tag.sold_by_ambassador ? (tag.ambassador_id ? 'Embajador' : 'N/A') : '—'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {tag.sold_at ? new Date(tag.sold_at).toLocaleDateString() : '—'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', fontWeight: 'medium', color: tag.sale_price ? '#059669' : '#6b7280' }}>
                            {tag.sale_price ? `$${tag.sale_price.toFixed(2)}` : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={() => setShowSponsorTagsModal(false)}
              variant="contained"
              sx={{
                backgroundColor: '#6b7280',
                '&:hover': { backgroundColor: '#4b5563' }
              }}
            >
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {showAmbassadorAssignmentsModal && selectedAmbassadorForAssignments && (
        <Dialog
          open={showAmbassadorAssignmentsModal}
          onClose={() => {
            setShowAmbassadorAssignmentsModal(false);
            setSelectedAmbassadorForAssignments(null);
            setAmbassadorAssignments([]);
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid #e5e7eb' }}>
            {t('ambassadormanagement.assignedContests')} — {selectedAmbassadorForAssignments.name}
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {loadingAmbassadorAssignments ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography>{t('ambassadormanagement.loadingAssignments')}</Typography>
              </Box>
            ) : ambassadorAssignments.length === 0 ? (
              <Typography color="text.secondary">{t('ambassadormanagement.noAssignments')}</Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('ambassadormanagement.contest')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('ambassadormanagement.sponsor')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">{t('ambassadormanagement.tags')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">{t('ambassadormanagement.digitalPasses')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">{t('ambassadormanagement.notificationStatus')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ambassadorAssignments.map((assignment: any) => (
                      <TableRow key={assignment.sponsorship_id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{assignment.contest_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{assignment.contest_phase}</Typography>
                        </TableCell>
                        <TableCell>{assignment.sponsor_name}</TableCell>
                        <TableCell align="center">{assignment.ambassador_tags_count || 0}</TableCell>
                        <TableCell align="center">{assignment.digital_passes_count || 0}</TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={
                              assignment.last_notification_email_sent === 1
                                ? t('ambassadormanagement.notificationSent')
                                : assignment.last_notification_email_sent === 0 && assignment.notifications_count > 0
                                  ? t('ambassadormanagement.notificationFailed')
                                  : t('ambassadormanagement.notificationPending')
                            }
                            color={
                              assignment.last_notification_email_sent === 1
                                ? 'success'
                                : assignment.last_notification_email_sent === 0 && assignment.notifications_count > 0
                                  ? 'error'
                                  : 'default'
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setShowAmbassadorAssignmentsModal(false);
              setSelectedAmbassadorForAssignments(null);
              setAmbassadorAssignments([]);
            }}>
              {t('ambassadormanagement.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para mostrar Tags del Embajador */}
      {showAmbassadorTagsModal && selectedAmbassadorForTags && (
        <Dialog
          open={showAmbassadorTagsModal}
          onClose={() => {
            setShowAmbassadorTagsModal(false);
            setSelectedAmbassadorForTags(null);
            setAmbassadorTagsSearchTerm('');
          }}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <QrCode className="w-5 h-5" style={{ color: '#9333ea' }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {t('ambassadortags.title')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {t('ambassadortags.subtitle')}
              </Typography>
            </Box>
            <IconButton onClick={() => {
              setShowAmbassadorTagsModal(false);
              setSelectedAmbassadorForTags(null);
              setAmbassadorTagsSearchTerm('');
            }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#111827' }}>
                {t('ambassadortags.ambassador')} <strong>{selectedAmbassadorForTags.name}</strong> ({selectedAmbassadorForTags.email})
              </Typography>
            </Box>
            {loadingAmbassadorTagsView ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <LinearProgress sx={{ mb: 2, width: '100%' }} />
                <Typography variant="body1" color="text.secondary">
                  {t('ambassadortags.loading')}
                </Typography>
              </Box>
            ) : ambassadorTagsView.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body1">
                  {t('ambassadortags.noTagsAssigned')}
                </Typography>
              </Alert>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#6b7280' }}>
                    {t('ambassadortags.totalTags')} <strong>{ambassadorTagsView.length}</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      label={`${t('ambassadortags.available')} ${ambassadorTagsView.filter((t: any) => !t.sold_at).length}`}
                      color="success"
                      size="small"
                    />
                    <Chip
                      label={`${t('ambassadortags.sold')} ${ambassadorTagsView.filter((t: any) => t.sold_at).length}`}
                      color="warning"
                      size="small"
                    />
                  </Box>
                </Box>

                {/* Buscador de tags */}
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder={t('ambassadortags.searchPlaceholder')}
                    value={ambassadorTagsSearchTerm}
                    onChange={(e) => setAmbassadorTagsSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Filter className="w-5 h-5" style={{ color: '#6b7280' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#9333ea',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#9333ea',
                        },
                      },
                    }}
                  />
                </Box>

                <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadortags.tagCode')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadortags.contest')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadortags.sponsor')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadortags.status')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadortags.type')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadortags.price')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadortags.assignmentDate')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadortags.saleDate')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ambassadortags.salePrice')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        // Función para verificar si el término de búsqueda es una subsecuencia del texto
                        const isSubsequence = (searchTerm: string, text: string): boolean => {
                          if (!text || !searchTerm) return false;
                          searchTerm = searchTerm.toLowerCase().trim();
                          text = text.toLowerCase().trim();
                          if (searchTerm.length === 0) return true;
                          if (searchTerm.length > text.length) return false;

                          let searchIndex = 0;
                          for (let i = 0; i < text.length && searchIndex < searchTerm.length; i++) {
                            if (text[i] === searchTerm[searchIndex]) {
                              searchIndex++;
                            }
                          }
                          return searchIndex === searchTerm.length;
                        };

                        // Filtrar tags según el término de búsqueda
                        const filteredTags = ambassadorTagsView.filter((tag: any) => {
                          const searchTerm = ambassadorTagsSearchTerm.trim();
                          if (!searchTerm) return true;

                          const contest = contestsForTags.find((c: any) => c.id === tag.contest_id);
                          const sponsor = sponsorshipsForTags.find((s: any) => s.id === tag.sponsor_id);

                          const tagCode = (tag.tag_code || '').trim();
                          const contestName = (contest ? contest.name : tag.contest_id || '').trim();
                          const sponsorName = (sponsor ? sponsor.sponsor_name : tag.sponsor_id || '').trim();

                          return (
                            isSubsequence(searchTerm, tagCode) ||
                            isSubsequence(searchTerm, contestName) ||
                            isSubsequence(searchTerm, sponsorName)
                          );
                        });

                        if (filteredTags.length === 0) {
                          return (
                            <TableRow key="no-results">
                              <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                  {ambassadorTagsView.length === 0
                                    ? t('ambassadortags.noTagsFound')
                                    : t('ambassadortags.noTagsMatchingSearch')}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        }

                        return filteredTags.map((tag: any) => {
                          const contest = contestsForTags.find((c: any) => c.id === tag.contest_id);
                          const sponsor = sponsorshipsForTags.find((s: any) => s.id === tag.sponsor_id);

                          return (
                            <TableRow key={tag.id} hover>
                              <TableCell sx={{ textAlign: 'center', fontWeight: 'medium' }}>
                                {tag.tag_code || 'N/A'}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                {contest ? contest.name : tag.contest_id || 'N/A'}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                {sponsor ? sponsor.sponsor_name : tag.sponsor_id || 'N/A'}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                <Chip
                                  label={tag.sold_at ? t('ambassadortags.soldStatus') : t('ambassadortags.availableStatus')}
                                  color={tag.sold_at ? 'warning' : 'success'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                <Chip
                                  label={tag.tag_type === 'physical' ? t('ambassadortags.physical') : t('ambassadortags.digital')}
                                  color="primary"
                                  size="small"
                                />
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                ${tag.price ? tag.price.toFixed(2) : '20.00'}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                {tag.created_at ? new Date(tag.created_at).toLocaleDateString() : 'N/A'}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                {tag.sold_at ? new Date(tag.sold_at).toLocaleDateString() : '—'}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center', fontWeight: 'medium', color: tag.sale_price ? '#059669' : '#6b7280' }}>
                                {tag.sale_price ? `$${tag.sale_price.toFixed(2)}` : '—'}
                              </TableCell>
                            </TableRow>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={() => {
                setShowAmbassadorTagsModal(false);
                setSelectedAmbassadorForTags(null);
                setAmbassadorTagsSearchTerm('');
              }}
              variant="contained"
              sx={{
                backgroundColor: '#9333ea',
                '&:hover': { backgroundColor: '#7e22ce' }
              }}
            >
              {t('ambassadortags.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para gestionar plataformas base */}
      {showPlatformsModal && (
        <Dialog
          open={showPlatformsModal}
          onClose={() => {
            setShowPlatformsModal(false);
            setPlatformsSearchTerm('');
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Settings className="w-5 h-5" style={{ color: '#7c3aed' }} />
              {t('platforms.managePlatforms')}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={async () => {
                  setShowSponsorshipTypesModal(true);
                  setLoadingSponsorshipTypes(true);
                  try {
                    const types = await fetch(`${environment.apiBaseUrl}/platforms/types/sponsorship`, {
                      headers: getApiHeadersForGet()
                    }).then(r => r.json());
                    setSponsorshipTypes(normalizeSponsorshipTypes(types));

                    // Cargar plataformas base para agregar a la lista disponible
                    const basePlatforms = await fetch(`${environment.apiBaseUrl}/platforms`, {
                      headers: getApiHeadersForGet()
                    }).then(r => r.json());
                    // Filtrar solo las plataformas habilitadas y excluir la de configuración
                    const enabledPlatforms = basePlatforms.filter((p: any) => p.enabled && p.id !== 'sponsorship_types_config');
                    const basePlatformIds = enabledPlatforms.map((p: any) => p.id);
                    setAvailablePlatformsForTypes(['voletaje', ...basePlatformIds]);

                    // Crear un mapa de ID -> nombre para mostrar nombres en lugar de IDs
                    const map: Record<string, string> = {
                      'voletaje': t('platforms.platformVoletaje')
                    };
                    enabledPlatforms.forEach((p: any) => {
                      map[p.id] = p.name || p.id;
                    });
                    setPlatformsMap(map);
                  } catch (error) {
                    console.error('Error loading sponsorship types:', error);
                  } finally {
                    setLoadingSponsorshipTypes(false);
                  }
                }}
                variant="outlined"
                startIcon={<Settings className="w-4 h-4" />}
                sx={{
                  borderColor: '#7c3aed',
                  color: '#7c3aed',
                  '&:hover': {
                    borderColor: '#6d28d9',
                    backgroundColor: 'rgba(124, 58, 237, 0.1)'
                  }
                }}
              >
                {t('platforms.configureSponsorshipTypes')}
              </Button>
              <Button
                onClick={() => {
                  setShowPlatformsModal(false);
                  setPlatformsSearchTerm('');
                }}
                sx={{ minWidth: 'auto', p: 1 }}
              >
                <X className="w-5 h-5" />
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {loadingPlatforms ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography>{t('platforms.loading')}</Typography>
              </Box>
            ) : platforms.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Settings className="w-12 h-12 mx-auto mb-4" style={{ color: '#d1d5db' }} />
                <Typography variant="body1" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
                  {t('platforms.noPlatforms')}
                </Typography>
              </Box>
            ) : (
              <Box>
                {/* Buscador de Plataformas */}
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder={t('platforms.searchPlaceholder')}
                    value={platformsSearchTerm}
                    onChange={(e) => setPlatformsSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Filter className="w-5 h-5" style={{ color: '#6b7280' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#7c3aed',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#7c3aed',
                        },
                      },
                    }}
                  />
                </Box>

                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('platforms.name')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('platforms.status')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('platforms.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        // Función para verificar si el término de búsqueda es una subsecuencia del texto
                        const isSubsequence = (searchTerm: string, text: string): boolean => {
                          if (!text || !searchTerm) return false;
                          searchTerm = searchTerm.toLowerCase().trim();
                          text = text.toLowerCase().trim();
                          if (searchTerm.length === 0) return true;
                          if (searchTerm.length > text.length) return false;

                          let searchIndex = 0;
                          for (let i = 0; i < text.length && searchIndex < searchTerm.length; i++) {
                            if (text[i] === searchTerm[searchIndex]) {
                              searchIndex++;
                            }
                          }
                          return searchIndex === searchTerm.length;
                        };

                        // Filtrar plataformas según el término de búsqueda
                        const filteredPlatforms = platforms.filter((platform) => {
                          const searchTerm = platformsSearchTerm.trim();
                          if (!searchTerm) return true;

                          const platformName = (platform.name || '').trim();
                          return isSubsequence(searchTerm, platformName);
                        });

                        if (filteredPlatforms.length === 0) {
                          return (
                            <TableRow key="no-results">
                              <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4 }}>
                                <Typography variant="body1" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
                                  {platforms.length === 0
                                    ? t('platforms.noPlatforms')
                                    : 'No se encontraron plataformas que coincidan con la búsqueda'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        }

                        return filteredPlatforms.map((platform) => (
                          <TableRow key={platform.id}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 24, height: 24, backgroundColor: '#7c3aed', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>P</Typography>
                                </Box>
                                {platform.name}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={platform.enabled ? t('platforms.enabled') : t('platforms.disabled')}
                                color={platform.enabled ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    setSelectedPlatformForPreview(platform);
                                    setPreviewImageError(false);
                                    setShowPlatformPreview(true);
                                  }}
                                >
                                  {t('platforms.thumbnail')} {platform.name}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    setEditingPlatform(platform);
                                    setPlatformImage(platform.image || null);
                                    setPlatformImageFileName(null); // Reset file name when editing existing platform
                                    setPlatformImageError(false);
                                    setPlatformName(platform.name);
                                    const width = platform.width || 200;
                                    const height = platform.height || 150;
                                    setPlatformPosition({
                                      x: platform.x || 50,
                                      y: platform.y || 50,
                                      width: width,
                                      height: height
                                    });
                                    setTempWidth(String(width));
                                    setTempHeight(String(height));
                                    setPlatformShape(platform.Image_form || platform.shape || 'circular');
                                    setPlatformLogoUrl(platform.logo_url || '');
                                    setPlatformCustomText(platform.custom_text || 'your ad goes here');
                                    setPlatformFontSize(platform.Text_size || 0.9);
                                    setPlatformRotation(platform.rotation || 0);
                                    setTempRotation(String(platform.rotation || 0));
                                    setPlatformBackgroundColor(platform.background_color || '#f5f5dc');
                                    setPlatformDisplayType(platform.display_type || 'volante');
                                    const logoW = platform.logo_width || 60;
                                    const logoH = platform.logo_height || 60;
                                    setLogoSize({ width: logoW, height: logoH });
                                    setTempLogoWidth(String(logoW));
                                    setTempLogoHeight(String(logoH));
                                    setShowAddPlatformModal(true);
                                  }}
                                >
                                  {t('platforms.edit')}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={async () => {
                                    if (await appDialog.confirm(t('platforms.confirmDelete'), { destructive: true })) {
                                      try {
                                        const response = await fetch(`${environment.apiBaseUrl}/platforms/${platform.id}`, {
                                          method: 'DELETE',
                                          headers: getApiHeaders()
                                        });
                                        if (response.ok) {
                                          // Recargar plataformas
                                          const updatedPlatforms = await fetch(`${environment.apiBaseUrl}/platforms`, {
                                            headers: getApiHeadersForGet()
                                          }).then(r => r.json());
                                          setPlatforms(updatedPlatforms);
                                        }
                                      } catch (error) {
                                        console.error('Error deleting platform:', error);
                                        void appDialog.alert(t('platforms.errorDeleting'));
                                      }
                                    }
                                  }}
                                >
                                  {t('platforms.delete')}
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ));
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                onClick={() => {
                  setEditingPlatform(null);
                  setPlatformImage(null);
                  setPlatformImageFileName(null);
                  setPlatformImageError(false);
                  setPlatformName('');
                  setPlatformPosition({ x: 50, y: 50, width: 200, height: 150 });
                  setTempWidth('200');
                  setTempHeight('150');
                  setPlatformShape('circular');
                  setPlatformLogoUrl('');
                  setPlatformCustomText('your ad goes here');
                  setPlatformFontSize(0.9);
                  setPlatformRotation(0);
                  setTempRotation('0');
                  setPlatformBackgroundColor('#f5f5dc');
                  setPlatformDisplayType('volante');
                  setLogoSize({ width: 60, height: 60 });
                  setTempLogoWidth('60');
                  setTempLogoHeight('60');
                  setShowAddPlatformModal(true);
                }}
                variant="contained"
                startIcon={<Plus />}
                sx={{
                  backgroundColor: '#7c3aed',
                  '&:hover': { backgroundColor: '#6d28d9' }
                }}
              >
                {t('platforms.addPlatform')}
              </Button>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={() => {
                setShowPlatformsModal(false);
                setPlatformsSearchTerm('');
              }}
              variant="outlined"
            >
              {t('buttonsforms.Close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para agregar/editar plataforma base */}
      {showAddPlatformModal && (
        <Dialog
          open={showAddPlatformModal}
          onClose={() => setShowAddPlatformModal(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            {editingPlatform ? t('platforms.editPlatform') : t('platforms.addPlatform')}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Columna izquierda: Formulario de datos de imagen */}
              <Box sx={{ flex: '0 0 400px', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <TextField
                  fullWidth
                  label={t('platforms.name')}
                  value={platformName}
                  onChange={(e) => setPlatformName(e.target.value)}
                  margin="normal"
                  required
                />

                {(!platformLogoUrl || platformLogoUrl.trim() === '') && (
                  <FormControl fullWidth margin="normal">
                    <InputLabel>{t('platforms.shape')}</InputLabel>
                    <Select
                      value={platformShape}
                      label={t('platforms.shape')}
                      onChange={(e) => setPlatformShape(e.target.value as 'circular' | 'square')}
                    >
                      <MenuItem value="circular">{t('platforms.circular')}</MenuItem>
                      <MenuItem value="square">{t('platforms.square')}</MenuItem>
                    </Select>
                  </FormControl>
                )}

                <TextField
                  fullWidth
                  label={t('platforms.logoImageUrl')}
                  value={platformLogoUrl}
                  onChange={(e) => setPlatformLogoUrl(e.target.value)}
                  margin="normal"
                  placeholder="https://ejemplo.com/logo.png"
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label={t('platforms.customText')}
                    value={platformCustomText}
                    onChange={(e) => setPlatformCustomText(e.target.value)}
                    margin="normal"
                    placeholder="your ad goes here"
                  />
                  <TextField
                    fullWidth
                    label={t('platforms.fontSize')}
                    type="number"
                    value={platformFontSize}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      if (!isNaN(value) && value > 0) {
                        setPlatformFontSize(value);
                      }
                    }}
                    margin="normal"
                    inputProps={{ step: 0.1, min: 0.1, max: 5 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">rem</InputAdornment>
                    }}
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('platforms.uploadImage')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setPlatformImageFileName(file.name);
                          setUploadingImage(true);
                          setPlatformImageError(false);

                          try {
                            // Subir imagen a S3
                            const response = await uploadImageToS3(file);
                            // Guardar la URL de S3 en lugar de base64
                            setPlatformImage(response.contentUrl);
                            setPlatformImageError(false);
                          } catch (error: any) {
                            console.error('Error uploading image to S3:', error);
                            setPlatformImageError(true);
                            void appDialog.alert(error.message || 'Error al subir la imagen a S3');
                            setPlatformImageFileName(null);
                          } finally {
                            setUploadingImage(false);
                          }
                        }
                      }}
                      style={{ display: 'none' }}
                      id="platform-image-upload"
                      disabled={uploadingImage}
                    />
                    <label htmlFor="platform-image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        disabled={uploadingImage}
                        sx={{
                          borderColor: '#d1d5db',
                          color: '#374151',
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: '#9ca3af',
                            backgroundColor: '#f9fafb'
                          }
                        }}
                      >
                        {uploadingImage ? t('platforms.loading') || 'Subiendo...' : t('platforms.selectFile')}
                      </Button>
                    </label>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      {uploadingImage ? 'Subiendo imagen...' : (platformImageFileName || t('platforms.noFileSelected'))}
                    </Typography>
                  </Box>
                </Box>

                {/* Controles de tamaño */}
                {platformImage && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {/* Columna de títulos */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end', pt: 1, minWidth: '80px' }}>
                        <Typography variant="body2">
                          {t('platforms.manual')}:
                        </Typography>
                        <Typography variant="body2">
                          {t('platforms.logoSize')}:
                        </Typography>
                      </Box>

                      {/* Columna de controles */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Controles manuales */}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            size="small"
                            label={t('platforms.width')}
                            type="number"
                            value={tempWidth}
                            onChange={(e) => {
                              setTempWidth(e.target.value);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const value = parseInt(tempWidth) || 200;
                                const validatedValue = Math.max(30, Math.min(1000, value));
                                setPlatformPosition(prev => ({
                                  ...prev,
                                  width: validatedValue
                                }));
                                setTempWidth(String(validatedValue));
                              }
                            }}
                            sx={{ width: '100px' }}
                          />
                          <Typography variant="body2">x</Typography>
                          <TextField
                            size="small"
                            label={t('platforms.height')}
                            type="number"
                            value={tempHeight}
                            onChange={(e) => {
                              setTempHeight(e.target.value);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const value = parseInt(tempHeight) || 150;
                                const validatedValue = Math.max(30, Math.min(1000, value));
                                setPlatformPosition(prev => ({
                                  ...prev,
                                  height: validatedValue
                                }));
                                setTempHeight(String(validatedValue));
                              }
                            }}
                            sx={{ width: '100px' }}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => {
                              const widthValue = parseInt(tempWidth) || 200;
                              const heightValue = parseInt(tempHeight) || 150;
                              const validatedWidth = Math.max(30, Math.min(1000, widthValue));
                              const validatedHeight = Math.max(30, Math.min(1000, heightValue));
                              setPlatformPosition(prev => ({
                                ...prev,
                                width: validatedWidth,
                                height: validatedHeight
                              }));
                              setTempWidth(String(validatedWidth));
                              setTempHeight(String(validatedHeight));
                            }}
                            sx={{ ml: 1, minWidth: 'auto', px: 2 }}
                          >
                            {t('platforms.applySize')}
                          </Button>
                        </Box>

                        {/* Controles de tamaño del logo */}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            size="small"
                            label={t('platforms.logoWidth')}
                            type="number"
                            value={tempLogoWidth}
                            onChange={(e) => {
                              setTempLogoWidth(e.target.value);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const value = parseInt(tempLogoWidth) || 60;
                                const validatedValue = Math.max(30, Math.min(1000, value));
                                setLogoSize(prev => ({
                                  ...prev,
                                  width: validatedValue
                                }));
                                setTempLogoWidth(String(validatedValue));
                              }
                            }}
                            sx={{ width: '100px' }}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                          />
                          <Typography variant="body2">x</Typography>
                          <TextField
                            size="small"
                            label={t('platforms.logoHeight')}
                            type="number"
                            value={tempLogoHeight}
                            onChange={(e) => {
                              setTempLogoHeight(e.target.value);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const value = parseInt(tempLogoHeight) || 60;
                                const validatedValue = Math.max(10, Math.min(1000, value));
                                setLogoSize(prev => ({
                                  ...prev,
                                  height: validatedValue
                                }));
                                setTempLogoHeight(String(validatedValue));
                              }
                            }}
                            sx={{ width: '100px' }}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => {
                              const widthValue = parseInt(tempLogoWidth) || 60;
                              const heightValue = parseInt(tempLogoHeight) || 60;
                              const validatedWidth = Math.max(30, Math.min(1000, widthValue));
                              const validatedHeight = Math.max(10, Math.min(1000, heightValue));
                              setLogoSize({
                                width: validatedWidth,
                                height: validatedHeight
                              });
                              setTempLogoWidth(String(validatedWidth));
                              setTempLogoHeight(String(validatedHeight));
                            }}
                            sx={{ ml: 1, minWidth: 'auto', px: 2 }}
                          >
                            {t('platforms.applySize')}
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Control de rotación del voleto */}
                {platformImage && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ minWidth: '80px' }}>
                        {t('platforms.rotation')}:
                      </Typography>
                      <TextField
                        size="small"
                        label={t('platforms.angleDegrees')}
                        type="number"
                        value={tempRotation}
                        onChange={(e) => {
                          setTempRotation(e.target.value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const value = parseInt(tempRotation) || 0;
                            const validatedValue = Math.max(-360, Math.min(360, value));
                            setPlatformRotation(validatedValue);
                            setTempRotation(String(validatedValue));
                          }
                        }}
                        sx={{ width: '250px' }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">°</InputAdornment>,
                        }}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          const value = parseInt(tempRotation) || 0;
                          const validatedValue = Math.max(-360, Math.min(360, value));
                          setPlatformRotation(validatedValue);
                          setTempRotation(String(validatedValue));
                        }}
                        sx={{ ml: 1, minWidth: 'auto', px: 2 }}
                      >
                        {t('platforms.apply')}
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Control de color de fondo del voleto */}
                {platformImage && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ minWidth: '80px' }}>
                        {t('platforms.backgroundColor')}:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <input
                          type="color"
                          value={platformBackgroundColor}
                          onChange={(e) => {
                            setPlatformBackgroundColor(e.target.value);
                          }}
                          style={{
                            width: '60px',
                            height: '40px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        />
                        <TextField
                          size="small"
                          label={t('platforms.colorCode')}
                          value={platformBackgroundColor}
                          onChange={(e) => {
                            setPlatformBackgroundColor(e.target.value);
                          }}
                          sx={{ width: '150px' }}
                          placeholder="#f5f5dc"
                        />
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Control de tipo de display del voleto */}
                {platformImage && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, minWidth: '80px' }}>
                      {t('platforms.displayType')}:
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        value={platformDisplayType}
                        onChange={(e) => setPlatformDisplayType(e.target.value as 'volante' | 'imagen' | 'nada')}
                        row
                      >
                        <FormControlLabel
                          value="volante"
                          control={<MuiRadio />}
                          label={t('platforms.flyer')}
                        />
                        <FormControlLabel
                          value="imagen"
                          control={<MuiRadio />}
                          label={t('platforms.image')}
                        />
                        <FormControlLabel
                          value="nada"
                          control={<MuiRadio />}
                          label={t('platforms.nothing')}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                )}
              </Box>

              {/* Columna derecha: Imagen con voleto */}
              {platformImage && (
                <Box sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  maxHeight: '100%',
                  border: '2px solid #d1d5db',
                  borderRadius: 2,
                  backgroundColor: '#fafafa',
                  p: 1.5,
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Scrollbar superior sincronizada - fija en la parte superior */}
                  <Box
                    id={`scrollbar-top-${editingPlatform?.id || 'new'}`}
                    sx={{
                      width: 'calc(100% - 16px)',
                      overflowX: 'auto',
                      overflowY: 'hidden',
                      height: '16px',
                      mb: 1.5,
                      position: 'sticky',
                      top: 0,
                      zIndex: 100,
                      backgroundColor: '#fafafa',
                      paddingTop: '8px',
                      paddingBottom: '8px',
                      marginTop: '-8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      '&::-webkit-scrollbar': {
                        height: '16px'
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#e5e7eb',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db'
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#6b7280',
                        borderRadius: '8px',
                        border: '1px solid #4b5563',
                        '&:hover': {
                          background: '#4b5563'
                        }
                      }
                    }}
                    onScroll={(e) => {
                      const bottomScroll = document.getElementById(`scrollbar-bottom-${editingPlatform?.id || 'new'}`);
                      if (bottomScroll) {
                        bottomScroll.scrollLeft = e.currentTarget.scrollLeft;
                      }
                    }}
                  >
                    <Box sx={{ width: '800px', height: '1px' }} />
                  </Box>

                  {/* Contenedor principal con imagen - scrollbar oculta */}
                  <Box
                    id={`scrollbar-bottom-${editingPlatform?.id || 'new'}`}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      overflowX: 'auto',
                      overflowY: 'hidden',
                      width: '100%',
                      position: 'relative',
                      paddingBottom: '0px',
                      marginBottom: '0px'
                    }}
                    style={{
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none'
                    } as React.CSSProperties}
                    onScroll={(e) => {
                      const topScroll = document.getElementById(`scrollbar-top-${editingPlatform?.id || 'new'}`);
                      if (topScroll) {
                        topScroll.scrollLeft = e.currentTarget.scrollLeft;
                      }
                    }}
                  >
                    <Box sx={{
                      position: 'relative',
                      width: '800px',
                      minWidth: '800px',
                      flexShrink: 0,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      overflow: 'hidden',
                      backgroundColor: '#f9f9f9'
                    }}>
                      {/* Imagen de fondo */}
                      {!platformImageError ? (
                        <img
                          src={platformImage}
                          alt="Platform background"
                          style={{
                            width: '800px',
                            height: 'auto',
                            display: 'block'
                          }}
                          onError={(e) => {
                            console.error('Error loading platform image:', platformImage);
                            setPlatformImageError(true);
                            e.currentTarget.style.display = 'none';
                          }}
                          onLoad={() => {
                            setPlatformImageError(false);
                          }}
                          crossOrigin={platformImage?.startsWith('data:') ? 'anonymous' : undefined}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '800px',
                            height: '600px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f0f0f0',
                            border: '2px dashed #ccc',
                            color: '#666',
                            p: 3
                          }}
                        >
                          <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>
                            {t('platforms.noImage')}
                          </Typography>
                          <Typography variant="body2" sx={{ textAlign: 'center', fontSize: '0.875rem' }}>
                            {platformImage?.includes('slack.com') || platformImage?.includes('files.slack.com')
                              ? 'Los enlaces de Slack no se pueden cargar directamente. Por favor, usa una URL pública de imagen o sube la imagen directamente.'
                              : 'No se pudo cargar la imagen. Verifica que la URL sea válida y accesible.'}
                          </Typography>
                        </Box>
                      )}

                      {/* Volante arrastrable con datos por defecto */}
                      {platformDisplayType !== 'nada' && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: `${platformPosition.x}%`,
                            top: `${platformPosition.y}%`,
                            width: `${platformPosition.width}px`,
                            height: `${platformPosition.height}px`,
                            backgroundColor: platformDisplayType === 'imagen' ? 'transparent' : platformBackgroundColor,
                            border: platformDisplayType === 'imagen' ? 'none' : '2px solid #7c3aed',
                            borderRadius: 1,
                            cursor: 'move',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: platformDisplayType === 'imagen' ? 'center' : 'flex-end',
                            padding: platformDisplayType === 'imagen' ? 0 : 1,
                            boxShadow: platformDisplayType === 'imagen' ? 'none' : '0 4px 8px rgba(0,0,0,0.3)',
                            transform: `rotate(${platformRotation}deg)`,
                            transformOrigin: 'center center',
                            overflow: platformDisplayType === 'imagen' ? 'hidden' : 'visible',
                            '&:hover': {
                              borderColor: platformDisplayType === 'imagen' ? 'transparent' : '#6d28d9',
                              boxShadow: platformDisplayType === 'imagen' ? 'none' : '0 4px 8px rgba(0,0,0,0.2)'
                            }
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            const startX = e.clientX;
                            const startY = e.clientY;
                            const startLeft = platformPosition.x;
                            const startTop = platformPosition.y;

                            const container = e.currentTarget?.parentElement;
                            if (!container) return;

                            const containerRect = container.getBoundingClientRect();

                            const handleMouseMove = (e: MouseEvent) => {
                              e.preventDefault();
                              const deltaX = e.clientX - startX;
                              const deltaY = e.clientY - startY;

                              const newX = Math.max(0, Math.min(100, startLeft + (deltaX / containerRect.width) * 100));
                              const newY = Math.max(0, Math.min(100, startTop + (deltaY / containerRect.height) * 100));

                              setPlatformPosition(prev => ({ ...prev, x: newX, y: newY }));
                            };

                            const handleMouseUp = () => {
                              document.removeEventListener('mousemove', handleMouseMove);
                              document.removeEventListener('mouseup', handleMouseUp);
                            };

                            document.addEventListener('mousemove', handleMouseMove);
                            document.addEventListener('mouseup', handleMouseUp);
                          }}
                        >
                          {/* Contenido según el tipo de display */}
                          {platformDisplayType === 'volante' && (
                            <>
                              {/* Logo con forma configurable */}
                              {platformLogoUrl ? (
                                <Box
                                  component="img"
                                  src={platformLogoUrl}
                                  alt="Logo"
                                  sx={{
                                    maxWidth: `${logoSize.width}%`,
                                    maxHeight: `${logoSize.height}%`,
                                    width: 'auto',
                                    height: 'auto',
                                    objectFit: 'contain',
                                    borderRadius: platformShape === 'circular' ? '50%' : 0,
                                    mb: 1
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <Box sx={{
                                  width: `${logoSize.width}%`,
                                  height: `${logoSize.height}%`,
                                  borderRadius: platformShape === 'circular' ? '50%' : 0,
                                  border: '2px solid black',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mb: 1,
                                  backgroundColor: '#7c3aed',
                                  color: 'white',
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}>
                                  LOGO
                                </Box>
                              )}

                              {/* Texto del concurso */}
                              <Typography
                                sx={{
                                  color: '#7c3aed',
                                  fontWeight: 'bold',
                                  fontSize: `${platformFontSize}rem`,
                                  textAlign: 'center',
                                  textTransform: 'uppercase',
                                  lineHeight: 1.2,
                                  mb: 1.5
                                }}
                              >
                                {t('platforms.contestName')}
                              </Typography>

                              {/* Texto personalizado */}
                              <Typography
                                sx={{
                                  color: '#7c3aed',
                                  fontWeight: 'bold',
                                  fontSize: `${platformFontSize}rem`,
                                  textAlign: 'center',
                                  textTransform: 'uppercase',
                                  lineHeight: 1.2,
                                  mt: 1.5
                                }}
                              >
                                {platformCustomText || 'your ad goes here'}
                              </Typography>
                            </>
                          )}

                          {platformDisplayType === 'imagen' && (
                            <>
                              {/* Solo imagen sin texto, sin borde ni fondo */}
                              {platformLogoUrl ? (
                                <Box
                                  component="img"
                                  src={platformLogoUrl}
                                  alt="Logo"
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: platformShape === 'circular' ? '50%' : 0
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <Box sx={{
                                  width: '100%',
                                  height: '100%',
                                  borderRadius: platformShape === 'circular' ? '50%' : 0,
                                  border: '2px solid black',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: '#7c3aed',
                                  color: 'white',
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}>
                                  LOGO
                                </Box>
                              )}
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ backgroundColor: '#f5f5f5', padding: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Box>
              {platformImage && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSelectedPlatformForPreview({
                      ...(editingPlatform || {}),
                      name: platformName || t('platforms.preview'),
                      image: platformImage,
                      x: platformPosition.x,
                      y: platformPosition.y,
                      width: platformPosition.width,
                      height: platformPosition.height,
                      Image_form: platformShape,
                      shape: platformShape,
                      logo_url: platformLogoUrl,
                      contest_text: t('platforms.contestName'),
                      custom_text: platformCustomText,
                      logo_width: logoSize.width,
                      logo_height: logoSize.height,
                      Text_size: platformFontSize,
                      rotation: platformRotation,
                      background_color: platformBackgroundColor,
                      display_type: platformDisplayType
                    });
                    setShowPlatformPreview(true);
                  }}
                  sx={{ mr: 1 }}
                >
                  {t('platforms.viewPreview')}
                </Button>
              )}
            </Box>
            <Box>
              <Button
                onClick={() => {
                  setShowAddPlatformModal(false);
                  if (!editingPlatform) {
                    setPlatformImage(null);
                    setPlatformImageFileName(null);
                    setPlatformImageError(false);
                    setPlatformName('');
                    setPlatformPosition({ x: 50, y: 50, width: 200, height: 150 });
                    setTempWidth('200');
                    setTempHeight('150');
                    setPlatformShape('circular');
                    setPlatformLogoUrl('');
                    setPlatformCustomText('your ad goes here');
                    setPlatformFontSize(0.9);
                    setPlatformRotation(0);
                    setTempRotation('0');
                    setPlatformBackgroundColor('#f5f5dc');
                    setLogoSize({ width: 60, height: 60 });
                    setTempLogoWidth('60');
                    setTempLogoHeight('60');
                  }
                  setEditingPlatform(null);
                }}
                variant="outlined"
                sx={{ borderColor: '#666', color: '#666', mr: 1 }}
              >
                {t('platforms.cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={async () => {
                  if (!platformName || !platformImage) {
                    void appDialog.alert(t('platforms.nameAndImageRequired'));
                    return;
                  }

                  try {
                    const platformData = {
                      name: platformName,
                      image: platformImage,
                      x: platformPosition.x,
                      y: platformPosition.y,
                      width: platformPosition.width,
                      height: platformPosition.height,
                      Image_form: platformShape,
                      logo_url: platformLogoUrl,
                      contest_text: t('platforms.contestName'),
                      custom_text: platformCustomText,
                      logo_width: logoSize.width,
                      logo_height: logoSize.height,
                      Text_size: platformFontSize,
                      rotation: platformRotation,
                      background_color: platformBackgroundColor,
                      display_type: platformDisplayType,
                      enabled: true
                    };

                    if (editingPlatform) {
                      // Actualizar
                      const response = await fetch(`${environment.apiBaseUrl}/platforms/${editingPlatform.id}`, {
                        method: 'PUT',
                        headers: getApiHeaders(),
                        body: JSON.stringify(platformData)
                      });
                      if (response.ok) {
                        const updatedPlatforms = await fetch(`${environment.apiBaseUrl}/platforms`, {
                          headers: getApiHeadersForGet()
                        }).then(r => r.json());
                        setPlatforms(updatedPlatforms);
                        setShowAddPlatformModal(false);
                        setEditingPlatform(null);
                      }
                    } else {
                      // Crear
                      const response = await fetch(`${environment.apiBaseUrl}/platforms`, {
                        method: 'POST',
                        headers: getApiHeaders(),
                        body: JSON.stringify(platformData)
                      });
                      if (response.ok) {
                        const updatedPlatforms = await fetch(`${environment.apiBaseUrl}/platforms`, {
                          headers: getApiHeadersForGet()
                        }).then(r => r.json());
                        setPlatforms(updatedPlatforms);
                        setShowAddPlatformModal(false);
                        setPlatformImage(null);
                        setPlatformImageFileName(null);
                        setPlatformName('');
                        setPlatformPosition({ x: 50, y: 50, width: 200, height: 150 });
                        setTempWidth('200');
                        setTempHeight('150');
                        setPlatformShape('circular');
                        setPlatformLogoUrl('');
                        setPlatformCustomText('your ad goes here');
                        setPlatformFontSize(0.9);
                        setLogoSize({ width: 60, height: 60 });
                        setTempLogoWidth('60');
                        setTempLogoHeight('60');
                      }
                    }
                  } catch (error) {
                    console.error('Error saving platform:', error);
                    void appDialog.alert(t('platforms.errorSaving'));
                  }
                }}
                sx={{ backgroundColor: '#7c3aed', '&:hover': { backgroundColor: '#6d28d9' } }}
              >
                {t('platforms.save')}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal Preview de Plataforma Base */}
      {showPlatformPreview && selectedPlatformForPreview && (
        <Dialog
          open={showPlatformPreview}
          onClose={() => setShowPlatformPreview(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6">
              {t('platforms.preview')} - {selectedPlatformForPreview.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                type="number"
                label={t('platforms.previewScale')}
                value={tempPreviewScale}
                onChange={(e) => {
                  setTempPreviewScale(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const value = parseFloat(tempPreviewScale);
                    if (!isNaN(value)) {
                      const clampedValue = Math.max(30, Math.min(100, value));
                      setPreviewScale(clampedValue);
                      setTempPreviewScale(String(clampedValue));
                    }
                  }
                }}
                inputProps={{
                  min: 30,
                  max: 100,
                  step: 1
                }}
                size="small"
                sx={{ width: '120px' }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  const value = parseFloat(tempPreviewScale);
                  if (!isNaN(value)) {
                    const clampedValue = Math.max(30, Math.min(100, value));
                    setPreviewScale(clampedValue);
                    setTempPreviewScale(String(clampedValue));
                  }
                }}
                sx={{ minWidth: 'auto' }}
              >
                {t('platforms.apply')}
              </Button>
              <Button onClick={() => {
                setShowPlatformPreview(false);
                setPreviewScale(100);
                setTempPreviewScale('100');
              }}>
                <X className="w-5 h-5" />
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedPlatformForPreview.image ? (
              <Box sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '800px',
                margin: '0 auto',
                borderRadius: 2,
                overflow: 'hidden',
                transform: `scale(${previewScale / 100})`,
                transformOrigin: 'center top'
              }}>
                {!previewImageError ? (
                  <img
                    src={selectedPlatformForPreview.image}
                    alt={selectedPlatformForPreview.name}
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                    onError={(e) => {
                      console.error('Error loading preview image:', selectedPlatformForPreview.image);
                      setPreviewImageError(true);
                      e.currentTarget.style.display = 'none';
                    }}
                    onLoad={() => {
                      setPreviewImageError(false);
                    }}
                    crossOrigin={selectedPlatformForPreview.image?.startsWith('data:') ? 'anonymous' : undefined}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      minHeight: '400px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f0f0f0',
                      border: '2px dashed #ccc',
                      color: '#666',
                      p: 3
                    }}
                  >
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>
                      {t('platforms.noImage')}
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: 'center', fontSize: '0.875rem' }}>
                      {selectedPlatformForPreview.image?.includes('slack.com') || selectedPlatformForPreview.image?.includes('files.slack.com')
                        ? 'Los enlaces de Slack no se pueden cargar directamente. Por favor, usa una URL pública de imagen o sube la imagen directamente.'
                        : 'No se pudo cargar la imagen. Verifica que la URL sea válida y accesible.'}
                    </Typography>
                  </Box>
                )}
                {/* Volante con datos por defecto (se reemplazará con datos del patrocinador) */}
                {((selectedPlatformForPreview.display_type || 'volante') !== 'nada') && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${selectedPlatformForPreview.x || 50}%`,
                      top: `${selectedPlatformForPreview.y || 50}%`,
                      width: `${selectedPlatformForPreview.width || 200}px`,
                      height: `${selectedPlatformForPreview.height || 150}px`,
                      backgroundColor: (selectedPlatformForPreview.display_type || 'volante') === 'imagen' ? 'transparent' : (selectedPlatformForPreview.background_color || '#f5f5dc'),
                      border: (selectedPlatformForPreview.display_type || 'volante') === 'imagen' ? 'none' : '2px solid #7c3aed',
                      borderRadius: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: (selectedPlatformForPreview.display_type || 'volante') === 'imagen' ? 'center' : 'flex-end',
                      padding: (selectedPlatformForPreview.display_type || 'volante') === 'imagen' ? 0 : 1,
                      boxShadow: (selectedPlatformForPreview.display_type || 'volante') === 'imagen' ? 'none' : '0 4px 8px rgba(0,0,0,0.3)',
                      transform: `rotate(${selectedPlatformForPreview.rotation || 0}deg)`,
                      transformOrigin: 'center center',
                      overflow: (selectedPlatformForPreview.display_type || 'volante') === 'imagen' ? 'hidden' : 'visible'
                    }}
                  >
                    {/* Contenido según el tipo de display */}
                    {(selectedPlatformForPreview.display_type || 'volante') === 'volante' && (
                      <>
                        {/* Logo con forma configurable */}
                        {selectedPlatformForPreview.logo_url ? (
                          <Box
                            component="img"
                            src={selectedPlatformForPreview.logo_url}
                            alt="Logo"
                            sx={{
                              maxWidth: `${selectedPlatformForPreview.logo_width || 60}%`,
                              maxHeight: `${selectedPlatformForPreview.logo_height || 60}%`,
                              width: 'auto',
                              height: 'auto',
                              objectFit: 'contain',
                              borderRadius: ((selectedPlatformForPreview.Image_form || selectedPlatformForPreview.shape || 'circular') === 'circular') ? '50%' : 0,
                              mb: 1
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <Box sx={{
                            width: `${selectedPlatformForPreview.logo_width || 60}%`,
                            height: `${selectedPlatformForPreview.logo_height || 60}%`,
                            borderRadius: ((selectedPlatformForPreview.Image_form || selectedPlatformForPreview.shape || 'circular') === 'circular') ? '50%' : 0,
                            border: '2px solid black',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 1,
                            backgroundColor: '#7c3aed',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            LOGO
                          </Box>
                        )}

                        {/* Texto del concurso */}
                        <Typography
                          sx={{
                            color: '#7c3aed',
                            fontWeight: 'bold',
                            fontSize: `${selectedPlatformForPreview.Text_size || 0.9}rem`,
                            textAlign: 'center',
                            textTransform: 'uppercase',
                            lineHeight: 1.2,
                            mb: 1.5
                          }}
                        >
                          {t('platforms.contestName')}
                        </Typography>

                        {/* Texto personalizado */}
                        <Typography
                          sx={{
                            color: '#7c3aed',
                            fontWeight: 'bold',
                            fontSize: `${selectedPlatformForPreview.Text_size || 0.9}rem`,
                            textAlign: 'center',
                            textTransform: 'uppercase',
                            lineHeight: 1.2,
                            mt: 1.5
                          }}
                        >
                          {selectedPlatformForPreview.custom_text || 'your ad goes here'}
                        </Typography>
                      </>
                    )}

                    {(selectedPlatformForPreview.display_type || 'volante') === 'imagen' && (
                      <>
                        {/* Solo imagen sin texto */}
                        {selectedPlatformForPreview.logo_url ? (
                          <OptimizedImage
                            src={selectedPlatformForPreview.logo_url}
                            alt="Logo"
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: ((selectedPlatformForPreview.Image_form || selectedPlatformForPreview.shape || 'circular') === 'circular') ? '50%' : 0
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <Box sx={{
                            width: '100%',
                            height: '100%',
                            borderRadius: ((selectedPlatformForPreview.Image_form || selectedPlatformForPreview.shape || 'circular') === 'circular') ? '50%' : 0,
                            border: '2px solid black',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#7c3aed',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            LOGO
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              <Typography>{t('platforms.noImage')}</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setShowPlatformPreview(false);
              setPreviewScale(100);
              setTempPreviewScale('100');
            }}>
              {t('buttonsforms.Close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para mostrar datos del usuario */}
      <Dialog
        open={showUserDataModal}
        onClose={() => setShowUserDataModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            Datos del Usuario Logueado
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Información completa del usuario obtenida desde localStorage y configuración:
            </Typography>

            <TextField
              fullWidth
              label="Datos del Usuario (JSON)"
              value={userDataText}
              onChange={(e) => setUserDataText(e.target.value)}
              multiline
              rows={20}
              variant="outlined"
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: '12px'
                }
              }}
              InputProps={{
                readOnly: true
              }}
            />

            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  navigator.clipboard.writeText(userDataText);
                  void appDialog.alert('Datos copiados al portapapeles');
                }}
                sx={{ flex: 1 }}
              >
                Copiar al Portapapeles
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUserDataModal(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para ingresar/actualizar token */}
      <Dialog
        open={showTokenInput}
        onClose={() => setShowTokenInput(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            Configurar Token de Autenticación
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ingresa el access_token de Petflix. El token se guardará en el archivo <code>src/config/petflixAuth.ts</code>
            </Typography>

            {/* Botón para obtener token automáticamente */}
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setShowTokenInput(false);
                setShowAdminLoginModal(true);
              }}
              sx={{
                mb: 2,
                borderColor: '#059669',
                color: '#059669',
                '&:hover': {
                  borderColor: '#047857',
                  backgroundColor: '#f0fdf4'
                }
              }}
            >
              🔐 Obtener Token Automáticamente (Login Admin)
            </Button>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
              ──── O ────
            </Typography>

            <TextField
              fullWidth
              label="Access Token"
              value={tokenInputValue}
              onChange={(e) => setTokenInputValue(e.target.value)}
              multiline
              rows={4}
              placeholder="Pega aquí tu access_token..."
              sx={{ mb: 2 }}
            />
            <Typography variant="caption" color="text.secondary">
              💡 Tip: Para actualizar el token permanentemente, edita el archivo <code>src/config/petflixAuth.ts</code> y coloca el token en la propiedad <code>accessToken</code>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTokenInput(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (tokenInputValue.trim()) {
                setPetflixAccessToken(tokenInputValue.trim());
                setShowTokenInput(false);
                setTokenInputValue('');
                // Cargar imágenes después de configurar el token
                loadTestImages(tokenInputValue.trim());
              } else {
                void appDialog.alert('Por favor, ingresa un token válido');
              }
            }}
            disabled={!tokenInputValue.trim()}
          >
            Guardar y Cargar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para login de admin y obtener token automáticamente */}
      <Dialog
        open={showAdminLoginModal}
        onClose={() => {
          setShowAdminLoginModal(false);
          setAdminPassword(''); // Limpiar contraseña por seguridad
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            🔐 Obtener Token de Admin Automáticamente
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Ingresa las credenciales de la cuenta de administrador para obtener el token automáticamente.
              El sistema hará login con Firebase y luego autenticará con Petflix para obtener el access token.
            </Typography>

            <TextField
              fullWidth
              label="Email de Admin"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="pavan-admin@yopmail.com"
              sx={{ mb: 2 }}
              disabled={loadingAdminToken}
            />

            <TextField
              fullWidth
              label="Contraseña"
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              placeholder="Ingresa tu contraseña"
              sx={{ mb: 2 }}
              disabled={loadingAdminToken}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !loadingAdminToken && adminEmail && adminPassword) {
                  getAdminToken();
                }
              }}
            />

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              ⚠️ Nota: La contraseña no se guardará, solo se usará para obtener el token.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAdminLoginModal(false);
              setAdminPassword(''); // Limpiar contraseña por seguridad
            }}
            disabled={loadingAdminToken}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={getAdminToken}
            disabled={!adminEmail || !adminPassword || loadingAdminToken}
            sx={{
              backgroundColor: '#059669',
              '&:hover': {
                backgroundColor: '#047857'
              }
            }}
          >
            {loadingAdminToken ? 'Obteniendo Token...' : 'Obtener Token'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de imágenes de prueba */}
      <Dialog
        open={showTestImagesModal}
        onClose={() => setShowTestImagesModal(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6">
            {t('buttonsforms.TestImages')} - Últimas 100 Imágenes
          </Typography>
          <Button onClick={() => setShowTestImagesModal(false)}>
            <X className="w-5 h-5" />
          </Button>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Mostrando imágenes de los últimos 100 posts
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => {
                  if (testImages.length > 0) {
                    processPetflixPosts(testImages);
                  } else {
                    void appDialog.alert('Primero debes cargar las imágenes de PetFlix');
                  }
                }}
                disabled={testImages.length === 0 || loadingTestImages}
              >
                Procesar Posts e Inscribir en Concursos
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setShowTokenInput(true);
                  setTokenInputValue(getPetflixAccessToken() || '');
                }}
              >
                Actualizar Token
              </Button>
            </Box>
          </Box>
          {loadingTestImages ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <Typography>Cargando imágenes...</Typography>
            </Box>
          ) : testImages.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <Typography>No se encontraron imágenes</Typography>
            </Box>
          ) : (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: 2,
              mt: 2
            }}>
              {testImages.map((post) => {
                // Obtener todas las imágenes del post
                const mediaItems = post.media || [];
                console.log('Procesando post:', post._id, 'Media items:', mediaItems.length);

                return mediaItems.map((mediaItem: any, mediaIndex: number) => {
                  const imageUrl = getImageUrlFromMedia(mediaItem);
                  console.log('Media item:', mediaItem, 'URL generada:', imageUrl);
                  if (!imageUrl) {
                    console.log('URL es null para mediaItem:', mediaItem);
                    return null;
                  }

                  return (
                    <Box
                      key={`${post._id}-${mediaItem._id || mediaIndex}`}
                      sx={{
                        position: 'relative',
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid #e5e7eb',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                        }
                      }}
                    >
                      <img
                        src={imageUrl}
                        alt={`Imagen ${mediaIndex + 1} del post ${post._id}`}
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          console.error('Error cargando imagen:', imageUrl);
                          e.currentTarget.style.display = 'none';
                        }}
                        loading="lazy"
                      />
                      {post.body && (
                        <Box sx={{ p: 1.5, borderTop: '1px solid #e5e7eb' }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#666',
                              fontSize: '0.875rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              mb: post.classifiers && post.classifiers.length > 0 ? 1 : 0
                            }}
                          >
                            {post.body}
                          </Typography>
                          {post.classifiers && post.classifiers.length > 0 && (
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {post.classifiers.map((classifier: any, classifierIndex: number) => (
                                <Chip
                                  key={classifierIndex}
                                  label={classifier.name || 'Sin nombre'}
                                  size="small"
                                  sx={{
                                    fontSize: '0.75rem',
                                    height: '20px'
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                          {post._id && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 1,
                                color: '#9ca3af',
                                fontSize: '0.7rem',
                                fontFamily: 'monospace'
                              }}
                            >
                              ID: {post._id}
                            </Typography>
                          )}
                          {post.user && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 0.5,
                                color: '#9ca3af',
                                fontSize: '0.7rem',
                                fontFamily: 'monospace'
                              }}
                            >
                              User ID: {typeof post.user === 'object' ? (post.user._id || post.user.id || 'N/A') : post.user}
                            </Typography>
                          )}
                        </Box>
                      )}
                      {!post.body && post.classifiers && post.classifiers.length > 0 && (
                        <Box sx={{ p: 1.5, borderTop: '1px solid #e5e7eb' }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {post.classifiers.map((classifier: any, classifierIndex: number) => (
                              <Chip
                                key={classifierIndex}
                                label={classifier.name || 'Sin nombre'}
                                size="small"
                                sx={{
                                  fontSize: '0.75rem',
                                  height: '20px'
                                }}
                              />
                            ))}
                          </Box>
                          {post._id && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 1,
                                color: '#9ca3af',
                                fontSize: '0.7rem',
                                fontFamily: 'monospace'
                              }}
                            >
                              ID: {post._id}
                            </Typography>
                          )}
                          {post.user && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 0.5,
                                color: '#9ca3af',
                                fontSize: '0.7rem',
                                fontFamily: 'monospace'
                              }}
                            >
                              User ID: {typeof post.user === 'object' ? (post.user._id || post.user.id || 'N/A') : post.user}
                            </Typography>
                          )}
                        </Box>
                      )}
                      {!post.body && (!post.classifiers || post.classifiers.length === 0) && post._id && (
                        <Box sx={{ p: 1.5, borderTop: '1px solid #e5e7eb' }}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              color: '#9ca3af',
                              fontSize: '0.7rem',
                              fontFamily: 'monospace'
                            }}
                          >
                            ID: {post._id}
                          </Typography>
                          {post.user && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 0.5,
                                color: '#9ca3af',
                                fontSize: '0.7rem',
                                fontFamily: 'monospace'
                              }}
                            >
                              User ID: {typeof post.user === 'object' ? (post.user._id || post.user.id || 'N/A') : post.user}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                });
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTestImagesModal(false)}>
            {t('buttonsforms.Close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para gestionar tipos de patrocinio */}
      {showSponsorshipTypesModal && (
        <Dialog
          open={showSponsorshipTypesModal}
          onClose={() => setShowSponsorshipTypesModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Settings className="w-5 h-5" style={{ color: '#7c3aed' }} />
              {t('platforms.sponsorshipTypes')}
            </Box>
            <Button
              onClick={() => setShowSponsorshipTypesModal(false)}
              sx={{ minWidth: 'auto', p: 1 }}
            >
              <X className="w-5 h-5" />
            </Button>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {loadingSponsorshipTypes ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography>{t('platforms.loading')}</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {sponsorshipTypes.map((type, index) => (
                  <Card key={type.value || index} sx={{ p: 2, border: '1px solid #e5e7eb' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <TextField
                        fullWidth
                        label={t('platforms.typeName')}
                        value={type.name || ''}
                        onChange={(e) => {
                          const newTypes = [...sponsorshipTypes];
                          newTypes[index] = {
                            ...type,
                            name: e.target.value,
                            platforms: Array.isArray(type.platforms) ? type.platforms : []
                          };
                          setSponsorshipTypes(newTypes);
                        }}
                        sx={{ mr: 2 }}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={async () => {
                            if (await appDialog.confirm(t('platforms.confirmDeleteType'), { destructive: true })) {
                              const newTypes = sponsorshipTypes.filter((_, i) => i !== index);
                              setSponsorshipTypes(newTypes);
                            }
                          }}
                        >
                          {t('platforms.deleteType')}
                        </Button>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t('platforms.selectPlatforms')}:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {availablePlatformsForTypes.map((platformId) => {
                        // Asegurar que type.platforms siempre sea un array
                        const typePlatforms = Array.isArray(type.platforms) ? type.platforms : [];
                        const isSelected = typePlatforms.includes(platformId);
                        return (
                          <Chip
                            key={platformId}
                            label={platformsMap[platformId] || platformId}
                            color={isSelected ? 'primary' : 'default'}
                            variant={isSelected ? 'filled' : 'outlined'}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newTypes = [...sponsorshipTypes];
                              // Asegurar que siempre sea un array
                              const currentPlatforms = Array.isArray(newTypes[index].platforms) ? newTypes[index].platforms : [];
                              if (currentPlatforms.includes(platformId)) {
                                newTypes[index] = {
                                  ...newTypes[index],
                                  platforms: currentPlatforms.filter((p: string) => p !== platformId)
                                };
                              } else {
                                newTypes[index] = {
                                  ...newTypes[index],
                                  platforms: [...currentPlatforms, platformId]
                                };
                              }
                              setSponsorshipTypes(newTypes);
                            }}
                            sx={{
                              cursor: 'pointer',
                              opacity: isSelected ? 1 : 0.7,
                              '&:hover': {
                                opacity: 1,
                                transform: 'scale(1.05)'
                              },
                              transition: 'all 0.2s'
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Card>
                ))}

                {sponsorshipTypes.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No hay tipos configurados. Agrega uno nuevo.
                    </Typography>
                  </Box>
                )}
                <Button
                  onClick={() => {
                    const newType = {
                      name: '',
                      value: `type_${Date.now()}`,
                      platforms: []
                    };
                    setSponsorshipTypes([...sponsorshipTypes, newType]);
                  }}
                  variant="outlined"
                  startIcon={<Plus />}
                  sx={{ mt: 2 }}
                >
                  {t('platforms.addSponsorshipType')}
                </Button>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={() => setShowSponsorshipTypesModal(false)}
              variant="outlined"
            >
              {t('buttonsforms.Close')}
            </Button>
            <Button
              onClick={async () => {
                try {
                  // Validar que todos los tipos tengan nombre
                  const invalidTypes = sponsorshipTypes.filter(t => !t.name || !t.name.trim());
                  if (invalidTypes.length > 0) {
                    void appDialog.alert('Todos los tipos deben tener un nombre');
                    return;
                  }

                  // Limpiar y normalizar los tipos antes de guardar
                  const normalizedTypes = sponsorshipTypes.map(t => ({
                    name: t.name.trim(),
                    value: t.value || `type_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    platforms: Array.isArray(t.platforms) ? t.platforms : []
                  }));

                  const response = await fetch(`${environment.apiBaseUrl}/platforms/types/sponsorship`, {
                    method: 'PUT',
                    headers: getApiHeaders(),
                    body: JSON.stringify(normalizedTypes)
                  });

                  if (response.ok) {
                    void appDialog.alert('Tipos de patrocinio guardados exitosamente');
                    setShowSponsorshipTypesModal(false);
                  } else {
                    const errorData = await response.json().catch(() => ({}));
                    void appDialog.alert('Error al guardar los tipos: ' + (errorData.error || 'Error desconocido'));
                  }
                } catch (error) {
                  console.error('Error saving sponsorship types:', error);
                  void appDialog.alert('Error al guardar los tipos');
                }
              }}
              variant="contained"
              sx={{
                backgroundColor: '#7c3aed',
                '&:hover': { backgroundColor: '#6d28d9' }
              }}
            >
              {t('platforms.saveTypes')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal de Estadísticas de Tags por Concurso */}
      {showTagStatisticsModal && selectedContestForTagStats && (
        <Dialog
          open={showTagStatisticsModal}
          onClose={() => {
            setShowTagStatisticsModal(false);
            setSelectedContestForTagStats(null);
            setTagStatistics(null);
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tag className="w-6 h-6" style={{ color: '#3b82f6' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('tagstatistics.title')} - {selectedContestForTagStats.name}
              </Typography>
            </Box>
            <IconButton onClick={() => {
              setShowTagStatisticsModal(false);
              setSelectedContestForTagStats(null);
              setTagStatistics(null);
            }}>
              <X className="w-5 h-5" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {loadingTagStatistics ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#6b7280' }}>
                  {t('tagstatistics.loadingStats')}
                </Typography>
              </Box>
            ) : tagStatistics ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 5 }}>
                {/* Resumen General */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Card sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px', p: 2, backgroundColor: '#f3e8ff' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        {t('tagstatistics.totalTags')}
                      </Typography>
                      <Tooltip
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                              {t('tagstatistics.totalTagsTooltip')}
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                        PopperProps={{
                          sx: {
                            '& .MuiTooltip-tooltip': {
                              maxWidth: '350px',
                              backgroundColor: '#1f2937',
                              fontSize: '0.875rem'
                            }
                          }
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{
                            p: 0,
                            color: '#6b7280',
                            '&:hover': { color: '#2563eb' }
                          }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#9333ea' }}>
                      {tagStatistics.total_tags || 0}
                    </Typography>
                  </Card>
                  <Card sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px', p: 2, backgroundColor: '#ecfdf5' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        {t('tagstatistics.tagsSold')}
                      </Typography>
                      <Tooltip
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                              {t('tagstatistics.tagsSoldTooltip')}
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                        PopperProps={{
                          sx: {
                            '& .MuiTooltip-tooltip': {
                              maxWidth: '350px',
                              backgroundColor: '#1f2937',
                              fontSize: '0.875rem'
                            }
                          }
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{
                            p: 0,
                            color: '#6b7280',
                            '&:hover': { color: '#2563eb' }
                          }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                      {tagStatistics.tags_sold || 0}
                    </Typography>
                  </Card>
                  <Card sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px', p: 2, backgroundColor: '#fef3c7' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        {t('tagstatistics.tagsAvailable')}
                      </Typography>
                      <Tooltip
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                              {t('tagstatistics.tagsAvailableTooltip')}
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                        PopperProps={{
                          sx: {
                            '& .MuiTooltip-tooltip': {
                              maxWidth: '350px',
                              backgroundColor: '#1f2937',
                              fontSize: '0.875rem'
                            }
                          }
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{
                            p: 0,
                            color: '#6b7280',
                            '&:hover': { color: '#2563eb' }
                          }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#d97706' }}>
                      {tagStatistics.tags_available || 0}
                    </Typography>
                  </Card>
                  <Card sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px', p: 2, backgroundColor: '#dbeafe' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        {t('tagstatistics.totalRevenue')}
                      </Typography>
                      <Tooltip
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                              {t('tagstatistics.totalRevenueTooltip')}
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                        PopperProps={{
                          sx: {
                            '& .MuiTooltip-tooltip': {
                              maxWidth: '350px',
                              backgroundColor: '#1f2937',
                              fontSize: '0.875rem'
                            }
                          }
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{
                            p: 0,
                            color: '#6b7280',
                            '&:hover': { color: '#2563eb' }
                          }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2563eb' }}>
                      ${(tagStatistics.total_revenue || 0).toFixed(2)}
                    </Typography>
                  </Card>
                </Box>

                {/* Estadísticas Adicionales */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Card sx={{ flex: '1 1 calc(33% - 16px)', minWidth: '200px', p: 2, backgroundColor: '#f0fdf4' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        {t('tagstatistics.potentialRevenue')}
                      </Typography>
                      <Tooltip
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                              {t('tagstatistics.potentialRevenueTooltip')}
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                        PopperProps={{
                          sx: {
                            '& .MuiTooltip-tooltip': {
                              maxWidth: '350px',
                              backgroundColor: '#1f2937',
                              fontSize: '0.875rem'
                            }
                          }
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{
                            p: 0,
                            color: '#6b7280',
                            '&:hover': { color: '#2563eb' }
                          }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#059669' }}>
                      ${(tagStatistics.potential_revenue || 0).toFixed(2)}
                    </Typography>
                  </Card>
                  <Card sx={{ flex: '1 1 calc(33% - 16px)', minWidth: '200px', p: 2, backgroundColor: '#fef2f2' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        {t('tagstatistics.ambassadorsWithSales')}
                      </Typography>
                      <Tooltip
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                              {t('tagstatistics.ambassadorsWithSalesTooltip')}
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                        PopperProps={{
                          sx: {
                            '& .MuiTooltip-tooltip': {
                              maxWidth: '350px',
                              backgroundColor: '#1f2937',
                              fontSize: '0.875rem'
                            }
                          }
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{
                            p: 0,
                            color: '#6b7280',
                            '&:hover': { color: '#2563eb' }
                          }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#dc2626' }}>
                      {tagStatistics.ambassadors_with_sales || 0}
                    </Typography>
                  </Card>
                  <Card sx={{ flex: '1 1 calc(33% - 16px)', minWidth: '200px', p: 2, backgroundColor: '#f5f3ff' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        {t('tagstatistics.sponsorsWithTags')}
                      </Typography>
                      <Tooltip
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                              {t('tagstatistics.sponsorsWithTagsTooltip')}
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                        PopperProps={{
                          sx: {
                            '& .MuiTooltip-tooltip': {
                              maxWidth: '350px',
                              backgroundColor: '#1f2937',
                              fontSize: '0.875rem'
                            }
                          }
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{
                            p: 0,
                            color: '#6b7280',
                            '&:hover': { color: '#2563eb' }
                          }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#7c3aed' }}>
                      {tagStatistics.sponsors_with_tags || 0}
                    </Typography>
                  </Card>
                </Box>

                {/* Distribución por Tier */}
                {tagStatistics.tier_distribution && tagStatistics.tier_distribution.length > 0 && (
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                      {t('tagstatistics.distributionBySponsorTier')}
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('tagstatistics.tier')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('tagstatistics.totalTagsHeader')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('tagstatistics.tagsSoldHeader')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('tagstatistics.revenue')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tagStatistics.tier_distribution.map((tier: any, index: number) => (
                            <TableRow key={index}>
                              <TableCell>{tier.tier}</TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>{tier.total_tags || 0}</TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>{tier.tags_sold || 0}</TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>${(tier.revenue || 0).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Distribución por Tipo */}
                {tagStatistics.type_distribution && tagStatistics.type_distribution.length > 0 && (
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                      {t('tagstatistics.distributionByType')}
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('tagstatistics.type')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('tagstatistics.totalTagsHeader')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('tagstatistics.tagsSoldHeader')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('tagstatistics.revenue')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tagStatistics.type_distribution.map((type: any, index: number) => (
                            <TableRow key={index}>
                              <TableCell>{type.tag_type}</TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>{type.total_tags || 0}</TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>{type.tags_sold || 0}</TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>${(type.revenue || 0).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            ) : (
              <Alert severity="info">
                {t('tagstatistics.noStatsAvailable')}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={() => {
                setShowTagStatisticsModal(false);
                setSelectedContestForTagStats(null);
                setTagStatistics(null);
              }}
              variant="outlined"
            >
              {t('tagstatistics.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal de Gestión de Referral Bonuses */}
      {showBonusManagementModal && selectedAmbassadorForBonus && (
        <Dialog
          open={showBonusManagementModal}
          onClose={() => {
            setShowBonusManagementModal(false);
            setSelectedAmbassadorForBonus(null);
            setBonusHistory([]);
            setBonusTagsQuantity('0');
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Award className="w-6 h-6" style={{ color: '#f59e0b' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('bonusmanagement.title')} - {selectedAmbassadorForBonus.name}
              </Typography>
            </Box>
            <IconButton onClick={() => {
              setShowBonusManagementModal(false);
              setSelectedAmbassadorForBonus(null);
              setBonusHistory([]);
              setBonusTagsQuantity('0');
            }}>
              <X className="w-5 h-5" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {loadingBonusHistory ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#6b7280' }}>
                  {t('bonusmanagement.loading')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 5 }}>
                {/* Información del Embajador */}
                <Card sx={{ p: 2, backgroundColor: '#f9fafb' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {t('bonusmanagement.ambassadorInfo')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="body2">
                      <strong>{t('bonusmanagement.email')}</strong> {selectedAmbassadorForBonus.email}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2">
                        <strong>{t('bonusmanagement.signedSponsors')}</strong> {selectedAmbassadorForBonus.total_sponsors_signed || 0}
                      </Typography>
                      <Tooltip
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                              {t('bonusmanagement.signedSponsorsTooltip')}
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                        PopperProps={{
                          sx: {
                            '& .MuiTooltip-tooltip': {
                              maxWidth: '350px',
                              backgroundColor: '#1f2937',
                              fontSize: '0.875rem'
                            }
                          }
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{
                            p: 0,
                            color: '#6b7280',
                            '&:hover': { color: '#2563eb' }
                          }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2">
                        <strong>{t('bonusmanagement.bonusTagsReceived')}</strong> {selectedAmbassadorForBonus.bonus_tags_received || 0}
                      </Typography>
                      <Tooltip
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                              {t('bonusmanagement.bonusTagsReceivedTooltip')}
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                        PopperProps={{
                          sx: {
                            '& .MuiTooltip-tooltip': {
                              maxWidth: '350px',
                              backgroundColor: '#1f2937',
                              fontSize: '0.875rem'
                            }
                          }
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{
                            p: 0,
                            color: '#6b7280',
                            '&:hover': { color: '#2563eb' }
                          }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    {selectedAmbassadorForBonus.recruiter_badge && (
                      <Tooltip
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                              {t('bonusmanagement.recruiterBadgeTooltip')}
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                        PopperProps={{
                          sx: {
                            '& .MuiTooltip-tooltip': {
                              maxWidth: '350px',
                              backgroundColor: '#1f2937',
                              fontSize: '0.875rem'
                            }
                          }
                        }}
                      >
                        <Chip label={t('bonusmanagement.recruiterBadge')} size="small" color="primary" />
                      </Tooltip>
                    )}
                    {selectedAmbassadorForBonus.senior_badge && (
                      <Tooltip
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                              {t('bonusmanagement.seniorBadgeTooltip')}
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                        PopperProps={{
                          sx: {
                            '& .MuiTooltip-tooltip': {
                              maxWidth: '350px',
                              backgroundColor: '#1f2937',
                              fontSize: '0.875rem'
                            }
                          }
                        }}
                      >
                        <Chip label={t('bonusmanagement.seniorBadge')} size="small" color="secondary" />
                      </Tooltip>
                    )}
                    {selectedAmbassadorForBonus.elite_badge && (
                      <Tooltip
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                              {t('bonusmanagement.eliteBadgeTooltip')}
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                        PopperProps={{
                          sx: {
                            '& .MuiTooltip-tooltip': {
                              maxWidth: '350px',
                              backgroundColor: '#1f2937',
                              fontSize: '0.875rem'
                            }
                          }
                        }}
                      >
                        <Chip label={t('bonusmanagement.eliteBadge')} size="small" sx={{ backgroundColor: '#fbbf24', color: '#fff' }} />
                      </Tooltip>
                    )}
                  </Box>
                </Card>

                {/* Asignar Badges */}
                <Card sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {t('bonusmanagement.assignBadgesManually')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                            {t('bonusmanagement.recruiterBadgeTooltip')}
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                      PopperProps={{
                        sx: {
                          '& .MuiTooltip-tooltip': {
                            maxWidth: '350px',
                            backgroundColor: '#1f2937',
                            fontSize: '0.875rem'
                          }
                        }
                      }}
                    >
                      <Button
                        variant={selectedAmbassadorForBonus.recruiter_badge ? "contained" : "outlined"}
                        color="primary"
                        size="small"
                        onClick={() => handleAssignBadge('recruiter')}
                        disabled={assigningBadge || selectedAmbassadorForBonus.recruiter_badge}
                      >
                        {selectedAmbassadorForBonus.recruiter_badge ? t('bonusmanagement.recruiterAssigned') : t('bonusmanagement.assignRecruiter')}
                      </Button>
                    </Tooltip>
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                            {t('bonusmanagement.seniorBadgeTooltip')}
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                      PopperProps={{
                        sx: {
                          '& .MuiTooltip-tooltip': {
                            maxWidth: '350px',
                            backgroundColor: '#1f2937',
                            fontSize: '0.875rem'
                          }
                        }
                      }}
                    >
                      <Button
                        variant={selectedAmbassadorForBonus.senior_badge ? "contained" : "outlined"}
                        color="secondary"
                        size="small"
                        onClick={() => handleAssignBadge('senior')}
                        disabled={assigningBadge || selectedAmbassadorForBonus.senior_badge}
                      >
                        {selectedAmbassadorForBonus.senior_badge ? t('bonusmanagement.seniorAssigned') : t('bonusmanagement.assignSenior')}
                      </Button>
                    </Tooltip>
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                            {t('bonusmanagement.eliteBadgeTooltip')}
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                      PopperProps={{
                        sx: {
                          '& .MuiTooltip-tooltip': {
                            maxWidth: '350px',
                            backgroundColor: '#1f2937',
                            fontSize: '0.875rem'
                          }
                        }
                      }}
                    >
                      <Button
                        variant={selectedAmbassadorForBonus.elite_badge ? "contained" : "outlined"}
                        size="small"
                        sx={{
                          backgroundColor: selectedAmbassadorForBonus.elite_badge ? '#fbbf24' : 'transparent',
                          color: selectedAmbassadorForBonus.elite_badge ? '#fff' : '#fbbf24',
                          borderColor: '#fbbf24',
                          '&:hover': {
                            backgroundColor: selectedAmbassadorForBonus.elite_badge ? '#fbbf24' : '#fef3c7',
                            borderColor: '#fbbf24'
                          }
                        }}
                        onClick={() => handleAssignBadge('elite')}
                        disabled={assigningBadge || selectedAmbassadorForBonus.elite_badge}
                      >
                        {selectedAmbassadorForBonus.elite_badge ? t('bonusmanagement.eliteAssigned') : t('bonusmanagement.assignElite')}
                      </Button>
                    </Tooltip>
                  </Box>
                </Card>

                {/* Otorgar Bonus Tags */}
                <Card sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {t('bonusmanagement.grantBonusTags')}
                    </Typography>
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                            {t('bonusmanagement.grantBonusTagsTooltip')}
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                      PopperProps={{
                        sx: {
                          '& .MuiTooltip-tooltip': {
                            maxWidth: '350px',
                            backgroundColor: '#1f2937',
                            fontSize: '0.875rem'
                          }
                        }
                      }}
                    >
                      <IconButton
                        size="small"
                        sx={{
                          p: 0,
                          color: '#6b7280',
                          '&:hover': { color: '#2563eb' }
                        }}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TextField
                        label={t('bonusmanagement.tagsQuantity')}
                        type="number"
                        value={bonusTagsQuantity}
                        onChange={(e) => setBonusTagsQuantity(e.target.value)}
                        inputProps={{ min: 1 }}
                        sx={{ width: '150px' }}
                      />
                      <Tooltip
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>
                              {t('bonusmanagement.tagsQuantityTooltip')}
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                        PopperProps={{
                          sx: {
                            '& .MuiTooltip-tooltip': {
                              maxWidth: '350px',
                              backgroundColor: '#1f2937',
                              fontSize: '0.875rem'
                            }
                          }
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{
                            p: 0,
                            color: '#6b7280',
                            '&:hover': { color: '#2563eb' }
                          }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleGrantBonusTags}
                      disabled={grantingTags || parseInt(bonusTagsQuantity) <= 0}
                    >
                      {grantingTags ? t('bonusmanagement.granting') : t('bonusmanagement.grantTags')}
                    </Button>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', mt: 1, display: 'block' }}>
                    {t('bonusmanagement.grantTagsHelper')}
                  </Typography>
                </Card>

                {/* Historial de Bonuses */}
                <Card sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {t('bonusmanagement.bonusHistory')}
                  </Typography>
                  {bonusHistory && bonusHistory.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('bonusmanagement.type')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('bonusmanagement.description')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('bonusmanagement.quantity')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('bonusmanagement.date')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {bonusHistory.map((item: any, index: number) => (
                            <TableRow key={index}>
                              <TableCell>
                                {item.type === 'badge' ? (
                                  <Chip
                                    label={t('bonusmanagement.badge')}
                                    size="small"
                                    color={item.badge_type === 'recruiter' ? 'primary' : item.badge_type === 'senior' ? 'secondary' : 'default'}
                                  />
                                ) : (
                                  <Chip label={t('bonusmanagement.bonusTags')} size="small" color="success" />
                                )}
                              </TableCell>
                              <TableCell>{item.description}</TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                {item.bonus_tags || item.quantity || 0}
                                {item.custom_tag && t('bonusmanagement.customTag')}
                              </TableCell>
                              <TableCell>
                                {item.date ? new Date(item.date).toLocaleDateString() : 'N/A'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">
                      {t('bonusmanagement.noHistory')}
                    </Alert>
                  )}
                </Card>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={() => {
                setShowBonusManagementModal(false);
                setSelectedAmbassadorForBonus(null);
                setBonusHistory([]);
                setBonusTagsQuantity('0');
              }}
              variant="outlined"
            >
              {t('bonusmanagement.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal de Reporte de Sponsors por Tier */}
      {showSponsorTierReportModal && (
        <Dialog
          open={showSponsorTierReportModal}
          onClose={() => {
            setShowSponsorTierReportModal(false);
            setSponsorTierStats(null);
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Building2 className="w-6 h-6" style={{ color: '#dc2626' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('sponsortierreport.title')}
              </Typography>
            </Box>
            <IconButton onClick={() => {
              setShowSponsorTierReportModal(false);
              setSponsorTierStats(null);
            }}>
              <X className="w-5 h-5" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {loadingSponsorTierStats ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#6b7280' }}>
                  {t('sponsortierreport.loadingStats')}
                </Typography>
              </Box>
            ) : sponsorTierStats ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Estadísticas Generales */}
                {sponsorTierStats.general_stats && (
                  <Card sx={{ p: 2, backgroundColor: '#f9fafb' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                      {t('sponsortierreport.generalStats')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">{t('sponsortierreport.totalSponsors')}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {sponsorTierStats.general_stats.total_sponsors || 0}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">{t('sponsortierreport.active')}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#059669' }}>
                          {sponsorTierStats.general_stats.total_active || 0}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">{t('sponsortierreport.inactive')}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#dc2626' }}>
                          {sponsorTierStats.general_stats.total_inactive || 0}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">{t('sponsortierreport.totalRevenue')}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2563eb' }}>
                          ${(sponsorTierStats.general_stats.total_revenue_all || 0).toFixed(2)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">{t('sponsortierreport.tagsAssigned')}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {sponsorTierStats.general_stats.total_tags_all || 0}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                )}

                {/* Distribución por Tier */}
                {sponsorTierStats.tier_distribution && sponsorTierStats.tier_distribution.length > 0 && (
                  <Card sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                      {t('sponsortierreport.distributionByTier')}
                    </Typography>

                    {/* Gráfico de Barras - Revenue por Tier (versión simple) */}
                    {sponsorTierStats.tier_distribution && sponsorTierStats.tier_distribution.length > 0 && (
                      <Card sx={{ p: 2, mb: 3, backgroundColor: '#ffffff' }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, color: '#6b7280' }}>
                          {t('sponsortierreport.revenueByTier')}
                        </Typography>
                        <SimpleBarChart
                          data={sponsorTierStats.tier_distribution.map((tier: any) => ({
                            label: tier.tier === 'gold' ? t('sponsortierreport.gold') : tier.tier === 'silver' ? t('sponsortierreport.silver') : tier.tier === 'bronze' ? t('sponsortierreport.bronze') : tier.tier,
                            value: Number(tier.total_revenue || 0),
                            maxValue: Math.max(...sponsorTierStats.tier_distribution.map((t: any) => Number(t.total_revenue || 0)))
                          }))}
                        />
                      </Card>
                    )}

                    {/* Gráfico de Pastel - Distribución de Sponsors por Tier (versión simple) */}
                    {sponsorTierStats.tier_distribution && sponsorTierStats.tier_distribution.length > 0 && (
                      <Card sx={{ p: 2, mb: 3, backgroundColor: '#ffffff' }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, color: '#6b7280' }}>
                          {t('sponsortierreport.sponsorsDistributionByTier')}
                        </Typography>
                        <SimplePieChart
                          data={sponsorTierStats.tier_distribution.map((tier: any, index: number) => ({
                            label: tier.tier === 'gold' ? t('sponsortierreport.gold') : tier.tier === 'silver' ? t('sponsortierreport.silver') : tier.tier === 'bronze' ? t('sponsortierreport.bronze') : tier.tier,
                            value: Number(tier.total_sponsors || 0),
                            color: ['#fbbf24', '#94a3b8', '#cd7f32', '#6b7280'][index % 4]
                          }))}
                        />
                      </Card>
                    )}

                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('sponsortierreport.tier')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('sponsortierreport.totalSponsorsHeader')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('sponsortierreport.activeHeader')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('sponsortierreport.inactiveHeader')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('sponsortierreport.revenue')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('sponsortierreport.tagsAssignedHeader')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('sponsortierreport.potentialRevenue')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('sponsortierreport.efficiency')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sponsorTierStats.tier_distribution.map((tier: any, index: number) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Chip
                                  label={tier.tier === 'gold' ? t('sponsortierreport.gold') : tier.tier === 'silver' ? t('sponsortierreport.silver') : tier.tier === 'bronze' ? t('sponsortierreport.bronze') : tier.tier}
                                  size="small"
                                  sx={{
                                    backgroundColor: tier.tier === 'gold' ? '#fbbf24' : tier.tier === 'silver' ? '#9ca3af' : tier.tier === 'bronze' ? '#cd7f32' : '#6b7280',
                                    color: '#fff',
                                    fontWeight: 'bold'
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>{tier.total_sponsors || 0}</TableCell>
                              <TableCell sx={{ textAlign: 'center', color: '#059669' }}>
                                {tier.active_sponsors || 0}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center', color: '#dc2626' }}>
                                {tier.inactive_sponsors || 0}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                                ${(tier.total_revenue || 0).toFixed(2)}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>
                                {tier.total_tags_assigned || 0}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right', color: '#6b7280' }}>
                                ${(tier.potential_revenue || 0).toFixed(2)}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                <Chip
                                  label={`${(tier.revenue_efficiency || 0).toFixed(1)}%`}
                                  size="small"
                                  color={tier.revenue_efficiency > 50 ? 'success' : tier.revenue_efficiency > 25 ? 'warning' : 'default'}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Card>
                )}

                {/* Distribución de Tags Vendidos por Tier */}
                {sponsorTierStats.tag_distribution && sponsorTierStats.tag_distribution.length > 0 && (
                  <Card sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                      {t('sponsortierreport.tagsSoldByTier')}
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('sponsortierreport.tier')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('sponsortierreport.tagsSold')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('sponsortierreport.revenueFromTags')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sponsorTierStats.tag_distribution.map((tier: any, index: number) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Chip
                                  label={tier.tier === 'gold' ? t('sponsortierreport.gold') : tier.tier === 'silver' ? t('sponsortierreport.silver') : tier.tier === 'bronze' ? t('sponsortierreport.bronze') : tier.tier}
                                  size="small"
                                  sx={{
                                    backgroundColor: tier.tier === 'gold' ? '#fbbf24' : tier.tier === 'silver' ? '#9ca3af' : tier.tier === 'bronze' ? '#cd7f32' : '#6b7280',
                                    color: '#fff',
                                    fontWeight: 'bold'
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>{tier.tags_sold || 0}</TableCell>
                              <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', color: '#059669' }}>
                                ${(tier.revenue_from_tags || 0).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Card>
                )}
              </Box>
            ) : (
              <Alert severity="info">
                {t('sponsortierreport.noStatsAvailable')}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={() => {
                setShowSponsorTierReportModal(false);
                setSponsorTierStats(null);
              }}
              variant="outlined"
            >
              {t('sponsortierreport.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal de Diagrama de Flujo Económico */}
      {showEconomyFlowDiagramModal && (
        <Dialog
          open={showEconomyFlowDiagramModal}
          onClose={() => {
            setShowEconomyFlowDiagramModal(false);
            setEconomyFlowData(null);
          }}
          maxWidth="xl"
          fullWidth
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 'bold',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUp className="w-6 h-6" style={{ color: '#10b981' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('economyflow.diagramTitle')}
              </Typography>
            </Box>
            <IconButton onClick={() => {
              setShowEconomyFlowDiagramModal(false);
              setEconomyFlowData(null);
            }}>
              <X className="w-5 h-5" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {loadingEconomyFlowData ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#6b7280' }}>
                  {t('economyflow.loading')}
                </Typography>
              </Box>
            ) : economyFlowData ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {/* Resumen General */}
                {economyFlowData.summary && (
                  <Card sx={{ p: 2, backgroundColor: '#f9fafb' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                      {t('economyflow.generalSummary')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">{t('economyflow.totalContests')}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {economyFlowData.summary.total_contests || 0}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">{t('economyflow.totalSponsors')}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2563eb' }}>
                          {economyFlowData.summary.total_sponsors || 0}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">{t('economyflow.totalAmbassadors')}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#9333ea' }}>
                          {economyFlowData.summary.total_ambassadors || 0}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">{t('economyflow.totalParticipants')}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#059669' }}>
                          {economyFlowData.summary.total_participants || 0}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">{t('economyflow.totalRevenue')}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                          ${(economyFlowData.summary.total_revenue || 0).toFixed(2)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">{t('economyflow.netProfit')}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f59e0b' }}>
                          ${(economyFlowData.summary.total_profit || 0).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                )}

                {/* Diagrama de Flujo */}
                {economyFlowData.flow && (
                  <Card sx={{ p: 3, backgroundColor: '#ffffff' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, textAlign: 'center' }}>
                      {t('economyflow.flowSubtitle')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'stretch', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                      {/* Sponsors */}
                      <Card sx={{ flex: '1 1 200px', minWidth: '180px', minHeight: '140px', p: 2, backgroundColor: '#dbeafe', border: '2px solid #3b82f6', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', textAlign: 'center' }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e40af', mb: 1, textAlign: 'center' }}>
                            SPONSORS
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1e3a8a', mb: 0.5, textAlign: 'center' }}>
                            ${(economyFlowData.flow.sponsors?.revenue || 0).toFixed(2)}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mt: 'auto', textAlign: 'center' }}>
                          {economyFlowData.flow.sponsors?.description || t('economyflow.sponsorsDescription')}
                        </Typography>
                      </Card>

                      <ArrowRight className="w-8 h-8" style={{ color: '#6b7280', flexShrink: 0, alignSelf: 'center' }} />

                      {/* Noah's Ark Initial */}
                      <Card sx={{ flex: '1 1 200px', minWidth: '180px', minHeight: '140px', p: 2, backgroundColor: '#fef3c7', border: '2px solid #fbbf24', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', textAlign: 'center' }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#92400e', mb: 1, textAlign: 'center' }}>
                            NOAH'S ARK
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#b45309', mb: 0.5, textAlign: 'center' }}>
                            ${(economyFlowData.flow.noahs_ark_initial?.revenue || 0).toFixed(2)}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mt: 'auto', textAlign: 'center' }}>
                          {economyFlowData.flow.noahs_ark_initial?.description || t('economyflow.noahsArkReceivesFunding')}
                        </Typography>
                      </Card>

                      <ArrowRight className="w-8 h-8" style={{ color: '#6b7280', flexShrink: 0, alignSelf: 'center' }} />

                      {/* Ambassadors */}
                      <Card sx={{ flex: '1 1 200px', minWidth: '180px', minHeight: '140px', p: 2, backgroundColor: '#f3e8ff', border: '2px solid #a855f7', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', textAlign: 'center' }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#6b21a8', mb: 1, textAlign: 'center' }}>
                            AMBASSADORS
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#7c3aed', mb: 0.5, textAlign: 'center' }}>
                            ${(economyFlowData.flow.ambassadors?.revenue || 0).toFixed(2)}
                          </Typography>
                          {economyFlowData.flow.ambassadors?.tags_sold > 0 && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#9333ea', fontWeight: 'bold', textAlign: 'center' }}>
                              {t('economyflow.tagsSold')} {economyFlowData.flow.ambassadors.tags_sold}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mt: 'auto', textAlign: 'center' }}>
                          {economyFlowData.flow.ambassadors?.description || t('economyflow.ambassadorsDescription')}
                        </Typography>
                      </Card>

                      <ArrowRight className="w-8 h-8" style={{ color: '#6b7280', flexShrink: 0, alignSelf: 'center' }} />

                      {/* Participants */}
                      <Card sx={{ flex: '1 1 200px', minWidth: '180px', minHeight: '140px', p: 2, backgroundColor: '#ecfdf5', border: '2px solid #10b981', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', textAlign: 'center' }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#065f46', mb: 1, textAlign: 'center' }}>
                            PARTICIPANTS
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#047857', mb: 0.5, textAlign: 'center' }}>
                            ${(economyFlowData.flow.participants?.revenue || 0).toFixed(2)}
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" sx={{ display: 'block', color: '#059669', textAlign: 'center' }}>
                              {t('economyflow.entries')} ${(economyFlowData.flow.participants?.entrance_fees || 0).toFixed(2)}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', color: '#f59e0b', textAlign: 'center' }}>
                              {t('economyflow.boosters')} ${(economyFlowData.flow.participants?.vote_boosters || 0).toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mt: 'auto', textAlign: 'center' }}>
                          {economyFlowData.flow.participants?.description || t('economyflow.participantsDescription')}
                        </Typography>
                      </Card>

                      <ArrowRight className="w-8 h-8" style={{ color: '#6b7280', flexShrink: 0, alignSelf: 'center' }} />

                      {/* Noah's Ark Final */}
                      <Card sx={{ flex: '1 1 200px', minWidth: '180px', minHeight: '140px', p: 2, backgroundColor: '#fef3c7', border: '2px solid #fbbf24', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', textAlign: 'center' }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#92400e', mb: 1, textAlign: 'center' }}>
                            NOAH'S ARK
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#b45309', mb: 0.5, textAlign: 'center' }}>
                            ${(economyFlowData.flow.noahs_ark_final?.profit || 0).toFixed(2)}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#10b981', fontWeight: 'bold', textAlign: 'center' }}>
                            {t('economyflow.revenueTotal')} ${(economyFlowData.flow.noahs_ark_final?.total_revenue || 0).toFixed(2)}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mt: 'auto', textAlign: 'center' }}>
                          {economyFlowData.flow.noahs_ark_final?.description || t('economyflow.noahsArkNetProfit')}
                        </Typography>
                      </Card>
                    </Box>
                  </Card>
                )}

                {/* Los 3 Loops de Valor */}
                {economyFlowData.value_loops && (
                  <Card sx={{ p: 3, backgroundColor: '#ffffff' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, textAlign: 'center' }}>
                      {t('economyflow.threeValueLoops')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {/* Sponsor Value Loop */}
                      <Paper elevation={2} sx={{ p: 3, backgroundColor: '#eff6ff', borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1e40af', mb: 1 }}>
                          {t('economyflow.sponsorValueLoopTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {economyFlowData.value_loops.sponsor_value_loop?.description || t('economyflow.sponsorValueLoopDescription')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">{t('economyflow.totalSponsorsLabel')}</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e40af' }}>
                              {economyFlowData.value_loops.sponsor_value_loop?.metrics?.total_sponsors || 0}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">{t('economyflow.totalInvestment')}</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e40af' }}>
                              ${(economyFlowData.value_loops.sponsor_value_loop?.metrics?.total_investment || 0).toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>

                      {/* Ambassador Profit Loop */}
                      <Paper elevation={2} sx={{ p: 3, backgroundColor: '#faf5ff', borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#6b21a8', mb: 1 }}>
                          {t('economyflow.ambassadorProfitLoopTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {economyFlowData.value_loops.ambassador_profit_loop?.description || t('economyflow.ambassadorProfitLoopDescription')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">{t('economyflow.totalAmbassadorsLabel')}</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#6b21a8' }}>
                              {economyFlowData.value_loops.ambassador_profit_loop?.metrics?.total_ambassadors || 0}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">{t('economyflow.tagsSoldLabel')}</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#6b21a8' }}>
                              {economyFlowData.value_loops.ambassador_profit_loop?.metrics?.total_tags_sold || 0}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">{t('economyflow.totalRevenue')}</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#6b21a8' }}>
                              ${(economyFlowData.value_loops.ambassador_profit_loop?.metrics?.total_revenue || 0).toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>

                      {/* Platform Revenue Loop */}
                      <Paper elevation={2} sx={{ p: 3, backgroundColor: '#f0fdf4', borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#065f46', mb: 1 }}>
                          {t('economyflow.platformRevenueLoopTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {economyFlowData.value_loops.platform_revenue_loop?.description || t('economyflow.platformRevenueLoopDescription')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">{t('economyflow.totalContestsLabel')}</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#065f46' }}>
                              {economyFlowData.value_loops.platform_revenue_loop?.metrics?.total_contests || 0}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">{t('economyflow.totalParticipantsLabel')}</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#065f46' }}>
                              {economyFlowData.value_loops.platform_revenue_loop?.metrics?.total_participants || 0}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">{t('economyflow.voteBoosters')}</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f59e0b' }}>
                              ${(economyFlowData.value_loops.platform_revenue_loop?.metrics?.total_vote_boosters || 0).toFixed(2)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">{t('economyflow.totalRevenue')}</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                              ${(economyFlowData.value_loops.platform_revenue_loop?.metrics?.total_revenue || 0).toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Box>
                  </Card>
                )}
              </Box>
            ) : (
              <Alert severity="info">
                {t('economyflow.noDataAvailable')}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={() => {
                setShowEconomyFlowDiagramModal(false);
                setEconomyFlowData(null);
              }}
              variant="outlined"
            >
              {t('economyflow.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal de Índice de Planillas */}
      {showImportsModal && (
        <Dialog
          open={showImportsModal}
          onClose={() => setShowImportsModal(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', pb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Índice de Planillas Importadas
            </Typography>
            <IconButton onClick={() => setShowImportsModal(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            {loadingImports ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : imports.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No hay planillas importadas aún
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>ID/Nombre</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Tipo de Fuente</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Archivo</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Total Filas</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Procesadas</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Errores</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {imports.map((importItem: any) => (
                      <TableRow key={importItem.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {importItem.import_name || importItem.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={importItem.source_type || 'N/A'}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {importItem.file_name || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={importItem.status || 'pending'}
                            size="small"
                            color={
                              importItem.status === 'committed' ? 'success' :
                                importItem.status === 'error' ? 'error' :
                                  importItem.status === 'staging' ? 'warning' :
                                    'default'
                            }
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          {importItem.total_rows || 0}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', color: '#10b981', fontWeight: 'medium' }}>
                          {importItem.processed_rows || 0}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', color: '#ef4444', fontWeight: 'medium' }}>
                          {importItem.error_rows || 0}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {importItem.created_at ? new Date(importItem.created_at).toLocaleDateString() : 'N/A'}
                          </Typography>
                          {importItem.committed_at && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Aplicado: {new Date(importItem.committed_at).toLocaleDateString()}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Eye className="w-4 h-4" />}
                            onClick={async () => {
                              setSelectedImport(importItem);
                              setLoadingMappings(true);
                              try {
                                const mappings = await importService.getFieldMappings(importItem.id);
                                setFieldMappings(mappings);
                                setShowMappingsModal(true);
                              } catch (error) {
                                console.error('Error loading mappings:', error);
                                void appDialog.alert('Error al cargar los mapeos');
                              } finally {
                                setLoadingMappings(false);
                              }
                            }}
                          >
                            Ver Mapeos
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button onClick={() => setShowImportsModal(false)} variant="outlined">
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal de Mapeos de Campos */}
      {showMappingsModal && selectedImport && (
        <Dialog
          open={showMappingsModal}
          onClose={() => {
            setShowMappingsModal(false);
            setSelectedImport(null);
            setFieldMappings([]);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', pb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Mapeos de Campos
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {selectedImport.import_name || selectedImport.id}
              </Typography>
            </Box>
            <IconButton onClick={() => {
              setShowMappingsModal(false);
              setSelectedImport(null);
              setFieldMappings([]);
            }} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            {loadingMappings ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : fieldMappings.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No hay mapeos configurados para esta planilla
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Campo Origen (Planilla)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Tabla Destino</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Campo Destino</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Tipo de Dato</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Requerido</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Transformación</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fieldMappings.map((mapping: any) => (
                      <TableRow key={mapping.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {mapping.source_field}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={mapping.target_table}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {mapping.target_field}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {mapping.data_type || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {mapping.is_required ? (
                            <Chip label="Sí" size="small" color="error" />
                          ) : (
                            <Chip label="No" size="small" color="default" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {mapping.transformation || 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button onClick={() => {
              setShowMappingsModal(false);
              setSelectedImport(null);
              setFieldMappings([]);
            }} variant="outlined">
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal de GEO-IP */}
      <Dialog
        open={showGeoIpModal}
        onClose={() => {
          setShowGeoIpModal(false);
          setGeoIpData(null);
          setGeoIpError(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e5e7eb',
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <MapPin className="w-5 h-5" style={{ color: '#7c3aed' }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Información de Ubicación GEO-IP
            </Typography>
          </Box>
          <IconButton onClick={() => {
            setShowGeoIpModal(false);
            setGeoIpData(null);
            setGeoIpError(null);
          }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {loadingGeoIp ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', flexDirection: 'column', gap: 2 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Obteniendo información de ubicación...
              </Typography>
            </Box>
          ) : geoIpError ? (
            <Box sx={{ p: 3 }}>
              <Box sx={{
                p: 2,
                backgroundColor: '#fef3c7',
                borderRadius: 2,
                border: '1px solid #fbbf24',
                mb: 2
              }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#92400e', mb: 1 }}>
                  ⚠️ Información
                </Typography>
                <Typography variant="body2" sx={{ color: '#78350f' }}>
                  {geoIpError}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={fetchGeoIpData}
                  sx={{
                    borderColor: '#7c3aed',
                    color: '#7c3aed',
                    '&:hover': {
                      borderColor: '#6d28d9',
                      backgroundColor: '#f3e8ff'
                    }
                  }}
                >
                  Reintentar
                </Button>
              </Box>
            </Box>
          ) : geoIpData ? (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {geoIpData.isPrivate && geoIpData.localIp && (
                  <Box sx={{
                    p: 1.5,
                    backgroundColor: '#fef3c7',
                    borderRadius: 2,
                    border: '1px solid #fbbf24'
                  }}>
                    <Typography variant="body2" sx={{ color: '#78350f', fontSize: '0.875rem' }}>
                      ℹ️ IP privada detectada: {geoIpData.localIp}. Mostrando ubicación de IP pública.
                    </Typography>
                  </Box>
                )}
                <Paper sx={{ p: 2, backgroundColor: '#f9fafb', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#6b7280', mb: 1 }}>
                    Dirección IP {geoIpData.isPrivate ? '(Pública)' : ''}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#111827' }}>
                    {geoIpData.ip || 'N/A'}
                  </Typography>
                  {geoIpData.localIp && (
                    <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5, fontSize: '0.75rem' }}>
                      IP local: {geoIpData.localIp}
                    </Typography>
                  )}
                </Paper>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Paper sx={{ p: 2, backgroundColor: '#f9fafb', borderRadius: 2, flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#6b7280', mb: 1 }}>
                      País
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#111827' }}>
                      {geoIpData.country || 'N/A'} {geoIpData.countryCode ? `(${geoIpData.countryCode})` : ''}
                    </Typography>
                  </Paper>

                  <Paper sx={{ p: 2, backgroundColor: '#f9fafb', borderRadius: 2, flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#6b7280', mb: 1 }}>
                      Región
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#111827' }}>
                      {geoIpData.regionName || geoIpData.region || 'N/A'}
                    </Typography>
                  </Paper>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Paper sx={{ p: 2, backgroundColor: '#f9fafb', borderRadius: 2, flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#6b7280', mb: 1 }}>
                      Ciudad
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#111827' }}>
                      {geoIpData.city || 'N/A'}
                    </Typography>
                  </Paper>

                  <Paper sx={{ p: 2, backgroundColor: '#f9fafb', borderRadius: 2, flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#6b7280', mb: 1 }}>
                      Código Postal
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#111827' }}>
                      {geoIpData.zip || 'N/A'}
                    </Typography>
                  </Paper>
                </Box>

                {geoIpData.coordinates && (geoIpData.coordinates.latitude || geoIpData.coordinates.longitude) && (
                  <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Paper sx={{ p: 2, backgroundColor: '#f9fafb', borderRadius: 2, flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#6b7280', mb: 1 }}>
                          Latitud
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#111827' }}>
                          {geoIpData.coordinates.latitude?.toFixed(6) || 'N/A'}
                        </Typography>
                      </Paper>

                      <Paper sx={{ p: 2, backgroundColor: '#f9fafb', borderRadius: 2, flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#6b7280', mb: 1 }}>
                          Longitud
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#111827' }}>
                          {geoIpData.coordinates.longitude?.toFixed(6) || 'N/A'}
                        </Typography>
                      </Paper>
                    </Box>

                    {geoIpData.coordinates.latitude && geoIpData.coordinates.longitude && (
                      <Paper sx={{ p: 2, backgroundColor: '#f9fafb', borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#6b7280', mb: 1 }}>
                          Coordenadas
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#111827', fontFamily: 'monospace' }}>
                          {geoIpData.coordinates.latitude.toFixed(6)}, {geoIpData.coordinates.longitude.toFixed(6)}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<MapPin className="w-4 h-4" />}
                          onClick={() => {
                            const url = `https://www.google.com/maps?q=${geoIpData.coordinates.latitude},${geoIpData.coordinates.longitude}`;
                            window.open(url, '_blank');
                          }}
                          sx={{ mt: 1 }}
                        >
                          Ver en Google Maps
                        </Button>
                      </Paper>
                    )}
                  </>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Paper sx={{ p: 2, backgroundColor: '#f9fafb', borderRadius: 2, flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#6b7280', mb: 1 }}>
                      Zona Horaria
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#111827' }}>
                      {geoIpData.timezone || 'N/A'}
                    </Typography>
                  </Paper>

                  <Paper sx={{ p: 2, backgroundColor: '#f9fafb', borderRadius: 2, flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#6b7280', mb: 1 }}>
                      ISP
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#111827' }}>
                      {geoIpData.isp || 'N/A'}
                    </Typography>
                  </Paper>
                </Box>

                {geoIpData.org && (
                  <Paper sx={{ p: 2, backgroundColor: '#f9fafb', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#6b7280', mb: 1 }}>
                      Organización
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#111827' }}>
                      {geoIpData.org || 'N/A'}
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Haz clic en el botón para obtener tu ubicación
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
          <Button
            onClick={fetchGeoIpData}
            variant="outlined"
            disabled={loadingGeoIp}
            startIcon={<MapPin className="w-4 h-4" />}
          >
            Actualizar
          </Button>
          <Button
            onClick={() => {
              setShowGeoIpModal(false);
              setGeoIpData(null);
              setGeoIpError(null);
            }}
            variant="contained"
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de subida a S3 */}
      <S3UploadModal
        open={s3UploadModalOpen}
        onClose={() => setS3UploadModalOpen(false)}
      />

      {/* Modal para generar Word con imagen */}
      <Dialog
        open={showWordDocModal}
        onClose={() => {
          setShowWordDocModal(false);
          setWordDocImageUrl('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e5e7eb',
          pb: 2
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t('buttonsforms.generateWordDoc')}
          </Typography>
          <IconButton onClick={() => {
            setShowWordDocModal(false);
            setWordDocImageUrl('');
          }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="URL de la imagen"
              value={wordDocImageUrl}
              onChange={(e) => setWordDocImageUrl(e.target.value)}
              placeholder="Pega aquí la URL de la imagen (S3, etc.)"
              fullWidth
              variant="outlined"
              helperText="Ejemplo: https://tu-bucket.s3.amazonaws.com/ruta/imagen.jpg"
            />
            <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
              Ingresa la URL completa de la imagen que deseas insertar en el documento Word.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
          <Button
            onClick={() => {
              setShowWordDocModal(false);
              setWordDocImageUrl('');
            }}
            variant="outlined"
          >
            {t('buttonsforms.Cancel')}
          </Button>
          <Button
            onClick={handleGenerateWordDoc}
            variant="contained"
            disabled={!wordDocImageUrl || wordDocLoading}
            startIcon={wordDocLoading ? <CircularProgress size={20} /> : <FileText className="w-4 h-4" />}
            sx={{
              backgroundColor: '#8b5cf6',
              '&:hover': {
                backgroundColor: '#7c3aed',
              }
            }}
          >
            {wordDocLoading ? 'Generando...' : 'Generar .docx'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para eliminar tags */}
      <DeleteTagsModal
        open={showDeleteTagsModal}
        onClose={() => setShowDeleteTagsModal(false)}
      />
    </div>
  );
};

export default PetContestDashboard;